import React, { useState, useEffect, useCallback, useRef } from 'react';
import Papa from 'papaparse';
import {
  DndContext,
  DragOverlay,
  useSensor,
  useSensors,
  PointerSensor,
  KeyboardSensor,
  closestCenter,
  useDroppable,
  useDraggable
} from '@dnd-kit/core';
import {
  sortableKeyboardCoordinates
} from '@dnd-kit/sortable';
import './BoardArena.css';
import './BoardArena_Battle.css';
import './BoardArena_Battle_Vertical.css';
import WarmupTutorial from './WarmupTutorial';
import BattleTutorial from './BattleTutorial';

const getFaceUrl = (cardId) => encodeURI(`/Kickopp Player Faces/${cardId}.png`);

// Toggle: board cards show player photo + flag (new layout) vs the original
// position/stat-only layout (backup). Set to false to revert instantly.
const BOARD_CARD_PHOTOS = true;

// ============================================================================
// DROPPABLE SLOT COMPONENT
// ============================================================================
const DroppableSlot = ({ id, children, className, onClick, dataColIndex, dataRowIndex }) => {
  const { isOver, setNodeRef } = useDroppable({ id });

  return (
    <div
      ref={setNodeRef}
      className={`${className} ${isOver ? 'drag-over' : ''}`}
      onClick={onClick}
      data-col-index={dataColIndex}
      data-row-index={dataRowIndex}
    >
      {children}
    </div>
  );
};

// ============================================================================
// DRAGGABLE CARD COMPONENT
// ============================================================================
const DraggableCard = ({ id, data, children, className, disabled, keepInPlace }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id,
    data,
    disabled
  });

  const style = transform ? {
    transform: keepInPlace ? 'none' : `translate3d(${transform.x}px, ${transform.y}px, 0)`,
    // During normal drag, hide original card. During Kickopp (keepInPlace), keep card visible
    opacity: (isDragging && !keepInPlace) ? 0 : 1,
  } : undefined;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`${className} ${isDragging ? 'dragging' : ''}`}
      {...listeners}
      {...attributes}
    >
      {children}
    </div>
  );
};

// ============================================================================
// ACTION CARD CONTENT RENDERER
// Position colours matching getCardTypeColor:
//   Keeper (#fbbf24) · DefCtr (#ef4444) · DefWide (#f97316)
//   MidCtr (#10b981) · MidWide (#a855f7) · FwdCtr/FwdWide (#3b82f6)
// Stat colours: Attack=#4258E0 (blue)  Defense=#E74C3C (red)
// ============================================================================



// ============================================================================
// ACTION CARDS DATA
// ============================================================================

/**
 * BoardArena Component
 *
 * Main game component for the Kickopp strategic card game. This component manages
 * the entire game flow from draft through battle to completion.
 *
 * GAME STAGES:
 *
 * 1. DRAFT (Initial Phase)
 *    - Players take turns selecting and placing 11 player cards on the board
 *    - Cards must be placed according to formation rules (min 1 Keeper, 2 DefenderCenter,
 *      2 MidfielderCenter, 1 Forward, and exactly 0 or 2 Wide positions)
 *    - Players can select up to 3 tactic cards during draft
 *    - Completed tactic formations at end of draft earn bonus boosts in Warm Up
 *
 * 2. WARMUP (Pre-Battle Setup)
 *    - Players apply boosts (attack/defense) earned from completed tactics
 *    - Boosts must be distributed according to tactic requirements
 *    - Players select one captain card (gets extra life during battle)
 *    - After both players complete warm up, battle begins
 *
 * 3. BATTLE (Main Gameplay)
 *    - Turn-based tactical gameplay using Action Points (AP) system
 *    - Players start with 1 AP and can increase it using Upgrade action
 *    - Available actions:
 *      * Kickopp (1 AP): Attack opponent cards, column distance adds bonus
 *      * Upgrade (1 AP): Increase max AP by 1 (once per turn)
 *      * Move (1+ AP): Move card to different row (1 AP per row change)
 *      * Substitute (3 AP): Replace a card with one from deck
 *      * Buy Boosts (2-3 AP): Purchase attack/defense boosts
 *      * Add Boost (1+ AP): Apply purchased boosts to cards
 *    - Successful Kickopp attacks earn K pawns (reduce future AP costs)
 *    - Captains have 2 lives (survive first elimination)
 *    - Last line penalty: Player with rightmost/leftmost occupied column
 *      gets bonus boosts at start of their turn
 *    - Game ends when: 21 total turns completed, or player voluntarily abandons
 *
 * 4. END (Game Over)
 *    - Final scores calculated based on eliminated cards
 *    - Displays winner or draw, with detailed score breakdown
 *    - Option to restart game
 *
 * KEY FEATURES:
 * - Drag-and-drop card placement and movement
 * - Real-time formation validation with visual feedback
 * - Dynamic tactic completion checking
 * - Event log tracking all game actions
 * - Captain mechanic (double elimination required)
 * - K pawn system (attack cost reduction)
 * - Column distance bonus for Kickopp attacks
 * - Comprehensive boost system (warmup and battle boosts)
 * - Turn-based AP management with strategic depth
 *
 * @component
 * @returns {JSX.Element} The complete game interface
 */
// ─── AI helpers (pure, defined outside component) ───────────────────────────

// Standard Poisson random (Knuth algorithm)
const poissonRandom = (lambda) => {
  if (lambda <= 0) return 0;
  const L = Math.exp(-lambda);
  let k = 0, p = 1;
  do { k++; p *= Math.random(); } while (p > L);
  return k - 1;
};

// Fallback when no stage config: level-based count (used in non-tournament play)
const computeAiBoostCount = (level) => {
  const base = level;
  const fluctuation = Math.floor(Math.random() * Math.ceil(level * 0.7));
  return Math.min(base + fluctuation, 10);
};

const AI_PLAYER1_COLS    = [0, 1, 3, 5];
const AI_PLAYER2_COLS    = [2, 4, 6, 7];
const AI_ATK_COL   = { 1: 5, 2: 2 }; // forward col per player
const AI_MID_COL   = { 1: 3, 2: 4 };
const AI_DEF_COL   = { 1: 1, 2: 6 };
const AI_KEEP_COL  = { 1: 0, 2: 7 };
const SUBSTITUTE_MIN_GAIN = 4; // min combined ATK+DEF gain to justify a 3 AP substitute
const KPAWN_DEFENSE_PRIORITY = 100; // priority boost for defending a K-pawn holder
const CAPTAIN_DEFENSE_PRIORITY = 50; // priority boost for defending the captain
const ATTACK_RISK_TOLERANCE = 1; // margin window for preferring a safer attack over the single best-margin one

const BoardArena = ({ soundEnabled, onNavigate, gameplayMusicRef, setGameEnded: setAppGameEnded, gameMode = 'basic', squadData = null, isSolo = false, aiPlayer = null, verticalBoard = false, cpuStage = null, challengeMode = false, onChallengeEnd = null, challengeActionCardIds = null, challengeDifficulty = null, challengeLives = null, requireSuperWin = false, onShowTutorial = null }) => {
  // ============================================================================
  // STATE VARIABLES
  // ============================================================================

  // Stage-specific tutorial overlays (shown on first visit to each stage)
  const [showWarmupTutorial, setShowWarmupTutorial] = useState(false);
  const [showBattleTutorial, setShowBattleTutorial] = useState(false);

  // Core game data and UI state
  const [loading, setLoading] = useState(true);
  const [cardGameZones, setCardGameZones] = useState({});
  const [decksByPosition, setDecksByPosition] = useState({});
  const [decksByPositionP2, setDecksByPositionP2] = useState({});
  const [draggedCard, setDraggedCard] = useState(null);
  const [dragPosition, setDragPosition] = useState({ x: 0, y: 0 });
  const dragPositionRef = useRef({ x: 0, y: 0 }); // Ref for immediate access (no stale state)
  const [highlightedSlots, setHighlightedSlots] = useState([]);

  // dnd-kit state
  const [, setActiveId] = useState(null);
  const [, setActiveDragData] = useState(null);

  // dnd-kit sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Require 8px movement before drag starts (prevents accidental drags)
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  const [recycledCardsCount, setRecycledCardsCount] = useState(0);
  const [selectedDeck, setSelectedDeck] = useState(null);
  const [visibleCards, setVisibleCards] = useState([]);
  const [cardSelections, setCardSelections] = useState({
    toPlay: null,
    toBench: null,
    toDiscard: null
  });
  const [draftCardForPlacement, setDraftCardForPlacement] = useState(null); // Card ready to be placed via click
  const [showInstructions, setShowInstructions] = useState(true);

  // Tactic cards state
  const [player1Tactics, setPlayer1Tactics] = useState([]);
  const [player2Tactics, setPlayer2Tactics] = useState([]);
  const [allTacticsData, setAllTacticsData] = useState([]); // full list used for CPU tactic-based draft

  // Turn-based system state
  const [gameStage, setGameStage] = useState('draft'); // 'draft', 'warmup', 'battle', 'end'
  const [currentPlayer, setCurrentPlayer] = useState(1); // 1 or 2
  const [cardsPlaced, setCardsPlaced] = useState(0); // Track total cards placed (0-22)

  // Track each player's formation counts
  const [player1Formation, setPlayer1Formation] = useState({
    Keeper: 0,
    DefenderCenter: 0,
    DefenderWide: 0,
    MidfielderCenter: 0,
    MidfielderWide: 0,
    Forward: 0
  });

  const [player2Formation, setPlayer2Formation] = useState({
    Keeper: 0,
    DefenderCenter: 0,
    DefenderWide: 0,
    MidfielderCenter: 0,
    MidfielderWide: 0,
    Forward: 0
  });

  // Warm Up stage state - track which tactic was completed at end of draft
  const [player1CompletedTactic, setPlayer1CompletedTactic] = useState(null);
  const [player2CompletedTactic, setPlayer2CompletedTactic] = useState(null);

  // Warm Up boost state
  const [player1AvailableBoosts, setPlayer1AvailableBoosts] = useState({ attBoost: 0, defBoost: 0, totalBoosts: 0 });
  const [player2AvailableBoosts, setPlayer2AvailableBoosts] = useState({ attBoost: 0, defBoost: 0, totalBoosts: 0 });

  // Track applied boosts for each card (stored by card ID)
  const [player1AppliedBoosts, setPlayer1AppliedBoosts] = useState({});
  const [player2AppliedBoosts, setPlayer2AppliedBoosts] = useState({});

  // Selected card panel — portrait shown below GK column when clicking any board card
  const [selectedBoardCard, setSelectedBoardCard] = useState(null);

  // Battle stage - Purchased boosts available to spend
  const [player1BattleBoosts, setPlayer1BattleBoosts] = useState({ attack: 0, defense: 0 });
  const [player2BattleBoosts, setPlayer2BattleBoosts] = useState({ attack: 0, defense: 0 });

  // Battle stage - Player scores (eliminated opponent cards, +1 for captain)
  const [player1Score, setPlayer1Score] = useState(0);
  const [player2Score, setPlayer2Score] = useState(0);

  // Battle stage - Boost purchase/apply mode
  const [boostMode, setBoostMode] = useState(null); // 'buy_single', 'buy_both', 'add_boost'
  const [boostStep, setBoostStep] = useState(null); // 'select_type', 'select_card', 'apply_boost'
  const [boostSelectedCard, setBoostSelectedCard] = useState(null); // card to apply boost to
  const [pendingBoosts, setPendingBoosts] = useState({}); // Pending boosts before confirmation: { [Card_Id]: { attack: 0, defense: 0 } }

  // Track captain selection (stored by card ID)
  const [player1Captain, setPlayer1Captain] = useState(null);
  const [player2Captain, setPlayer2Captain] = useState(null);

  // Track former captains (captains who lost status but still on board - for +1 bonus on elimination)
  const [player1FormerCaptain, setPlayer1FormerCaptain] = useState(null);
  const [player2FormerCaptain, setPlayer2FormerCaptain] = useState(null);

  // Track warm up turn completion
  const [warmUpPlayer1Done, setWarmUpPlayer1Done] = useState(false);
  const [warmUpPlayer2Done, setWarmUpPlayer2Done] = useState(false);

  // Track Random All mode - separate state for each player
  const [player1RandomAllActive, setPlayer1RandomAllActive] = useState(false);
  const [player2RandomAllActive, setPlayer2RandomAllActive] = useState(false);
  const [player1RandomAllStartCount, setPlayer1RandomAllStartCount] = useState(0);
  const [player2RandomAllStartCount, setPlayer2RandomAllStartCount] = useState(0);

  // Warm Up selected card for boost application
  const [warmUpSelectedCard, setWarmUpSelectedCard] = useState(null);

  // Event Log - tracks all game events
  const [eventLog, setEventLog] = useState([]);
  const [eventLogExpanded, setEventLogExpanded] = useState(false);
  const [actionCardTooltip, setActionCardTooltip] = useState(null); // { name, content } shown on click of an action card title in the event log

  // Battle stage - Action Points system
  const [player1ActionPoints, setPlayer1ActionPoints] = useState(1); // Start with 1 AP
  const [player2ActionPoints, setPlayer2ActionPoints] = useState(1); // Start with 1 AP
  const [player1MaxActionPoints, setPlayer1MaxActionPoints] = useState(1); // Max AP (increases with Upgrade)
  const [player2MaxActionPoints, setPlayer2MaxActionPoints] = useState(1); // Max AP (increases with Upgrade)
  // Refs mirror AP state synchronously so buy/play guards can't be beaten by rapid clicks
  // before React re-renders with the new state value.
  const player1APRef = useRef(1);
  const player2APRef = useRef(1);
  useEffect(() => { player1APRef.current = player1ActionPoints; }, [player1ActionPoints]);
  useEffect(() => { player2APRef.current = player2ActionPoints; }, [player2ActionPoints]);
  
  // Track if player used Upgrade in their current turn (to add +1 AP in next turn)
  const [, setPlayer1UpgradeUsedThisTurn] = useState(false);
  const [, setPlayer2UpgradeUsedThisTurn] = useState(false);

  // Track the starting AP for each player's current turn (used to calculate next turn's starting AP)
  const [, setPlayer1StartingAPThisTurn] = useState(1);
  const [, setPlayer2StartingAPThisTurn] = useState(1);

  // Battle stage - Selected Action Card (mutually exclusive: Kickopp or Upgrade)
  const [player1SelectedActionCard, setPlayer1SelectedActionCard] = useState(null); // 'kickopp' or 'upgrade' or null
  const [player2SelectedActionCard, setPlayer2SelectedActionCard] = useState(null); // 'kickopp' or 'upgrade' or null

  // Battle stage - Track once-per-turn actions (Kickopp and Upgrade)
  const [player1UsedKickopp, setPlayer1UsedKickopp] = useState(false); // true if Kickopp used this turn
  const [player2UsedKickopp, setPlayer2UsedKickopp] = useState(false);
  const [player1UsedUpgrade, setPlayer1UsedUpgrade] = useState(false); // true if Upgrade used this turn
  const [player2UsedUpgrade, setPlayer2UsedUpgrade] = useState(false);
  const [pendingActionCard, setPendingActionCard] = useState(null); // { card, handIndex } — waiting for AP warning confirm
  const [player1ExtraMainActions, setPlayer1ExtraMainActions] = useState(0); // extra main action uses from A_012
  const [player2ExtraMainActions, setPlayer2ExtraMainActions] = useState(0);

  // Battle stage - Kickopp selection state
  const [kickoppMode, setKickoppMode] = useState(false); // true when in kickopp drag mode

  // Battle stage - Move selection state
  const [moveMode, setMoveMode] = useState(false); // true when in move drag mode
  const [moveOriginalPosition, setMoveOriginalPosition] = useState(null); // stores original position {colIndex, rowIndex} when move starts
  const [freeMovesActive, setFreeMovesActive] = useState(false); // true when A_004 Move On has been played this turn
  const [freeSubsRemaining, setFreeSubsRemaining] = useState(0); // remaining free substitutions from A_007/A_016
  const [freeBoostsActive, setFreeBoostsActive] = useState(false); // true when A_011 All Boost In has been played this turn
  const [doubleBoostsActive, setDoubleBoostsActive] = useState(false); // true when A_013 Extra Boost has been played this turn
  const [attackAnyoneActive, setAttackAnyoneActive] = useState(false); // true when A_015 Attack Anyone has been played this turn
  const [ignoreOppDefActive, setIgnoreOppDefActive] = useState(false); // true when A_020 Ignore Opp Def has been played this turn
  const [removeBoostMode, setRemoveBoostMode] = useState(false); // true when A_017 Remove Boost is active — waiting for opponent card selection
  const [activeDefencePlayer, setActiveDefencePlayer] = useState(null); // 1 or 2 — which player's cards have the A_018 +2 DEF shield active
  const [player1PendingKickoppBonus, setPlayer1PendingKickoppBonus] = useState(0); // accumulated +N from A_005/A_014/A_021
  const [player2PendingKickoppBonus, setPlayer2PendingKickoppBonus] = useState(0);

  // Battle stage - Confirmation popup for spending last AP without Upgrade/Kickopp
  const [showAPConfirmation, setShowAPConfirmation] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // Stores the action to execute after confirmation

  // Battle stage - Substitute selection state
  const [substituteMode, setSubstituteMode] = useState(false); // true when in substitute mode
  const [substituteStep, setSubstituteStep] = useState(null); // 'select_board_card', 'confirm_remove', 'select_deck', 'select_card', 'place_card'
  const [substituteRemovedCard, setSubstituteRemovedCard] = useState(null); // card removed from board {card, position, wasCaptain, wasFormerCaptain}
  const [substitutePendingCard, setSubstitutePendingCard] = useState(null); // card selected but not yet confirmed for removal
  const [substituteSelectedDeck, setSubstituteSelectedDeck] = useState(null); // deck type selected for substitute
  const [substituteVisibleCards, setSubstituteVisibleCards] = useState([]); // 2 cards shown for selection
  const [substituteSelectedCard, setSubstituteSelectedCard] = useState(null); // card chosen to play

  // Battle stage - Kickopp Inactive tracking (K pawns after successful attacks)
  const [player1KickoppPawns, setPlayer1KickoppPawns] = useState([]); // Array of { cardId, order } - FIFO queue
  const [player2KickoppPawns, setPlayer2KickoppPawns] = useState([]); // Array of { cardId, order } - FIFO queue
  const [player1LessInactives, setPlayer1LessInactives] = useState(false); // A_006: reduces max K pawns by 1 per level
  const [player1CardsPlayedThisTurn, setPlayer1CardsPlayedThisTurn] = useState([]); // A_022: track cards played this turn
  const [player2CardsPlayedThisTurn, setPlayer2CardsPlayedThisTurn] = useState([]);
  const [player1BoomerangActive, setPlayer1BoomerangActive] = useState(false); // A_022: return all played cards at turn end
  const [player2BoomerangActive, setPlayer2BoomerangActive] = useState(false);
  const [player1ExtraTurnActive, setPlayer1ExtraTurnActive] = useState(false); // A_023: extra turn pending
  const [player2ExtraTurnActive, setPlayer2ExtraTurnActive] = useState(false);
  const [player2LessInactives, setPlayer2LessInactives] = useState(false);

  // Battle stage - Turn counter for each player
  const [player1TurnCount, setPlayer1TurnCount] = useState(0);
  const [player2TurnCount, setPlayer2TurnCount] = useState(0);

  // Solo PvE — CPU boost bank removed; training boosts go directly into player1/2BattleBoosts

  // Battle stage - Action Card decks (shared between both players)
  const [actionDeck1, setActionDeck1] = useState([]); // 1-AP buy cost cards (face-down)
  const [actionDeck3, setActionDeck3] = useState([]); // 3-AP buy cost cards (face-down)
  const [actionDeck5, setActionDeck5] = useState([]); // 5-AP buy cost cards (face-down)
  const [revealedCard1, setRevealedCard1] = useState(null); // face-up card for 1-AP deck
  const [revealedCard3, setRevealedCard3] = useState(null); // face-up card for 3-AP deck
  const [revealedCard5, setRevealedCard5] = useState(null); // face-up card for 5-AP deck
  const [player1Hand, setPlayer1Hand] = useState([]);  // bought cards for P1
  const [player2Hand, setPlayer2Hand] = useState([]);  // bought cards for P2
  const [player1HandOffset, setPlayer1HandOffset] = useState(0); // carousel page for P1
  const [player2HandOffset, setPlayer2HandOffset] = useState(0); // carousel page for P2
  const [a003ChoiceState, setA003ChoiceState] = useState(null); // { card, count, attackSplit } for A_003 boost split
  const [holdingCardKey, setHoldingCardKey] = useState(null); // key of card being held to play
  const holdTimerRef = useRef(null);
  const liveGameStateRef = useRef({}); // always-current AP/flags, updated every render
  const [holdingButtonId, setHoldingButtonId] = useState(null); // id of action/finish button being held
  const buttonHoldTimerRef = useRef(null);

  // Solo / AI state — string message shown in overlay, null when not thinking
  const [aiThinking, setAiThinking] = useState(null);
  const aiTimerRef = useRef(null);

  // Boost flash animation — fires when boost pool increases
  const [p1BoostFlash, setP1BoostFlash] = useState(false);
  const [p2BoostFlash, setP2BoostFlash] = useState(false);
  const p1BoostPrev = useRef(0);
  const p2BoostPrev = useRef(0);
  useEffect(() => {
    const total = player1BattleBoosts.attack + player1BattleBoosts.defense;
    if (total > p1BoostPrev.current) { setP1BoostFlash(true); setTimeout(() => setP1BoostFlash(false), 700); }
    p1BoostPrev.current = total;
  }, [player1BattleBoosts]);
  useEffect(() => {
    const total = player2BattleBoosts.attack + player2BattleBoosts.defense;
    if (total > p2BoostPrev.current) { setP2BoostFlash(true); setTimeout(() => setP2BoostFlash(false), 700); }
    p2BoostPrev.current = total;
  }, [player2BattleBoosts]);

  // Battle stage - Game end state
  const [gameEnded, setGameEnded] = useState(false);
  const [gameResult, setGameResult] = useState(null); // { winner: 'player1' | 'player2' | 'draw', p1Score, p2Score, reason: 'score' | 'turns' | 'abandon' }
  const [showAbandonConfirmation, setShowAbandonConfirmation] = useState(false);
  // Completion celebration after winning Trial 8; × dismisses to end screen
  const [dismissedCompletePopup, setDismissedCompletePopup] = useState(false);
  // Retry confirmation popup for Easy/Medium losses when retries remain
  const [showRetryPopup, setShowRetryPopup] = useState(false);
  // Show Super Win briefing popup at match start when the trial demands it — but only once per difficulty.
  // The popup is suppressed for subsequent trials once the player has dismissed it.
  const [showSuperWinPopup, setShowSuperWinPopup] = useState(() => {
    if (!challengeMode || !cpuStage) return false;
    const lvl = parseInt(cpuStage.Stage, 10);
    const needsSW = challengeDifficulty === 'hard' || (challengeDifficulty === 'medium' && lvl >= 5);
    if (!needsSW) return false;
    try { return !localStorage.getItem(`kickopp-superwin-popup-seen-${challengeDifficulty}`); } catch (e) { return true; }
  });
  const dismissSuperWinPopup = () => {
    try { localStorage.setItem(`kickopp-superwin-popup-seen-${challengeDifficulty}`, '1'); } catch (e) {}
    setShowSuperWinPopup(false);
  };

  // Battle stage - Kickopp hover preview state
  const [kickoppPreview, setKickoppPreview] = useState(null); // { attackerPos, targetPos, wouldSucceed, attackValue, defenseValue, baseAttack, columnBonus }

  // Battle stage - Card elimination animation state
  const [eliminatingCard, setEliminatingCard] = useState(null); // { colIndex, rowIndex } - card being eliminated

  // Action Cards deck data loaded from CSV
  const [actionCardsData, setActionCardsData] = useState([]);

  // Battle stage - Action Cards available to players (ONCE per turn only)
  const [player1ActionCards] = useState([
    { id: 'kickopp', name: 'Kickopp', icon: 'K', iconType: 'square', cost: 1, type: 'attack', description: 'Attack an opponent card (once per turn)' },
    { id: 'upgrade', name: 'Upgrade', icon: '↑', iconType: 'circle', cost: 1, type: 'upgrade', description: 'Increase max Action Points by 1 (once per turn)' }
  ]);
  const [player2ActionCards] = useState([
    { id: 'kickopp', name: 'Kickopp', icon: 'K', iconType: 'square', cost: 1, type: 'attack', description: 'Attack an opponent card (once per turn)' },
    { id: 'upgrade', name: 'Upgrade', icon: '↑', iconType: 'circle', cost: 1, type: 'upgrade', description: 'Increase max Action Points by 1 (once per turn)' }
  ]);

  // Battle stage - Special Actions available to players (can be used multiple times)
  const [player1SpecialActions] = useState([
    { id: 'move', name: 'Move', icon: '←→', iconBelow: true, cost: '1+', type: 'move', description: 'Move card to different row (1 AP per row change)' },
    { id: 'add_boost', name: 'Add Boost', icon: '+', iconBelow: true, cost: '1+', type: 'boost', description: 'Add a boost to a player card' },
    { id: 'buy_1a_or_1d', name: 'Buy', icon: '1', cost: 2, type: 'buy_single', description: 'Buy 1 Attack or 1 Defense' },
    { id: 'buy_1a_and_1d', name: 'Buy', icon: '1', cost: 3, type: 'buy_both', description: 'Buy 1 Attack and 1 Defense' },
    { id: 'substitute', name: 'Substitute', icon: '⇄', cost: 3, type: 'substitute', description: 'Substitute a player card' }
  ]);
  const [player2SpecialActions] = useState([
    { id: 'move', name: 'Move', icon: '←→', iconBelow: true, cost: '1+', type: 'move', description: 'Move card to different row (1 AP per row change)' },
    { id: 'add_boost', name: 'Add Boost', icon: '+', iconBelow: true, cost: '1+', type: 'boost', description: 'Add a boost to a player card' },
    { id: 'buy_1a_or_1d', name: 'Buy', icon: '1', cost: 2, type: 'buy_single', description: 'Buy 1 Attack or 1 Defense' },
    { id: 'buy_1a_and_1d', name: 'Buy', icon: '1', cost: 3, type: 'buy_both', description: 'Buy 1 Attack and 1 Defense' },
    { id: 'substitute', name: 'Substitute', icon: '⇄', cost: 3, type: 'substitute', description: 'Substitute a player card' }
  ]);

  // Note: gameplayMusicRef is now passed from App.jsx as a prop

  // ============================================================================
  // HELPER FUNCTIONS
  // ============================================================================

  // Helper function to add event to log
  const addEventLog = (eventData) => {
    const timestamp = new Date().toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
    setEventLog(prev => { const next = [...prev, { timestamp, ...eventData }]; return next.length > 50 ? next.slice(-50) : next; });
  };

  // Helper to render an action card's name in the event log, underlined with a tooltip
  // showing the card's effect text (hover on desktop, tap on mobile/touch).
  const renderActionCardName = (cardName) => {
    const cardInfo = actionCardsData.find(c => c.name === cardName);
    if (!cardInfo?.content) {
      return <span style={{ fontWeight: 'bold' }}>{cardName}</span>;
    }
    return (
      <span
        className="action-card-name-tooltip"
        title={cardInfo.content}
        onClick={(e) => {
          e.stopPropagation();
          setActionCardTooltip(prev => (prev?.name === cardName ? null : { name: cardName, content: cardInfo.content }));
        }}
        style={{ fontWeight: 'bold' }}
      >
        {cardName}
      </span>
    );
  };

  // Helper function to render event log entry with colors
  const renderEventLogEntry = (event) => {
    const playerColor = event.player === 1 ? '#4FA8DB' : '#FF6B6B'; // Team 1: cyan-blue, Team 2: coral-red
    const isCpuEvent = isSolo && event.player === aiPlayer;
    const playerText = `Team ${event.player}${isCpuEvent ? ' (CPU)' : ''}`;

    switch (event.type) {
      case 'draft':
        return (
          <>
            <span style={{ color: playerColor, fontWeight: 'bold' }}>{playerText}</span>
            {' drafted '}
            <span style={{ fontWeight: 'bold' }}>{event.playerName}</span>
            {' ('}
            <span style={{ color: event.positionColor, fontWeight: 'bold' }}>{event.positionType}</span>
            {')'}
          </>
        );
      case 'boost_add':
        return (
          <>
            <span style={{ color: playerColor, fontWeight: 'bold' }}>{playerText}</span>
            {' added '}
            <span style={{ color: '#ffffff', fontWeight: 'bold' }}>+{event.count || 1}</span>
            {event.doubled && <span style={{ color: '#f59e0b', fontWeight: 'bold' }}> ×2</span>}
            {' '}
            <span style={{ color: event.stat === 'attack' ? '#4258E0' : '#E74C3C', fontWeight: 'bold' }}>{event.stat === 'attack' ? 'Attack' : 'Defense'}</span>
            {' to '}
            <span style={{ fontWeight: 'bold' }}>{event.playerName}</span>
            {' ('}
            <span style={{ color: event.positionColor, fontWeight: 'bold' }}>{event.positionType}</span>
            {') - Cost: '}
            <span style={{ color: '#D4A017', fontWeight: 'bold' }}>{event.apCost ?? event.count ?? 1} AP</span>
          </>
        );
      case 'boost_remove_all':
        return (
          <>
            <span style={{ color: playerColor, fontWeight: 'bold' }}>{playerText}</span>
            {' removed all boosts from '}
            <span style={{ fontWeight: 'bold' }}>{event.playerName}</span>
            {' ('}
            <span style={{ color: event.positionColor, fontWeight: 'bold' }}>{event.positionType}</span>
            {')'}
            {event.apCost > 0 && (
              <><span style={{ color: '#94a3b8' }}>{' - Cost: '}</span><span style={{ color: '#D4A017', fontWeight: 'bold' }}>{event.apCost} AP</span></>
            )}
          </>
        );
      case 'boost_remove_all':
        return (
          <>
            <span style={{ color: playerColor, fontWeight: 'bold' }}>{playerText}</span>
            {' removed all boosts from '}
            <span style={{ fontWeight: 'bold' }}>{event.playerName}</span>
            {' ('}
            <span style={{ color: event.positionColor, fontWeight: 'bold' }}>{event.positionType}</span>
            {')'}
            {(event.attackBoost > 0 || event.defenseBoost > 0) && (
              <>
                {' — '}
                {event.attackBoost > 0 && <span style={{ color: '#4258E0', fontWeight: 'bold' }}>-{event.attackBoost} ATK </span>}
                {event.defenseBoost > 0 && <span style={{ color: '#E74C3C', fontWeight: 'bold' }}>-{event.defenseBoost} DEF</span>}
              </>
            )}
          </>
        );
      case 'boost_remove':
        return (
          <>
            <span style={{ color: playerColor, fontWeight: 'bold' }}>{playerText}</span>
            {' removed '}
            <span style={{ color: '#ffffff', fontWeight: 'bold' }}>-{event.count || 1}</span>
            {' '}
            <span style={{ color: event.stat === 'attack' ? '#4258E0' : '#E74C3C', fontWeight: 'bold' }}>{event.stat === 'attack' ? 'Attack' : 'Defense'}</span>
            {' from '}
            <span style={{ fontWeight: 'bold' }}>{event.playerName}</span>
            {' ('}
            <span style={{ color: event.positionColor, fontWeight: 'bold' }}>{event.positionType}</span>
            {')'}
          </>
        );
      case 'captain_add':
        return (
          <>
            <span style={{ color: playerColor, fontWeight: 'bold' }}>{playerText}</span>
            {' selected '}
            <span style={{ fontWeight: 'bold' }}>{event.playerName}</span>
            {event.positionType && (
              <>
                {' ('}
                <span style={{ color: event.positionColor, fontWeight: 'bold' }}>{event.positionType}</span>
                {')'}
              </>
            )}
            {' as captain'}
          </>
        );
      case 'captain_remove':
        // Check if this was a captain surviving a Kickopp attack
        if (event.captainSurvived) {
          return (
            <>
              <span style={{ fontWeight: 'bold' }}>{event.attacker}</span>
              {event.attackerPosition && (
                <>
                  {' ('}
                  <span style={{ color: event.attackerPositionColor, fontWeight: 'bold' }}>{event.attackerPosition}</span>
                  {')'}
                </>
              )}
              {' attacked Captain '}
              <span style={{ fontWeight: 'bold', color: '#D4A017' }}>{event.target}</span>
              {' (ATK: '}
              <span style={{ color: '#E74C3C' }}>{event.attack}</span>
              {' vs DEF: '}
              <span style={{ color: '#4258E0' }}>{event.defense}</span>
              {') - ⭐ Captaincy removed!'}
            </>
          );
        }
        // Regular captain removal (manual toggle)
        return (
          <>
            <span style={{ color: playerColor, fontWeight: 'bold' }}>{playerText}</span>
            {' removed captain from '}
            <span style={{ fontWeight: 'bold' }}>{event.playerName}</span>
            {event.positionType && (
              <>
                {' ('}
                <span style={{ color: event.positionColor, fontWeight: 'bold' }}>{event.positionType}</span>
                {')'}
              </>
            )}
          </>
        );
      case 'upgrade':
        return (
          <>
            <span style={{ color: playerColor, fontWeight: 'bold' }}>{playerText}</span>
            {' used Upgrade - Cost: '}
            <span style={{ color: '#D4A017', fontWeight: 'bold' }}>{event.cost ?? 1} AP</span>
          </>
        );
      case 'kickopp':
        return (
          <>
            <span style={{ color: playerColor, fontWeight: 'bold' }}>{playerText}</span>
            {' used Kickopp: '}
            <span style={{ fontWeight: 'bold' }}>{event.attacker}</span>
            {event.attackerPosition && (
              <>
                {' ('}
                <span style={{ color: event.attackerPositionColor, fontWeight: 'bold' }}>{event.attackerPosition}</span>
                {')'}
              </>
            )}
            {' (ATK: '}
            <span style={{ color: '#4258E0', fontWeight: 'bold' }}>{event.attack}</span>
            {') vs '}
            <span style={{ fontWeight: 'bold' }}>{event.target}</span>
            {event.targetPosition && (
              <>
                {' ('}
                <span style={{ color: event.targetPositionColor, fontWeight: 'bold' }}>{event.targetPosition}</span>
                {')'}
              </>
            )}
            {' (DEF: '}
            <span style={{ color: '#E74C3C', fontWeight: 'bold' }}>{event.defense}</span>
            {') - '}
            {event.success ? (
              <span style={{ color: '#64B978', fontWeight: 'bold' }}>
                ✅ {event.target}
                {event.targetPosition && (
                  <>
                    {' ('}
                    <span style={{ color: event.targetPositionColor }}>{event.targetPosition}</span>
                    {')'}
                  </>
                )}
                {' eliminated'}
              </span>
            ) : (
              <span style={{ color: '#D4A017', fontWeight: 'bold' }}>❌ FAILED - Action undone</span>
            )}
          </>
        );
      case 'move':
        return (
          <>
            <span style={{ color: playerColor, fontWeight: 'bold' }}>{playerText}</span>
            {' moved '}
            <span style={{ fontWeight: 'bold' }}>{event.playerName}</span>
            {event.moveType === 'swap' ? (
              <>
                {' swapped with '}
                <span style={{ fontWeight: 'bold' }}>{event.targetName}</span>
                {' (row '}
                {event.fromRow}
                {' ↔ row '}
                {event.toRow}
                {') - Cost: '}
                <span style={{ color: '#D4A017', fontWeight: 'bold' }}>{event.apCost} AP</span>
              </>
            ) : (
              <>
                {' from row '}
                {event.fromRow}
                {' → row '}
                {event.toRow}
                {' ('}
                {Math.abs(event.toRow - event.fromRow)}
                {' row'}
                {Math.abs(event.toRow - event.fromRow) > 1 ? 's' : ''}
                {') - Cost: '}
                <span style={{ color: '#D4A017', fontWeight: 'bold' }}>{event.apCost} AP</span>
              </>
            )}
          </>
        );
      case 'move_failed':
        return (
          <>
            <span style={{ color: playerColor, fontWeight: 'bold' }}>{playerText}</span>
            {' tried to move '}
            <span style={{ fontWeight: 'bold' }}>{event.playerName}</span>
            {' - '}
            <span style={{ color: '#E74C3C', fontWeight: 'bold' }}>❌ FAILED: Insufficient AP</span>
            {' (needed '}
            {event.apCost}
            {', had '}
            {event.currentAP}
            {')'}
          </>
        );
      case 'substitute':
        return (
          <>
            <span style={{ color: playerColor, fontWeight: 'bold' }}>{playerText}</span>
            {' substituted '}
            <span style={{ fontWeight: 'bold' }}>{event.oldCard}</span>
            {' with '}
            <span style={{ fontWeight: 'bold' }}>{event.newCard}</span>
            {' ('}
            <span style={{ color: event.positionColor, fontWeight: 'bold' }}>{event.position}</span>
            {') - Cost: '}
            <span style={{ color: '#D4A017', fontWeight: 'bold' }}>{event.apCost ?? 3} AP</span>
          </>
        );
      case 'boost_buy':
        return (
          <>
            <span style={{ color: playerColor, fontWeight: 'bold' }}>{playerText}</span>
            {' bought '}
            {event.boostType === 'both' ? (
              <>
                <span style={{ color: '#4258E0', fontWeight: 'bold' }}>+1 Attack</span>
                {' & '}
                <span style={{ color: '#E74C3C', fontWeight: 'bold' }}>+1 Defense</span>
              </>
            ) : (
              <span style={{ color: event.boostType === 'attack' ? '#4258E0' : '#E74C3C', fontWeight: 'bold' }}>+1 {event.boostType === 'attack' ? 'Attack' : 'Defense'}</span>
            )}
            {' - Cost: '}
            <span style={{ color: '#D4A017', fontWeight: 'bold' }}>{event.cost} AP</span>
          </>
        );
      case 'kickopp_inactive':
        return (
          <>
            <span style={{ color: playerColor, fontWeight: 'bold' }}>{playerText}</span>
            {' tried Kickopp with '}
            <span style={{ fontWeight: 'bold' }}>{event.attacker}</span>
            {event.attackerPosition && (
              <>
                {' ('}
                <span style={{ color: event.attackerPositionColor, fontWeight: 'bold' }}>{event.attackerPosition}</span>
                {')'}
              </>
            )}
            {' - '}
            <span style={{ color: '#E74C3C', fontWeight: 'bold' }}>❌ FAILED: Card is inactive (has K pawn)</span>
          </>
        );
      case 'kickopp_invalid':
        return (
          <>
            <span style={{ color: playerColor, fontWeight: 'bold' }}>{playerText}</span>
            {' tried Kickopp - '}
            <span style={{ color: '#E74C3C', fontWeight: 'bold' }}>❌ FAILED: {event.reason}</span>
          </>
        );
      case 'action_card_buy':
        return (
          <>
            <span style={{ color: playerColor, fontWeight: 'bold' }}>{playerText}</span>
            {' bought '}
            {renderActionCardName(event.cardName)}
            {' - Cost: '}
            <span style={{ color: '#D4A017', fontWeight: 'bold' }}>{event.cost} AP</span>
          </>
        );
      case 'action_card_play':
        return (
          <>
            <span style={{ color: playerColor, fontWeight: 'bold' }}>{playerText}</span>
            {' played '}
            {renderActionCardName(event.cardName)}
            {' - Cost: '}
            <span style={{ color: '#D4A017', fontWeight: 'bold' }}>{event.cost} AP</span>
          </>
        );
      case 'action_card_boost':
        return (
          <>
            <span style={{ color: playerColor, fontWeight: 'bold' }}>{playerText}</span>
            {' gained '}
            <span style={{ color: '#ffffff', fontWeight: 'bold' }}>+{event.count}</span>
            {' '}
            <span style={{ color: event.stat === 'attack' ? '#4258E0' : '#E74C3C', fontWeight: 'bold' }}>{event.stat === 'attack' ? 'Attack' : 'Defense'}</span>
            {' boost from '}
            {renderActionCardName(event.cardName)}
          </>
        );
      case 'a024_remove':
        return (
          <>
            <span style={{ color: playerColor, fontWeight: 'bold' }}>{playerText}</span>
            {' played '}
            {renderActionCardName(event.cardName)}
            {' — removed '}
            <span style={{ fontWeight: 'bold' }}>{event.removedName}</span>
            {event.removedPosition && (
              <> {' ('}<span style={{ color: '#cbd5e1', fontWeight: 'bold' }}>{event.removedPosition}</span>{')'}</>
            )}
            {' - Cost: '}
            <span style={{ color: '#D4A017', fontWeight: 'bold' }}>{event.cost} AP</span>
          </>
        );
      case 'extra_turn':
        return (
          <>
            <span style={{ color: playerColor, fontWeight: 'bold' }}>{playerText}</span>
            {' gets an extra turn — '}
            <span style={{ color: '#D4A017', fontWeight: 'bold' }}>{event.accumulatedAP} AP</span>
            {' available (accumulated)'}
          </>
        );
      case 'ai_upgrade':
        return (
          <>
            <span style={{ color: playerColor, fontWeight: 'bold' }}>{playerText}</span>
            {' upgraded to '}
            <span style={{ color: '#D4A017', fontWeight: 'bold' }}>Level {event.newLevel}</span>
            {' — Kickopp skipped this turn'}
          </>
        );
      case 'ai_training_received':
        return (
          <>
            <span style={{ color: playerColor, fontWeight: 'bold' }}>{playerText}</span>
            {' received '}
            <span style={{ color: '#4258E0', fontWeight: 'bold' }}>{event.trainAtk} Attack</span>
            {' and '}
            <span style={{ color: '#E74C3C', fontWeight: 'bold' }}>{event.trainDef} Defense</span>
            {' boosts this turn → added to A/D Boost pool'}
          </>
        );
      case 'ai_move':
        return (
          <>
            <span style={{ color: playerColor, fontWeight: 'bold' }}>{playerText}</span>
            {event.reason === 'defense' ? ' repositioned ' : ' moved '}
            <span style={{ fontWeight: 'bold' }}>{event.cardName}</span>
            {' ('}
            <span style={{ color: event.positionColor, fontWeight: 'bold' }}>{event.positionType}</span>
            {') → row '}
            <span style={{ color: '#fbbf24', fontWeight: 'bold' }}>{event.toRow + 1}</span>
            {event.reason === 'defense' ? ' — out of danger' : ' — into attack position'}
            {' — Cost: '}
            <span style={{ color: '#D4A017', fontWeight: 'bold' }}>1 AP</span>
          </>
        );
      case 'ai_pass':
        return (
          <>
            <span style={{ color: playerColor, fontWeight: 'bold' }}>{playerText}</span>
            {' ends turn — '}
            <span style={{ color: '#94a3b8' }}>{event.reason}</span>
          </>
        );
      default:
        return event.message || 'Unknown event';
    }
  };

  // Get stage display name
  const getStageDisplayName = () => {
    switch (gameStage) {
      case 'draft':
        return 'DRAFT';
      case 'warmup':
        return 'WARM UP';
      case 'battle':
        return 'MATCH';
      case 'end':
        return 'GAME OVER';
      default:
        return 'DRAFT';
    }
  };

  // ============================================================================
  // GAME LOGIC - useEffect Hooks
  // ============================================================================

  // Returns max K pawns allowed at a given maxAP level, respecting A_006 Less Inactives
  // Default scheme: 3-2-1-0 at levels 1/5/7/10  (thresholds: <=4, <=6, <=9, 10+)
  // A_006 scheme:   2-1-0   at levels 1/3/6       (same thresholds as default, -1 pawn each)
  const getMaxKPawns = (maxAP, lessInactives) => {
    // Default scheme: 3/2/1/0 at levels 1/3/6/9
    if (maxAP <= 2) return lessInactives ? 2 : 3;
    if (maxAP <= 5) return lessInactives ? 1 : 2;
    if (maxAP <= 8) return lessInactives ? 0 : 1;
    return 0;
  };

  // Fisher-Yates shuffle — defined here so it can be used anywhere in the component
  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // Per-player deck helpers (squad mode and solo CPU mode use separate decks per team)
  const useSeparateDecks = gameMode === 'squads' || (isSolo && aiPlayer !== null);
  const getDecksForPlayer = (player) =>
    (useSeparateDecks && player === 2) ? decksByPositionP2 : decksByPosition;

  const setDecksForPlayer = (player, updater) => {
    if (useSeparateDecks && player === 2) setDecksByPositionP2(updater);
    else setDecksByPosition(updater);
  };

  // Active deck for current player (used in draw/display logic)
  const activeDecks = getDecksForPlayer(currentPlayer);

  // Cleanup AI timer on unmount
  useEffect(() => () => { if (aiTimerRef.current) clearTimeout(aiTimerRef.current); }, []);

  // Load and initialize game data (cards, tactics, decks)
  useEffect(() => {
    const loadPlayers = async () => {
      try {
        const response = await fetch('/PlayersDataUnityImport.csv');
        const fileContent = await response.text();

        const parsed = Papa.parse(fileContent, {
          header: true,
          dynamicTyping: true,
          skipEmptyLines: true
        });

        const cleanedData = parsed.data.map(row => {
          const cleanRow = {};
          Object.keys(row).forEach(key => {
            cleanRow[key.trim()] = row[key];
          });
          return cleanRow;
        });

        const buildGrouped = (cards) => {
          const allFwd = cards.filter(p => p.CardType === 'ForwardCenter' || p.CardType === 'ForwardWide');
          return {
            Keeper: shuffleArray(cards.filter(p => p.CardType === 'Keeper')),
            DefenderCenter: shuffleArray(cards.filter(p => p.CardType === 'DefenderCenter')),
            DefenderWide: shuffleArray(cards.filter(p => p.CardType === 'DefenderWide')),
            MidfielderCenter: shuffleArray(cards.filter(p => p.CardType === 'MidfielderCenter')),
            MidfielderWide: shuffleArray(cards.filter(p => p.CardType === 'MidfielderWide')),
            ForwardCenter: shuffleArray(allFwd),
            ForwardWide: []
          };
        };

        if (isSolo && aiPlayer !== null && cpuStage) {
          // Solo vs CPU: human uses their Player 1 squad (squads mode) or all cards
          // (basic/All Collection mode); CPU uses players marked for the selected stage.
          const levelCol = `Level ${cpuStage.Stage}`;
          const cpuLevelPlayers = cleanedData.filter(row => row[levelCol]);
          const humanCards = (gameMode === 'squads' && squadData && squadData['Player 1'])
            ? squadData['Player 1']
            : cleanedData; // All Collection — give the human the full card pool
          if (aiPlayer === 2) {
            // Human = player1, CPU = player2
            setDecksByPosition(buildGrouped(humanCards));
            setDecksByPositionP2(buildGrouped(cpuLevelPlayers));
          } else {
            // Human = player2, CPU = player1
            setDecksByPosition(buildGrouped(cpuLevelPlayers));
            setDecksByPositionP2(buildGrouped(humanCards));
          }
        } else if (gameMode === 'squads' && squadData) {
          // Individual Squads mode: each team uses their own chosen 44 cards
          setDecksByPosition(buildGrouped(squadData['Player 1'] || []));
          setDecksByPositionP2(buildGrouped(squadData['Player 2'] || []));
        } else {
          // Basic Collection mode: all cards from CSV, both teams share the same pool.
          // In solo mode each player needs their own independent copy of the full deck
          // (useSeparateDecks is true whenever isSolo && aiPlayer !== null).
          setDecksByPosition(buildGrouped(cleanedData));
          if (isSolo && aiPlayer !== null) {
            setDecksByPositionP2(buildGrouped(cleanedData));
          }
        }
      } catch (error) {
        console.error('Error loading players:', error);
      }
    };

    const loadTactics = async () => {
      try {
        const response = await fetch('/Tactic Cards.csv');
        const fileContent = await response.text();

        const parsed = Papa.parse(fileContent, {
          header: true,
          dynamicTyping: true,
          skipEmptyLines: true
        });

        const cleanedTactics = parsed.data.map(row => {
          const cleanRow = {};
          Object.keys(row).forEach(key => {
            cleanRow[key.trim()] = row[key];
          });
          return cleanRow;
        }).filter(tactic => tactic['TACTIC NAME']); // Remove empty rows

        setAllTacticsData(cleanedTactics); // keep full list for CPU tactic-based draft

        // Randomly assign 3 tactics to each player
        const shuffledForPlayer1 = shuffleArray([...cleanedTactics]);
        const shuffledForPlayer2 = shuffleArray([...cleanedTactics]);

        setPlayer1Tactics(shuffledForPlayer1.slice(0, 3));
        setPlayer2Tactics(shuffledForPlayer2.slice(0, 3));

        console.log('Loaded tactics for Team 1:', shuffledForPlayer1.slice(0, 3));
        console.log('Loaded tactics for Team 2:', shuffledForPlayer2.slice(0, 3));

      } catch (error) {
        console.error('Error loading tactics:', error);
      }
    };

    const loadActionCards = async () => {
      try {
        const response = await fetch('/Action%20Cards.csv');
        const fileContent = await response.text();
        const parsed = Papa.parse(fileContent, {
          header: true,
          dynamicTyping: true,
          skipEmptyLines: true
        });
        const cleaned = parsed.data.map(row => {
          const cleanRow = {};
          Object.keys(row).forEach(key => { cleanRow[key.trim()] = typeof row[key] === 'string' ? row[key].trim() : row[key]; });
          return cleanRow;
        }).filter(r => r['ActionCard_Id']);
        const mapped = cleaned.map(r => ({
          id: r['ActionCard_Id'],
          name: r['ActionCard_Name'],
          buyCost: Number(r['AC_Buy']),
          playCost: Number(r['AC_Play']),
          content: r['ActionCard_Content'],
        }));
        setActionCardsData(mapped);
      } catch (error) {
        console.error('Error loading action cards:', error);
      }
    };

    // Wait for ALL three to complete before revealing the board — prevents a
    // race where loadTactics finishes first and the board renders with empty decks.
    Promise.all([loadPlayers(), loadTactics(), loadActionCards()])
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset instructions visibility when game stage changes
  useEffect(() => {
    setShowInstructions(true);
  }, [gameStage]);

  // Auto-show stage guides every time the player enters each stage within a game session
  useEffect(() => {
    if (gameStage === 'warmup') setShowWarmupTutorial(true);
    if (gameStage === 'battle') setShowBattleTutorial(true);
  }, [gameStage]);

  // Effect to handle transition from DRAFT to WARMUP
  useEffect(() => {
    if (cardsPlaced === 22 && gameStage === 'draft') {
      // Find completed tactics for each player
      const player1Statuses = getTacticStatus(1);
      const player2Statuses = getTacticStatus(2);

      // Get ALL completed tactics (not just the first one)
      const player1CompletedTactics = player1Statuses.filter(s => s.displayStatus === 'completed');
      const player2CompletedTactics = player2Statuses.filter(s => s.displayStatus === 'completed');

      // For Player 1: Group by tactic name and take maximum boosts
      if (player1CompletedTactics.length > 0) {
        const tacticsByName = {};

        player1CompletedTactics.forEach(status => {
          const tacticName = status.tactic['TACTIC NAME'];
          const boosts = calculateAvailableBoosts(1, status.tactic);

          if (!tacticsByName[tacticName]) {
            tacticsByName[tacticName] = { tactic: status.tactic, boosts };
          } else {
            // If same tactic name exists, take the maximum boosts
            const existingBoosts = tacticsByName[tacticName].boosts;
            tacticsByName[tacticName].boosts = {
              attBoost: Math.max(existingBoosts.attBoost, boosts.attBoost),
              defBoost: Math.max(existingBoosts.defBoost, boosts.defBoost),
              totalBoosts: Math.max(existingBoosts.totalBoosts, boosts.totalBoosts)
            };
          }
        });

        // Use the first tactic for display, but with maximum boosts
        const firstTacticName = Object.keys(tacticsByName)[0];
        setPlayer1CompletedTactic(tacticsByName[firstTacticName].tactic);
        setPlayer1AvailableBoosts(tacticsByName[firstTacticName].boosts);
      }

      // For Player 2: Group by tactic name and take maximum boosts
      if (player2CompletedTactics.length > 0) {
        const tacticsByName = {};

        player2CompletedTactics.forEach(status => {
          const tacticName = status.tactic['TACTIC NAME'];
          const boosts = calculateAvailableBoosts(2, status.tactic);

          if (!tacticsByName[tacticName]) {
            tacticsByName[tacticName] = { tactic: status.tactic, boosts };
          } else {
            // If same tactic name exists, take the maximum boosts
            const existingBoosts = tacticsByName[tacticName].boosts;
            tacticsByName[tacticName].boosts = {
              attBoost: Math.max(existingBoosts.attBoost, boosts.attBoost),
              defBoost: Math.max(existingBoosts.defBoost, boosts.defBoost),
              totalBoosts: Math.max(existingBoosts.totalBoosts, boosts.totalBoosts)
            };
          }
        });

        // Use the first tactic for display, but with maximum boosts
        const firstTacticName = Object.keys(tacticsByName)[0];
        setPlayer2CompletedTactic(tacticsByName[firstTacticName].tactic);
        setPlayer2AvailableBoosts(tacticsByName[firstTacticName].boosts);
      }

      // Transition to warmup and set current player to 1 (Team 1 goes first in warmup)
      setGameStage('warmup');
      setCurrentPlayer(1); // Team 1 goes first in warmup
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardsPlaced, gameStage]);

  // Effect to check for game end when score reaches 12 (all opponent cards eliminated)
  useEffect(() => {
    if (gameStage === 'battle' && !gameEnded) {
      if (player1Score >= 12 || player2Score >= 12) {
        // Game ends! Determine winner
        let winner;
        if (player1Score > player2Score) {
          winner = 'player1';
        } else if (player2Score > player1Score) {
          winner = 'player2';
        } else {
          winner = 'draw';
        }

        setGameResult({
          winner: winner,
          p1Score: player1Score,
          p2Score: player2Score,
          reason: 'score'
        });
        setGameEnded(true);
        if (setAppGameEnded) setAppGameEnded(true);
        setGameStage('end');
        console.log(`🏁 Game Over! Team reached 12 points. ${winner === 'draw' ? 'Draw' : `Team ${winner === 'player1' ? '1' : '2'} wins`}! Score: ${player1Score}-${player2Score}`);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [player1Score, player2Score, gameStage, gameEnded]);

  // Effect to apply Last Line Penalty at start of each turn in battle stage
  useEffect(() => {
    if (gameStage === 'battle') {
      console.log(`Player ${currentPlayer}'s turn started with ${currentPlayer === 1 ? player1ActionPoints : player2ActionPoints} AP (Level ${currentPlayer === 1 ? player1MaxActionPoints : player2MaxActionPoints})`);

      // Check for last line penalty (applied at start of turn)
      const player1Columns = [0, 1, 3, 5];
      const allColumns = [0, 1, 2, 3, 4, 5, 6, 7];
      const rows = [0, 1, 2, 3, 4];

      // Find rightmost occupied column
      let rightmostCol = -1;
      let rightmostIsP1 = false;
      for (let col = allColumns.length - 1; col >= 0; col--) {
        let hasCard = false;
        rows.forEach(row => {
          const slotKey = `${allColumns[col]}-${row}`;
          if (cardGameZones[slotKey] && typeof cardGameZones[slotKey] === 'object') {
            hasCard = true;
          }
        });
        if (hasCard) {
          rightmostCol = allColumns[col];
          rightmostIsP1 = player1Columns.includes(rightmostCol);
          break;
        }
      }

      // Find leftmost occupied column
      let leftmostCol = -1;
      let leftmostIsP1 = false;
      for (let col = 0; col < allColumns.length; col++) {
        let hasCard = false;
        rows.forEach(row => {
          const slotKey = `${allColumns[col]}-${row}`;
          if (cardGameZones[slotKey] && typeof cardGameZones[slotKey] === 'object') {
            hasCard = true;
          }
        });
        if (hasCard) {
          leftmostCol = allColumns[col];
          leftmostIsP1 = player1Columns.includes(leftmostCol);
          break;
        }
      }

      // Apply boosts based on rightmost/leftmost columns
      // If P1's column is rightmost and it's P1's turn (after P2 finished), P1 gets +1 ATK/DEF
      if (currentPlayer === 1 && rightmostCol >= 0 && rightmostIsP1) {
        setPlayer1BattleBoosts(prev => ({
          attack: prev.attack + 1,
          defense: prev.defense + 1
        }));
        addEventLog({
          type: 'last_line_penalty',
          player: 1,
          exposedCards: 1,
          message: `Team 1 gains +1 ATK and +1 DEF (rightmost position)`
        });
        console.log(`⚠️ Last line penalty: P1 gains +1 ATK/DEF (rightmost column ${rightmostCol})`);
      }

      // If P2's column is leftmost and it's P2's turn (after P1 finished), P2 gets +1 ATK/DEF
      if (currentPlayer === 2 && leftmostCol >= 0 && !leftmostIsP1) {
        setPlayer2BattleBoosts(prev => ({
          attack: prev.attack + 1,
          defense: prev.defense + 1
        }));
        addEventLog({
          type: 'last_line_penalty',
          player: 2,
          exposedCards: 1,
          message: `Team 2 gains +1 ATK and +1 DEF (leftmost position)`
        });
        console.log(`⚠️ Last line penalty: P2 gains +1 ATK/DEF (leftmost column ${leftmostCol})`);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPlayer, gameStage, cardGameZones]); // Run when turn changes or board state changes

  // Background music for gameplay is now managed by App.jsx and starts 3 seconds after Play button click
  // This useEffect just handles cleanup when game ends
  useEffect(() => {
    // Cleanup: stop music when game ends
    if (gameEnded && gameplayMusicRef && gameplayMusicRef.current) {
      gameplayMusicRef.current.pause();
      gameplayMusicRef.current.currentTime = 0;
      gameplayMusicRef.current = null;
    }
  }, [gameEnded, gameplayMusicRef]);

  // Background music for end game screen
  useEffect(() => {
    let audio = null;

    if (gameEnded && soundEnabled) {
      audio = new Audio('/relaxing-guitar-loop-398646.mp3');
      audio.loop = true;
      audio.volume = 0.3;
      audio.play().catch(() => {});
    }

    // Cleanup: stop music when game ends popup closes or component unmounts
    return () => {
      if (audio) {
        audio.pause();
        audio.currentTime = 0;
      }
    };
  }, [gameEnded, soundEnabled]);

  // ============================================================================
  // GAME LOGIC FUNCTIONS
  // ============================================================================

  // Get color for card type/position
  const getCardTypeColor = (cardType, defaultColor = '#6B7280') => {
    const colors = {
      'Keeper': '#fbbf24',
      'DefenderCenter': '#ef4444',
      'DefenderWide': '#f97316',
      'MidfielderCenter': '#10b981',
      'MidfielderWide': '#a855f7',
      'ForwardCenter': '#3b82f6',
      'ForwardWide': '#3b82f6'
    };
    return colors[cardType] || defaultColor;
  };

  // Alias for compatibility (both functions do the same thing)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getPositionColor = React.useCallback((cardType) => getCardTypeColor(cardType, '#374151'), []);

  // Card Game Zones (center) - 8 columns running horizontally (landscape)
  const cardGameZonesData = [
    // Column 0 (left) - Team 1 Keeper
    [{ row: 2, color: 'yellow', centered: true }],
    // Column 1 - Team 1 Defense
    [
      { row: 0, color: 'orange' },
      { row: 1, color: 'red' },
      { row: 2, color: 'red' },
      { row: 3, color: 'red' },
      { row: 4, color: 'orange' }
    ],
    // Column 2 - Team 2 Attack
    [
      { row: 0, color: 'white' },
      { row: 1, color: 'blue' },
      { row: 2, color: 'blue' },
      { row: 3, color: 'blue' },
      { row: 4, color: 'white' }
    ],
    // Column 3 - Team 1 Midfield
    [
      { row: 0, color: 'pink' },
      { row: 1, color: 'green' },
      { row: 2, color: 'green' },
      { row: 3, color: 'green' },
      { row: 4, color: 'pink' }
    ],
    // Column 4 - Team 2 Midfield
    [
      { row: 0, color: 'pink' },
      { row: 1, color: 'green' },
      { row: 2, color: 'green' },
      { row: 3, color: 'green' },
      { row: 4, color: 'pink' }
    ],
    // Column 5 - Team 1 Attack
    [
      { row: 0, color: 'white' },
      { row: 1, color: 'blue' },
      { row: 2, color: 'blue' },
      { row: 3, color: 'blue' },
      { row: 4, color: 'white' }
    ],
    // Column 6 - Team 2 Defense
    [
      { row: 0, color: 'orange' },
      { row: 1, color: 'red' },
      { row: 2, color: 'red' },
      { row: 3, color: 'red' },
      { row: 4, color: 'orange' }
    ],
    // Column 7 (right) - Team 2 Keeper
    [{ row: 2, color: 'yellow', centered: true }]
  ];

  const playerDeckZonesData = [
    { type: 'Keeper', color: 'yellow', label: 'KEEPER\n ' },
    { type: 'DefenderCenter', color: 'red', label: 'DEFENDER\nCENTER' },
    { type: 'DefenderWide', color: 'orange', label: 'DEFENDER\nWIDE' },
    { type: 'MidfielderCenter', color: 'green', label: 'MIDFIELDER\nCENTER' },
    { type: 'MidfielderWide', color: 'pink', label: 'MIDFIELDER\nWIDE' },
    { type: 'ForwardCenter', color: 'cyan', label: 'FORWARD\n ' }
  ];

  // Buy the face-down top card from a shared deck (revealed card stays unchanged)
  const handleBuyActionCard = (deck, setDeck, buyCost) => {
    const apRef = currentPlayer === 1 ? player1APRef : player2APRef;
    const currentAP = apRef.current;
    if (deck.length === 0 || currentAP < buyCost) return;
    if (shouldWarnAboutLastAP(buyCost)) {
      setPendingAction({ type: 'buy_action_card_facedown', buyCost });
      setShowAPConfirmation(true);
      return;
    }
    apRef.current -= buyCost; // deduct synchronously so rapid re-calls are blocked
    const [drawnCard, ...rest] = deck;
    setDeck(rest);
    if (currentPlayer === 1) {
      setPlayer1Hand(prev => [...prev, drawnCard]);
      setPlayer1ActionPoints(prev => prev - buyCost);
    } else {
      setPlayer2Hand(prev => [...prev, drawnCard]);
      setPlayer2ActionPoints(prev => prev - buyCost);
    }
    addEventLog({ type: 'action_card_buy', player: currentPlayer, cardName: 'Random Action Card', cost: buyCost });
  };

  // Buy the face-up revealed card (next deck card becomes new revealed)
  const handleBuyRevealedCard = (card, deck, setDeck, setRevealedCard, buyCost) => {
    const apRef = currentPlayer === 1 ? player1APRef : player2APRef;
    const currentAP = apRef.current;
    if (!card || currentAP < buyCost) return;
    if (shouldWarnAboutLastAP(buyCost)) {
      setPendingAction({ type: 'buy_action_card_revealed', buyCost });
      setShowAPConfirmation(true);
      return;
    }
    apRef.current -= buyCost; // deduct synchronously so rapid re-calls are blocked
    // Reveal next card from deck
    if (deck.length > 0) {
      setRevealedCard(deck[0]);
      setDeck(deck.slice(1));
    } else {
      setRevealedCard(null);
    }
    if (currentPlayer === 1) {
      setPlayer1Hand(prev => [...prev, card]);
      setPlayer1ActionPoints(prev => prev - buyCost);
    } else {
      setPlayer2Hand(prev => [...prev, card]);
      setPlayer2ActionPoints(prev => prev - buyCost);
    }
    addEventLog({ type: 'action_card_buy', player: currentPlayer, cardName: card.name, cost: buyCost });
  };

  const handleCardHoldStart = (card, idx, canPlay) => {
    if (!canPlay) return;
    const key = `${card.id}-${idx}`;
    setHoldingCardKey(key);
    holdTimerRef.current = setTimeout(() => {
      setHoldingCardKey(null);
      // Always read the freshest state via the ref updated on every render
      const { p1AP, p2AP, p1UsedK, p2UsedK, p1UsedU, p2UsedU, currentPlayer: cp } = liveGameStateRef.current;
      const ap    = cp === 1 ? p1AP : p2AP;
      const usedK = cp === 1 ? p1UsedK : p2UsedK;
      const usedU = cp === 1 ? p1UsedU : p2UsedU;
      if (ap - card.playCost === 0 && !usedK && !usedU) {
        setPendingActionCard({ card, handIndex: idx });
      } else {
        handlePlayActionCard(card, idx);
      }
    }, 1000);
  };

  const handleCardHoldEnd = () => {
    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
    setHoldingCardKey(null);
  };

  const handleButtonHoldStart = (id, callback, duration = 600) => {
    setHoldingButtonId(id);
    buttonHoldTimerRef.current = setTimeout(() => {
      setHoldingButtonId(null);
      callback();
    }, duration);
  };

  const handleButtonHoldEnd = () => {
    if (buttonHoldTimerRef.current) {
      clearTimeout(buttonHoldTimerRef.current);
      buttonHoldTimerRef.current = null;
    }
    setHoldingButtonId(null);
  };

  const handlePlayActionCard = (card, handIndex) => {
    const { p1AP, p2AP, currentPlayer: cp } = liveGameStateRef.current;
    const currentAP = cp === 1 ? p1AP : p2AP;
    if (currentAP < card.playCost) return;

    // Identify current player's board columns
    const playerCols = currentPlayer === 1 ? [0, 1, 3, 5] : [2, 4, 6, 7];
    const boardCards = Object.entries(cardGameZones)
      .filter(([key]) => playerCols.includes(parseInt(key.split('-')[0])))
      .map(([, c]) => c)
      .filter(Boolean);

    const setBoosts = currentPlayer === 1 ? setPlayer1BattleBoosts : setPlayer2BattleBoosts;
    const setAP = currentPlayer === 1 ? setPlayer1ActionPoints : setPlayer2ActionPoints;
    const setHand = currentPlayer === 1 ? setPlayer1Hand : setPlayer2Hand;
    const removeFromHand = () => setHand(prev => prev.filter((_, i) => i !== handIndex));

    // A_022 Boomerang: track every card played this turn (except A_022 itself)
    if (card.id !== 'A_022') {
      const setPlayed = currentPlayer === 1 ? setPlayer1CardsPlayedThisTurn : setPlayer2CardsPlayedThisTurn;
      setPlayed(prev => [...prev, card]);
    }

    switch (card.id) {
      case 'A_001': {
        const positions = ['SK', 'BPD', 'AM', 'S'];
        const count = boardCards.filter(c => positions.includes(c.PositionType?.trim())).length;
        removeFromHand();
        setAP(prev => prev - card.playCost);
        if (count > 0) {
          setBoosts(prev => ({ ...prev, attack: prev.attack + count }));
          addEventLog({ type: 'action_card_boost', player: currentPlayer, stat: 'attack', count, cardName: card.name });
        }
        addEventLog({ type: 'action_card_play', player: currentPlayer, cardName: card.name, cost: card.playCost });
        break;
      }
      case 'A_002': {
        const positions = ['GK', 'DCB', 'DM', 'DF'];
        const count = boardCards.filter(c => positions.includes(c.PositionType?.trim())).length;
        removeFromHand();
        setAP(prev => prev - card.playCost);
        if (count > 0) {
          setBoosts(prev => ({ ...prev, defense: prev.defense + count }));
          addEventLog({ type: 'action_card_boost', player: currentPlayer, stat: 'defense', count, cardName: card.name });
        }
        addEventLog({ type: 'action_card_play', player: currentPlayer, cardName: card.name, cost: card.playCost });
        break;
      }
      case 'A_003': {
        const positions = ['CD', 'CM', 'CF'];
        const count = boardCards.filter(c => positions.includes(c.PositionType?.trim())).length;
        removeFromHand();
        setAP(prev => prev - card.playCost);
        addEventLog({ type: 'action_card_play', player: currentPlayer, cardName: card.name, cost: card.playCost });
        if (count > 0) {
          setA003ChoiceState({ card, count, attackSplit: count }); // default all to attack
        }
        break;
      }
      case 'A_009': {
        const types = ['MidfielderCenter', 'MidfielderWide', 'ForwardCenter', 'ForwardWide'];
        const count = boardCards.filter(c => types.includes(c.CardType)).length;
        removeFromHand();
        setAP(prev => prev - card.playCost);
        if (count > 0) {
          setBoosts(prev => ({ ...prev, attack: prev.attack + count }));
          addEventLog({ type: 'action_card_boost', player: currentPlayer, stat: 'attack', count, cardName: card.name });
        }
        addEventLog({ type: 'action_card_play', player: currentPlayer, cardName: card.name, cost: card.playCost });
        break;
      }
      case 'A_010': {
        const types = ['Keeper', 'DefenderCenter', 'DefenderWide'];
        const count = boardCards.filter(c => types.includes(c.CardType)).length;
        removeFromHand();
        setAP(prev => prev - card.playCost);
        if (count > 0) {
          setBoosts(prev => ({ ...prev, defense: prev.defense + count }));
          addEventLog({ type: 'action_card_boost', player: currentPlayer, stat: 'defense', count, cardName: card.name });
        }
        addEventLog({ type: 'action_card_play', player: currentPlayer, cardName: card.name, cost: card.playCost });
        break;
      }
      case 'A_006': {
        removeFromHand();
        setAP(prev => prev - card.playCost);
        const setLessInactives = currentPlayer === 1 ? setPlayer1LessInactives : setPlayer2LessInactives;
        setLessInactives(true);
        // Immediately trim excess pawns based on the new reduced scheme
        const maxAPNow = currentPlayer === 1 ? player1MaxActionPoints : player2MaxActionPoints;
        const newMaxPawnsNow = getMaxKPawns(maxAPNow, true);
        const pawnsNow = currentPlayer === 1 ? player1KickoppPawns : player2KickoppPawns;
        const setPawnsNow = currentPlayer === 1 ? setPlayer1KickoppPawns : setPlayer2KickoppPawns;
        if (pawnsNow.length > newMaxPawnsNow) {
          const sorted = [...pawnsNow].sort((a, b) => a.order - b.order);
          setPawnsNow(sorted.slice(pawnsNow.length - newMaxPawnsNow));
        }
        addEventLog({ type: 'action_card_play', player: currentPlayer, cardName: card.name, cost: card.playCost });
        break;
      }
      case 'A_018': {
        removeFromHand();
        setAP(prev => prev - card.playCost);
        // Apply +2 DEF to every card on the board belonging to current player
        const player1Cols = [0, 1, 3, 5];
        const player2Cols = [2, 4, 6, 7];
        const myCols = currentPlayer === 1 ? player1Cols : player2Cols;
        const setApplied018 = currentPlayer === 1 ? setPlayer1AppliedBoosts : setPlayer2AppliedBoosts;
        setApplied018(prev => {
          const updated = { ...prev };
          Object.keys(cardGameZones).forEach(key => {
            const col = parseInt(key.split('-')[0]);
            if (myCols.includes(col)) {
              const card018 = cardGameZones[key];
              if (card018 && typeof card018 === 'object') {
                updated[card018.Card_Id] = {
                  attack: updated[card018.Card_Id]?.attack || 0,
                  defense: (updated[card018.Card_Id]?.defense || 0) + 2
                };
              }
            }
          });
          return updated;
        });
        setActiveDefencePlayer(currentPlayer);
        addEventLog({ type: 'action_card_play', player: currentPlayer, cardName: card.name, cost: card.playCost });
        break;
      }
      case 'A_020': {
        removeFromHand();
        setAP(prev => prev - card.playCost);
        setIgnoreOppDefActive(true);
        addEventLog({ type: 'action_card_play', player: currentPlayer, cardName: card.name, cost: card.playCost });
        break;
      }
      case 'A_019': {
        removeFromHand();
        setAP(prev => prev - card.playCost);
        const opponentPlayer019 = currentPlayer === 1 ? 2 : 1;
        // Zero out the opponent's unspent battle boost pool
        const setOpponentBattleBoosts019 = opponentPlayer019 === 1 ? setPlayer1BattleBoosts : setPlayer2BattleBoosts;
        setOpponentBattleBoosts019({ attack: 0, defense: 0 });
        addEventLog({ type: 'action_card_play', player: currentPlayer, cardName: card.name, cost: card.playCost });
        break;
      }
      case 'A_017': {
        removeFromHand();
        setAP(prev => prev - card.playCost);
        setRemoveBoostMode(true);
        setSelectedBoardCard(null);
        addEventLog({ type: 'action_card_play', player: currentPlayer, cardName: card.name, cost: card.playCost });
        break;
      }
      case 'A_015': {
        removeFromHand();
        setAP(prev => prev - card.playCost);
        setAttackAnyoneActive(true);
        addEventLog({ type: 'action_card_play', player: currentPlayer, cardName: card.name, cost: card.playCost });
        break;
      }
      case 'A_013': {
        removeFromHand();
        setAP(prev => prev - card.playCost);
        setDoubleBoostsActive(true);
        addEventLog({ type: 'action_card_play', player: currentPlayer, cardName: card.name, cost: card.playCost });
        break;
      }
      case 'A_012': {
        removeFromHand();
        setAP(prev => prev - card.playCost);
        if (currentPlayer === 1) setPlayer1ExtraMainActions(prev => prev + 1);
        else setPlayer2ExtraMainActions(prev => prev + 1);
        addEventLog({ type: 'action_card_play', player: currentPlayer, cardName: card.name, cost: card.playCost });
        break;
      }
      case 'A_011': {
        removeFromHand();
        setAP(prev => prev - card.playCost);
        setFreeBoostsActive(true);
        addEventLog({ type: 'action_card_play', player: currentPlayer, cardName: card.name, cost: card.playCost });
        break;
      }
      case 'A_008': {
        removeFromHand();
        const setMaxAP008 = currentPlayer === 1 ? setPlayer1MaxActionPoints : setPlayer2MaxActionPoints;
        const setAP008 = currentPlayer === 1 ? setPlayer1ActionPoints : setPlayer2ActionPoints;
        setMaxAP008(prev => prev + 1);
        // Pay playCost but gain +1 AP from the increase
        setAP008(prev => prev - card.playCost + 1);
        addEventLog({ type: 'action_card_play', player: currentPlayer, cardName: card.name, cost: card.playCost });
        break;
      }
      case 'A_007': {
        removeFromHand();
        setAP(prev => prev - card.playCost);
        setFreeSubsRemaining(prev => prev + 3);
        addEventLog({ type: 'action_card_play', player: currentPlayer, cardName: card.name, cost: card.playCost });
        break;
      }
      case 'A_016': {
        removeFromHand();
        setAP(prev => prev - card.playCost);
        setFreeSubsRemaining(prev => prev + 5);
        addEventLog({ type: 'action_card_play', player: currentPlayer, cardName: card.name, cost: card.playCost });
        break;
      }
      case 'A_004': {
        removeFromHand();
        setAP(prev => prev - card.playCost);
        setFreeMovesActive(true);
        addEventLog({ type: 'action_card_play', player: currentPlayer, cardName: card.name, cost: card.playCost });
        break;
      }
      case 'A_005': {
        removeFromHand();
        setAP(prev => prev - card.playCost);
        if (currentPlayer === 1) setPlayer1PendingKickoppBonus(prev => prev + 2);
        else setPlayer2PendingKickoppBonus(prev => prev + 2);
        addEventLog({ type: 'action_card_play', player: currentPlayer, cardName: card.name, cost: card.playCost });
        break;
      }
      case 'A_014': {
        removeFromHand();
        setAP(prev => prev - card.playCost);
        if (currentPlayer === 1) setPlayer1PendingKickoppBonus(prev => prev + 3);
        else setPlayer2PendingKickoppBonus(prev => prev + 3);
        addEventLog({ type: 'action_card_play', player: currentPlayer, cardName: card.name, cost: card.playCost });
        break;
      }
      case 'A_021': {
        removeFromHand();
        setAP(prev => prev - card.playCost);
        if (currentPlayer === 1) setPlayer1PendingKickoppBonus(prev => prev + 4);
        else setPlayer2PendingKickoppBonus(prev => prev + 4);
        addEventLog({ type: 'action_card_play', player: currentPlayer, cardName: card.name, cost: card.playCost });
        break;
      }
      case 'A_024': {
        removeFromHand();
        setAP(prev => prev - card.playCost);
        // Collect all opponent cards currently on the board
        const opponentCols024 = currentPlayer === 1 ? [2, 4, 6, 7] : [0, 1, 3, 5];
        const opponentEntries = Object.entries(cardGameZones).filter(([key, zCard]) => {
          if (!zCard || typeof zCard !== 'object') return false;
          const col = parseInt(key.split('-')[0]);
          return opponentCols024.includes(col);
        });
        if (opponentEntries.length > 0) {
          const [removedKey, removedCard024] = opponentEntries[Math.floor(Math.random() * opponentEntries.length)];
          const removedCol = parseInt(removedKey.split('-')[0]);
          setEliminatingCard({ colIndex: removedCol, rowIndex: parseInt(removedKey.split('-')[1]) });
          setTimeout(() => {
            setCardGameZones(prev => {
              const newZones = { ...prev };
              const colCards = Object.keys(prev).filter(k => parseInt(k.split('-')[0]) === removedCol && prev[k] && typeof prev[k] === 'object');
              const previousCardCount = colCards.length;
              delete newZones[removedKey];
              return repositionCardsInColumn(removedCol, newZones, previousCardCount);
            });
            setEliminatingCard(null);
          }, 1500);
          setRecycledCardsCount(prev => prev + 1);
          if (currentPlayer === 1) setPlayer1Score(prev => prev + 1);
          else setPlayer2Score(prev => prev + 1);
          addEventLog({
            type: 'a024_remove',
            player: currentPlayer,
            cardName: card.name,
            removedName: removedCard024.PlayerName,
            removedPosition: removedCard024.PositionType?.trim(),
            cost: card.playCost,
          });
        }
        break;
      }
      case 'A_023': {
        removeFromHand();
        setAP(prev => prev - card.playCost);
        if (currentPlayer === 1) setPlayer1ExtraTurnActive(true);
        else setPlayer2ExtraTurnActive(true);
        addEventLog({ type: 'action_card_play', player: currentPlayer, cardName: card.name, cost: card.playCost });
        break;
      }
      case 'A_022': {
        // Deferred: set flag so all cards played this turn are returned at end of turn
        removeFromHand();
        setAP(prev => prev - card.playCost);
        if (currentPlayer === 1) setPlayer1BoomerangActive(true);
        else setPlayer2BoomerangActive(true);
        addEventLog({ type: 'action_card_play', player: currentPlayer, cardName: card.name, cost: card.playCost });
        break;
      }
      default:
        // Other cards: remove from hand and deduct AP (effect not yet implemented)
        removeFromHand();
        setAP(prev => prev - card.playCost);
        addEventLog({ type: 'action_card_play', player: currentPlayer, cardName: card.name, cost: card.playCost });
        break;
    }
  };

  const handleA003Confirm = () => {
    if (!a003ChoiceState) return;
    const { card, count, attackSplit } = a003ChoiceState;
    const defenseSplit = count - attackSplit;
    const setBoosts = currentPlayer === 1 ? setPlayer1BattleBoosts : setPlayer2BattleBoosts;
    if (attackSplit > 0) {
      setBoosts(prev => ({ ...prev, attack: prev.attack + attackSplit }));
      addEventLog({ type: 'action_card_boost', player: currentPlayer, stat: 'attack', count: attackSplit, cardName: card.name });
    }
    if (defenseSplit > 0) {
      setBoosts(prev => ({ ...prev, defense: prev.defense + defenseSplit }));
      addEventLog({ type: 'action_card_boost', player: currentPlayer, stat: 'defense', count: defenseSplit, cardName: card.name });
    }
    setA003ChoiceState(null);
  };

  // Map card types to their valid slot colors
  const getValidSlotsForCardType = (cardType) => {
    const validSlots = {
      'Keeper': ['yellow'],
      'DefenderCenter': ['red'],
      'DefenderWide': ['orange'],
      'MidfielderCenter': ['green'],
      'MidfielderWide': ['pink'],
      'ForwardCenter': ['blue', 'cyan'],
      'ForwardWide': ['blue', 'cyan']
    };
    return validSlots[cardType] || [];
  };

  // Check if a column is valid for the current player during DRAFT stage
  const isValidColumnForPlayer = (colIndex, player) => {
    // CORRECTED: 
    // Team 1 can place on columns 0, 1, 3, 5 (Player 1's side: Keeper, Defense, Attack, Midfield)
    // Team 2 can place on columns 2, 4, 6, 7 (Player 2's side: Defense, Midfield, Attack, Keeper)
    const player1Columns = [0, 1, 3, 5];
    const player2Columns = [2, 4, 6, 7];

    if (player === 1) {
      return player1Columns.includes(parseInt(colIndex));
    } else {
      return player2Columns.includes(parseInt(colIndex));
    }
  };

  // Determine which opponent cards can be attacked from a given card position
  // Attacks must be on the SAME ROW as the attacker
  const getAttackablePositions = (attackerCol, attackerRow, currentPlayer) => {
    const attackablePositions = [];
    
    console.log(`🔍 getAttackablePositions: attackerCol=${attackerCol}, attackerRow=${attackerRow}, currentPlayer=${currentPlayer}`);
    
    // Attack rules (must match same row):
    // Team 1 attacks:
    // - From Column 0 or 1 → Column 2 (same row)
    // - From Column 3 → Column 4 (same row)
    // - From Column 5 → Column 6 and 7 (same row)
    //
    // Team 2 attacks:
    // - From Column 7 and 6 → Column 5 (same row)
    // - From Column 4 → Column 3 (same row)
    // - From Column 2 → Column 1 and 0 (same row)
    
    let targetCols = [];
    
    if (currentPlayer === 1) {
      // Team 1 attacks LEFT → RIGHT (can only attack columns with HIGHER numbers)
      // Team 1 columns: [0, 1, 3, 5]
      // Team 2 columns: [2, 4, 6, 7]
      // Filter to only include opponent columns that are HIGHER than attacker column
      const allOpponentCols = [2, 4, 6, 7];
      targetCols = allOpponentCols.filter(col => col > attackerCol);
    } else {
      // Team 2 attacks RIGHT → LEFT (can only attack columns with LOWER numbers)
      // Team 2 columns: [2, 4, 6, 7]
      // Team 1 columns: [0, 1, 3, 5]
      // Filter to only include opponent columns that are LOWER than attacker column
      const allOpponentCols = [0, 1, 3, 5];
      targetCols = allOpponentCols.filter(col => col < attackerCol);
    }

    console.log(`🔍 Calculated targetCols: ${targetCols} for attackerCol: ${attackerCol}, currentPlayer: ${currentPlayer} (directional attack)`);
    console.log(`🔍 Looking for cards on row ${attackerRow} in target columns: ${targetCols.join(', ')}`);

    // Check each target column for cards — same row only, unless A_015 Attack Anyone is active
    const rowsToCheck = attackAnyoneActive
      ? [...new Set(Object.keys(cardGameZones).map(k => parseInt(k.split('-')[1])).filter(r => !isNaN(r)))]
      : [attackerRow];

    targetCols.forEach(targetCol => {
      rowsToCheck.forEach(rowIndex => {
        const targetKey = `${targetCol}-${rowIndex}`;
        const targetCard = cardGameZones[targetKey];

        if (targetCard && typeof targetCard === 'object' && !Array.isArray(targetCard)) {
          attackablePositions.push({ colIndex: targetCol, rowIndex });
          console.log(`  ✅ Found attackable card at ${targetCol}-${rowIndex}: ${targetCard.PlayerName || targetCard.Card_Id || 'unknown'}`);
        }
      });
    });
    
    console.log(`🔍 Returning ${attackablePositions.length} attackable positions`);
    return attackablePositions;
  };

  // Get card's attack value (including boosts)
  const getCardAttack = React.useCallback((card, player) => {
    if (!card) return 0;
    const boosts = player === 1 ? player1AppliedBoosts[card.Card_Id] : player2AppliedBoosts[card.Card_Id];
    return card.Attack + (boosts?.attack || 0);
  }, [player1AppliedBoosts, player2AppliedBoosts]);

  // Get card's defense value (including boosts)
  const getCardDefense = React.useCallback((card, player) => {
    if (!card) return 0;
    const boosts = player === 1 ? player1AppliedBoosts[card.Card_Id] : player2AppliedBoosts[card.Card_Id];
    return card.Defence + (boosts?.defense || 0);
  }, [player1AppliedBoosts, player2AppliedBoosts]);

  // Returns [{ attackerPos, attackerCard, atkVal, defVal, margin }] for every
  // enemy-of-targetPlayer card that can currently attack targetCard at targetPos
  // (same-row, correct direction incl. column-distance bonus + applied boosts),
  // where atkVal > defVal.
  const getThreatsAgainst = (targetPos, targetCard, targetPlayer) => {
    const attackerPlayer = targetPlayer === 1 ? 2 : 1;
    const attackerCols = attackerPlayer === 1 ? AI_PLAYER1_COLS : AI_PLAYER2_COLS;
    const defVal = getCardDefense(targetCard, targetPlayer);
    const threats = [];

    Object.entries(cardGameZones).forEach(([key, card]) => {
      if (!card?.Card_Id || card.eliminating) return;
      const col = parseInt(key.split('-')[0]);
      const row = parseInt(key.split('-')[1]);
      if (!attackerCols.includes(col)) return;

      const reachable = getAttackablePositions(col, row, attackerPlayer);
      const inRange = reachable.some(p => p.colIndex === targetPos.colIndex && p.rowIndex === targetPos.rowIndex);
      if (!inRange) return;

      const dist = Math.abs(col - targetPos.colIndex);
      const distBonus = dist > 1 ? dist - 1 : 0;
      const atkVal = getCardAttack(card, attackerPlayer) + distBonus;
      if (atkVal > defVal) {
        threats.push({ attackerPos: { colIndex: col, rowIndex: row }, attackerCard: card, atkVal, defVal, margin: atkVal - defVal });
      }
    });

    return threats;
  };

  // Check if player should be warned about spending last AP without Upgrade/Kickopp
  const shouldWarnAboutLastAP = (apCost) => {
    const currentAP = currentPlayer === 1 ? player1ActionPoints : player2ActionPoints;
    const usedUpgrade = currentPlayer === 1 ? player1UsedUpgrade : player2UsedUpgrade;
    const usedKickopp = currentPlayer === 1 ? player1UsedKickopp : player2UsedKickopp;

    // Warn if: spending this AP would leave 0 AP AND player hasn't used Upgrade or Kickopp yet
    const willHaveZeroAP = (currentAP - apCost) === 0;
    const hasNotUsedSpecialActions = !usedUpgrade && !usedKickopp;

    return willHaveZeroAP && hasNotUsedSpecialActions;
  };

  // Execute the pending action after confirmation
  const executePendingAction = () => {
    if (!pendingAction) return;

    if (pendingAction.type === 'move') {
      // Execute move without confirmation check (already confirmed)
      const { sourcePos, targetPos, apCost, moveType, sourceCard, targetCard } = pendingAction;
      const sourceKey = `${sourcePos.colIndex}-${sourcePos.rowIndex}`;
      const targetKey = `${targetPos.colIndex}-${targetPos.rowIndex}`;

      // Execute the move
      setCardGameZones(prev => {
        const newZones = { ...prev };
        if (moveType === 'swap') {
          newZones[sourceKey] = targetCard;
          newZones[targetKey] = sourceCard;
        } else {
          newZones[targetKey] = sourceCard;
          delete newZones[sourceKey];
        }
        return newZones;
      });

      // Deduct AP
      const currentAP = currentPlayer === 1 ? player1ActionPoints : player2ActionPoints;
      const setAP = currentPlayer === 1 ? setPlayer1ActionPoints : setPlayer2ActionPoints;
      setAP(currentAP - apCost);

      // Exit move mode
      setMoveMode(false);
      const setSelected = currentPlayer === 1 ? setPlayer1SelectedActionCard : setPlayer2SelectedActionCard;
      setSelected(null);

      // Log event
      addEventLog({
        type: 'move',
        player: currentPlayer,
        playerName: sourceCard.PlayerName,
        moveType,
        fromRow: sourcePos.rowIndex,
        toRow: targetPos.rowIndex,
        apCost,
        targetName: targetCard ? targetCard.PlayerName : null
      });

      console.log(`✅ Move successful! ${sourceCard.PlayerName} moved from row ${sourcePos.rowIndex} to row ${targetPos.rowIndex}. AP cost: ${apCost}`);

    } else if (pendingAction.type === 'kickopp') {
      // Execute kickopp without confirmation check (already confirmed)
      const { targetPos, attackerCard, targetCard, attackValue, baseAttackValue, columnDistanceBonus, defenseValue, targetPlayer } = pendingAction;
      const targetKey = `${targetPos.colIndex}-${targetPos.rowIndex}`;

      // CAPTAIN MECHANIC: Check if target is a captain (has 2 lives)
      const targetIsCaptain =
        (targetPlayer === 1 && player1Captain === targetCard.Card_Id) ||
        (targetPlayer === 2 && player2Captain === targetCard.Card_Id);

      if (targetIsCaptain) {
        // Captain survives - handle captain logic
        console.log(`⭐ Captain ${targetCard.PlayerName} survives the attack but loses captain status!`);

        if (targetPlayer === 1) {
          setPlayer1FormerCaptain(targetCard.Card_Id);
          setPlayer1Captain(null);
        } else {
          setPlayer2FormerCaptain(targetCard.Card_Id);
          setPlayer2Captain(null);
        }

        // Award +1 score point
        console.log(`🎯 Score Update: Team ${currentPlayer} attacked captain ${targetCard.PlayerName}. Adding 1 point.`);
        if (currentPlayer === 1) {
          setPlayer1Score(prev => prev + 1);
        } else {
          setPlayer2Score(prev => prev + 1);
        }

        // Award K pawn (using existing logic from executeKickopp)
        const maxActionPoints = currentPlayer === 1 ? player1MaxActionPoints : player2MaxActionPoints;
        const currentPawns = currentPlayer === 1 ? player1KickoppPawns : player2KickoppPawns;
        const setPawns = currentPlayer === 1 ? setPlayer1KickoppPawns : setPlayer2KickoppPawns;

        const maxPawns = getMaxKPawns(maxActionPoints, currentPlayer === 1 ? player1LessInactives : player2LessInactives);

        const attackerCardId = attackerCard.Card_Id;
        const alreadyHasPawn = currentPawns.some(p => p.cardId === attackerCardId);

        if (!alreadyHasPawn && maxPawns > 0) {
          const nextOrder = currentPawns.length > 0 ? Math.max(...currentPawns.map(p => p.order)) + 1 : 1;
          if (currentPawns.length < maxPawns) {
            setPawns([...currentPawns, { cardId: attackerCardId, order: nextOrder }]);
            console.log(`⭐ K Pawn Awarded: Card ${attackerCardId} receives K${currentPawns.length + 1}`);
          } else if (currentPawns.length === maxPawns) {
            const oldestPawnIndex = currentPawns.reduce((minIdx, pawn, idx, arr) =>
              pawn.order < arr[minIdx].order ? idx : minIdx, 0
            );
            setPawns([
              ...currentPawns.slice(0, oldestPawnIndex),
              ...currentPawns.slice(oldestPawnIndex + 1),
              { cardId: attackerCardId, order: nextOrder }
            ]);
            console.log(`⭐ K Pawn Replaced: Card ${attackerCardId} receives K${maxPawns} (FIFO)`);
          }
        }

        addEventLog({
          type: 'kickopp',
          player: currentPlayer,
          attacker: attackerCard.PlayerName,
          attackerPosition: attackerCard.PositionType,
          attackerPositionColor: getPositionColor(attackerCard.CardType),
          target: targetCard.PlayerName,
          targetPosition: targetCard.PositionType,
          targetPositionColor: getPositionColor(targetCard.CardType),
          success: true,
          captainSurvived: true,
          attack: attackValue,
          baseAttack: baseAttackValue,
          columnBonus: columnDistanceBonus,
          defense: defenseValue
        });

      } else {
        // Regular elimination - copy full logic from executeKickopp
        const previousCardCount = Object.keys(cardGameZones).filter(key => {
          const col = parseInt(key.split('-')[0]);
          return col === targetPos.colIndex && cardGameZones[key] && typeof cardGameZones[key] === 'object';
        }).length;

        setCardGameZones(prev => {
          const newZones = { ...prev };
          delete newZones[targetKey];
          return repositionCardsInColumn(targetPos.colIndex, newZones, previousCardCount);
        });

        setRecycledCardsCount(prev => prev + 1);

        // Update score
        const wasFormerCaptain =
          (targetPlayer === 1 && player1FormerCaptain === targetCard.Card_Id) ||
          (targetPlayer === 2 && player2FormerCaptain === targetCard.Card_Id);
        const scoreToAdd = 1;

        console.log(`🎯 Score Update: Team ${currentPlayer} eliminated ${targetCard.PlayerName}. Former captain: ${wasFormerCaptain}. Adding ${scoreToAdd} point.`);

        if (currentPlayer === 1) {
          setPlayer1Score(prev => prev + scoreToAdd);
        } else {
          setPlayer2Score(prev => prev + scoreToAdd);
        }

        // Award K pawn (same logic as above)
        const maxActionPoints = currentPlayer === 1 ? player1MaxActionPoints : player2MaxActionPoints;
        const currentPawns = currentPlayer === 1 ? player1KickoppPawns : player2KickoppPawns;
        const setPawns = currentPlayer === 1 ? setPlayer1KickoppPawns : setPlayer2KickoppPawns;

        const maxPawns = getMaxKPawns(maxActionPoints, currentPlayer === 1 ? player1LessInactives : player2LessInactives);

        const attackerCardId = attackerCard.Card_Id;
        const alreadyHasPawn = currentPawns.some(p => p.cardId === attackerCardId);

        if (!alreadyHasPawn && maxPawns > 0) {
          const nextOrder = currentPawns.length > 0 ? Math.max(...currentPawns.map(p => p.order)) + 1 : 1;
          if (currentPawns.length < maxPawns) {
            setPawns([...currentPawns, { cardId: attackerCardId, order: nextOrder }]);
          } else if (currentPawns.length === maxPawns) {
            const oldestPawnIndex = currentPawns.reduce((minIdx, pawn, idx, arr) =>
              pawn.order < arr[minIdx].order ? idx : minIdx, 0
            );
            setPawns([
              ...currentPawns.slice(0, oldestPawnIndex),
              ...currentPawns.slice(oldestPawnIndex + 1),
              { cardId: attackerCardId, order: nextOrder }
            ]);
          }
        }

        // Clear former captain tracking if eliminated
        if (wasFormerCaptain) {
          if (targetPlayer === 1) {
            setPlayer1FormerCaptain(null);
          } else {
            setPlayer2FormerCaptain(null);
          }
        }

        // Remove K pawn if eliminated card had one
        const targetPawns = targetPlayer === 1 ? player1KickoppPawns : player2KickoppPawns;
        const setTargetPawns = targetPlayer === 1 ? setPlayer1KickoppPawns : setPlayer2KickoppPawns;
        const eliminatedCardPawnIndex = targetPawns.findIndex(p => p.cardId === targetCard.Card_Id);

        if (eliminatedCardPawnIndex !== -1) {
          setTargetPawns(targetPawns.filter((_, idx) => idx !== eliminatedCardPawnIndex));
        }

        addEventLog({
          type: 'kickopp',
          player: currentPlayer,
          attacker: attackerCard.PlayerName,
          attackerPosition: attackerCard.PositionType,
          attackerPositionColor: getPositionColor(attackerCard.CardType),
          target: targetCard.PlayerName,
          targetPosition: targetCard.PositionType,
          targetPositionColor: getPositionColor(targetCard.CardType),
          success: true,
          attack: attackValue,
          baseAttack: baseAttackValue,
          columnBonus: columnDistanceBonus,
          defense: defenseValue
        });
      }

      // Deduct AP
      const setAP = currentPlayer === 1 ? setPlayer1ActionPoints : setPlayer2ActionPoints;
      const currentAP = currentPlayer === 1 ? player1ActionPoints : player2ActionPoints;
      setAP(currentAP - 1);

      // Mark Kickopp as used this turn (consume extra action if A_012 active)
      if (currentPlayer === 1) {
        setPlayer1UsedKickopp(true);
        if (player1ExtraMainActions > 0) setPlayer1ExtraMainActions(prev => prev - 1);
      } else {
        setPlayer2UsedKickopp(true);
        if (player2ExtraMainActions > 0) setPlayer2ExtraMainActions(prev => prev - 1);
      }

      // A_018: strip +2 DEF shield from opponent if active
      {
        const opponentPlayer = currentPlayer === 1 ? 2 : 1;
        if (activeDefencePlayer === opponentPlayer) {
          const setApplied = opponentPlayer === 1 ? setPlayer1AppliedBoosts : setPlayer2AppliedBoosts;
          setApplied(prev => {
            const updated = { ...prev };
            Object.keys(updated).forEach(cardId => {
              updated[cardId] = { ...updated[cardId], defense: Math.max(0, (updated[cardId].defense || 0) - 2) };
            });
            return updated;
          });
          setActiveDefencePlayer(null);
        }
      }

      // Exit kickopp mode
      setKickoppMode(false);
      const setSelected = currentPlayer === 1 ? setPlayer1SelectedActionCard : setPlayer2SelectedActionCard;
      setSelected(null);

      console.log(`✅ Successful Kickopp! ${attackerCard.PlayerName} attacked ${targetCard.PlayerName}`);
    } else if (pendingAction.type === 'buy_boost') {
      // Execute boost purchase without confirmation check (already confirmed)
      const { boostType, cost } = pendingAction;
      const setBoosts = currentPlayer === 1 ? setPlayer1BattleBoosts : setPlayer2BattleBoosts;
      const setAP = currentPlayer === 1 ? setPlayer1ActionPoints : setPlayer2ActionPoints;
      const currentAP = currentPlayer === 1 ? player1ActionPoints : player2ActionPoints;

      if (boostType === 'attack') {
        setBoosts(prev => ({ ...prev, attack: prev.attack + 1 }));
      } else if (boostType === 'defense') {
        setBoosts(prev => ({ ...prev, defense: prev.defense + 1 }));
      } else if (boostType === 'both') {
        setBoosts(prev => ({
          attack: prev.attack + 1,
          defense: prev.defense + 1
        }));
      }

      setAP(currentAP - cost);
      setBoostMode(null);
      setBoostStep(null);

      addEventLog({
        type: 'boost_buy',
        player: currentPlayer,
        boostType: boostType,
        cost: cost
      });

      console.log(`✅ Boost purchased: ${boostType} (${cost} AP)`);
    } else if (pendingAction.type === 'substitute') {
      // Execute substitute action without confirmation check (already confirmed)
      console.log(`🔄 Substitute action confirmed - select card to remove from board`);
      setSubstituteMode(true);
      setSubstituteStep('select_board_card');
    } else if (pendingAction.type === 'add_boost') {
      // Execute add boost action - select card on board first
      console.log(`💪 Add Boost action confirmed - select card on board`);
      setBoostMode('add_boost');
      setBoostStep('select_card');
      setPendingBoosts({});
    } else if (pendingAction.type === 'move_activate') {
      // Enter move mode after confirmation
      console.log(`🎯 Move action confirmed - activating Move mode`);
      setMoveMode(true);
    } else if (pendingAction.type === 'finish_turn') {
      executeFinishTurn();
    } else if (pendingAction.type === 'buy_action_card_facedown') {
      const { buyCost } = pendingAction;
      const deck = buyCost === 1 ? actionDeck1 : buyCost === 3 ? actionDeck3 : actionDeck5;
      const setDeck = buyCost === 1 ? setActionDeck1 : buyCost === 3 ? setActionDeck3 : setActionDeck5;
      if (deck.length === 0) return;
      const [drawnCard, ...rest] = deck;
      setDeck(rest);
      if (currentPlayer === 1) {
        player1APRef.current -= buyCost;
        setPlayer1Hand(prev => [...prev, drawnCard]);
        setPlayer1ActionPoints(prev => prev - buyCost);
      } else {
        player2APRef.current -= buyCost;
        setPlayer2Hand(prev => [...prev, drawnCard]);
        setPlayer2ActionPoints(prev => prev - buyCost);
      }
      addEventLog({ type: 'action_card_buy', player: currentPlayer, cardName: 'Random Action Card', cost: buyCost });
    } else if (pendingAction.type === 'buy_action_card_revealed') {
      const { buyCost } = pendingAction;
      const revealed = buyCost === 1 ? revealedCard1 : buyCost === 3 ? revealedCard3 : revealedCard5;
      const deck = buyCost === 1 ? actionDeck1 : buyCost === 3 ? actionDeck3 : actionDeck5;
      const setDeck = buyCost === 1 ? setActionDeck1 : buyCost === 3 ? setActionDeck3 : setActionDeck5;
      const setRevCard = buyCost === 1 ? setRevealedCard1 : buyCost === 3 ? setRevealedCard3 : setRevealedCard5;
      if (!revealed) return;
      if (deck.length > 0) { setRevCard(deck[0]); setDeck(deck.slice(1)); } else { setRevCard(null); }
      if (currentPlayer === 1) {
        player1APRef.current -= buyCost;
        player1APRef.current -= buyCost;
        setPlayer1Hand(prev => [...prev, revealed]);
        setPlayer1ActionPoints(prev => prev - buyCost);
      } else {
        player2APRef.current -= buyCost;
        setPlayer2Hand(prev => [...prev, revealed]);
        setPlayer2ActionPoints(prev => prev - buyCost);
      }
      addEventLog({ type: 'action_card_buy', player: currentPlayer, cardName: revealed.name, cost: buyCost });
    } else if (pendingAction.type === 'upgrade_kickopp_warning') {
      const { computedNewAP, computedNewMaxAP, prevMaxAP, prevSelected, extraMainActions: eMA, lessInactives } = pendingAction;
      // Reverse previous selection effects
      if (prevSelected === 'kickopp') setKickoppMode(false);
      // K pawn trimming
      const prevMaxPawns = getMaxKPawns(prevMaxAP, lessInactives);
      const newMaxPawnsVal = getMaxKPawns(computedNewMaxAP, lessInactives);
      if (newMaxPawnsVal < prevMaxPawns) {
        const currentPawns = currentPlayer === 1 ? player1KickoppPawns : player2KickoppPawns;
        const setPawns = currentPlayer === 1 ? setPlayer1KickoppPawns : setPlayer2KickoppPawns;
        const excessCount = currentPawns.length - newMaxPawnsVal;
        if (excessCount > 0) {
          const sorted = [...currentPawns].sort((a, b) => a.order - b.order);
          setPawns(sorted.slice(excessCount));
        }
      }
      const setMaxAPu = currentPlayer === 1 ? setPlayer1MaxActionPoints : setPlayer2MaxActionPoints;
      const setAPu = currentPlayer === 1 ? setPlayer1ActionPoints : setPlayer2ActionPoints;
      const setSelectedu = currentPlayer === 1 ? setPlayer1SelectedActionCard : setPlayer2SelectedActionCard;
      setMaxAPu(computedNewMaxAP);
      setAPu(computedNewAP);
      setSelectedu('upgrade');
      if (currentPlayer === 1) {
        setPlayer1UsedUpgrade(true);
        if (eMA > 0) setPlayer1ExtraMainActions(prev => prev - 1);
      } else {
        setPlayer2UsedUpgrade(true);
        if (eMA > 0) setPlayer2ExtraMainActions(prev => prev - 1);
      }
      addEventLog({ type: 'upgrade', player: currentPlayer, cost: 1 });
    }

    // Clear pending action and close popup
    setPendingAction(null);
    setShowAPConfirmation(false);
  };

  // Finish turn logic — shared by button click and confirmation modal
  const executeFinishTurn = () => {
    // A_023 Extra Turn: same player repeats, unused AP accumulates
    const extraTurnActive = currentPlayer === 1 ? player1ExtraTurnActive : player2ExtraTurnActive;
    if (extraTurnActive) {
      const currentAP = currentPlayer === 1 ? player1ActionPoints : player2ActionPoints;
      const maxAP = currentPlayer === 1 ? player1MaxActionPoints : player2MaxActionPoints;
      const setAP = currentPlayer === 1 ? setPlayer1ActionPoints : setPlayer2ActionPoints;
      // Reset used states for this player's repeat turn
      if (currentPlayer === 1) {
        setPlayer1UpgradeUsedThisTurn(false);
        setPlayer1SelectedActionCard(null);
        setPlayer1UsedKickopp(false);
        setPlayer1UsedUpgrade(false);
        setPlayer1ExtraTurnActive(false);
        setPlayer1ExtraMainActions(0);
        setPlayer1PendingKickoppBonus(0);
        if (player1BoomerangActive && player1CardsPlayedThisTurn.length > 0) {
          setPlayer1Hand(prev => [...prev, ...player1CardsPlayedThisTurn]);
        }
        setPlayer1BoomerangActive(false);
        setPlayer1CardsPlayedThisTurn([]);
      } else {
        setPlayer2UpgradeUsedThisTurn(false);
        setPlayer2SelectedActionCard(null);
        setPlayer2UsedKickopp(false);
        setPlayer2UsedUpgrade(false);
        setPlayer2ExtraTurnActive(false);
        setPlayer2ExtraMainActions(0);
        setPlayer2PendingKickoppBonus(0);
        if (player2BoomerangActive && player2CardsPlayedThisTurn.length > 0) {
          setPlayer2Hand(prev => [...prev, ...player2CardsPlayedThisTurn]);
        }
        setPlayer2BoomerangActive(false);
        setPlayer2CardsPlayedThisTurn([]);
      }
      // Accumulated AP: maxAP + leftover from current turn
      setAP(maxAP + currentAP);
      // Reset all modes (same as normal turn end)
      setBoostMode(null);
      setBoostStep(null);
      setBoostSelectedCard(null);
      setPendingBoosts({});
      setKickoppMode(false);
      setKickoppPreview(null);
      setMoveMode(false);
      setMoveOriginalPosition(null);
      setSubstituteMode(false);
      setSubstituteStep(null);
      setSubstituteSelectedDeck(null);
      setSubstituteVisibleCards([]);
      setSubstituteSelectedCard(null);
      setSubstituteRemovedCard(null);
      setHighlightedSlots([]);
      setDraggedCard(null);
      setSelectedBoardCard(null);
      setFreeMovesActive(false);
      setFreeSubsRemaining(0);
      setFreeBoostsActive(false);
      setDoubleBoostsActive(false);
      setAttackAnyoneActive(false);
      setIgnoreOppDefActive(false);
      setRemoveBoostMode(false);
      addEventLog({ type: 'extra_turn', player: currentPlayer, accumulatedAP: maxAP + currentAP });
      return; // don't switch player, don't increment turn count
    }

    if (currentPlayer === 1) {
      setPlayer1UpgradeUsedThisTurn(false);
      setPlayer1SelectedActionCard(null);
      setPlayer1UsedKickopp(false);
      setPlayer1UsedUpgrade(false);
      setPlayer2ActionPoints(player2MaxActionPoints);
      setPlayer2TurnCount(prev => prev + 1);
    } else {
      setPlayer2UpgradeUsedThisTurn(false);
      setPlayer2SelectedActionCard(null);
      setPlayer2UsedKickopp(false);
      setPlayer2UsedUpgrade(false);

      if (player2TurnCount >= 20) {
        let winner;
        if (player1Score > player2Score) winner = 'player1';
        else if (player2Score > player1Score) winner = 'player2';
        else winner = 'draw';
        setGameResult({ winner, p1Score: player1Score, p2Score: player2Score, reason: 'turns' });
        setGameEnded(true);
        if (setAppGameEnded) setAppGameEnded(true);
        setGameStage('end');
        return;
      }

      setPlayer1ActionPoints(player1MaxActionPoints);
      setPlayer1TurnCount(prev => prev + 1);
    }

    setBoostMode(null);
    setBoostStep(null);
    setBoostSelectedCard(null);
    setPendingBoosts({});
    setKickoppMode(false);
    setKickoppPreview(null);
    setMoveMode(false);
    setMoveOriginalPosition(null);
    setSubstituteMode(false);
    setSubstituteStep(null);
    setSubstituteSelectedDeck(null);
    setSubstituteVisibleCards([]);
    setSubstituteSelectedCard(null);
    setSubstituteRemovedCard(null);
    setHighlightedSlots([]);
    setDraggedCard(null);
    setSelectedBoardCard(null);
    setFreeMovesActive(false);
    setFreeSubsRemaining(0);
    setFreeBoostsActive(false);
    setDoubleBoostsActive(false);
    setAttackAnyoneActive(false);
    setIgnoreOppDefActive(false);
    setRemoveBoostMode(false);
    setPlayer1ExtraMainActions(0);
    setPlayer2ExtraMainActions(0);
    if (currentPlayer === 1) setPlayer1PendingKickoppBonus(0);
    else setPlayer2PendingKickoppBonus(0);
    // A_022 Boomerang: return all cards played this turn to hand at turn end
    if (currentPlayer === 1) {
      if (player1BoomerangActive && player1CardsPlayedThisTurn.length > 0) {
        setPlayer1Hand(prev => [...prev, ...player1CardsPlayedThisTurn]);
      }
      setPlayer1BoomerangActive(false);
      setPlayer1CardsPlayedThisTurn([]);
    } else {
      if (player2BoomerangActive && player2CardsPlayedThisTurn.length > 0) {
        setPlayer2Hand(prev => [...prev, ...player2CardsPlayedThisTurn]);
      }
      setPlayer2BoomerangActive(false);
      setPlayer2CardsPlayedThisTurn([]);
    }
    setCurrentPlayer(prev => prev === 1 ? 2 : 1);
  };

  // Execute Move action
  const executeMove = (sourcePos, targetPos) => {
    const sourceKey = `${sourcePos.colIndex}-${sourcePos.rowIndex}`;
    const targetKey = `${targetPos.colIndex}-${targetPos.rowIndex}`;

    const sourceCard = cardGameZones[sourceKey];
    const targetCard = cardGameZones[targetKey];

    if (!sourceCard) {
      console.error('Move: Missing source card');
      return false;
    }

    // Ensure move is within the same column
    if (sourcePos.colIndex !== targetPos.colIndex) {
      console.log(`❌ Move failed: Can only move cards within the same column. Source col: ${sourcePos.colIndex}, Target col: ${targetPos.colIndex}`);
      return false;
    }

    // If dropped on the same position, no-op (no cost, no action)
    if (sourcePos.colIndex === targetPos.colIndex && sourcePos.rowIndex === targetPos.rowIndex) {
      console.log(`🔄 Card released on same position - no action taken`);
      setMoveMode(false);
      setMoveOriginalPosition(null);
      const setSelected = currentPlayer === 1 ? setPlayer1SelectedActionCard : setPlayer2SelectedActionCard;
      setSelected(null);
      return true;
    }

    // Calculate AP cost based on row distance (1 AP per row change per card)
    const rowsChanged = Math.abs(targetPos.rowIndex - sourcePos.rowIndex);
    let apCost = 0;
    let moveType = '';

    if (targetCard && typeof targetCard === 'object') {
      // SWAP: Two cards exchanging positions - cost based on distance for each card
      moveType = 'swap';
      apCost = rowsChanged * 2; // Both cards move the same row distance
      console.log(`Move type: SWAP - ${sourceCard.PlayerName} ↔ ${targetCard.PlayerName}, ${rowsChanged} row(s) each`);
    } else {
      // SINGLE MOVE: One card moving to empty slot
      moveType = 'single';
      apCost = rowsChanged;
      console.log(`Move type: SINGLE - ${sourceCard.PlayerName} moving ${rowsChanged} row(s)`);
    }

    // Check if card is being moved back to its original position
    if (moveOriginalPosition &&
        targetPos.colIndex === moveOriginalPosition.colIndex &&
        targetPos.rowIndex === moveOriginalPosition.rowIndex) {
      console.log(`🔄 Card returned to original position - Cost: 0 AP (was ${apCost} AP)`);
      apCost = 0;
      moveType = 'return_to_original';
    }

    // Free moves (A_004): skip all AP checks and set cost to 0
    if (freeMovesActive) {
      apCost = 0;
    }

    // Check if player has enough AP
    const currentAP = currentPlayer === 1 ? player1ActionPoints : player2ActionPoints;
    if (currentAP < apCost) {
      console.log(`❌ Move failed: Insufficient AP. Required: ${apCost}, Available: ${currentAP}`);
      addEventLog({
        type: 'move_failed',
        player: currentPlayer,
        playerName: sourceCard.PlayerName,
        reason: 'insufficient_ap',
        apCost,
        currentAP
      });
      return false;
    }

    // Check if we should warn about spending last AP without Upgrade/Kickopp
    if (!freeMovesActive && shouldWarnAboutLastAP(apCost)) {
      console.log(`⚠️ Warning: This move will use all AP without Upgrade/Kickopp. Showing confirmation.`);
      // Store the pending action and show confirmation popup
      setPendingAction({
        type: 'move',
        sourcePos,
        targetPos,
        apCost,
        moveType,
        sourceCard,
        targetCard
      });
      setShowAPConfirmation(true);
      return false; // Don't execute yet, wait for confirmation
    }

    // Execute the move
    setCardGameZones(prev => {
      const newZones = { ...prev };

      if (moveType === 'swap') {
        // Swap the two cards
        newZones[sourceKey] = targetCard;
        newZones[targetKey] = sourceCard;
      } else {
        // Move single card to empty slot
        newZones[targetKey] = sourceCard;
        delete newZones[sourceKey];
      }

      return newZones;
    });

    // Deduct AP
    const setAP = currentPlayer === 1 ? setPlayer1ActionPoints : setPlayer2ActionPoints;
    setAP(currentAP - apCost);

    // Exit move mode and clear original position
    setMoveMode(false);
    setMoveOriginalPosition(null);
    const setSelected = currentPlayer === 1 ? setPlayer1SelectedActionCard : setPlayer2SelectedActionCard;
    setSelected(null);

    // Log event
    addEventLog({
      type: 'move',
      player: currentPlayer,
      playerName: sourceCard.PlayerName,
      moveType,
      fromRow: sourcePos.rowIndex,
      toRow: targetPos.rowIndex,
      apCost,
      targetName: targetCard ? targetCard.PlayerName : null
    });

    console.log(`✅ Move successful! ${sourceCard.PlayerName} moved from row ${sourcePos.rowIndex} to row ${targetPos.rowIndex}. AP cost: ${apCost}`);
    return true;
  };

  // Re-Positioning Rule: Center cards after elimination
  // ONLY triggers when going from 4→3 or 2→1 in a column (NOT from 3→2)
  const repositionCardsInColumn = (colIndex, currentZones, previousCardCount) => {
    const column = cardGameZonesData[colIndex];
    if (!column) return currentZones;

    const cardsInColumn = [];

    // Collect all cards currently in this column with their positions
    column.forEach(slot => {
      const key = `${colIndex}-${slot.row}`;
      const card = currentZones[key];
      if (card && typeof card === 'object') {
        cardsInColumn.push({ card, originalRow: slot.row });
      }
    });

    const currentCardCount = cardsInColumn.length;

    // IMPORTANT: Only reposition on specific transitions
    // 4→3: previousCardCount = 4, currentCardCount = 3
    // 2→1: previousCardCount = 2, currentCardCount = 1
    // Do NOT reposition on 3→2 or any other transition
    const shouldReposition =
      (previousCardCount === 4 && currentCardCount === 3) ||
      (previousCardCount === 2 && currentCardCount === 1);

    if (!shouldReposition) {
      return currentZones; // No repositioning for this transition
    }

    // Determine target rows based on current count (centered positions)
    let targetRows = [];
    if (currentCardCount === 3) {
      targetRows = [1, 2, 3]; // Centered: skip row 0 and row 4
    } else if (currentCardCount === 1) {
      targetRows = [2]; // Center position
    }

    // Check if repositioning is actually needed (cards may already be centered)
    const currentRows = cardsInColumn.map(c => c.originalRow).sort((a, b) => a - b);
    const needsRepositioning = !currentRows.every((row, idx) => row === targetRows[idx]);

    if (!needsRepositioning) {
      return currentZones; // Cards already in correct positions
    }

    console.log(`Re-Positioning Rule: Column ${colIndex} transition ${previousCardCount}→${currentCardCount}, moving cards to centered positions [${targetRows.join(', ')}]`);

    // Remove cards from old positions
    const newZones = { ...currentZones };
    column.forEach(slot => {
      const key = `${colIndex}-${slot.row}`;
      if (newZones[key] && typeof newZones[key] === 'object') {
        delete newZones[key];
      }
    });

    // Place cards in new centered positions (maintaining original order)
    cardsInColumn.forEach((cardData, idx) => {
      if (idx < targetRows.length) {
        const key = `${colIndex}-${targetRows[idx]}`;
        newZones[key] = cardData.card;
      }
    });

    return newZones;
  };

  // Execute Kickopp action
  const executeKickopp = (attackerPos, targetPos) => {
    const attackerKey = `${attackerPos.colIndex}-${attackerPos.rowIndex}`;
    const targetKey = `${targetPos.colIndex}-${targetPos.rowIndex}`;
    
    const attackerCard = cardGameZones[attackerKey];
    const targetCard = cardGameZones[targetKey];
    
    if (!attackerCard || !targetCard) {
      console.error('Kickopp: Missing attacker or target card');
      return false;
    }
    
    // Determine which player owns the attacker
    const player1Columns = [0, 1, 3, 5];
    const attackerPlayer = player1Columns.includes(attackerPos.colIndex) ? 1 : 2;
    const targetPlayer = attackerPlayer === 1 ? 2 : 1;
    
    // Calculate column distance bonus (+1 attack per column jumped)
    // Only add bonus when columns are "jumped" (not adjacent)
    const columnDistance = Math.abs(attackerPos.colIndex - targetPos.colIndex);
    // Bonus is the number of columns BETWEEN attacker and target (not including the columns themselves)
    // So adjacent columns (distance 1) = 0 bonus, distance 2 = +1 bonus, distance 3 = +2 bonus, etc.
    const columnDistanceBonus = columnDistance > 1 ? columnDistance - 1 : 0;
    
    // Get attack and defense values (including boosts)
    const baseAttackValue = getCardAttack(attackerCard, attackerPlayer);
    const pendingKickoppBonus = attackerPlayer === 1 ? player1PendingKickoppBonus : player2PendingKickoppBonus;
    const attackValue = baseAttackValue + columnDistanceBonus + pendingKickoppBonus;
    const defenseValue = ignoreOppDefActive ? targetCard.Defence : getCardDefense(targetCard, targetPlayer);

    // Debug logging for boost application
    const attackerBoosts = attackerPlayer === 1 ? player1AppliedBoosts[attackerCard.Card_Id] : player2AppliedBoosts[attackerCard.Card_Id];
    const targetBoosts = targetPlayer === 1 ? player1AppliedBoosts[targetCard.Card_Id] : player2AppliedBoosts[targetCard.Card_Id];
    console.log(`🎯 Kickopp Debug:
  Attacker: ${attackerCard.PlayerName} (Card_Id: ${attackerCard.Card_Id}, Player ${attackerPlayer})
    - Base Attack: ${attackerCard.Attack}
    - Applied Boosts: ${JSON.stringify(attackerBoosts)}
    - Boosted Attack: ${baseAttackValue}
    - Column Distance: ${columnDistance} (from col ${attackerPos.colIndex} to col ${targetPos.colIndex})
    - Column Jump Bonus: +${columnDistanceBonus}
    - TOTAL ATTACK: ${attackValue}

  Target: ${targetCard.PlayerName} (Card_Id: ${targetCard.Card_Id}, Player ${targetPlayer})
    - Base Defense: ${targetCard.Defence}
    - Applied Boosts: ${JSON.stringify(targetBoosts)}
    - TOTAL DEFENSE: ${defenseValue}

  Result: ${attackValue} vs ${defenseValue} = ${attackValue > defenseValue ? 'SUCCESS ✅' : 'FAIL ❌'}`);

    console.log(`Kickopp: ${attackerCard.PlayerName} (Base ATK: ${baseAttackValue} + Column Bonus: +${columnDistanceBonus} = Total ATK: ${attackValue}) vs ${targetCard.PlayerName} (DEF: ${defenseValue})`);

    // Check if attack > defense (strictly higher)
    if (attackValue > defenseValue) {
      // Successful Kickopp! Consume any pending Kickopp bonus (A_005/A_014/A_021)
      if (pendingKickoppBonus > 0) {
        if (attackerPlayer === 1) setPlayer1PendingKickoppBonus(0);
        else setPlayer2PendingKickoppBonus(0);
      }

      // Play whoosh sound effect
      if (soundEnabled) {
        const kickoppSound = new Audio('/whoosh-cinematic-376875.mp3');
        kickoppSound.volume = 0.3;
        kickoppSound.play().catch(() => {});
      }

      // No confirmation needed - player is intentionally using Kickopp action

      // CAPTAIN MECHANIC: Check if target is a captain (has 2 lives)
      const targetIsCaptain =
        (targetPlayer === 1 && player1Captain === targetCard.Card_Id) ||
        (targetPlayer === 2 && player2Captain === targetCard.Card_Id);

      if (targetIsCaptain) {
        // Captain survives but loses captain status (uses first life)
        console.log(`⭐ Captain ${targetCard.PlayerName} survives the attack but loses captain status!`);

        // Remove captain status and track as former captain (for +1 bonus on elimination)
        if (targetPlayer === 1) {
          setPlayer1FormerCaptain(targetCard.Card_Id);
          setPlayer1Captain(null);
        } else {
          setPlayer2FormerCaptain(targetCard.Card_Id);
          setPlayer2Captain(null);
        }

        // Award +1 score point for successful attack on captain
        console.log(`🎯 Score Update: Team ${currentPlayer} attacked captain ${targetCard.PlayerName}. Adding 1 point.`);
        if (currentPlayer === 1) {
          setPlayer1Score(prev => {
            console.log(`Team 1 score: ${prev} → ${prev + 1}`);
            return prev + 1;
          });
        } else {
          setPlayer2Score(prev => {
            console.log(`Team 2 score: ${prev} → ${prev + 1}`);
            return prev + 1;
          });
        }

        // Award K pawn to attacker for successful Kickopp attack
        const maxActionPoints = currentPlayer === 1 ? player1MaxActionPoints : player2MaxActionPoints;
        const currentPawns = currentPlayer === 1 ? player1KickoppPawns : player2KickoppPawns;
        const setPawns = currentPlayer === 1 ? setPlayer1KickoppPawns : setPlayer2KickoppPawns;

        // Calculate max K pawns based on level (action points)
        const maxPawns = getMaxKPawns(maxActionPoints, attackerPlayer === 1 ? player1LessInactives : player2LessInactives);

        // Award K pawn if max > 0
        if (maxPawns > 0) {
          const attackerCardId = attackerCard.Card_Id;

          // Check if attacker already has a pawn
          const existingPawnIndex = currentPawns.findIndex(p => p.cardId === attackerCardId);

          if (existingPawnIndex === -1) {
            // Attacker doesn't have a pawn, add new one
            const nextOrder = currentPawns.length > 0 ? Math.max(...currentPawns.map(p => p.order)) + 1 : 1;

            if (currentPawns.length >= maxPawns) {
              // At max capacity, implement FIFO rotation
              const oldestPawnIndex = currentPawns.reduce((minIdx, pawn, idx, arr) =>
                pawn.order < arr[minIdx].order ? idx : minIdx, 0
              );
              const oldestPawn = currentPawns[oldestPawnIndex];

              // Remove oldest pawn and add new pawn
              setPawns([
                ...currentPawns.filter((_, idx) => idx !== oldestPawnIndex),
                { cardId: attackerCardId, order: nextOrder }
              ]);

              console.log(`🔄 K Pawn Rotation: Removed K pawn from card ${oldestPawn.cardId}, awarded to card ${attackerCardId}`);
            } else {
              // Below max capacity, simply add new pawn
              setPawns([...currentPawns, { cardId: attackerCardId, order: nextOrder }]);
              console.log(`⭐ K Pawn Awarded: Card ${attackerCardId} receives K${currentPawns.length + 1}`);
            }
          } else {
            // Attacker already has a pawn, don't add another one
            console.log(`ℹ️ Card ${attackerCardId} already has a K pawn, no new pawn awarded`);
          }
        }

        // Deduct AP (attack was successful, just didn't eliminate)
        const setAP = currentPlayer === 1 ? setPlayer1ActionPoints : setPlayer2ActionPoints;
        const currentAP = currentPlayer === 1 ? player1ActionPoints : player2ActionPoints;
        setAP(currentAP - 1);

        // Mark Kickopp as used this turn (consume extra action if A_012 active)
        if (currentPlayer === 1) {
          setPlayer1UsedKickopp(true);
          if (player1ExtraMainActions > 0) setPlayer1ExtraMainActions(prev => prev - 1);
        } else {
          setPlayer2UsedKickopp(true);
          if (player2ExtraMainActions > 0) setPlayer2ExtraMainActions(prev => prev - 1);
        }

        // A_018: strip +2 DEF shield from opponent if active
        {
          const opponentPlayer = currentPlayer === 1 ? 2 : 1;
          if (activeDefencePlayer === opponentPlayer) {
            const setApplied = opponentPlayer === 1 ? setPlayer1AppliedBoosts : setPlayer2AppliedBoosts;
            setApplied(prev => {
              const updated = { ...prev };
              Object.keys(updated).forEach(cardId => {
                updated[cardId] = { ...updated[cardId], defense: Math.max(0, (updated[cardId].defense || 0) - 2) };
              });
              return updated;
            });
            setActiveDefencePlayer(null);
          }
        }

        // Exit kickopp mode
        setKickoppMode(false);
        const setSelected = currentPlayer === 1 ? setPlayer1SelectedActionCard : setPlayer2SelectedActionCard;
        setSelected(null);

        // Log event
        addEventLog({
          type: 'captain_remove',
          player: currentPlayer,
          attacker: attackerCard.PlayerName,
          attackerPosition: attackerCard.PositionType,
          attackerPositionColor: getPositionColor(attackerCard.CardType),
          target: targetCard.PlayerName,
          captainSurvived: true,
          attack: attackValue,
          baseAttack: baseAttackValue,
          columnBonus: columnDistanceBonus,
          defense: defenseValue
        });

        return true;
      }

      // Regular elimination (non-captain or captain already lost their status)
      // Show elimination effect
      setEliminatingCard({ colIndex: targetPos.colIndex, rowIndex: targetPos.rowIndex });

      // Mark the card as eliminating immediately so the CPU plan-builder skips it.
      // The card is kept in cardGameZones until the animation finishes so the visual still plays.
      setCardGameZones(prev => {
        const newZones = { ...prev };
        if (newZones[targetKey]) newZones[targetKey] = { ...newZones[targetKey], eliminating: true };
        return newZones;
      });

      // Wait for elimination animation, then remove card and reposition
      setTimeout(() => {
        setCardGameZones(prev => {
          const newZones = { ...prev };

          // Count cards BEFORE deletion to track the transition
          const column = cardGameZonesData[targetPos.colIndex];
          let previousCardCount = 0;
          column.forEach(slot => {
            const key = `${targetPos.colIndex}-${slot.row}`;
            if (prev[key] && typeof prev[key] === 'object') {
              previousCardCount++;
            }
          });

          // Delete the eliminated card
          delete newZones[targetKey];

          // Re-Positioning Rule: Check if this transition triggers repositioning (4→3 or 2→1 only)
          const repositionedZones = repositionCardsInColumn(targetPos.colIndex, newZones, previousCardCount);

          return repositionedZones;
        });
          setEliminatingCard(null);
        }, 1500); // Elimination animation duration (1.5s)

      // 3. Add to Cards Out stack
      setRecycledCardsCount(prev => prev + 1);

      // 3.5. Update player score (current player eliminated an opponent card)
      // Always +1 for elimination (no bonus for former captain)
      const wasFormerCaptain =
        (targetPlayer === 1 && player1FormerCaptain === targetCard.Card_Id) ||
        (targetPlayer === 2 && player2FormerCaptain === targetCard.Card_Id);

      const scoreToAdd = 1;

      console.log(`🎯 Score Update: Team ${currentPlayer} eliminated ${targetCard.PlayerName}. Former captain: ${wasFormerCaptain}. Adding ${scoreToAdd} point.`);

      if (currentPlayer === 1) {
        setPlayer1Score(prev => {
          console.log(`Team 1 score: ${prev} → ${prev + scoreToAdd}`);
          return prev + scoreToAdd;
        });
      } else {
        setPlayer2Score(prev => {
          console.log(`Team 2 score: ${prev} → ${prev + scoreToAdd}`);
          return prev + scoreToAdd;
        });
      }

      // 3.6. Award K pawn to attacker after successful score increase
      // K pawn tracking uses FIFO queue with max capacity based on level
      const maxActionPoints = currentPlayer === 1 ? player1MaxActionPoints : player2MaxActionPoints;
      const currentPawns = currentPlayer === 1 ? player1KickoppPawns : player2KickoppPawns;
      const setPawns = currentPlayer === 1 ? setPlayer1KickoppPawns : setPlayer2KickoppPawns;

      // Calculate max K pawns based on level (action points)
      const maxPawns = getMaxKPawns(maxActionPoints, attackerPlayer === 1 ? player1LessInactives : player2LessInactives);

      // Award K pawn if max > 0
      if (maxPawns > 0) {
        const attackerCardId = attackerCard.Card_Id;

        // Check if attacker already has a pawn
        const existingPawnIndex = currentPawns.findIndex(p => p.cardId === attackerCardId);

        if (existingPawnIndex === -1) {
          // Attacker doesn't have a pawn, add new one
          const nextOrder = currentPawns.length > 0 ? Math.max(...currentPawns.map(p => p.order)) + 1 : 1;

          if (currentPawns.length >= maxPawns) {
            // At max capacity, implement FIFO rotation
            // Remove oldest pawn (lowest order) and transfer to newest attacker
            const oldestPawnIndex = currentPawns.reduce((minIdx, pawn, idx, arr) =>
              pawn.order < arr[minIdx].order ? idx : minIdx, 0
            );
            const oldestPawn = currentPawns[oldestPawnIndex];

            // Remove oldest pawn and add new pawn
            setPawns([
              ...currentPawns.filter((_, idx) => idx !== oldestPawnIndex),
              { cardId: attackerCardId, order: nextOrder }
            ]);

            console.log(`🔄 K Pawn Rotation: Removed K pawn from card ${oldestPawn.cardId}, awarded to card ${attackerCardId}`);
          } else {
            // Below max capacity, simply add new pawn
            setPawns([...currentPawns, { cardId: attackerCardId, order: nextOrder }]);
            console.log(`⭐ K Pawn Awarded: Card ${attackerCardId} receives K${currentPawns.length + 1}`);
          }
        } else {
          // Attacker already has a pawn, don't add another one
          console.log(`ℹ️ Card ${attackerCardId} already has a K pawn, no new pawn awarded`);
        }
      }

      // Clear former captain tracking if eliminated
      if (wasFormerCaptain) {
        if (targetPlayer === 1) {
          setPlayer1FormerCaptain(null);
        } else {
          setPlayer2FormerCaptain(null);
        }
      }

      // 3.7. Remove K pawn if eliminated card had one (pawn doesn't transfer)
      const targetPawns = targetPlayer === 1 ? player1KickoppPawns : player2KickoppPawns;
      const setTargetPawns = targetPlayer === 1 ? setPlayer1KickoppPawns : setPlayer2KickoppPawns;
      const eliminatedCardPawnIndex = targetPawns.findIndex(p => p.cardId === targetCard.Card_Id);

      if (eliminatedCardPawnIndex !== -1) {
        // Remove the pawn from the eliminated card
        setTargetPawns(targetPawns.filter((_, idx) => idx !== eliminatedCardPawnIndex));
        console.log(`🗑️ K pawn removed from eliminated card ${targetCard.Card_Id}`);
      }

      // 4. Deduct AP (only now, on success)
      const setAP = currentPlayer === 1 ? setPlayer1ActionPoints : setPlayer2ActionPoints;
      const currentAP = currentPlayer === 1 ? player1ActionPoints : player2ActionPoints;
      setAP(currentAP - 1);
      
      // 5. Mark Kickopp as used this turn (consume extra action if A_012 active)
      if (currentPlayer === 1) {
        setPlayer1UsedKickopp(true);
        if (player1ExtraMainActions > 0) setPlayer1ExtraMainActions(prev => prev - 1);
      } else {
        setPlayer2UsedKickopp(true);
        if (player2ExtraMainActions > 0) setPlayer2ExtraMainActions(prev => prev - 1);
      }

      // A_018: strip +2 DEF shield from opponent if active
      {
        const opponentPlayer = currentPlayer === 1 ? 2 : 1;
        if (activeDefencePlayer === opponentPlayer) {
          const setApplied = opponentPlayer === 1 ? setPlayer1AppliedBoosts : setPlayer2AppliedBoosts;
          setApplied(prev => {
            const updated = { ...prev };
            Object.keys(updated).forEach(cardId => {
              updated[cardId] = { ...updated[cardId], defense: Math.max(0, (updated[cardId].defense || 0) - 2) };
            });
            return updated;
          });
          setActiveDefencePlayer(null);
        }
      }

      // 6. Exit kickopp mode
      setKickoppMode(false);
      const setSelected = currentPlayer === 1 ? setPlayer1SelectedActionCard : setPlayer2SelectedActionCard;
      setSelected(null);

      // 7. Log event
      addEventLog({
        type: 'kickopp',
        player: currentPlayer,
        attacker: attackerCard.PlayerName,
        attackerPosition: attackerCard.PositionType,
        attackerPositionColor: getPositionColor(attackerCard.CardType),
        target: targetCard.PlayerName,
        targetPosition: targetCard.PositionType,
        targetPositionColor: getPositionColor(targetCard.CardType),
        success: true,
        attack: attackValue,
        baseAttack: baseAttackValue,
        columnBonus: columnDistanceBonus,
        defense: defenseValue
      });
      
      console.log(`✅ Successful Kickopp! ${attackerCard.PlayerName} eliminated ${targetCard.PlayerName}`);
      return true;
    } else {
      // Failed Kickopp - keep mode active so player can retry
      // Don't exit kickopp mode, don't deselect - allow retry
      // Consume A_020 (single-use per Kickopp attempt)
      if (ignoreOppDefActive) setIgnoreOppDefActive(false);

      // Log event
      addEventLog({
        type: 'kickopp',
        player: currentPlayer,
        attacker: attackerCard.PlayerName,
        target: targetCard.PlayerName,
        success: false,
        attack: attackValue,
        baseAttack: baseAttackValue,
        columnBonus: columnDistanceBonus,
        defense: defenseValue
      });
      
      console.log(`❌ Failed Kickopp! ${attackerCard.PlayerName} (ATK: ${attackValue}) cannot eliminate ${targetCard.PlayerName} (DEF: ${defenseValue}). Action undone.`);
      return false;
    }
  };


  // Get the card type category for formation tracking
  const getCardTypeCategory = (cardType) => {
    if (cardType === 'ForwardCenter' || cardType === 'ForwardWide') {
      return 'Forward';
    }
    return cardType;
  };

  // Map PositionType to color based on position category
  const getPositionTypeColor = (positionType) => {
    // Keepers (yellow)
    if (['GK', 'SK'].includes(positionType)) return '#D4A017';

    // Defenders Center (red)
    if (['CD', 'DCB', 'BPD'].includes(positionType)) return '#E74C3C';

    // Defenders Wide (orange)
    if (['FB', 'WB'].includes(positionType)) return '#E67E22';

    // Midfielders Center (green)
    if (['CM', 'DM', 'AM'].includes(positionType)) return '#64B978';

    // Midfielders Wide (purple)
    if (['WM', 'W'].includes(positionType)) return '#B6499A';

    // Forwards (blue)
    if (['CF', 'S', 'WF', 'DF'].includes(positionType)) return '#4258E0';

    // Default
    return '#6b7280';
  };

  // Get the PositionTypes of cards placed on the field by a player
  const getPlayerPositionTypes = (player) => {
    const positionTypes = [];

    // Iterate through all placed cards on the field
    Object.values(cardGameZones).forEach(card => {
      if (card && typeof card === 'object' && card.PositionType) {
        // Determine which player owns this card based on column
        Object.keys(cardGameZones).forEach(key => {
          if (cardGameZones[key] === card) {
            const colIndex = parseInt(key.split('-')[0]);
            const player1Columns = [0, 1, 3, 5];
            const player2Columns = [2, 4, 6, 7];

            if ((player === 1 && player1Columns.includes(colIndex)) ||
                (player === 2 && player2Columns.includes(colIndex))) {
              positionTypes.push(card.PositionType.trim());
            }
          }
        });
      }
    });

    return positionTypes;
  };

  // Get all cards placed by a player (returns array of card objects)
  // Calculate available boosts based on tactic and checked boost requirements
  const calculateAvailableBoosts = (player, tactic) => {
    if (!tactic) return { attBoost: 0, defBoost: 0, totalBoosts: 0 };

    // Count how many boost requirements are met
    let checkedBoosts = 0;
    [tactic.Boost1, tactic.Boost2, tactic.Boost3, tactic.Boost4, tactic.Boost5, tactic.Boost6]
      .filter(boost => boost && boost.trim() !== '')
      .forEach(boost => {
        if (isBoostRequirementMet(boost, player)) {
          checkedBoosts++;
        }
      });

    // Maximum boosts available = min(checkedBoosts, max boosts for tactic)
    const maxAttBoost = tactic.AttBoost || 0;
    const maxDefBoost = tactic.DefBoost || 0;
    const totalAvailable = Math.min(checkedBoosts, maxAttBoost + maxDefBoost);

    // If tactic has only attack or only defense boosts, all checked boosts go to that type
    if (maxAttBoost > 0 && maxDefBoost === 0) {
      return { attBoost: Math.min(checkedBoosts, maxAttBoost), defBoost: 0, totalBoosts: totalAvailable };
    } else if (maxDefBoost > 0 && maxAttBoost === 0) {
      return { attBoost: 0, defBoost: Math.min(checkedBoosts, maxDefBoost), totalBoosts: totalAvailable };
    } else {
      // Both types available - player can distribute up to totalAvailable across both types
      // Individual maximums still apply (can't exceed maxAttBoost or maxDefBoost for each type)
      return {
        attBoost: Math.min(checkedBoosts, maxAttBoost),
        defBoost: Math.min(checkedBoosts, maxDefBoost),
        totalBoosts: totalAvailable
      };
    }
  };

  // Check if a boost requirement is met for current player
  const isBoostRequirementMet = (boostString, player) => {
    const playerPositionTypes = getPlayerPositionTypes(player);

    // Parse the boost string (e.g., "GK + CM" or "FB + FB" or "SK")
    const requiredPositions = boostString.split('+').map(p => p.trim());

    // Check if player has all required position types
    return requiredPositions.every(reqPos => {
      const hasPosition = playerPositionTypes.includes(reqPos);
      // For duplicates like "FB + FB", remove the first occurrence after finding it
      if (hasPosition) {
        const index = playerPositionTypes.indexOf(reqPos);
        playerPositionTypes.splice(index, 1);
      }
      return hasPosition;
    });
  };

  // Check if a tactic requirement is met for current player
  const isTacticRequirementMet = (tactic, player) => {
    const formation = player === 1 ? player1Formation : player2Formation;

    return (
      formation.Keeper >= tactic.KEEPER &&
      formation.DefenderCenter >= tactic['DEFENDER CENTER'] &&
      formation.DefenderWide >= tactic['DEFENDER WIDE'] &&
      formation.MidfielderCenter >= tactic['MIDFIELDER CENTER'] &&
      formation.MidfielderWide >= tactic['MIDFIELDER WIDE'] &&
      formation.Forward >= tactic.FORWARD
    );
  };

  // Check if a tactic is still achievable given current formation and remaining cards
  const isTacticStillAchievable = (tactic, player) => {
    const formation = player === 1 ? player1Formation : player2Formation;
    const totalPlaced = Object.values(formation).reduce((sum, count) => sum + count, 0);
    const cardsRemaining = 11 - totalPlaced;

    // Check each position requirement
    const keeper = formation.Keeper;
    const defenderCenter = formation.DefenderCenter;
    const defenderWide = formation.DefenderWide;
    const midfielderCenter = formation.MidfielderCenter;
    const midfielderWide = formation.MidfielderWide;
    const forward = formation.Forward;

    // Calculate gaps between current and required
    const keeperGap = Math.max(0, tactic.KEEPER - keeper);
    const dcGap = Math.max(0, tactic['DEFENDER CENTER'] - defenderCenter);
    const dwGap = Math.max(0, tactic['DEFENDER WIDE'] - defenderWide);
    const mcGap = Math.max(0, tactic['MIDFIELDER CENTER'] - midfielderCenter);
    const mwGap = Math.max(0, tactic['MIDFIELDER WIDE'] - midfielderWide);
    const fwdGap = Math.max(0, tactic.FORWARD - forward);

    // If we already exceeded a requirement, tactic is impossible
    if (keeper > tactic.KEEPER) return false;
    if (defenderCenter > tactic['DEFENDER CENTER']) return false;
    if (defenderWide > tactic['DEFENDER WIDE']) return false;
    if (midfielderCenter > tactic['MIDFIELDER CENTER']) return false;
    if (midfielderWide > tactic['MIDFIELDER WIDE']) return false;
    if (forward > tactic.FORWARD) return false;

    // Check if we have enough remaining cards to fill the gaps
    const totalGap = keeperGap + dcGap + dwGap + mcGap + mwGap + fwdGap;

    // We need to fill gaps AND reach exactly 11 cards total
    // So we need: totalGap + (cardsRemaining - totalGap) to fill other positions within max limits
    if (totalGap > cardsRemaining) return false;

    return true;
  };

  // Get tactic status: 'completed', 'locked', 'invalid', or 'active'
  const getTacticStatus = (player) => {
    const tactics = player === 1 ? player1Tactics : player2Tactics;

    const statuses = tactics.map(tactic => {
      const isMet = isTacticRequirementMet(tactic, player);
      const isAchievable = isTacticStillAchievable(tactic, player);

      return {
        tactic,
        isMet,
        isAchievable
      };
    });

    // Count how many are still achievable (not completed, not invalid)
    const achievableCount = statuses.filter(s => !s.isMet && s.isAchievable).length;
    const completedCount = statuses.filter(s => s.isMet).length;

    return statuses.map(status => {
      if (status.isMet) {
        return { ...status, displayStatus: 'completed' };
      } else if (!status.isAchievable) {
        return { ...status, displayStatus: 'invalid' };
      } else if (achievableCount === 1 && completedCount === 0) {
        // Only one tactic left achievable and none completed yet
        return { ...status, displayStatus: 'locked' };
      } else {
        return { ...status, displayStatus: 'active' };
      }
    });
  };

  // Check if current player can take a card from this deck type
  const canPlayerSelectFromDeck = (deckType, player) => {
    if (gameStage !== 'draft') return true; // Only apply in draft stage

    const formation = player === 1 ? player1Formation : player2Formation;
    const category = getCardTypeCategory(deckType);

    // Formation rules - designed to guarantee at least one tactic is achievable
    const rules = {
      Keeper: { min: 1, max: 1 },
      DefenderCenter: { min: 2, max: 3 },
      DefenderWide: { min: 0, max: 2 },
      MidfielderCenter: { min: 2, max: 3 },
      MidfielderWide: { min: 0, max: 2 },
      Forward: { min: 1, max: 3 }
    };

    const currentCount = formation[category] || 0;
    const rule = rules[category];

    // Check if player has reached maximum for this position
    if (currentCount >= rule.max) {
      return false;
    }

    // Calculate how many cards this player has left to place
    const totalPlaced = Object.values(formation).reduce((sum, count) => sum + count, 0);
    const cardsRemaining = 11 - totalPlaced;

    // Get tactic statuses to see if we have a locked tactic
    const tacticStatuses = getTacticStatus(player);
    const lockedTactic = tacticStatuses.find(s => s.displayStatus === 'locked');

    // NEW VALIDATION: Check if ALL achievable tactics would be violated by this selection
    // Get all tactics that are still achievable (not completed and not invalid)
    const achievableTactics = tacticStatuses.filter(s =>
      s.displayStatus === 'active' || s.displayStatus === 'locked'
    );

    // If there are achievable tactics, check if adding one card to this position would exceed
    // the maximum for ALL of them
    if (achievableTactics.length > 0) {
      const wouldViolateAllTactics = achievableTactics.every(statusObj => {
        const tactic = statusObj.tactic;

        // Check if current count already meets or exceeds this tactic's requirement
        if (category === 'Keeper' && currentCount >= tactic.KEEPER) return true;
        if (category === 'DefenderCenter' && currentCount >= tactic['DEFENDER CENTER']) return true;
        if (category === 'DefenderWide' && currentCount >= tactic['DEFENDER WIDE']) return true;
        if (category === 'MidfielderCenter' && currentCount >= tactic['MIDFIELDER CENTER']) return true;
        if (category === 'MidfielderWide' && currentCount >= tactic['MIDFIELDER WIDE']) return true;
        if (category === 'Forward' && currentCount >= tactic.FORWARD) return true;

        return false;
      });

      // If selecting from this deck would violate ALL achievable tactics, block it
      if (wouldViolateAllTactics) {
        return false;
      }
    }

    // If there's a locked tactic, prioritize its requirements
    if (lockedTactic) {
      const tactic = lockedTactic.tactic;

      // Check if selecting this category would violate the locked tactic's max requirements
      if (category === 'Keeper' && formation.Keeper >= tactic.KEEPER) return false;
      if (category === 'DefenderCenter' && formation.DefenderCenter >= tactic['DEFENDER CENTER']) return false;
      if (category === 'DefenderWide' && formation.DefenderWide >= tactic['DEFENDER WIDE']) return false;
      if (category === 'MidfielderCenter' && formation.MidfielderCenter >= tactic['MIDFIELDER CENTER']) return false;
      if (category === 'MidfielderWide' && formation.MidfielderWide >= tactic['MIDFIELDER WIDE']) return false;
      if (category === 'Forward' && formation.Forward >= tactic.FORWARD) return false;

      // Simulate taking a card from this category
      const wouldHaveAfterSelection = {
        Keeper: formation.Keeper + (category === 'Keeper' ? 1 : 0),
        DefenderCenter: formation.DefenderCenter + (category === 'DefenderCenter' ? 1 : 0),
        DefenderWide: formation.DefenderWide + (category === 'DefenderWide' ? 1 : 0),
        MidfielderCenter: formation.MidfielderCenter + (category === 'MidfielderCenter' ? 1 : 0),
        MidfielderWide: formation.MidfielderWide + (category === 'MidfielderWide' ? 1 : 0),
        Forward: formation.Forward + (category === 'Forward' ? 1 : 0)
      };

      // Calculate remaining gaps for locked tactic
      const gaps = {
        Keeper: Math.max(0, tactic.KEEPER - wouldHaveAfterSelection.Keeper),
        DefenderCenter: Math.max(0, tactic['DEFENDER CENTER'] - wouldHaveAfterSelection.DefenderCenter),
        DefenderWide: Math.max(0, tactic['DEFENDER WIDE'] - wouldHaveAfterSelection.DefenderWide),
        MidfielderCenter: Math.max(0, tactic['MIDFIELDER CENTER'] - wouldHaveAfterSelection.MidfielderCenter),
        MidfielderWide: Math.max(0, tactic['MIDFIELDER WIDE'] - wouldHaveAfterSelection.MidfielderWide),
        Forward: Math.max(0, tactic.FORWARD - wouldHaveAfterSelection.Forward)
      };

      const totalGapAfter = Object.values(gaps).reduce((sum, gap) => sum + gap, 0);
      const cardsAfterSelection = cardsRemaining - 1;

      // Ensure we can still fill the locked tactic's requirements
      if (totalGapAfter > cardsAfterSelection) {
        return false;
      }

      return true;
    }

    // No locked tactic yet - use flexible rules that maintain at least one achievable tactic

    // Simulate taking a card from this category
    const wouldHaveAfterSelection = {
      Keeper: formation.Keeper + (category === 'Keeper' ? 1 : 0),
      DefenderCenter: formation.DefenderCenter + (category === 'DefenderCenter' ? 1 : 0),
      DefenderWide: formation.DefenderWide + (category === 'DefenderWide' ? 1 : 0),
      MidfielderCenter: formation.MidfielderCenter + (category === 'MidfielderCenter' ? 1 : 0),
      MidfielderWide: formation.MidfielderWide + (category === 'MidfielderWide' ? 1 : 0),
      Forward: formation.Forward + (category === 'Forward' ? 1 : 0)
    };

    // Calculate what would still be needed after this selection
    let stillNeededAfter = 0;

    // Keeper
    if (wouldHaveAfterSelection.Keeper < 1) stillNeededAfter += (1 - wouldHaveAfterSelection.Keeper);

    // DefenderCenter
    if (wouldHaveAfterSelection.DefenderCenter < 2) stillNeededAfter += (2 - wouldHaveAfterSelection.DefenderCenter);

    // MidfielderCenter
    if (wouldHaveAfterSelection.MidfielderCenter < 2) stillNeededAfter += (2 - wouldHaveAfterSelection.MidfielderCenter);

    // Forward
    if (wouldHaveAfterSelection.Forward < 1) stillNeededAfter += (1 - wouldHaveAfterSelection.Forward);

    // Wide positions after selection
    const wouldHaveDefWide = wouldHaveAfterSelection.DefenderWide;
    const wouldHaveMidWide = wouldHaveAfterSelection.MidfielderWide;

    // If at 1, must complete to 2
    if (wouldHaveDefWide === 1) {
      stillNeededAfter += 1;
    }
    if (wouldHaveMidWide === 1) {
      stillNeededAfter += 1;
    }

    // If both would be 0, need at least one complete set
    if (wouldHaveDefWide === 0 && wouldHaveMidWide === 0) {
      stillNeededAfter += 2;
    }

    // Cards remaining after this selection
    const cardsAfterSelection = cardsRemaining - 1;

    // If we wouldn't have enough cards left to meet requirements, block this selection
    if (cardsAfterSelection < stillNeededAfter) {
      return false;
    }

    return true;
  };

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================

  const handleCardGameZoneClick = (colIndex, rowIndex) => {
    const slotKey = `${colIndex}-${rowIndex}`;

    // During DRAFT stage, handle click-to-place card placement
    if (gameStage === 'draft' && draftCardForPlacement) {
      // Check if this slot is highlighted (valid for placement)
      if (!highlightedSlots.includes(slotKey)) {
        console.log('❌ Invalid slot - not highlighted');
        return;
      }

      // Get the slot color to validate placement
      let targetSlotColor = null;
      cardGameZonesData.forEach((column, cIdx) => {
        if (cIdx === colIndex) {
          column.forEach((slot) => {
            if (slot.row === rowIndex) {
              targetSlotColor = slot.color;
            }
          });
        }
      });

      // Validate placement
      const validColors = getValidSlotsForCardType(draftCardForPlacement.card.CardType);
      const isColorValid = validColors.includes(targetSlotColor);
      const isColumnValid = isValidColumnForPlayer(colIndex, currentPlayer);
      const isOccupied = cardGameZones[slotKey] && typeof cardGameZones[slotKey] === 'object';

      if (!isColorValid || !isColumnValid || isOccupied) {
        console.log('❌ Invalid placement:', { isColorValid, isColumnValid, isOccupied });
        return;
      }

      // Place card
      setCardGameZones(prev => {
        const newZones = { ...prev };
        newZones[slotKey] = draftCardForPlacement.card;
        return newZones;
      });

      // Remove card from deck (only if not already removed)
      if (!draftCardForPlacement.alreadyRemovedFromDeck) {
        setDecksForPlayer(currentPlayer, prev => {
          const newDecks = { ...prev };
          if (draftCardForPlacement.deckType === 'ForwardCenter') {
            const forwardCenter = [...(newDecks.ForwardCenter || [])];
            const forwardWide = [...(newDecks.ForwardWide || [])];
            const centerIndex = forwardCenter.findIndex(c => c.ID === draftCardForPlacement.card.ID);
            const wideIndex = forwardWide.findIndex(c => c.ID === draftCardForPlacement.card.ID);

            if (centerIndex !== -1) {
              forwardCenter.splice(centerIndex, 1);
              newDecks.ForwardCenter = forwardCenter;
            } else if (wideIndex !== -1) {
              forwardWide.splice(wideIndex, 1);
              newDecks.ForwardWide = forwardWide;
            }
          } else {
            const deck = [...(newDecks[draftCardForPlacement.deckType] || [])];
            const cardIndex = deck.findIndex(c => c.ID === draftCardForPlacement.card.ID);
            if (cardIndex !== -1) {
              deck.splice(cardIndex, 1);
              newDecks[draftCardForPlacement.deckType] = deck;
            }
          }
          return newDecks;
        });
      }

      // Update card count and formation
      setCardsPlaced(prev => prev + 1);
      const category = getCardTypeCategory(draftCardForPlacement.card.CardType);
      if (currentPlayer === 1) {
        setPlayer1Formation(prev => ({ ...prev, [category]: prev[category] + 1 }));
      } else {
        setPlayer2Formation(prev => ({ ...prev, [category]: prev[category] + 1 }));
      }

      // Log event
      const positionColor = getPositionColor(draftCardForPlacement.card.CardType);
      addEventLog({
        type: 'draft',
        player: currentPlayer,
        playerName: draftCardForPlacement.card.PlayerName,
        cardType: draftCardForPlacement.card.CardType,
        positionType: draftCardForPlacement.card.PositionType,
        positionColor: positionColor
      });

      // Switch player and clear state
      setCurrentPlayer(prev => prev === 1 ? 2 : 1);
      setVisibleCards([]);
      setSelectedDeck(null);
      setCardSelections({ toPlay: null, toBench: null, toDiscard: null });
      setHighlightedSlots([]);
      setDraftCardForPlacement(null);

      return;
    }

    // During BATTLE stage, handle substitute card placement
    if (gameStage === 'battle' && substituteMode && substituteStep === 'place_card') {
      // Check if this slot is highlighted (valid for placement)
      if (highlightedSlots.includes(slotKey)) {
        // Place the selected card
        setCardGameZones(prev => ({
          ...prev,
          [slotKey]: substituteSelectedCard
        }));

        // Transfer captaincy if the removed card was captain
        if (substituteRemovedCard?.wasCaptain) {
          if (currentPlayer === 1) {
            setPlayer1Captain(substituteSelectedCard.Card_Id);
          } else {
            setPlayer2Captain(substituteSelectedCard.Card_Id);
          }
          console.log(`👑 Captaincy transferred to ${substituteSelectedCard.PlayerName}`);
        }

        // Deduct AP cost (3) unless a free sub is available
        if (freeSubsRemaining > 0) {
          setFreeSubsRemaining(prev => prev - 1);
        } else {
          const setAP = currentPlayer === 1 ? setPlayer1ActionPoints : setPlayer2ActionPoints;
          const currentAP = currentPlayer === 1 ? player1ActionPoints : player2ActionPoints;
          setAP(currentAP - 3);
        }

        // Add event log
        addEventLog({
          type: 'substitute',
          player: currentPlayer,
          oldCard: substituteRemovedCard?.card?.PlayerName,
          newCard: substituteSelectedCard.PlayerName,
          position: substituteSelectedCard.PositionType,
          positionColor: getPositionColor(substituteSelectedCard.CardType),
          captainTransferred: substituteRemovedCard?.wasCaptain,
          apCost: freeSubsRemaining > 0 ? 0 : 3
        });

        // Reset substitute mode
        setSubstituteMode(false);
        setSubstituteStep(null);
        setSubstituteSelectedDeck(null);
        setSubstituteVisibleCards([]);
        setSubstituteSelectedCard(null);
        setSubstituteRemovedCard(null);
        setHighlightedSlots([]);

        console.log(`🔄 Substituted ${substituteRemovedCard?.card?.PlayerName} with ${substituteSelectedCard.PlayerName} at ${slotKey}`);
      }
      return;
    }

    // During WARMUP stage, allow clicking on own cards to select them for boosting
    if (gameStage === 'warmup') {
      const card = cardGameZones[slotKey];

      if (card && typeof card === 'object') {
        // Check if this card belongs to current player
        const player1Columns = [0, 1, 3, 5];
        const player2Columns = [2, 4, 6, 7];

        const isPlayer1Card = player1Columns.includes(colIndex);
        const isPlayer2Card = player2Columns.includes(colIndex);

        if ((currentPlayer === 1 && isPlayer1Card) || (currentPlayer === 2 && isPlayer2Card)) {
          setWarmUpSelectedCard(card);
        }
      }
    }
  };

  const handleDeckClick = (deckType, event) => {
    event.preventDefault();

    // If a card is being held to place, don't allow deck clicks
    if (draggedCard) return;

    // If a deck is already selected, don't allow clicking other decks
    if (selectedDeck && selectedDeck !== deckType) {
      return;
    }

    // Check if current player can select from this deck (formation rules)
    if (!canPlayerSelectFromDeck(deckType, currentPlayer)) {
      // Optional: Show a message or visual feedback that this deck is not available
      return;
    }

    const deckCards = deckType === 'ForwardCenter'
      ? [...(activeDecks.ForwardCenter || []), ...(activeDecks.ForwardWide || [])]
      : activeDecks[deckType] || [];

    if (deckCards.length === 0) return;

    // Show top 3 cards in sidebar — shuffle display order so bench card position is not predictable
    setSelectedDeck(deckType);
    setVisibleCards(shuffleArray(deckCards.slice(0, 3)));
    setCardSelections({
      toPlay: null,
      toBench: null,
      toDiscard: null
    });
  };

  const handleCardRoleClick = (card, index) => {
    // Determine which role to assign based on current selections
    if (!cardSelections.toPlay) {
      setCardSelections(prev => ({ ...prev, toPlay: { card, index } }));
    } else if (!cardSelections.toBench) {
      setCardSelections(prev => ({ ...prev, toBench: { card, index } }));
    } else if (!cardSelections.toDiscard) {
      setCardSelections(prev => ({ ...prev, toDiscard: { card, index } }));
    }
  };

  const handleResetSelections = () => {
    setCardSelections({
      toPlay: null,
      toBench: null,
      toDiscard: null
    });
  };

  // Get available decks for current player (respecting formation rules)
  const getAvailableDecks = (player) => {
    const decks = getDecksForPlayer(player);
    const availableTypes = [];
    playerDeckZonesData.forEach(zone => {
      if (canPlayerSelectFromDeck(zone.type, player)) {
        const deckCards = zone.type === 'ForwardCenter'
          ? [...(decks.ForwardCenter || []), ...(decks.ForwardWide || [])]
          : decks[zone.type] || [];
        if (deckCards.length > 0) {
          availableTypes.push(zone.type);
        }
      }
    });
    return availableTypes;
  };

  // Get available decks for substitute action (only decks matching card types on player's board)
  const getAvailableSubstituteDecks = (player) => {
    // Condition 1: there must be at least one card on the board to remove
    const playerColumns = player === 1 ? [0, 1, 3, 5] : [2, 4, 6, 7];
    let hasCardOnBoard = false;

    playerColumns.forEach(colIndex => {
      cardGameZonesData[colIndex].forEach(slot => {
        const slotKey = `${colIndex}-${slot.row}`;
        const card = cardGameZones[slotKey];
        if (card && typeof card === 'object') {
          hasCardOnBoard = true;
        }
      });
    });

    if (!hasCardOnBoard) return [];

    // Condition 2: return every deck that has at least 1 card available.
    // Which decks are actually selectable (empty slot check) is handled
    // per-deck in the step-2 isDisabled logic — no need to duplicate it here.
    const playerDecks = getDecksForPlayer(player);
    const availableDecks = [];

    playerDeckZonesData.forEach(zone => {
      const deckCards = zone.type === 'ForwardCenter'
        ? [...(playerDecks.ForwardCenter || []), ...(playerDecks.ForwardWide || [])]
        : playerDecks[zone.type] || [];

      if (deckCards.length >= 1) {
        availableDecks.push(zone.type);
      }
    });

    return availableDecks;
  };

  // Get valid slots for a card on current player's side
  const getValidSlotsForPlayer = (card, player) => {
    const validColors = getValidSlotsForCardType(card.CardType);
    const validSlots = [];

    cardGameZonesData.forEach((column, colIndex) => {
      column.forEach((slot) => {
        const slotKey = `${colIndex}-${slot.row}`;
        const isColorValid = validColors.includes(slot.color);
        const isColumnValid = isValidColumnForPlayer(colIndex, player);
        const isOccupied = cardGameZones[slotKey] && typeof cardGameZones[slotKey] === 'object';

        if (isColorValid && isColumnValid && !isOccupied) {
          validSlots.push(slotKey);
        }
      });
    });

    return validSlots;
  };

  // Handle Random Next - draft one random card for current player
  const handleRandomNext = () => {
    if (gameStage !== 'draft' || selectedDeck !== null) return;

    // Get available decks for current player
    const availableDecks = getAvailableDecks(currentPlayer);
    if (availableDecks.length === 0) return;

    // Pick deck — CPU in Solo mode strictly follows the stage tactic formation
    let randomDeckType = availableDecks[Math.floor(Math.random() * availableDecks.length)];
    if (isSolo && currentPlayer === aiPlayer && cpuStage && allTacticsData.length > 0) {
      const tacticName = (cpuStage['Tactic '] || cpuStage['Tactic'] || '').trim();
      const tactic = allTacticsData.find(t => (t['TACTIC NAME'] || '').trim() === tacticName);
      if (tactic) {
        // Required counts from tactic CSV (ForwardCenter covers both FC and FW)
        const required = {
          Keeper:           parseInt(tactic['KEEPER'])            || 0,
          DefenderCenter:   parseInt(tactic['DEFENDER CENTER'])   || 0,
          DefenderWide:     parseInt(tactic['DEFENDER WIDE'])     || 0,
          MidfielderCenter: parseInt(tactic['MIDFIELDER CENTER']) || 0,
          MidfielderWide:   parseInt(tactic['MIDFIELDER WIDE'])   || 0,
          ForwardCenter:    parseInt(tactic['FORWARD'])           || 0,
        };
        // Count already placed cards per type for this AI player
        const aiCols = aiPlayer === 1 ? [0,1,3,5] : [2,4,6,7];
        const placed = { Keeper:0, DefenderCenter:0, DefenderWide:0, MidfielderCenter:0, MidfielderWide:0, ForwardCenter:0 };
        Object.entries(cardGameZones).forEach(([k, c]) => {
          if (!c?.CardType) return;
          if (!aiCols.includes(parseInt(k.split('-')[0]))) return;
          const bucket = (c.CardType === 'ForwardWide') ? 'ForwardCenter' : c.CardType;
          placed[bucket] = (placed[bucket] || 0) + 1;
        });
        // Find deck types still needed and with cards available
        const aiDecks = getDecksForPlayer(aiPlayer);
        const stillNeeded = Object.keys(required).filter(dt => {
          if ((required[dt] - (placed[dt] || 0)) <= 0) return false;
          const cards = dt === 'ForwardCenter'
            ? [...(aiDecks.ForwardCenter || []), ...(aiDecks.ForwardWide || [])]
            : aiDecks[dt] || [];
          return cards.length > 0;
        });
        // Pick randomly within still-needed types (natural order, strict quota)
        if (stillNeeded.length > 0) {
          randomDeckType = stillNeeded[Math.floor(Math.random() * stillNeeded.length)];
        }
      }
    }

    // Get top 3 cards from that deck — shuffle the combined Forward deck so FC/FW cards interleave
    const deckCards = randomDeckType === 'ForwardCenter'
      ? [...(activeDecks.ForwardCenter || []), ...(activeDecks.ForwardWide || [])]
      : activeDecks[randomDeckType] || [];

    if (deckCards.length === 0) return;

    const top3Cards = deckCards.slice(0, 3);

    // Randomly assign toPlay, toBench, toDiscard (Fisher-Yates, not biased sort)
    const shuffled = shuffleArray([...top3Cards]);
    const playCard = shuffled[0];
    const benchCard = shuffled[1];
    const discardCard = shuffled[2];

    // Find valid placement slot
    const validSlots = getValidSlotsForPlayer(playCard, currentPlayer);
    if (validSlots.length === 0) return;

    const randomSlot = validSlots[Math.floor(Math.random() * validSlots.length)];

    // Execute the draft similar to handleConfirmSelections + handleMouseUp
    // 1. Update decks
    setDecksForPlayer(currentPlayer, prev => {
      const newDecks = { ...prev };
      const deckType = randomDeckType;

      if (deckType === 'ForwardCenter') {
        let forwardCenter = [...(newDecks.ForwardCenter || [])];
        let forwardWide = [...(newDecks.ForwardWide || [])];

        [playCard, discardCard].forEach(card => {
          const centerIdx = forwardCenter.findIndex(c => c.ID === card.ID);
          const wideIdx = forwardWide.findIndex(c => c.ID === card.ID);
          if (centerIdx !== -1) {
            forwardCenter.splice(centerIdx, 1);
          } else if (wideIdx !== -1) {
            forwardWide.splice(wideIdx, 1);
          }
        });

        const benchCenterIdx = forwardCenter.findIndex(c => c.ID === benchCard.ID);
        const benchWideIdx = forwardWide.findIndex(c => c.ID === benchCard.ID);
        if (benchCenterIdx !== -1) {
          forwardCenter.splice(benchCenterIdx, 1);
          forwardCenter.unshift(benchCard);
        } else if (benchWideIdx !== -1) {
          forwardWide.splice(benchWideIdx, 1);
          forwardWide.unshift(benchCard);
        }

        newDecks.ForwardCenter = forwardCenter;
        newDecks.ForwardWide = forwardWide;
      } else {
        let deck = [...(newDecks[deckType] || [])];

        [playCard, discardCard].forEach(card => {
          const idx = deck.findIndex(c => c.ID === card.ID);
          if (idx !== -1) deck.splice(idx, 1);
        });

        const benchIdx = deck.findIndex(c => c.ID === benchCard.ID);
        if (benchIdx !== -1) {
          deck.splice(benchIdx, 1);
          deck.unshift(benchCard);
        }

        newDecks[deckType] = deck;
      }

      return newDecks;
    });

    // 2. Add discard to cards out
    setRecycledCardsCount(prev => prev + 1);

    // 3. Place card on board
    setCardGameZones(prev => ({
      ...prev,
      [randomSlot]: playCard
    }));

    // 3.5 Log the draft event
    const positionColor = getPositionColor(playCard.CardType);
    addEventLog({
      type: 'draft',
      player: currentPlayer,
      playerName: playCard.PlayerName,
      cardType: playCard.CardType,
      positionType: playCard.PositionType,
      positionColor: positionColor
    });

    // 4. Update formation and turn
    const category = getCardTypeCategory(playCard.CardType);
    if (currentPlayer === 1) {
      setPlayer1Formation(prev => ({
        ...prev,
        [category]: prev[category] + 1
      }));
    } else {
      setPlayer2Formation(prev => ({
        ...prev,
        [category]: prev[category] + 1
      }));
    }

    setCardsPlaced(prev => prev + 1);
    setCurrentPlayer(prev => prev === 1 ? 2 : 1);
  };

  // Handle Random All - draft entire team randomly for current player only
  const handleRandomAll = () => {
    if (gameStage !== 'draft' || selectedDeck !== null) return;

    // Calculate how many cards this player has already placed
    const formation = currentPlayer === 1 ? player1Formation : player2Formation;
    const cardsAlreadyPlaced = Object.values(formation).reduce((sum, count) => sum + count, 0);

    if (currentPlayer === 1) {
      setPlayer1RandomAllActive(true);
      setPlayer1RandomAllStartCount(cardsAlreadyPlaced);
    } else {
      setPlayer2RandomAllActive(true);
      setPlayer2RandomAllStartCount(cardsAlreadyPlaced);
    }
  };

  // useEffect to handle Random All mode - works for both players independently
  useEffect(() => {
    if (gameStage !== 'draft' || cardsPlaced >= 22) return;

    // Check if current player has Random All active
    const isCurrentPlayerRandomAll = currentPlayer === 1 ? player1RandomAllActive : player2RandomAllActive;

    if (!isCurrentPlayerRandomAll) return;

    // Check how many cards current player has placed
    const formation = currentPlayer === 1 ? player1Formation : player2Formation;
    const startCount = currentPlayer === 1 ? player1RandomAllStartCount : player2RandomAllStartCount;
    const currentCardCount = Object.values(formation).reduce((sum, count) => sum + count, 0);
    const cardsPlacedByPlayer = currentCardCount - startCount;

    // Stop Random All after player has placed 11 cards
    if (cardsPlacedByPlayer >= 11) {
      if (currentPlayer === 1) {
        setPlayer1RandomAllActive(false);
        setPlayer1RandomAllStartCount(0);
      } else {
        setPlayer2RandomAllActive(false);
        setPlayer2RandomAllStartCount(0);
      }
      return;
    }

    // Continue if this player hasn't placed all 11 cards yet
    if (cardsPlacedByPlayer < 11) {
      // Small delay to allow UI to update between drafts
      const timer = setTimeout(() => {
        handleRandomNext();
      }, 100); // 100ms delay between each draft

      return () => clearTimeout(timer);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameStage, cardsPlaced, currentPlayer, player1RandomAllActive, player2RandomAllActive, player1Formation, player2Formation, player1RandomAllStartCount, player2RandomAllStartCount]);

  // ── Solo: AI draft auto-play ─────────────────────────────────────────────
  useEffect(() => {
    if (!isSolo || gameStage !== 'draft' || currentPlayer !== aiPlayer) return;
    const isActive = aiPlayer === 1 ? player1RandomAllActive : player2RandomAllActive;
    if (isActive) return;
    const t = setTimeout(() => handleRandomAll(), 800);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSolo, gameStage, currentPlayer, aiPlayer, player1RandomAllActive, player2RandomAllActive]);

  // ── Solo: AI warmup auto-play ────────────────────────────────────────────
  useEffect(() => {
    if (!isSolo || gameStage !== 'warmup' || currentPlayer !== aiPlayer) return;

    const t = setTimeout(() => {
      // Determine AI's columns
      const aiCols = aiPlayer === 1 ? AI_PLAYER1_COLS : AI_PLAYER2_COLS;
      const atkCol  = AI_ATK_COL[aiPlayer];
      const midCol  = AI_MID_COL[aiPlayer];

      // Collect all AI cards on board with their positions
      const aiCards = Object.entries(cardGameZones)
        .filter(([key, card]) => {
          if (!card || !card.Card_Id) return false;
          const col = parseInt(key.split('-')[0]);
          return aiCols.includes(col);
        })
        .map(([key, card]) => ({ key, card, col: parseInt(key.split('-')[0]) }));

      if (aiCards.length === 0) { executeWarmupFinish(); return; }

      // Pick captain: random from top-3 cards by combined (Attack + Defence)
      const sortedByTotal = [...aiCards].sort((a, b) =>
        ((b.card.Attack || 0) + (b.card.Defence || 0)) - ((a.card.Attack || 0) + (a.card.Defence || 0))
      );
      const topPool = sortedByTotal.slice(0, Math.min(3, sortedByTotal.length));
      const captainEntry = topPool[Math.floor(Math.random() * topPool.length)];
      if (aiPlayer === 1) setPlayer1Captain(captainEntry.card.Card_Id);
      else setPlayer2Captain(captainEntry.card.Card_Id);
      addEventLog({
        type: 'captain_add',
        player: aiPlayer,
        playerName: captainEntry.card.PlayerName,
        positionType: captainEntry.card.PositionType?.trim() || captainEntry.card.CardType,
        positionColor: getPositionColor(captainEntry.card.CardType),
      });

      // Distribute warmup boosts — respect totalBoosts as the budget, attBoost/defBoost as per-type caps
      const available = aiPlayer === 1 ? player1AvailableBoosts : player2AvailableBoosts;
      const totalBudget = available.totalBoosts || 0;
      const atkBudget = Math.min(available.attBoost || 0, Math.round(totalBudget * 0.8));
      const defBudget = Math.min(available.defBoost || 0, totalBudget - atkBudget);
      const maxAtt = atkBudget;
      const maxDef = defBudget;
      if (totalBudget > 0) {
        const offensiveCards = aiCards.filter(c => c.col === atkCol || c.col === midCol);
        const defensiveCards = aiCards.filter(c => c.col !== atkCol && c.col !== midCol);
        const boosts = {};
        let attLeft = maxAtt, defLeft = maxDef;

        // 80% of att boosts → offensive cards, 20% → defensive (cross-allocation)
        const crossAtt = Math.floor(maxAtt * 0.2);
        const standardAtt = maxAtt - crossAtt;

        const distributeBoosts = (cards, atkCount, defCount) => {
          if (cards.length === 0) return;
          let aIdx = 0, dIdx = 0;
          for (let i = 0; i < atkCount && attLeft > 0; i++) {
            const c = cards[aIdx % cards.length];
            if (!boosts[c.card.Card_Id]) boosts[c.card.Card_Id] = { attack: 0, defense: 0 };
            boosts[c.card.Card_Id].attack++;
            attLeft--;
            aIdx++;
          }
          for (let i = 0; i < defCount && defLeft > 0; i++) {
            const c = cards[dIdx % cards.length];
            if (!boosts[c.card.Card_Id]) boosts[c.card.Card_Id] = { attack: 0, defense: 0 };
            boosts[c.card.Card_Id].defense++;
            defLeft--;
            dIdx++;
          }
        };

        distributeBoosts(offensiveCards, standardAtt, Math.floor(maxDef * 0.2));
        distributeBoosts(defensiveCards, crossAtt, Math.floor(maxDef * 0.8));
        // Spend any remaining
        const allCards = [...offensiveCards, ...defensiveCards];
        let idx = 0;
        while (attLeft > 0) {
          const c = allCards[idx % allCards.length];
          if (!boosts[c.card.Card_Id]) boosts[c.card.Card_Id] = { attack: 0, defense: 0 };
          boosts[c.card.Card_Id].attack++;
          attLeft--; idx++;
        }
        idx = 0;
        while (defLeft > 0) {
          const c = allCards[idx % allCards.length];
          if (!boosts[c.card.Card_Id]) boosts[c.card.Card_Id] = { attack: 0, defense: 0 };
          boosts[c.card.Card_Id].defense++;
          defLeft--; idx++;
        }

        // Log each boost assignment so the human can see CPU warmup choices
        Object.entries(boosts).forEach(([cardId, b]) => {
          const entry = aiCards.find(c => c.card.Card_Id === parseInt(cardId) || c.card.Card_Id === cardId);
          if (!entry) return;
          if (b.attack > 0) addEventLog({
            type: 'boost_add', player: aiPlayer,
            playerName: entry.card.PlayerName,
            positionType: entry.card.PositionType?.trim() || entry.card.CardType,
            positionColor: getPositionColor(entry.card.CardType),
            stat: 'attack', count: b.attack, apCost: 0,
          });
          if (b.defense > 0) addEventLog({
            type: 'boost_add', player: aiPlayer,
            playerName: entry.card.PlayerName,
            positionType: entry.card.PositionType?.trim() || entry.card.CardType,
            positionColor: getPositionColor(entry.card.CardType),
            stat: 'defense', count: b.defense, apCost: 0,
          });
        });

        if (aiPlayer === 1) setPlayer1AppliedBoosts(boosts);
        else setPlayer2AppliedBoosts(boosts);
      }

      // Small additional delay so state updates settle before finishing warmup
      setTimeout(() => executeWarmupFinish(), 400);
    }, 1400);

    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSolo, gameStage, currentPlayer, aiPlayer, cardGameZones]);

  // ── Solo: AI battle turn engine ──────────────────────────────────────────
  useEffect(() => {
    if (!isSolo || gameStage !== 'battle' || currentPlayer !== aiPlayer) return;
    setAiThinking('CPU planning...');
    aiTimerRef.current = setTimeout(() => executeAiTurn(), 1100);
    return () => { if (aiTimerRef.current) clearTimeout(aiTimerRef.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSolo, gameStage, currentPlayer, aiPlayer]);

  const executeAiTurn = () => {
    // ── Snapshot current state ───────────────────────────────────────────────
    const aiCols      = aiPlayer === 1 ? AI_PLAYER1_COLS : AI_PLAYER2_COLS;
    const aiAP        = aiPlayer === 1 ? player1ActionPoints : player2ActionPoints;
    const aiLevel     = aiPlayer === 1 ? player1MaxActionPoints : player2MaxActionPoints;
    const usedU       = aiPlayer === 1 ? player1UsedUpgrade : player2UsedUpgrade;
    const usedK       = aiPlayer === 1 ? player1UsedKickopp : player2UsedKickopp;
    const appliedB    = aiPlayer === 1 ? { ...player1AppliedBoosts } : { ...player2AppliedBoosts };
    const humanPlayer = aiPlayer === 1 ? 2 : 1;
    const aiKPawns    = aiPlayer === 1 ? player1KickoppPawns : player2KickoppPawns;
    const aiBattleBoosts = aiPlayer === 1 ? player1BattleBoosts : player2BattleBoosts;

    // ── Pre-compute action plan ──────────────────────────────────────────────
    const plan = [];
    let apLeft = aiAP;
    let mainActionUsed = false;
    // Card removed by the Step 0.5 defensive gap-fill (if any) — every later step plans
    // against the same static snapshot, so it must be excluded everywhere else too.
    let removedByGapFill = null;

    // Step 0: Training — add fresh boosts directly to A/D Boost pool each turn (free, no AP cost)
    const trainCount = (() => {
      if (cpuStage) {
        const lambda = parseFloat(cpuStage[`L${aiLevel}`] || cpuStage['L1'] || 0);
        return poissonRandom(lambda);
      }
      return computeAiBoostCount(aiLevel);
    })();
    const propAtk = cpuStage ? parseFloat(cpuStage.PropAtt || 0.5) : (0.7 + Math.random() * 0.15);
    const trainAtk = Math.round(trainCount * propAtk);
    const trainDef = trainCount - trainAtk;
    const boostedAtk = aiBattleBoosts.attack + trainAtk;
    const boostedDef = aiBattleBoosts.defense + trainDef;
    plan.push({ type: 'training', trainAtk, trainDef, newBattleBoosts: { attack: boostedAtk, defense: boostedDef } });

    // Step 0.5: Last-line defensive gap — if the AI's own boundary column (Keeper, then
    // Defender, then Midfielder, working inward from its own edge) is completely empty,
    // the human's most advanced card becomes the board's global leftmost/rightmost occupied
    // column, handing them a free +1 ATK/+1 DEF bonus (worth 3 AP) every single turn this
    // persists. Fixing this takes priority over offense: substitute a bench Keeper/Defender/
    // Midfielder straight into the empty slot, discarding the AI's weakest expendable card.
    if (apLeft >= 3) {
      const isColOccupied = (col) => [0, 1, 2, 3, 4].some(row => cardGameZones[`${col}-${row}`]?.Card_Id);
      let extremeCol = -1;
      if (aiPlayer === 1) {
        for (let col = 0; col <= 7; col++) { if (isColOccupied(col)) { extremeCol = col; break; } }
      } else {
        for (let col = 7; col >= 0; col--) { if (isColOccupied(col)) { extremeCol = col; break; } }
      }
      const vulnerable = extremeCol >= 0 && !aiCols.includes(extremeCol);

      if (vulnerable) {
        const boundaryOrder = aiPlayer === 1 ? [0, 1, 3] : [7, 6, 4]; // Keeper, Defender, Midfielder
        const gapCol = boundaryOrder.find(col => !isColOccupied(col));

        if (gapCol !== undefined) {
          const aiDecks = getDecksForPlayer(aiPlayer);
          const colRows = cardGameZonesData[gapCol] || [];
          const eligibleTypes = ['Keeper', 'DefenderCenter', 'DefenderWide', 'MidfielderCenter', 'MidfielderWide']
            .filter(t => getValidSlotsForCardType(t).some(color => colRows.some(slot => slot.color === color)));

          let fillCard = null, fillType = null, fillRow = null;
          for (const t of eligibleTypes) {
            const candidates = aiDecks[t] || [];
            if (candidates.length === 0) continue;
            const validColors = getValidSlotsForCardType(t);
            const row = colRows.find(slot => validColors.includes(slot.color) && !cardGameZones[`${gapCol}-${slot.row}`]?.Card_Id);
            if (!row) continue;
            const best = candidates.reduce((b, c) => (c.Attack + c.Defence) > (b.Attack + b.Defence) ? c : b, candidates[0]);
            fillCard = best; fillType = t; fillRow = row.row;
            break;
          }

          if (fillCard) {
            const boardCardsList = Object.entries(cardGameZones)
              .filter(([k, c]) => c?.Card_Id && !c.eliminating && aiCols.includes(parseInt(k.split('-')[0])))
              .map(([k, c]) => ({ key: k, card: c }));
            const expendable = boardCardsList.filter(({ card }) =>
              !aiKPawns.some(p => p.cardId === card.Card_Id) &&
              (aiPlayer === 1 ? player1Captain : player2Captain) !== card.Card_Id
            );
            if (expendable.length > 0) {
              const weakest = expendable.reduce((w, c) =>
                (c.card.Attack + c.card.Defence) < (w.card.Attack + w.card.Defence) ? c : w, expendable[0]);

              plan.push({
                type: 'fill_gap',
                removeKey: weakest.key,
                removeCard: weakest.card,
                placeCol: gapCol,
                placeRow: fillRow,
                newCard: fillCard,
                deckType: fillType,
              });
              apLeft -= 3;
              removedByGapFill = weakest.card.Card_Id;
            }
          }
        }
      }
    }

    // Step 1: Find valid Kickopp targets (same column/row rules as human)
    if (!usedK && apLeft >= 1) {
      const aiCardsList = Object.entries(cardGameZones)
        .filter(([k, c]) => c?.Card_Id && !c.eliminating && aiCols.includes(parseInt(k.split('-')[0])) && c.Card_Id !== removedByGapFill)
        .map(([k, c]) => ({ pos: { colIndex: parseInt(k.split('-')[0]), rowIndex: parseInt(k.split('-')[1]) }, card: c }));

      // Cache each attacker's exposure to human counter-threats, so risk doesn't
      // need to be recomputed per target (it only depends on the attacker's position).
      const exposureCache = new Map();
      const getAttackerRisk = (atk) => {
        const key = `${atk.pos.colIndex}-${atk.pos.rowIndex}`;
        if (!exposureCache.has(key)) {
          const threats = getThreatsAgainst(atk.pos, atk.card, aiPlayer);
          exposureCache.set(key, threats.length > 0 ? Math.max(...threats.map(t => t.margin)) : -1);
        }
        return exposureCache.get(key);
      };

      const validAttacks = [];
      for (const atk of aiCardsList) {
        if (aiKPawns.some(p => p.cardId === atk.card.Card_Id)) continue;
        const attackablePositions = getAttackablePositions(atk.pos.colIndex, atk.pos.rowIndex, aiPlayer);
        for (const tgtPos of attackablePositions) {
          const tgtCard = cardGameZones[`${tgtPos.colIndex}-${tgtPos.rowIndex}`];
          if (!tgtCard?.Card_Id || tgtCard.eliminating) continue;
          const dist = Math.abs(atk.pos.colIndex - tgtPos.colIndex);
          const distBonus = dist > 1 ? dist - 1 : 0;
          const atkVal = getCardAttack(atk.card, aiPlayer) + distBonus;
          const defVal = getCardDefense(tgtCard, humanPlayer);
          if (atkVal > defVal) {
            validAttacks.push({ atk, tgt: { pos: tgtPos, card: tgtCard }, margin: atkVal - defVal, atkVal, defVal, risk: getAttackerRisk(atk) });
          }
        }
      }

      if (validAttacks.length > 0) {
        const bestMargin = validAttacks.reduce((m, a) => Math.max(m, a.margin), -Infinity);
        const topTier = validAttacks.filter(a => a.margin >= bestMargin - ATTACK_RISK_TOLERANCE);

        // Among near-best attacks, prefer one whose attacker isn't already
        // exposed to a human counter-threat (lower risk = safer).
        const safestInTopTier = topTier.reduce((best, a) => {
          if (!best) return a;
          if (a.risk < best.risk) return a;
          if (a.risk === best.risk && a.margin > best.margin) return a;
          return best;
        }, null);

        const chosen = Math.random() < 0.7
          ? safestInTopTier
          : validAttacks[Math.floor(Math.random() * validAttacks.length)];
        plan.push({ type: 'attack', atk: chosen.atk, tgt: chosen.tgt, atkVal: chosen.atkVal, defVal: chosen.defVal });
        apLeft--;
        mainActionUsed = true;
      }
    }

    // Step 2: Upgrade if Kickopp was not used (mutually exclusive)
    if (!usedU && !mainActionUsed && apLeft >= 1) {
      plan.push({ type: 'upgrade', newLevel: aiLevel + 1 });
      apLeft--;
      mainActionUsed = true;
    }

    // Step 2.5: Move (1 AP) — reposition a card 1 row to align with a human card for a future attack
    // Only tried when AP remains and no main action fired yet (or after main action if AP > 1)
    if (apLeft >= 1) {
      // findAiMove: find an AI card that is 1 row away from aligning with a human target
      const humanOpponentCols = aiPlayer === 1 ? AI_PLAYER2_COLS : AI_PLAYER1_COLS;
      const aiCardsForMove = Object.entries(cardGameZones)
        .filter(([k, c]) => c?.Card_Id && !c.eliminating && aiCols.includes(parseInt(k.split('-')[0])) && c.Card_Id !== removedByGapFill)
        .map(([k, c]) => ({ pos: { colIndex: parseInt(k.split('-')[0]), rowIndex: parseInt(k.split('-')[1]) }, card: c, key: k }));

      const possibleMoves = [];
      for (const aiCard of aiCardsForMove) {
        if (aiKPawns.some(p => p.cardId === aiCard.card.Card_Id)) continue;
        if (aiCard.pos.colIndex === AI_KEEP_COL[aiPlayer]) continue; // keepers have only 1 row — no valid moves
        // Columns this card can attack
        const targetCols = aiPlayer === 1
          ? humanOpponentCols.filter(col => col > aiCard.pos.colIndex)
          : humanOpponentCols.filter(col => col < aiCard.pos.colIndex);
        if (targetCols.length === 0) continue;

        for (const tCol of targetCols) {
          const humanCardsInCol = Object.entries(cardGameZones)
            .filter(([k, c]) => c?.Card_Id && !c.eliminating && parseInt(k.split('-')[0]) === tCol)
            .map(([k, c]) => ({ rowIndex: parseInt(k.split('-')[1]), card: c }));
          for (const hCard of humanCardsInCol) {
            const rowDiff = hCard.rowIndex - aiCard.pos.rowIndex;
            if (Math.abs(rowDiff) !== 1) continue; // only 1-row moves (= 1 AP)
            const newRow = aiCard.pos.rowIndex + rowDiff;
            const newKey = `${aiCard.pos.colIndex}-${newRow}`;
            if (cardGameZones[newKey]?.Card_Id) continue; // slot must be empty
            const newPos = { colIndex: aiCard.pos.colIndex, rowIndex: newRow };
            // Skip if any human card would be able to kickopp the CPU card at newPos
            // on the NEXT turn (human plays first). getThreatsAgainst only works for
            // occupied positions; newPos is empty until after the move, so we check
            // geometry directly: same row, opponent's column can reach newPos.colIndex,
            // and opponent's ATK beats our DEF.
            const cpuDefVal = getCardDefense(aiCard.card, aiPlayer);
            const isVulnerableAfterMove = Object.entries(cardGameZones).some(([hKey, hCard_]) => {
              if (!hCard_?.Card_Id || hCard_.eliminating) return false;
              const hCol_ = parseInt(hKey.split('-')[0]);
              const hRow_ = parseInt(hKey.split('-')[1]);
              if (!humanOpponentCols.includes(hCol_)) return false;
              if (hRow_ !== newPos.rowIndex) return false; // must be same row
              // humanOpponentCols attacks in direction opposite to aiPlayer
              const canReach = aiPlayer === 1
                ? newPos.colIndex < hCol_  // human(p2) attacks right→left
                : newPos.colIndex > hCol_; // human(p1) attacks left→right
              if (!canReach) return false;
              const dist_ = Math.abs(hCol_ - newPos.colIndex);
              const distBonus_ = dist_ > 1 ? dist_ - 1 : 0;
              return (getCardAttack(hCard_, humanPlayer) + distBonus_) > cpuDefVal;
            });
            if (isVulnerableAfterMove) continue;
            // Compute margin (positive = winning attack, negative = positioning move that
            // gets into range so boosts can bridge the gap in future turns).
            const dist = Math.abs(aiCard.pos.colIndex - tCol);
            const distBonus = dist > 1 ? dist - 1 : 0;
            const atkVal = getCardAttack(aiCard.card, aiPlayer) + distBonus;
            const defVal = getCardDefense(hCard.card, humanPlayer);
            possibleMoves.push({ srcPos: aiCard.pos, tgtPos: newPos, card: aiCard.card, margin: atkVal - defVal });
          }
        }
      }
      // Defensive moves: relocate a card that's currently under threat to a row
      // where it's safe. K-pawn holders and the captain take priority — losing
      // them to elimination is worse than missing an offensive setup.
      const defensiveMoves = [];
      for (const aiCard of aiCardsForMove) {
        if (aiCard.pos.colIndex === AI_KEEP_COL[aiPlayer]) continue; // keepers have only 1 row — no valid moves
        const threats = getThreatsAgainst(aiCard.pos, aiCard.card, aiPlayer);
        if (threats.length === 0) continue;

        const worstMargin = Math.max(...threats.map(t => t.margin));
        const isKPawnHolder = aiKPawns.some(p => p.cardId === aiCard.card.Card_Id);
        const isCaptain = (aiPlayer === 1 ? player1Captain : player2Captain) === aiCard.card.Card_Id;

        for (const newRow of [aiCard.pos.rowIndex - 1, aiCard.pos.rowIndex + 1]) {
          if (newRow < 0 || newRow > 4) continue;
          const newKey = `${aiCard.pos.colIndex}-${newRow}`;
          if (cardGameZones[newKey]?.Card_Id) continue; // slot must be empty
          const newThreats = getThreatsAgainst({ colIndex: aiCard.pos.colIndex, rowIndex: newRow }, aiCard.card, aiPlayer);
          if (newThreats.length === 0) {
            const priorityScore = worstMargin
              + (isKPawnHolder ? KPAWN_DEFENSE_PRIORITY : 0)
              + (isCaptain ? CAPTAIN_DEFENSE_PRIORITY : 0);
            defensiveMoves.push({ srcPos: aiCard.pos, tgtPos: { colIndex: aiCard.pos.colIndex, rowIndex: newRow }, card: aiCard.card, priorityScore, reason: 'defense' });
            break; // one valid escape is enough for this card
          }
        }
      }

      if (defensiveMoves.length > 0) {
        const bestDefMove = defensiveMoves.reduce((best, m) => m.priorityScore > best.priorityScore ? m : best, defensiveMoves[0]);
        plan.push({ type: 'move', ...bestDefMove });
        apLeft--;
      } else if (possibleMoves.length > 0) {
        const bestMove = possibleMoves.reduce((best, m) => m.margin > best.margin ? m : best, possibleMoves[0]);
        plan.push({ type: 'move', ...bestMove, reason: 'offense' });
        apLeft--;
      }
    }

    // Step 2.7: Substitute — replace a weak board card with a significantly better deck card (costs 3 AP)
    if (apLeft >= 3) {
      const aiDecks = getDecksForPlayer(aiPlayer);
      // Cards already committed to a 'move' step earlier in this plan have a stale
      // boardKey here (they'll have relocated by the time substitution executes) —
      // substituting at their old key would leave the moved card duplicated on the board.
      const movedCardIds = new Set(plan.filter(p => p.type === 'move').map(p => p.card.Card_Id));
      const boardCardsList = Object.entries(cardGameZones)
        .filter(([k, c]) => c?.Card_Id && !c.eliminating && aiCols.includes(parseInt(k.split('-')[0])) && !movedCardIds.has(c.Card_Id) && c.Card_Id !== removedByGapFill)
        .map(([k, c]) => ({ key: k, card: c }));

      let bestSub = null;
      for (const { key, card } of boardCardsList) {
        // Don't substitute out a card holding a K-pawn or the captain — that
        // would forfeit those bonuses for a stat gain.
        const holdsKPawn = aiKPawns.some(p => p.cardId === card.Card_Id);
        const isCaptain = (aiPlayer === 1 ? player1Captain : player2Captain) === card.Card_Id;
        if (holdsKPawn || isCaptain) continue;

        const deckCards = aiDecks[card.CardType] || [];
        if (deckCards.length === 0) continue;
        const currentQuality = card.Attack + card.Defence;
        const posCol = parseInt(key.split('-')[0]);
        const posRow = parseInt(key.split('-')[1]);

        // Find human targets this card can attack from its current position.
        const atkPositions = getAttackablePositions(posCol, posRow, aiPlayer);
        const humanTargetsNearby = atkPositions
          .map(tp => ({ pos: tp, card: cardGameZones[`${tp.colIndex}-${tp.rowIndex}`] }))
          .filter(t => t.card?.Card_Id);

        // Select the best deck card for this slot: prefer one that can immediately
        // attack a human in range (attack-viable), then fall back to best ATK+DEF sum.
        let bestDeckCard = null;
        let bestDcScore = -Infinity;
        for (const dc of deckCards) {
          let dcScore;
          if (humanTargetsNearby.length > 0) {
            const bestAtkMargin = humanTargetsNearby.reduce((best, t) => {
              const dist = Math.abs(posCol - t.pos.colIndex);
              const distBonus = dist > 1 ? dist - 1 : 0;
              return Math.max(best, dc.Attack + distBonus - getCardDefense(t.card, humanPlayer));
            }, -Infinity);
            dcScore = bestAtkMargin > 0 ? 1000 + bestAtkMargin : dc.Attack + dc.Defence;
          } else {
            dcScore = dc.Attack + dc.Defence;
          }
          if (dcScore > bestDcScore) { bestDcScore = dcScore; bestDeckCard = dc; }
        }
        if (!bestDeckCard) continue;
        const gain = (bestDeckCard.Attack + bestDeckCard.Defence) - currentQuality;
        const attackViable = bestDcScore >= 1000; // new card can immediately win an attack from this slot
        const minGain = attackViable ? 0 : SUBSTITUTE_MIN_GAIN; // accept any stat improvement if attack is unlocked
        if (gain >= minGain && (!bestSub || bestDcScore > bestSub.dcScore)) {
          bestSub = { boardKey: key, oldCard: card, newCard: bestDeckCard, deckType: card.CardType, gain, dcScore: bestDcScore };
        }
      }

      if (bestSub) {
        plan.push({ type: 'substitute', ...bestSub });
        apLeft -= 3;
      }
    }

    // Step 3: Spend from A/D Boost pool with remaining AP (1 AP per boost applied)
    // Build boost assignments — pure helper, no side effects
    const buildBoostPlan = (availAtk, availDef, apBudget, baseApplied) => {
      const boostActions = [];
      const totalAvail = availAtk + availDef;
      const spendable = Math.min(apBudget, totalAvail);
      if (spendable <= 0) return { boostActions, newApplied: baseApplied, atkSpent: 0, defSpent: 0 };

      const boardCards = Object.entries(cardGameZones)
        .filter(([k, c]) => c?.Card_Id && !c.eliminating && aiCols.includes(parseInt(k.split('-')[0])) && c.Card_Id !== removedByGapFill)
        .map(([k, c]) => ({ key: k, card: c, col: parseInt(k.split('-')[0]),
                             pos: { colIndex: parseInt(k.split('-')[0]), rowIndex: parseInt(k.split('-')[1]) } }));
      if (boardCards.length === 0) return { boostActions, newApplied: baseApplied, atkSpent: 0, defSpent: 0 };

      const newApplied = { ...baseApplied };
      let atkSpent = 0, defSpent = 0;
      let atkBudgetLeft = availAtk;
      let defBudgetLeft = availDef;
      let apBudgetLeft = spendable;

      // Build threatened-card list once; split into critical (K-pawn/captain) and general.
      const threatenedAll = boardCards
        .map(bc => {
          const threats = getThreatsAgainst(bc.pos, bc.card, aiPlayer);
          if (threats.length === 0) return null;
          const maxAtkThreat = threats.reduce((m, t) => t.atkVal > m.atkVal ? t : m, threats[0]);
          const curDef = getCardDefense(bc.card, aiPlayer);
          const defNeeded = Math.max(0, maxAtkThreat.atkVal - curDef);
          if (defNeeded === 0) return null;
          const isKPawn = aiKPawns.some(p => p.cardId === bc.card.Card_Id);
          const isCaptain = (aiPlayer === 1 ? player1Captain : player2Captain) === bc.card.Card_Id;
          return { ...bc, defNeeded, isKPawn, isCaptain };
        })
        .filter(Boolean);
      const criticalThreatened = threatenedAll
        .filter(t => t.isKPawn || t.isCaptain)
        .sort((a, b) => a.defNeeded - b.defNeeded);
      const generalThreatened = threatenedAll
        .filter(t => !t.isKPawn && !t.isCaptain)
        .sort((a, b) => a.defNeeded - b.defNeeded);

      // Pass 1 — Critical DEF: always protect K-pawn holders and the captain first.
      for (const t of criticalThreatened) {
        if (defBudgetLeft <= 0 || apBudgetLeft <= 0) break;
        const cur = newApplied[t.card.Card_Id] || { attack: 0, defense: 0 };
        const room = 20 - (cur.defense || 0);
        const amount = Math.min(t.defNeeded, defBudgetLeft, apBudgetLeft, room);
        if (amount <= 0) continue;
        newApplied[t.card.Card_Id] = { ...cur, defense: (cur.defense || 0) + amount };
        for (let i = 0; i < amount; i++) {
          boostActions.push({ cardName: t.card.PlayerName, stat: 'DEF', positionType: t.card.PositionType?.trim() || t.card.CardType, positionColor: getPositionColor(t.card.CardType) });
        }
        defSpent += amount;
        defBudgetLeft -= amount;
        apBudgetLeft -= amount;
      }

      // Pass 2 — Targeted ATK: bridge gaps to enable attacks before spending on general defence.
      // Eliminating a threat card is better value than defending against it.
      if (atkBudgetLeft > 0 && apBudgetLeft > 0) {
        const atkGaps = [];
        for (const bc of boardCards) {
          if (aiKPawns.some(p => p.cardId === bc.card.Card_Id)) continue; // K-pawns can't attack
          const tgtPositions = getAttackablePositions(bc.pos.colIndex, bc.pos.rowIndex, aiPlayer);
          for (const tgtPos of tgtPositions) {
            const tgtCard = cardGameZones[`${tgtPos.colIndex}-${tgtPos.rowIndex}`];
            if (!tgtCard?.Card_Id || tgtCard.eliminating) continue;
            const curApplied = newApplied[bc.card.Card_Id] || { attack: 0, defense: 0 };
            const dist = Math.abs(bc.pos.colIndex - tgtPos.colIndex);
            const distBonus = dist > 1 ? dist - 1 : 0;
            const curAtkVal = bc.card.Attack + (curApplied.attack || 0) + distBonus;
            const defVal = getCardDefense(tgtCard, humanPlayer);
            const gap = defVal - curAtkVal;
            if (gap > 0) {
              atkGaps.push({ bc, gap });
            }
          }
        }
        // Sort smallest gap first; deduplicate per card (one pass per attacker card)
        atkGaps.sort((a, b) => a.gap - b.gap);
        const seenAtkCards = new Set();
        for (const { bc, gap } of atkGaps) {
          if (atkBudgetLeft <= 0 || apBudgetLeft <= 0) break;
          if (seenAtkCards.has(bc.card.Card_Id)) continue;
          seenAtkCards.add(bc.card.Card_Id);
          const cur = newApplied[bc.card.Card_Id] || { attack: 0, defense: 0 };
          const room = 20 - (cur.attack || 0);
          const amount = Math.min(gap, atkBudgetLeft, apBudgetLeft, room);
          if (amount <= 0) continue;
          newApplied[bc.card.Card_Id] = { ...cur, attack: (cur.attack || 0) + amount };
          for (let i = 0; i < amount; i++) {
            boostActions.push({ cardName: bc.card.PlayerName, stat: 'ATK', positionType: bc.card.PositionType?.trim() || bc.card.CardType, positionColor: getPositionColor(bc.card.CardType) });
          }
          atkSpent += amount;
          atkBudgetLeft -= amount;
          apBudgetLeft -= amount;
        }
      }

      // Pass 3 — General DEF: shore up remaining threatened cards after attack-enabling boosts.
      for (const t of generalThreatened) {
        if (defBudgetLeft <= 0 || apBudgetLeft <= 0) break;
        const cur = newApplied[t.card.Card_Id] || { attack: 0, defense: 0 };
        const room = 20 - (cur.defense || 0);
        const amount = Math.min(t.defNeeded, defBudgetLeft, apBudgetLeft, room);
        if (amount <= 0) continue;
        newApplied[t.card.Card_Id] = { ...cur, defense: (cur.defense || 0) + amount };
        for (let i = 0; i < amount; i++) {
          boostActions.push({ cardName: t.card.PlayerName, stat: 'DEF', positionType: t.card.PositionType?.trim() || t.card.CardType, positionColor: getPositionColor(t.card.CardType) });
        }
        defSpent += amount;
        defBudgetLeft -= amount;
        apBudgetLeft -= amount;
      }

      // Pass 4 — Role-based: spend whatever pool/AP remains
      if (apBudgetLeft > 0 && (atkBudgetLeft + defBudgetLeft) > 0) {
        const useCross = Math.random() < 0.2;
        const atkCol = AI_ATK_COL[aiPlayer], midCol = AI_MID_COL[aiPlayer];
        const offCards = boardCards.filter(c => c.col === atkCol || c.col === midCol);
        const defCards = boardCards.filter(c => c.col !== atkCol && c.col !== midCol);
        const allCards = [...offCards, ...defCards];

        const atkTargets = useCross ? allCards : (offCards.length > 0 ? offCards : allCards);
        const defTargets = useCross ? allCards : (defCards.length > 0 ? defCards : allCards);

        // Spend proportionally from each remaining pool, capped by pool size and remaining AP
        const remainingTotal = atkBudgetLeft + defBudgetLeft;
        let atkLeft = Math.min(atkBudgetLeft, Math.round(apBudgetLeft * (atkBudgetLeft / remainingTotal)));
        let defLeft = Math.min(defBudgetLeft, apBudgetLeft - atkLeft);

        let bi = 0;
        while (atkLeft > 0) {
          const c = atkTargets[bi % atkTargets.length]; bi++;
          const cur = newApplied[c.card.Card_Id] || { attack: 0, defense: 0 };
          if ((cur.attack || 0) < 20) {
            newApplied[c.card.Card_Id] = { ...cur, attack: (cur.attack || 0) + 1 };
            boostActions.push({ cardName: c.card.PlayerName, stat: 'ATK', positionType: c.card.PositionType?.trim() || c.card.CardType, positionColor: getPositionColor(c.card.CardType) });
            atkSpent++;
          }
          atkLeft--;
        }
        bi = 0;
        while (defLeft > 0) {
          const c = defTargets[bi % defTargets.length]; bi++;
          const cur = newApplied[c.card.Card_Id] || { attack: 0, defense: 0 };
          if ((cur.defense || 0) < 20) {
            newApplied[c.card.Card_Id] = { ...cur, defense: (cur.defense || 0) + 1 };
            boostActions.push({ cardName: c.card.PlayerName, stat: 'DEF', positionType: c.card.PositionType?.trim() || c.card.CardType, positionColor: getPositionColor(c.card.CardType) });
            defSpent++;
          }
          defLeft--;
        }
      }

      return { boostActions, newApplied, atkSpent, defSpent };
    };

    const totalPool = boostedAtk + boostedDef;
    if (totalPool > 0 && apLeft > 0) {
      const { boostActions, newApplied, atkSpent, defSpent } = buildBoostPlan(boostedAtk, boostedDef, apLeft, appliedB);
      const spentAP = boostActions.length;
      const newBattleBoosts = { attack: boostedAtk - atkSpent, defense: boostedDef - defSpent };
      plan.push({ type: 'boosts', boostActions, newApplied, spentAP, newBattleBoosts });
    }

    // Log pass if no main action, move, or defensive gap-fill happened
    if (!mainActionUsed && !plan.some(s => s.type === 'move' || s.type === 'fill_gap')) {
      const noAttackReason = usedK ? 'Kickopp already used' : 'no valid targets in range';
      const noUpgradeReason = usedU ? 'Upgrade already used' : null;
      const reasons = [noAttackReason, noUpgradeReason].filter(Boolean).join(', ');
      plan.push({ type: 'log_pass', reason: reasons });
    }

    // ── Execute plan with sequential delays ──────────────────────────────────
    let delay = 0;
    for (const step of plan) {
      const d = delay;
      if (step.type === 'training') {
        aiTimerRef.current = setTimeout(() => {
          addEventLog({ type: 'ai_training_received', player: aiPlayer, trainAtk: step.trainAtk, trainDef: step.trainDef });
          if (aiPlayer === 1) setPlayer1BattleBoosts(step.newBattleBoosts);
          else setPlayer2BattleBoosts(step.newBattleBoosts);
        }, d);
        // No delay increment — training logs immediately and execution continues
      } else if (step.type === 'log_pass') {
        aiTimerRef.current = setTimeout(() => {
          addEventLog({ type: 'ai_pass', player: aiPlayer, reason: step.reason });
        }, d);
      } else if (step.type === 'upgrade') {
        aiTimerRef.current = setTimeout(() => {
          setAiThinking('CPU upgrading level...');
          addEventLog({ type: 'ai_upgrade', player: aiPlayer, newLevel: step.newLevel });
          if (aiPlayer === 1) {
            setPlayer1MaxActionPoints(p => p + 1);
            setPlayer1ActionPoints(p => p - 1);
            setPlayer1UsedUpgrade(true);
          } else {
            setPlayer2MaxActionPoints(p => p + 1);
            setPlayer2ActionPoints(p => p - 1);
            setPlayer2UsedUpgrade(true);
          }
        }, d);
        delay += 800;
      } else if (step.type === 'attack') {
        aiTimerRef.current = setTimeout(() => {
          setAiThinking(`CPU attacking ${step.tgt.card.PlayerName}...`);
          executeKickopp(step.atk.pos, step.tgt.pos);
        }, d);
        delay += 1400;
      } else if (step.type === 'move') {
        aiTimerRef.current = setTimeout(() => {
          setAiThinking(`CPU repositioning ${step.card.PlayerName}...`);
          // Directly update zones (bypass executeMove's human-specific AP/mode checks)
          const srcKey = `${step.srcPos.colIndex}-${step.srcPos.rowIndex}`;
          const tgtKey = `${step.tgtPos.colIndex}-${step.tgtPos.rowIndex}`;
          setCardGameZones(prev => {
            const newZones = { ...prev };
            newZones[tgtKey] = newZones[srcKey];
            delete newZones[srcKey];
            return newZones;
          });
          if (aiPlayer === 1) setPlayer1ActionPoints(p => Math.max(0, p - 1));
          else setPlayer2ActionPoints(p => Math.max(0, p - 1));
          addEventLog({
            type: 'ai_move', player: aiPlayer,
            cardName: step.card.PlayerName,
            positionType: step.card.PositionType?.trim() || step.card.CardType,
            positionColor: getPositionColor(step.card.CardType),
            fromRow: step.srcPos.rowIndex, toRow: step.tgtPos.rowIndex,
            reason: step.reason,
          });
        }, d);
        delay += 700;
      } else if (step.type === 'substitute') {
        aiTimerRef.current = setTimeout(() => {
          setAiThinking(`CPU substituting ${step.oldCard.PlayerName}...`);
          setCardGameZones(prev => ({ ...prev, [step.boardKey]: step.newCard }));
          setDecksForPlayer(aiPlayer, prev => ({
            ...prev,
            [step.deckType]: (prev[step.deckType] || []).filter(c => c.Card_Id !== step.newCard.Card_Id)
          }));
          if (aiPlayer === 1) setPlayer1ActionPoints(p => Math.max(0, p - 3));
          else setPlayer2ActionPoints(p => Math.max(0, p - 3));
          addEventLog({
            type: 'substitute', player: aiPlayer,
            oldCard: step.oldCard.PlayerName,
            newCard: step.newCard.PlayerName,
            position: step.newCard.PositionType?.trim() || step.newCard.CardType,
            positionColor: getPositionColor(step.newCard.CardType),
            captainTransferred: false, apCost: 3
          });
        }, d);
        delay += 1000;
      } else if (step.type === 'fill_gap') {
        aiTimerRef.current = setTimeout(() => {
          setAiThinking(`CPU covering its last line with ${step.newCard.PlayerName}...`);
          setCardGameZones(prev => {
            const newZones = { ...prev };
            delete newZones[step.removeKey];
            newZones[`${step.placeCol}-${step.placeRow}`] = step.newCard;
            return newZones;
          });
          setDecksForPlayer(aiPlayer, prev => ({
            ...prev,
            [step.deckType]: (prev[step.deckType] || []).filter(c => c.Card_Id !== step.newCard.Card_Id)
          }));
          if (aiPlayer === 1) setPlayer1ActionPoints(p => Math.max(0, p - 3));
          else setPlayer2ActionPoints(p => Math.max(0, p - 3));
          addEventLog({
            type: 'substitute', player: aiPlayer,
            oldCard: step.removeCard.PlayerName,
            newCard: step.newCard.PlayerName,
            position: step.newCard.PositionType?.trim() || step.newCard.CardType,
            positionColor: getPositionColor(step.newCard.CardType),
            captainTransferred: false, apCost: 3
          });
        }, d);
        delay += 1000;
      } else if (step.type === 'boosts') {
        aiTimerRef.current = setTimeout(() => {
          setAiThinking('CPU applying boosts...');
          step.boostActions.forEach(ba => {
            addEventLog({
              type: 'boost_add', player: aiPlayer,
              playerName: ba.cardName,
              positionType: ba.positionType,
              positionColor: ba.positionColor,
              stat: ba.stat === 'ATK' ? 'attack' : 'defense',
              count: 1, apCost: 1, // bank boosts cost 1 AP each
            });
          });
          if (aiPlayer === 1) {
            setPlayer1AppliedBoosts(step.newApplied);
            if (step.spentAP > 0) setPlayer1ActionPoints(p => Math.max(0, p - step.spentAP));
            setPlayer1BattleBoosts(step.newBattleBoosts);
          } else {
            setPlayer2AppliedBoosts(step.newApplied);
            if (step.spentAP > 0) setPlayer2ActionPoints(p => Math.max(0, p - step.spentAP));
            setPlayer2BattleBoosts(step.newBattleBoosts);
          }
          // Do NOT call setCardGameZones here — boosts are tracked in appliedBoosts,
          // and the stale newZones snapshot would undo any moves/eliminations that
          // occurred after the plan was built.
        }, d);
        delay += 800;
      }
    }

    // Final: finish turn after all steps
    aiTimerRef.current = setTimeout(() => {
      setAiThinking(null);
      executeFinishTurn();
    }, delay + 400);
  };

  const getCardRole = (index) => {
    if (cardSelections.toPlay?.index === index) return 'play';
    if (cardSelections.toBench?.index === index) return 'bench';
    if (cardSelections.toDiscard?.index === index) return 'discard';
    return null;
  };

  const handleConfirmSelections = () => {
    if (!cardSelections.toPlay || !cardSelections.toBench || !cardSelections.toDiscard) {
      return; // All three must be selected
    }

    const playCard = cardSelections.toPlay.card;
    const benchCard = cardSelections.toBench.card;
    const discardCard = cardSelections.toDiscard.card;

    // Remove only the play and discard cards from the deck
    // Keep the bench card in the deck (it will be moved to top)
    setDecksForPlayer(currentPlayer, prev => {
      const newDecks = { ...prev };
      const deckType = selectedDeck;

      if (deckType === 'ForwardCenter') {
        // Handle forward decks specially
        let forwardCenter = [...(newDecks.ForwardCenter || [])];
        let forwardWide = [...(newDecks.ForwardWide || [])];

        // Only remove play and discard cards
        [playCard, discardCard].forEach(card => {
          const centerIdx = forwardCenter.findIndex(c => c.ID === card.ID);
          const wideIdx = forwardWide.findIndex(c => c.ID === card.ID);

          if (centerIdx !== -1) {
            forwardCenter.splice(centerIdx, 1);
          } else if (wideIdx !== -1) {
            forwardWide.splice(wideIdx, 1);
          }
        });

        // Move bench card to top of its appropriate deck
        const benchCenterIdx = forwardCenter.findIndex(c => c.ID === benchCard.ID);
        const benchWideIdx = forwardWide.findIndex(c => c.ID === benchCard.ID);

        if (benchCenterIdx !== -1) {
          forwardCenter.splice(benchCenterIdx, 1);
          forwardCenter.unshift(benchCard);
        } else if (benchWideIdx !== -1) {
          forwardWide.splice(benchWideIdx, 1);
          forwardWide.unshift(benchCard);
        }

        newDecks.ForwardCenter = forwardCenter;
        newDecks.ForwardWide = forwardWide;
      } else {
        // Regular deck handling
        let deck = [...(newDecks[deckType] || [])];

        // Only remove play and discard cards
        [playCard, discardCard].forEach(card => {
          const idx = deck.findIndex(c => c.ID === card.ID);
          if (idx !== -1) {
            deck.splice(idx, 1);
          }
        });

        // Move bench card to top of deck
        const benchIdx = deck.findIndex(c => c.ID === benchCard.ID);
        if (benchIdx !== -1) {
          deck.splice(benchIdx, 1);
          deck.unshift(benchCard);
        }

        newDecks[deckType] = deck;
      }

      return newDecks;
    });

    // Handle toDiscard - add to cards out
    setRecycledCardsCount(prev => prev + 1);

    // Handle toPlay - set up click-to-place
    const validColors = getValidSlotsForCardType(playCard.CardType);
    const validSlotKeys = [];
    cardGameZonesData.forEach((column, colIndex) => {
      column.forEach((slot) => {
        // During DRAFT stage, also check if column is valid for current player
        const isColorValid = validColors.includes(slot.color);
        const isColumnValid = gameStage === 'draft'
          ? isValidColumnForPlayer(colIndex, currentPlayer)
          : true;

        if (isColorValid && isColumnValid) {
          validSlotKeys.push(`${colIndex}-${slot.row}`);
        }
      });
    });
    setHighlightedSlots(validSlotKeys);

    // Set card ready for click-to-place
    setDraftCardForPlacement({
      card: playCard,
      deckType: selectedDeck,
      alreadyRemovedFromDeck: true // Mark that this card is already removed from deck
    });

    // Show only the play card in sidebar
    setVisibleCards([playCard]);
    setCardSelections({
      toPlay: { card: playCard, index: 0 },
      toBench: null,
      toDiscard: null
    });
  };

  // ============================================================================
  // OLD MOUSE HANDLERS REMOVED
  // ============================================================================
  // handleMouseMove - Removed (now using dnd-kit's handleDragOver)
  // handleMouseUp - Removed (now using dnd-kit's handleDragEnd)

  // ============================================================================
  // DND-KIT DRAG HANDLERS (New Implementation)
  // ============================================================================

  const handleDragStart = useCallback((event) => {
    const { active } = event;
    setActiveId(active.id);
    setActiveDragData(active.data.current);

    // Also set legacy draggedCard state for compatibility during transition
    if (active.data.current) {
      setDraggedCard(active.data.current);
    }

    // Store original position when starting a move in move mode
    if (moveMode && active.data.current?.sourceKey) {
      const sourceKey = active.data.current.sourceKey;
      const [colIndex, rowIndex] = sourceKey.split('-').map(Number);
      setMoveOriginalPosition({ colIndex, rowIndex });
      console.log(`📍 Move started from original position: col ${colIndex}, row ${rowIndex}`);
    }

    // Add mouse move listener to track position for Kickopp arrow
    const handlePointerMove = (e) => {
      dragPositionRef.current = { x: e.clientX, y: e.clientY };
      setDragPosition({ x: e.clientX, y: e.clientY });
    };

    window.addEventListener('pointermove', handlePointerMove);

    // Store the cleanup function
    window.__dragPointerMoveHandler = handlePointerMove;
  }, [moveMode]);

  const handleDragOver = useCallback((event) => {
    const { active, over } = event;

    // Handle Kickopp preview logic
    if (gameStage === 'battle' && kickoppMode && active.data.current?.sourceKey) {
      const sourceKey = active.data.current.sourceKey;

      // Use DOM hit-testing via ref for accurate slot detection
      const slot = findSlotAtPointer(over);
      if (slot) {
        const { colIndex, rowIndex } = slot;
        const targetKey = `${colIndex}-${rowIndex}`;
        const targetCard = cardGameZones[targetKey];
        const attackerCard = cardGameZones[sourceKey];

        if (targetCard && typeof targetCard === 'object' && attackerCard) {
          const player1Columns = [0, 1, 3, 5];
          const player2Columns = [2, 4, 6, 7];
          const attackerCol = parseInt(sourceKey.split('-')[0]);
          const attackerRow = parseInt(sourceKey.split('-')[1]);
          const isAttackerPlayer1 = player1Columns.includes(attackerCol);
          const isAttackerPlayer2 = player2Columns.includes(attackerCol);
          const isTargetPlayer1 = player1Columns.includes(colIndex);
          const isTargetPlayer2 = player2Columns.includes(colIndex);

          const isAttackerCurrentPlayer = (currentPlayer === 1 && isAttackerPlayer1) || (currentPlayer === 2 && isAttackerPlayer2);
          const isTargetOpponent = (currentPlayer === 1 && isTargetPlayer2) || (currentPlayer === 2 && isTargetPlayer1);

          if (isAttackerCurrentPlayer && isTargetOpponent) {
            const attackablePositions = getAttackablePositions(attackerCol, attackerRow, currentPlayer);
            const isAttackable = attackablePositions.some(
              pos => pos.colIndex === colIndex && pos.rowIndex === rowIndex
            );

            if (isAttackable) {
              const attackerPlayer = isAttackerPlayer1 ? 1 : 2;
              const targetPlayer = attackerPlayer === 1 ? 2 : 1;

              // Check if attacker has a K pawn (inactive card cannot attack)
              const attackerPawns = attackerPlayer === 1 ? player1KickoppPawns : player2KickoppPawns;
              const hasKPawn = attackerPawns.some(p => p.cardId === attackerCard.Card_Id);

              const columnDistance = Math.abs(attackerCol - colIndex);
              const columnDistanceBonus = columnDistance > 1 ? columnDistance - 1 : 0;
              const baseAttackValue = getCardAttack(attackerCard, attackerPlayer);
              const attackValue = baseAttackValue + columnDistanceBonus;
              const defenseValue = ignoreOppDefActive ? targetCard.Defence : getCardDefense(targetCard, targetPlayer);
              // Kickopp pawns cannot attack (always fail)
              const wouldSucceed = !hasKPawn && (attackValue > defenseValue);

              setKickoppPreview({
                attackerPos: { colIndex: attackerCol, rowIndex: attackerRow },
                targetPos: { colIndex, rowIndex },
                wouldSucceed,
                attackValue,
                baseAttack: baseAttackValue,
                columnBonus: columnDistanceBonus,
                defenseValue,
                hasKPawn // Include this info for potential UI feedback
              });
              return;
            }
          }
        }
      }
    }

    // Clear preview if not applicable
    setKickoppPreview(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameStage, kickoppMode, cardGameZones, currentPlayer, getAttackablePositions, getCardAttack, getCardDefense, player1KickoppPawns, player2KickoppPawns]);

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;

    // Clear drag state
    setActiveId(null);
    setActiveDragData(null);
    setKickoppPreview(null);

    // Remove pointer move listener
    if (window.__dragPointerMoveHandler) {
      window.removeEventListener('pointermove', window.__dragPointerMoveHandler);
      window.__dragPointerMoveHandler = null;
    }

    if (!over) {
      // Dropped outside valid target
      setDraggedCard(null);
      document.body.classList.remove('dragging-kickopp');
      return;
    }

    // Route to appropriate handler based on game stage and mode
    if (gameStage === 'draft') {
      handleDraftDrop(active, over);
    } else if (gameStage === 'battle' && kickoppMode) {
      handleKickoppDrop(active, over);
    } else if (gameStage === 'battle' && moveMode) {
      handleMoveDrop(active, over);
    } else {
      // Warmup or other stages - allow repositioning within same column
      handleWarmupDrop(active, over);
    }

    setDraggedCard(null);
    document.body.classList.remove('dragging-kickopp');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameStage, kickoppMode, moveMode]);

  // Helper: Find the board slot at the current pointer position using DOM hit-testing.
  // Uses dragPositionRef (always current) instead of dragPosition state (can be stale).
  // Falls back to dnd-kit's 'over' if DOM hit-test fails.
  const findSlotAtPointer = (over) => {
    const pos = dragPositionRef.current;
    const elementsAtPointer = document.elementsFromPoint(pos.x, pos.y);
    const slotElement = elementsAtPointer.find(el => el.dataset.colIndex !== undefined && el.dataset.rowIndex !== undefined);

    if (slotElement) {
      const col = parseInt(slotElement.dataset.colIndex);
      const row = parseInt(slotElement.dataset.rowIndex);
      console.log(`🎯 DOM hit-test: col=${col}, row=${row} (pointer: ${Math.round(pos.x)},${Math.round(pos.y)}, dnd-kit: ${over?.id})`);
      return { colIndex: col, rowIndex: row };
    }

    if (over && over.id.startsWith('slot-')) {
      const parts = over.id.split('-');
      const col = parseInt(parts[1]);
      const row = parseInt(parts[2]);
      console.log(`🎯 dnd-kit fallback: col=${col}, row=${row}`);
      return { colIndex: col, rowIndex: row };
    }

    return null;
  };

  const handleDraftDrop = useCallback((active, over) => {
    const dragData = active.data.current;
    if (!dragData || !dragData.card) return;

    const slot = findSlotAtPointer(over);
    if (!slot) return;
    const { colIndex, rowIndex } = slot;
    const targetKey = `${colIndex}-${rowIndex}`;

    // Get the slot color to validate placement
    let targetSlotColor = null;
    cardGameZonesData.forEach((column, cIdx) => {
      if (cIdx === colIndex) {
        column.forEach((slot) => {
          if (slot.row === rowIndex) {
            targetSlotColor = slot.color;
          }
        });
      }
    });

    // Validate placement
    const validColors = getValidSlotsForCardType(dragData.card.CardType);
    const isColorValid = validColors.includes(targetSlotColor);
    const isColumnValid = isValidColumnForPlayer(colIndex, currentPlayer);
    const isOccupied = cardGameZones[targetKey] && typeof cardGameZones[targetKey] === 'object';

    if (!isColorValid || !isColumnValid || isOccupied) {
      console.log('❌ Invalid placement:', { isColorValid, isColumnValid, isOccupied });
      return;
    }

    // Place card
    setCardGameZones(prev => {
      const newZones = { ...prev };
      newZones[targetKey] = dragData.card;
      return newZones;
    });

    // Remove card from deck (only if not already removed)
    if (!dragData.alreadyRemovedFromDeck) {
      setDecksForPlayer(currentPlayer, prev => {
        const newDecks = { ...prev };
        if (dragData.deckType === 'ForwardCenter') {
          const forwardCenter = [...(newDecks.ForwardCenter || [])];
          const forwardWide = [...(newDecks.ForwardWide || [])];
          const centerIndex = forwardCenter.findIndex(c => c.ID === dragData.card.ID);
          const wideIndex = forwardWide.findIndex(c => c.ID === dragData.card.ID);

          if (centerIndex !== -1) {
            forwardCenter.splice(centerIndex, 1);
            newDecks.ForwardCenter = forwardCenter;
          } else if (wideIndex !== -1) {
            forwardWide.splice(wideIndex, 1);
            newDecks.ForwardWide = forwardWide;
          }
        } else {
          const deck = [...(newDecks[dragData.deckType] || [])];
          const cardIndex = deck.findIndex(c => c.ID === dragData.card.ID);
          if (cardIndex !== -1) {
            deck.splice(cardIndex, 1);
            newDecks[dragData.deckType] = deck;
          }
        }
        return newDecks;
      });
    }

    // Update card count and formation
    setCardsPlaced(prev => prev + 1);
    const category = getCardTypeCategory(dragData.card.CardType);
    if (currentPlayer === 1) {
      setPlayer1Formation(prev => ({ ...prev, [category]: prev[category] + 1 }));
    } else {
      setPlayer2Formation(prev => ({ ...prev, [category]: prev[category] + 1 }));
    }

    // Log event
    const positionColor = getPositionColor(dragData.card.CardType);
    addEventLog({
      type: 'draft',
      player: currentPlayer,
      playerName: dragData.card.PlayerName,
      cardType: dragData.card.CardType,
      positionType: dragData.card.PositionType,
      positionColor: positionColor
    });

    // Switch player and close sidebar
    setCurrentPlayer(prev => prev === 1 ? 2 : 1);
    setVisibleCards([]);
    setSelectedDeck(null);
    setCardSelections({ toPlay: null, toBench: null, toDiscard: null });
    setHighlightedSlots([]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardGameZones, cardGameZonesData, currentPlayer, getValidSlotsForCardType, isValidColumnForPlayer, getCardTypeCategory, getPositionColor, addEventLog]);

  const handleKickoppDrop = useCallback((active, over) => {
    console.log('🎯 handleKickoppDrop called', { active: active.id, over: over?.id });

    const dragData = active.data.current;
    if (!dragData?.sourceKey || !over.id.startsWith('slot-')) {
      console.log('⚠️ Early return: no sourceKey or not a slot');
      return;
    }

    const attackerKey = dragData.sourceKey;
    const attackerCard = cardGameZones[attackerKey];

    if (!attackerCard) {
      console.log('⚠️ Early return: no attacker card');
      return;
    }

    const player1Columns = [0, 1, 3, 5];
    const player2Columns = [2, 4, 6, 7];
    const attackerCol = parseInt(attackerKey.split('-')[0]);
    const attackerRow = parseInt(attackerKey.split('-')[1]);
    const isAttackerPlayer1 = player1Columns.includes(attackerCol);
    const isAttackerPlayer2 = player2Columns.includes(attackerCol);

    console.log('🔍 Attacker:', {
      name: attackerCard.PlayerName,
      col: attackerCol,
      row: attackerRow,
      isP1: isAttackerPlayer1,
      isP2: isAttackerPlayer2,
      currentPlayer
    });

    // Check if attacker has a K pawn EARLY (before other validations)
    const attackerPlayer = isAttackerPlayer1 ? 1 : 2;
    const attackerPawns = attackerPlayer === 1 ? player1KickoppPawns : player2KickoppPawns;
    const hasKPawn = attackerPawns.some(p => p.cardId === attackerCard.Card_Id);

    console.log('🔍 K pawn check:', { hasKPawn, cardId: attackerCard.Card_Id });

    if (hasKPawn) {
      console.log(`❌ Cannot Kickopp with ${attackerCard.PlayerName} - card has K pawn (inactive)`);

      const eventData = {
        type: 'kickopp_inactive',
        player: currentPlayer,
        attacker: attackerCard.PlayerName,
        attackerPosition: attackerCard.PositionType,
        attackerPositionColor: getPositionColor(attackerCard.CardType)
      };
      console.log('📋 Adding K pawn event log:', eventData);
      addEventLog(eventData);

      // Exit Kickopp mode and deselect action card
      setKickoppMode(false);
      const setSelected = currentPlayer === 1 ? setPlayer1SelectedActionCard : setPlayer2SelectedActionCard;
      setSelected(null);

      return;
    }

    const slot = findSlotAtPointer(over);
    if (!slot) {
      console.log('⚠️ No target slot found at pointer position');
      return;
    }
    const { colIndex, rowIndex } = slot;
    const targetKey = `${colIndex}-${rowIndex}`;
    const targetCard = cardGameZones[targetKey];

    if (!targetCard || typeof targetCard !== 'object') {
      addEventLog({
        type: 'kickopp_invalid',
        player: currentPlayer,
        reason: 'Invalid target - must attack an opponent\'s card'
      });
      return;
    }

    const isTargetPlayer1 = player1Columns.includes(colIndex);
    const isTargetPlayer2 = player2Columns.includes(colIndex);

    console.log('🔍 Target:', {
      name: targetCard.PlayerName,
      col: colIndex,
      row: rowIndex,
      isP1: isTargetPlayer1,
      isP2: isTargetPlayer2
    });

    const isAttackerCurrentPlayer = (currentPlayer === 1 && isAttackerPlayer1) || (currentPlayer === 2 && isAttackerPlayer2);
    const isTargetOpponent = (currentPlayer === 1 && isTargetPlayer2) || (currentPlayer === 2 && isTargetPlayer1);

    console.log('🔍 Validation:', {
      isAttackerCurrentPlayer,
      isTargetOpponent,
      willPass: isAttackerCurrentPlayer && isTargetOpponent
    });

    if (!isAttackerCurrentPlayer || !isTargetOpponent) {
      console.log('❌ Invalid Kickopp: Not current player or not opponent');
      addEventLog({
        type: 'kickopp_invalid',
        player: currentPlayer,
        reason: 'Must attack an opponent\'s card'
      });
      return;
    }

    const attackablePositions = getAttackablePositions(attackerCol, attackerRow, currentPlayer);
    const isAttackable = attackablePositions.some(pos => pos.colIndex === colIndex && pos.rowIndex === rowIndex);

    if (isAttackable) {
      console.log('✅ Executing Kickopp via dnd-kit!');
      const attackerPos = { colIndex: attackerCol, rowIndex: attackerRow };
      const targetPos = { colIndex, rowIndex };
      executeKickopp(attackerPos, targetPos);
    } else {
      console.log('❌ Target not attackable');
      addEventLog({
        type: 'kickopp_invalid',
        player: currentPlayer,
        reason: `${attackerCard.PlayerName} cannot reach that position`
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardGameZones, currentPlayer, getAttackablePositions, executeKickopp, player1KickoppPawns, player2KickoppPawns, addEventLog, getPositionColor, setKickoppMode, setPlayer1SelectedActionCard, setPlayer2SelectedActionCard]);

  const handleMoveDrop = useCallback((active, over) => {
    const dragData = active.data.current;
    if (!dragData?.sourceKey) return;

    const slot = findSlotAtPointer(over);
    if (!slot) return;
    const { colIndex, rowIndex } = slot;
    const sourceKey = dragData.sourceKey;
    const sourceCol = parseInt(sourceKey.split('-')[0]);
    const sourceRow = parseInt(sourceKey.split('-')[1]);

    // Verify card belongs to current player
    const player1Columns = [0, 1, 3, 5];
    const isSourcePlayer1 = player1Columns.includes(sourceCol);
    const isCurrentPlayerCard = (currentPlayer === 1 && isSourcePlayer1) || (currentPlayer === 2 && !isSourcePlayer1);

    if (!isCurrentPlayerCard) {
      console.log('❌ Move failed: Card does not belong to current player');
      return;
    }

    const sourcePos = { colIndex: sourceCol, rowIndex: sourceRow };
    const targetPos = { colIndex, rowIndex };
    executeMove(sourcePos, targetPos);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPlayer, executeMove]);

  const handleWarmupDrop = useCallback((active, over) => {
    const dragData = active.data.current;
    if (!dragData?.sourceKey) return;

    const slot = findSlotAtPointer(over);
    if (!slot) return;
    const targetColIndex = slot.colIndex;
    const targetRowIndex = slot.rowIndex;
    const sourceKey = dragData.sourceKey;
    const sourceColIndex = parseInt(sourceKey.split('-')[0]);
    const targetKey = `${targetColIndex}-${targetRowIndex}`;

    // Only allow movement within same column during warmup
    if (sourceColIndex !== targetColIndex) {
      console.log('❌ Warmup: Cannot move between columns');
      return;
    }

    // Swap cards or move to empty slot
    setCardGameZones(prev => {
      const newZones = { ...prev };
      if (newZones[targetKey] && typeof newZones[targetKey] === 'object') {
        const targetCard = newZones[targetKey];
        newZones[targetKey] = dragData.card;
        newZones[sourceKey] = targetCard;
      } else {
        newZones[targetKey] = dragData.card;
        delete newZones[sourceKey];
      }
      return newZones;
    });
  }, []);

  if (loading) {
    return (
      <div className="arena-loading">
        <div className="loading-text">
          Loading Board Arena...
        </div>
      </div>
    );
  }

  // ============================================================================
  // RENDER
  // ============================================================================

  // Keep ref in sync with latest state so timeout callbacks always read fresh values
  liveGameStateRef.current = {
    p1AP: player1ActionPoints,
    p2AP: player2ActionPoints,
    p1UsedK: player1UsedKickopp,
    p2UsedK: player2UsedKickopp,
    p1UsedU: player1UsedUpgrade,
    p2UsedU: player2UsedUpgrade,
    currentPlayer,
  };

  // ── executeWarmupFinish: extracted from warmup confirm button onClick ─────
  const executeWarmupFinish = () => {
    const appliedBoosts = currentPlayer === 1 ? player1AppliedBoosts : player2AppliedBoosts;

    setCardGameZones(prev => {
      const newZones = { ...prev };
      Object.keys(newZones).forEach(key => {
        const card = newZones[key];
        if (card && typeof card === 'object' && card.Card_Id) {
          const boosts = appliedBoosts[card.Card_Id];
          if (boosts) {
            newZones[key] = {
              ...card,
              Attack: card.Attack + (boosts.attack || 0),
              Defence: card.Defence + (boosts.defense || 0)
            };
          }
        }
      });
      return newZones;
    });

    const startBattle = () => {
      setGameStage('battle');
      setSelectedBoardCard(null);
      setFreeMovesActive(false);
      setPlayer1PendingKickoppBonus(0);
      setPlayer2PendingKickoppBonus(0);
      setPlayer1LessInactives(false);
      setPlayer2LessInactives(false);
      setFreeSubsRemaining(0);
      setFreeBoostsActive(false);
      setDoubleBoostsActive(false);
      setAttackAnyoneActive(false);
      setIgnoreOppDefActive(false);
      setRemoveBoostMode(false);
      setActiveDefencePlayer(null);
      setPlayer1ExtraMainActions(0);
      setPlayer2ExtraMainActions(0);
      setCurrentPlayer(1);
      setPlayer1ActionPoints(1);
      setPlayer2ActionPoints(1);
      setPlayer1MaxActionPoints(1);
      setPlayer2MaxActionPoints(1);
      setPlayer1StartingAPThisTurn(1);
      setPlayer2StartingAPThisTurn(1);
      setPlayer1TurnCount(1);
      setPlayer2TurnCount(0);
      // In Challenge mode, restrict the shared deck to the action cards enabled for this level
      const actionCardPool = (challengeMode && challengeActionCardIds)
        ? actionCardsData.filter(c => challengeActionCardIds.includes(c.id))
        : actionCardsData;
      const _d1 = shuffleArray(actionCardPool.filter(c => c.buyCost === 1));
      const _d3 = shuffleArray(actionCardPool.filter(c => c.buyCost === 3));
      const _d5 = shuffleArray(actionCardPool.filter(c => c.buyCost === 5));
      setRevealedCard1(_d1[0] || null); setActionDeck1(_d1.slice(1));
      setRevealedCard3(_d3[0] || null); setActionDeck3(_d3.slice(1));
      setRevealedCard5(_d5[0] || null); setActionDeck5(_d5.slice(1));
      setPlayer1Hand([]);
      setPlayer2Hand([]);
      setPlayer1HandOffset(0);
      setPlayer2HandOffset(0);
      const p1TotalUsed = Object.values(player1AppliedBoosts).reduce((s, b) => s + (b.attack || 0) + (b.defense || 0), 0);
      const p2TotalUsed = Object.values(player2AppliedBoosts).reduce((s, b) => s + (b.attack || 0) + (b.defense || 0), 0);
      const p1Rem = Math.max(0, (player1AvailableBoosts.totalBoosts || 0) - p1TotalUsed);
      const p2Rem = Math.max(0, (player2AvailableBoosts.totalBoosts || 0) - p2TotalUsed);
      const p1MaxAtt = player1AvailableBoosts.attBoost || 0, p1MaxDef = player1AvailableBoosts.defBoost || 0;
      const p2MaxAtt = player2AvailableBoosts.attBoost || 0, p2MaxDef = player2AvailableBoosts.defBoost || 0;
      let p1BA = 0, p1BD = 0;
      for (let i = 0; i < p1Rem; i++) { if (i % 2 === 0 && p1BA < p1MaxAtt) p1BA++; else if (p1BD < p1MaxDef) p1BD++; else if (p1BA < p1MaxAtt) p1BA++; }
      let p2BA = 0, p2BD = 0;
      for (let i = 0; i < p2Rem; i++) { if (i % 2 === 0 && p2BA < p2MaxAtt) p2BA++; else if (p2BD < p2MaxDef) p2BD++; else if (p2BA < p2MaxAtt) p2BA++; }
      setPlayer1BattleBoosts({ attack: p1BA, defense: p1BD });
      setPlayer2BattleBoosts({ attack: p2BA, defense: p2BD });
      // Boosts are already baked into card stats by executeWarmupFinish — clear to avoid double-counting in renderer
      setPlayer1AppliedBoosts({});
      setPlayer2AppliedBoosts({});
    };

    if (currentPlayer === 2) {
      setWarmUpPlayer2Done(true);
      setWarmUpSelectedCard(null);
      if (warmUpPlayer1Done) startBattle(); else setCurrentPlayer(1);
    } else {
      setWarmUpPlayer1Done(true);
      setWarmUpSelectedCard(null);
      if (warmUpPlayer2Done) startBattle(); else setCurrentPlayer(2);
    }
  };

  return (
    <>
<DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
    <div className={`arena-container stage-${gameStage}${verticalBoard ? ' vertical-mode' : ''}`}>
      {/* AI thinking overlay — blocks clicks during CPU turn */}
      {isSolo && aiThinking && (
        <div className="ai-thinking-overlay">
          <span className="ai-thinking-label">{aiThinking}</span>
        </div>
      )}

      {/* Top Header with Stage Indicator and Player Turn */}
      <div className="arena-header">
        {/* Stage label + ? guide button grouped together */}
        <div className="stage-and-guide">
          <div className={`stage-indicator stage-${gameStage}`}>
            {getStageDisplayName()}
          </div>
          {(gameStage === 'draft' || gameStage === 'warmup' || gameStage === 'battle') && (
            <button
              className="arena-tutorial-btn"
              onClick={() => {
                if (gameStage === 'draft') onShowTutorial?.();
                else if (gameStage === 'warmup') setShowWarmupTutorial(true);
                else if (gameStage === 'battle') setShowBattleTutorial(true);
              }}
              title="Open game guide"
            >
              ?
            </button>
          )}
        </div>

        {/* Player Turn Indicator - Show during DRAFT, WARMUP, and BATTLE stages */}
        {(gameStage === 'draft' || gameStage === 'warmup' || gameStage === 'battle') && (
          <div className="player-turn-container">
            <div className={`player-turn-indicator player-${currentPlayer}`}>
              <div className="player-turn-name">TEAM {currentPlayer}</div>
              {gameStage === 'draft' && (
                <div className="player-turn-cards">Card {Math.floor(cardsPlaced / 2) + 1} of 11</div>
              )}
              {gameStage === 'warmup' && (
                <div className="player-turn-cards">Set Captain & Boosts</div>
              )}
              {gameStage === 'battle' && (
                <div className="player-turn-cards">Turn {currentPlayer === 1 ? player1TurnCount : player2TurnCount} / 20</div>
              )}
            </div>
            {gameStage === 'battle' && (
              <div className={`attack-direction-arrows player-${currentPlayer}`}>
                <span className="arrow arrow-1">{currentPlayer === 1 ? '→' : '←'}</span>
                <span className="arrow arrow-2">{currentPlayer === 1 ? '→' : '←'}</span>
                <span className="arrow arrow-3">{currentPlayer === 1 ? '→' : '←'}</span>
              </div>
            )}
          </div>
        )}

        {/* Action Points Display - Show during BATTLE stage only, positioned separately */}
        {gameStage === 'battle' && (
          <>
            <div className="action-points-display-battle">
              <div className="action-points-label">Action Points</div>
              <div className="action-points-value">
                {currentPlayer === 1 ? player1ActionPoints : player2ActionPoints}
              </div>
            </div>
            
            {/* Finish Turn Button - Separate box below Action Points */}
            <button
              className={`finish-turn-btn-battle${holdingButtonId === 'finish-turn' ? ' btn-holding' : ''}`}
              onMouseDown={() => {
                const currentAP = currentPlayer === 1 ? player1ActionPoints : player2ActionPoints;
                const usedUpgrade = currentPlayer === 1 ? player1UsedUpgrade : player2UsedUpgrade;
                const usedKickopp = currentPlayer === 1 ? player1UsedKickopp : player2UsedKickopp;
                if (currentAP === 0) {
                  // No AP left — fire instantly, no hold needed
                  executeFinishTurn();
                } else {
                  handleButtonHoldStart('finish-turn', () => {
                    if (currentAP > 0 && !usedUpgrade && !usedKickopp) {
                      setPendingAction({ type: 'finish_turn' });
                      setShowAPConfirmation(true);
                    } else {
                      executeFinishTurn();
                    }
                  });
                }
              }}
              onMouseUp={handleButtonHoldEnd}
              onMouseLeave={handleButtonHoldEnd}
              onTouchStart={(e) => { e.preventDefault(); handleButtonHoldStart('finish-turn', () => { executeFinishTurn(); }); }}
              onTouchEnd={handleButtonHoldEnd}
              onTouchCancel={handleButtonHoldEnd}
            >
              {holdingButtonId === 'finish-turn' && (
                <svg className="hold-progress-svg" viewBox="0 0 100 100" preserveAspectRatio="none">
                  <path
                    className="hold-progress-path hold-progress-path-btn"
                    pathLength="100"
                    d="M 50,2 H 90 Q 98,2 98,10 V 90 Q 98,98 90,98 H 10 Q 2,98 2,90 V 10 Q 2,2 10,2 H 50"
                  />
                </svg>
              )}
              Finish Turn
            </button>
          </>
        )}
      </div>

      {/* Game Instructions - Show during Draft and Warmup stages */}
      {showInstructions && (
        <>
          {gameStage === 'draft' && selectedDeck === null && (
            <div className="draft-instruction">
              <span className="instruction-text">Select a Position Deck to Draft a Player</span>
              <button
                className="instruction-hide-btn"
                onClick={() => setShowInstructions(false)}
                title="Hide instructions"
              >
                ✕
              </button>
            </div>
          )}
          {gameStage === 'warmup' &&
           ((currentPlayer === 1 && player1Captain === null) ||
            (currentPlayer === 2 && player2Captain === null)) &&
           !warmUpSelectedCard && (
            <div className="draft-instruction">
              <span className="instruction-text">Choose a Player Captain and Add Boosts</span>
              <button
                className="instruction-hide-btn"
                onClick={() => setShowInstructions(false)}
                title="Hide instructions"
              >
                ✕
              </button>
            </div>
          )}
          {gameStage === 'battle' && (
            <div className="draft-instruction battle-instruction">
              <span className="instruction-text">Choose your Action</span>
              <button
                className="instruction-hide-btn"
                onClick={() => setShowInstructions(false)}
                title="Hide instructions"
              >
                ✕
              </button>
            </div>
          )}
        </>
      )}

      {/* Bottom-Left - Tactic Cards Display (DRAFT) or Boost Display (WARMUP) */}
      {gameStage === 'draft' && (
        <div className="tactic-cards-container">
          <div className="tactic-cards-header">
            <h3 className="tactic-cards-title">YOUR TACTICS</h3>
          </div>
          <div className="tactic-cards-list">
            {getTacticStatus(currentPlayer).map((statusObj, index) => {
            const tactic = statusObj.tactic;
            const displayStatus = statusObj.displayStatus;
            const formation = currentPlayer === 1 ? player1Formation : player2Formation;

            return (
              <div key={index} className={`tactic-card tactic-status-${displayStatus}`}>
                <div className="tactic-card-header">
                  <span className="tactic-name">{tactic['TACTIC NAME'].replace(' (A)', '').replace(' (B)', '')}</span>

                  <div className="tactic-requirements">
                    {/* Keeper */}
                    <div className={`tactic-req ${formation.Keeper >= tactic.KEEPER ? 'req-completed' : ''}`}>
                      <div className="tactic-req-badge" style={{ backgroundColor: '#D4A017' }}>
                        {formation.Keeper}/{tactic.KEEPER}
                      </div>
                    </div>

                    {/* Defender Center */}
                    <div className={`tactic-req ${formation.DefenderCenter >= tactic['DEFENDER CENTER'] ? 'req-completed' : ''}`}>
                      <div className="tactic-req-badge" style={{ backgroundColor: '#E74C3C' }}>
                        {formation.DefenderCenter}/{tactic['DEFENDER CENTER']}
                      </div>
                    </div>

                    {/* Defender Wide */}
                    <div className={`tactic-req ${formation.DefenderWide >= tactic['DEFENDER WIDE'] ? 'req-completed' : ''}`}>
                      <div className="tactic-req-badge" style={{ backgroundColor: '#E67E22' }}>
                        {formation.DefenderWide}/{tactic['DEFENDER WIDE']}
                      </div>
                    </div>

                    {/* Midfielder Center */}
                    <div className={`tactic-req ${formation.MidfielderCenter >= tactic['MIDFIELDER CENTER'] ? 'req-completed' : ''}`}>
                      <div className="tactic-req-badge" style={{ backgroundColor: '#64B978' }}>
                        {formation.MidfielderCenter}/{tactic['MIDFIELDER CENTER']}
                      </div>
                    </div>

                    {/* Midfielder Wide */}
                    <div className={`tactic-req ${formation.MidfielderWide >= tactic['MIDFIELDER WIDE'] ? 'req-completed' : ''}`}>
                      <div className="tactic-req-badge" style={{ backgroundColor: '#B6499A' }}>
                        {formation.MidfielderWide}/{tactic['MIDFIELDER WIDE']}
                      </div>
                    </div>

                    {/* Forward */}
                    <div className={`tactic-req ${formation.Forward >= tactic.FORWARD ? 'req-completed' : ''}`}>
                      <div className="tactic-req-badge" style={{ backgroundColor: '#4258E0' }}>
                        {formation.Forward}/{tactic.FORWARD}
                      </div>
                    </div>
                  </div>

                  <div className="tactic-status-container">
                    {displayStatus === 'completed' && <span className="tactic-status-icon tactic-completed-icon">✓</span>}
                    {displayStatus === 'locked' && <span className="tactic-status-icon tactic-locked-icon">🔒</span>}
                    {displayStatus === 'invalid' && <span className="tactic-status-icon tactic-invalid-icon">✕</span>}
                  </div>
                </div>
                <div className="tactic-card-footer">
                  {/* Boost badges */}
                  <div className="tactic-boosts">
                    {[tactic.Boost1, tactic.Boost2, tactic.Boost3, tactic.Boost4, tactic.Boost5, tactic.Boost6]
                      .filter(boost => boost && boost.trim() !== '')
                      .map((boost, idx) => {
                        // Check if boost requirement is met
                        const isMet = isBoostRequirementMet(boost, currentPlayer);

                        // Split boost by '+' to color each part individually
                        const parts = boost.split('+').map(p => p.trim());

                        return (
                          <div
                            key={idx}
                            className={`tactic-boost-badge ${isMet ? 'boost-completed' : ''}`}
                          >
                            {parts.map((part, partIdx) => (
                              <span key={partIdx}>
                                <span style={{ color: getPositionTypeColor(part) }}>
                                  {part}
                                </span>
                                {partIdx < parts.length - 1 && <span style={{ color: 'rgba(255, 255, 255, 0.5)' }}> + </span>}
                              </span>
                            ))}
                          </div>
                        );
                      })
                    }
                  </div>

                  {/* AttBoost and DefBoost symbols */}
                  <div className="tactic-boost-symbols">
                    {/* Attack Boost */}
                    {tactic.AttBoost > 0 && (
                      <div className="boost-symbol attack-boost">
                        <span className="boost-icon">+</span>
                        <span className="boost-value">{tactic.AttBoost}</span>
                      </div>
                    )}

                    {/* Defense Boost */}
                    {tactic.DefBoost > 0 && (
                      <div className="boost-symbol defense-boost">
                        <span className="boost-icon">+</span>
                        <span className="boost-value">{tactic.DefBoost}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      )}

      {/* Upper Left - Warm Up Card Boost Application (shows when card selected) */}
      {gameStage === 'warmup' && warmUpSelectedCard && (
        <div className="warmup-card-boost-window-compact">
          <div className="warmup-window-header-compact">
            <span className="warmup-window-title-compact">SELECTED CARD</span>
            <button className="warmup-close-btn-compact" onClick={() => setWarmUpSelectedCard(null)}>✕</button>
          </div>
          <div className="warmup-card-content-compact">
            {/* Attack stat with +/- */}
            <div className="warmup-stat-row-compact attack">
              <span className="warmup-stat-label-compact">ATK:</span>
              <span className="warmup-stat-value-compact">
                {warmUpSelectedCard.Attack}
                {(currentPlayer === 1 ? player1AppliedBoosts[warmUpSelectedCard.Card_Id]?.attack : player2AppliedBoosts[warmUpSelectedCard.Card_Id]?.attack) > 0 && (
                  <span className="warmup-boost-value-compact">
                    +{currentPlayer === 1 ? player1AppliedBoosts[warmUpSelectedCard.Card_Id]?.attack : player2AppliedBoosts[warmUpSelectedCard.Card_Id]?.attack}
                  </span>
                )}
              </span>
              <div className="warmup-boost-btns-compact">
                <button
                  className="warmup-btn-compact minus"
                  onClick={() => {
                    const boost = currentPlayer === 1 ? player1AppliedBoosts[warmUpSelectedCard.Card_Id]?.attack : player2AppliedBoosts[warmUpSelectedCard.Card_Id]?.attack;
                    if (boost > 0) {
                      if (currentPlayer === 1) {
                        setPlayer1AppliedBoosts(prev => ({
                          ...prev,
                          [warmUpSelectedCard.Card_Id]: {
                            attack: Math.max(0, (prev[warmUpSelectedCard.Card_Id]?.attack || 0) - 1),
                            defense: prev[warmUpSelectedCard.Card_Id]?.defense || 0
                          }
                        }));
                      } else {
                        setPlayer2AppliedBoosts(prev => ({
                          ...prev,
                          [warmUpSelectedCard.Card_Id]: {
                            attack: Math.max(0, (prev[warmUpSelectedCard.Card_Id]?.attack || 0) - 1),
                            defense: prev[warmUpSelectedCard.Card_Id]?.defense || 0
                          }
                        }));
                      }
                      // Log boost removal
                      addEventLog({
                        type: 'boost_remove',
                        player: currentPlayer,
                        stat: 'attack',
                        playerName: warmUpSelectedCard.PlayerName,
                        positionType: warmUpSelectedCard.PositionType,
                        positionColor: getPositionColor(warmUpSelectedCard.CardType)
                      });
                    }
                  }}
                  disabled={!(currentPlayer === 1 ? player1AppliedBoosts[warmUpSelectedCard.Card_Id]?.attack : player2AppliedBoosts[warmUpSelectedCard.Card_Id]?.attack)}
                >−</button>
                <button
                  className="warmup-btn-compact plus"
                  onClick={() => {
                    const boosts = currentPlayer === 1 ? player1AppliedBoosts[warmUpSelectedCard.Card_Id] || { attack: 0, defense: 0 } : player2AppliedBoosts[warmUpSelectedCard.Card_Id] || { attack: 0, defense: 0 };
                    const total = boosts.attack + boosts.defense;
                    const availableBoosts = currentPlayer === 1 ? player1AvailableBoosts : player2AvailableBoosts;
                    const appliedBoosts = currentPlayer === 1 ? player1AppliedBoosts : player2AppliedBoosts;
                    const remainingAttack = availableBoosts.attBoost - Object.values(appliedBoosts).reduce((sum, b) => sum + (b.attack || 0), 0);
                    const totalApplied = Object.values(appliedBoosts).reduce((sum, b) => sum + (b.attack || 0) + (b.defense || 0), 0);
                    const remainingTotal = availableBoosts.totalBoosts - totalApplied;
                    if (total < 6 && remainingAttack > 0 && remainingTotal > 0) {
                      if (currentPlayer === 1) {
                        setPlayer1AppliedBoosts(prev => ({
                          ...prev,
                          [warmUpSelectedCard.Card_Id]: {
                            attack: (prev[warmUpSelectedCard.Card_Id]?.attack || 0) + 1,
                            defense: prev[warmUpSelectedCard.Card_Id]?.defense || 0
                          }
                        }));
                      } else {
                        setPlayer2AppliedBoosts(prev => ({
                          ...prev,
                          [warmUpSelectedCard.Card_Id]: {
                            attack: (prev[warmUpSelectedCard.Card_Id]?.attack || 0) + 1,
                            defense: prev[warmUpSelectedCard.Card_Id]?.defense || 0
                          }
                        }));
                      }
                      // Log boost application (warmup boosts are free — no AP cost)
                      addEventLog({
                        type: 'boost_add',
                        player: currentPlayer,
                        stat: 'attack',
                        count: 1,
                        apCost: 0,
                        playerName: warmUpSelectedCard.PlayerName,
                        positionType: warmUpSelectedCard.PositionType,
                        positionColor: getPositionColor(warmUpSelectedCard.CardType)
                      });
                    }
                  }}
                  disabled={(() => {
                    const boosts = currentPlayer === 1 ? player1AppliedBoosts[warmUpSelectedCard.Card_Id] || { attack: 0, defense: 0 } : player2AppliedBoosts[warmUpSelectedCard.Card_Id] || { attack: 0, defense: 0 };
                    const total = boosts.attack + boosts.defense;
                    const availableBoosts = currentPlayer === 1 ? player1AvailableBoosts : player2AvailableBoosts;
                    const appliedBoosts = currentPlayer === 1 ? player1AppliedBoosts : player2AppliedBoosts;
                    const remainingAttack = availableBoosts.attBoost - Object.values(appliedBoosts).reduce((sum, b) => sum + (b.attack || 0), 0);
                    const totalApplied = Object.values(appliedBoosts).reduce((sum, b) => sum + (b.attack || 0) + (b.defense || 0), 0);
                    const remainingTotal = availableBoosts.totalBoosts - totalApplied;
                    return total >= 6 || remainingAttack <= 0 || remainingTotal <= 0;
                  })()}
                >+</button>
              </div>
            </div>

            {/* Defense stat with +/- */}
            <div className="warmup-stat-row-compact defence">
              <span className="warmup-stat-label-compact">DEF:</span>
              <span className="warmup-stat-value-compact">
                {warmUpSelectedCard.Defence}
                {(currentPlayer === 1 ? player1AppliedBoosts[warmUpSelectedCard.Card_Id]?.defense : player2AppliedBoosts[warmUpSelectedCard.Card_Id]?.defense) > 0 && (
                  <span className="warmup-boost-value-compact">
                    +{currentPlayer === 1 ? player1AppliedBoosts[warmUpSelectedCard.Card_Id]?.defense : player2AppliedBoosts[warmUpSelectedCard.Card_Id]?.defense}
                  </span>
                )}
              </span>
              <div className="warmup-boost-btns-compact">
                <button
                  className="warmup-btn-compact minus"
                  onClick={() => {
                    const boost = currentPlayer === 1 ? player1AppliedBoosts[warmUpSelectedCard.Card_Id]?.defense : player2AppliedBoosts[warmUpSelectedCard.Card_Id]?.defense;
                    if (boost > 0) {
                      if (currentPlayer === 1) {
                        setPlayer1AppliedBoosts(prev => ({
                          ...prev,
                          [warmUpSelectedCard.Card_Id]: {
                            attack: prev[warmUpSelectedCard.Card_Id]?.attack || 0,
                            defense: Math.max(0, (prev[warmUpSelectedCard.Card_Id]?.defense || 0) - 1)
                          }
                        }));
                      } else {
                        setPlayer2AppliedBoosts(prev => ({
                          ...prev,
                          [warmUpSelectedCard.Card_Id]: {
                            attack: prev[warmUpSelectedCard.Card_Id]?.attack || 0,
                            defense: Math.max(0, (prev[warmUpSelectedCard.Card_Id]?.defense || 0) - 1)
                          }
                        }));
                      }
                      // Log boost removal
                      addEventLog({
                        type: 'boost_remove',
                        player: currentPlayer,
                        stat: 'defense',
                        playerName: warmUpSelectedCard.PlayerName,
                        positionType: warmUpSelectedCard.PositionType,
                        positionColor: getPositionColor(warmUpSelectedCard.CardType)
                      });
                    }
                  }}
                  disabled={!(currentPlayer === 1 ? player1AppliedBoosts[warmUpSelectedCard.Card_Id]?.defense : player2AppliedBoosts[warmUpSelectedCard.Card_Id]?.defense)}
                >−</button>
                <button
                  className="warmup-btn-compact plus"
                  onClick={() => {
                    const boosts = currentPlayer === 1 ? player1AppliedBoosts[warmUpSelectedCard.Card_Id] || { attack: 0, defense: 0 } : player2AppliedBoosts[warmUpSelectedCard.Card_Id] || { attack: 0, defense: 0 };
                    const total = boosts.attack + boosts.defense;
                    const availableBoosts = currentPlayer === 1 ? player1AvailableBoosts : player2AvailableBoosts;
                    const appliedBoosts = currentPlayer === 1 ? player1AppliedBoosts : player2AppliedBoosts;
                    const remainingDefense = availableBoosts.defBoost - Object.values(appliedBoosts).reduce((sum, b) => sum + (b.defense || 0), 0);
                    const totalApplied = Object.values(appliedBoosts).reduce((sum, b) => sum + (b.attack || 0) + (b.defense || 0), 0);
                    const remainingTotal = availableBoosts.totalBoosts - totalApplied;
                    if (total < 6 && remainingDefense > 0 && remainingTotal > 0) {
                      if (currentPlayer === 1) {
                        setPlayer1AppliedBoosts(prev => ({
                          ...prev,
                          [warmUpSelectedCard.Card_Id]: {
                            attack: prev[warmUpSelectedCard.Card_Id]?.attack || 0,
                            defense: (prev[warmUpSelectedCard.Card_Id]?.defense || 0) + 1
                          }
                        }));
                      } else {
                        setPlayer2AppliedBoosts(prev => ({
                          ...prev,
                          [warmUpSelectedCard.Card_Id]: {
                            attack: prev[warmUpSelectedCard.Card_Id]?.attack || 0,
                            defense: (prev[warmUpSelectedCard.Card_Id]?.defense || 0) + 1
                          }
                        }));
                      }
                      // Log boost application (warmup boosts are free — no AP cost)
                      addEventLog({
                        type: 'boost_add',
                        player: currentPlayer,
                        stat: 'defense',
                        count: 1,
                        apCost: 0,
                        playerName: warmUpSelectedCard.PlayerName,
                        positionType: warmUpSelectedCard.PositionType,
                        positionColor: getPositionColor(warmUpSelectedCard.CardType)
                      });
                    }
                  }}
                  disabled={(() => {
                    const boosts = currentPlayer === 1 ? player1AppliedBoosts[warmUpSelectedCard.Card_Id] || { attack: 0, defense: 0 } : player2AppliedBoosts[warmUpSelectedCard.Card_Id] || { attack: 0, defense: 0 };
                    const total = boosts.attack + boosts.defense;
                    const availableBoosts = currentPlayer === 1 ? player1AvailableBoosts : player2AvailableBoosts;
                    const appliedBoosts = currentPlayer === 1 ? player1AppliedBoosts : player2AppliedBoosts;
                    const remainingDefense = availableBoosts.defBoost - Object.values(appliedBoosts).reduce((sum, b) => sum + (b.defense || 0), 0);
                    const totalApplied = Object.values(appliedBoosts).reduce((sum, b) => sum + (b.attack || 0) + (b.defense || 0), 0);
                    const remainingTotal = availableBoosts.totalBoosts - totalApplied;
                    return total >= 6 || remainingDefense <= 0 || remainingTotal <= 0;
                  })()}
                >+</button>
              </div>
            </div>

            {/* Player name and Captain button */}
            <div className="warmup-player-row-compact">
              <span className="warmup-player-name-compact">{warmUpSelectedCard.PlayerName}</span>
              <button
                className={`warmup-captain-btn-tiny ${
                  (currentPlayer === 1 && player1Captain === warmUpSelectedCard.Card_Id) ||
                  (currentPlayer === 2 && player2Captain === warmUpSelectedCard.Card_Id) ? 'active' : ''
                }`}
                onClick={() => {
                  if (currentPlayer === 1) {
                    // Toggle: if this card is already captain, unset it; otherwise set it
                    if (player1Captain === warmUpSelectedCard.Card_Id) {
                      setPlayer1Captain(null);
                      addEventLog({
                        type: 'captain_remove',
                        player: currentPlayer,
                        playerName: warmUpSelectedCard.PlayerName,
                        positionType: warmUpSelectedCard.PositionType,
                        positionColor: getPositionColor(warmUpSelectedCard.CardType)
                      });
                    } else {
                      setPlayer1Captain(warmUpSelectedCard.Card_Id);
                      addEventLog({
                        type: 'captain_add',
                        player: currentPlayer,
                        playerName: warmUpSelectedCard.PlayerName,
                        positionType: warmUpSelectedCard.PositionType,
                        positionColor: getPositionColor(warmUpSelectedCard.CardType)
                      });
                    }
                  } else {
                    // Toggle: if this card is already captain, unset it; otherwise set it
                    if (player2Captain === warmUpSelectedCard.Card_Id) {
                      setPlayer2Captain(null);
                      addEventLog({
                        type: 'captain_remove',
                        player: currentPlayer,
                        playerName: warmUpSelectedCard.PlayerName,
                        positionType: warmUpSelectedCard.PositionType,
                        positionColor: getPositionColor(warmUpSelectedCard.CardType)
                      });
                    } else {
                      setPlayer2Captain(warmUpSelectedCard.Card_Id);
                      addEventLog({
                        type: 'captain_add',
                        player: currentPlayer,
                        playerName: warmUpSelectedCard.PlayerName,
                        positionType: warmUpSelectedCard.PositionType,
                        positionColor: getPositionColor(warmUpSelectedCard.CardType)
                      });
                    }
                  }
                }}
              >
                C
              </button>
            </div>

            {/* Position badge */}
            <div className="warmup-position-compact" style={{ backgroundColor: getPositionColor(warmUpSelectedCard.CardType) }}>
              {warmUpSelectedCard.PositionType}
            </div>
          </div>
        </div>
      )}

      {/* Right of Selected Card - Warm Up Boosts Compact */}
      {gameStage === 'warmup' && (
        <div className="warmup-boosts-compact">
          <div className="warmup-boosts-header">
            YOUR BOOSTS ({currentPlayer === 1 ? player1AvailableBoosts.totalBoosts : player2AvailableBoosts.totalBoosts} avail.)
          </div>

          {/* Attack Boosts */}
          <div className="warmup-boost-row attack">
            <span className="boost-label">ATK</span>
            <span className="boost-count">
              {currentPlayer === 1
                ? player1AvailableBoosts.attBoost - Object.values(player1AppliedBoosts).reduce((sum, b) => sum + (b.attack || 0), 0)
                : player2AvailableBoosts.attBoost - Object.values(player2AppliedBoosts).reduce((sum, b) => sum + (b.attack || 0), 0)
              } / {currentPlayer === 1 ? player1AvailableBoosts.attBoost : player2AvailableBoosts.attBoost}
            </span>
          </div>

          {/* Defense Boosts */}
          <div className="warmup-boost-row defense">
            <span className="boost-label">DEF</span>
            <span className="boost-count">
              {currentPlayer === 1
                ? player1AvailableBoosts.defBoost - Object.values(player1AppliedBoosts).reduce((sum, b) => sum + (b.defense || 0), 0)
                : player2AvailableBoosts.defBoost - Object.values(player2AppliedBoosts).reduce((sum, b) => sum + (b.defense || 0), 0)
              } / {currentPlayer === 1 ? player1AvailableBoosts.defBoost : player2AvailableBoosts.defBoost}
            </span>
          </div>

          {/* Tactic Name Display */}
          <div className="warmup-tactic-name">
            {(currentPlayer === 1 ? player1CompletedTactic : player2CompletedTactic)?.['TACTIC NAME'] && (
              <div className="tactic-name-display">
                {(currentPlayer === 1 ? player1CompletedTactic : player2CompletedTactic)['TACTIC NAME']
                  .replace(' (A)', '')
                  .replace(' (B)', '')}
              </div>
            )}
          </div>

          {/* Confirm button — logic extracted to executeWarmupFinish() */}
          <button
            className="warmup-confirm-btn-compact"
            onClick={executeWarmupFinish}
            disabled={currentPlayer === 1 ? player1Captain === null : player2Captain === null}
          >
            ✓ CONFIRM
          </button>
        </div>
      )}


      {/* Bottom-Left - Action Cards and Special Actions Display (BATTLE) */}
      {gameStage === 'battle' && (
        <>
          {/* Action Cards - Toggle Buttons Box (Kickopp and Upgrade) */}
          <div className="action-cards-box">
            <div className="action-cards-toggle-container">
              {(currentPlayer === 1 ? player1ActionCards : player2ActionCards).map((actionCard) => {
                const currentAP = currentPlayer === 1 ? player1ActionPoints : player2ActionPoints;
                const selectedActionCard = currentPlayer === 1 ? player1SelectedActionCard : player2SelectedActionCard;
                const isSelected = selectedActionCard === actionCard.id;

                // Check if action was already used this turn (once-per-turn limit)
                // IMPORTANT: Kickopp and Upgrade are mutually exclusive - if one is used, both are disabled
                const usedKickopp = currentPlayer === 1 ? player1UsedKickopp : player2UsedKickopp;
                const usedUpgrade = currentPlayer === 1 ? player1UsedUpgrade : player2UsedUpgrade;
                const extraMainActions = currentPlayer === 1 ? player1ExtraMainActions : player2ExtraMainActions;
                const eitherActionUsed = (usedKickopp || usedUpgrade) && extraMainActions === 0;

                let isDisabled = currentAP < actionCard.cost || eitherActionUsed;

                // CRITICAL: Check if ANY other action is in progress (prevents multiple actions)
                // Allow the currently selected action card to remain clickable (for deselection)
                const isThisActionCardActive = isSelected;
                const anyOtherActionInProgress =
                  (moveMode) ||
                  (boostMode !== null && boostStep !== null) ||
                  (substituteMode && substituteStep !== null) ||
                  (kickoppMode && !isThisActionCardActive); // Allow deselecting kickopp even when active

                // Disable action cards if another action is in progress
                if (anyOtherActionInProgress) {
                  isDisabled = true;
                }

                // Debug logging for Kickopp button state
                if (actionCard.id === 'kickopp') {
                  console.log(`🔍 Kickopp button render: isSelected=${isSelected}, selectedActionCard=${selectedActionCard}, isDisabled=${isDisabled}, currentAP=${currentAP}, kickoppMode=${kickoppMode}`);
                }
                
                const isHoldingBtn = holdingButtonId === actionCard.id;
                return (
                  <div
                    key={actionCard.id}
                    className={`action-card-toggle ${isSelected ? 'action-card-selected' : ''} ${isDisabled ? 'action-card-disabled' : ''}${isHoldingBtn ? ' btn-holding' : ''}`}
                    onMouseDown={() => {
                      if (!isDisabled && isSelected) {
                        // Already active — instant deselect on mouseDown
                        const setSelected = currentPlayer === 1 ? setPlayer1SelectedActionCard : setPlayer2SelectedActionCard;
                        const setMaxAP = currentPlayer === 1 ? setPlayer1MaxActionPoints : setPlayer2MaxActionPoints;
                        const currentMaxAP = currentPlayer === 1 ? player1MaxActionPoints : player2MaxActionPoints;
                        const setUpgradeUsed = currentPlayer === 1 ? setPlayer1UpgradeUsedThisTurn : setPlayer2UpgradeUsedThisTurn;
                        if (actionCard.id === 'upgrade') { setMaxAP(Math.max(1, currentMaxAP - 1)); setUpgradeUsed(false); }
                        else if (actionCard.id === 'kickopp') { setKickoppMode(false); }
                        setSelected(null);
                        return;
                      }
                      if (!isDisabled && !isSelected) {
                        handleButtonHoldStart(actionCard.id, () => {
                          const setSelected = currentPlayer === 1 ? setPlayer1SelectedActionCard : setPlayer2SelectedActionCard;
                          const setAP = currentPlayer === 1 ? setPlayer1ActionPoints : setPlayer2ActionPoints;
                          const setMaxAP = currentPlayer === 1 ? setPlayer1MaxActionPoints : setPlayer2MaxActionPoints;
                          const currentAP = currentPlayer === 1 ? player1ActionPoints : player2ActionPoints;
                          const currentMaxAP = currentPlayer === 1 ? player1MaxActionPoints : player2MaxActionPoints;
                          const previousSelected = currentPlayer === 1 ? player1SelectedActionCard : player2SelectedActionCard;
                          const setUpgradeUsed = currentPlayer === 1 ? setPlayer1UpgradeUsedThisTurn : setPlayer2UpgradeUsedThisTurn;
                          let newAP = currentAP;
                          let newMaxAP = currentMaxAP;
                          if (previousSelected === 'upgrade') {
                            newMaxAP = Math.max(1, currentMaxAP - 1);
                            setUpgradeUsed(false);
                          } else if (previousSelected === 'kickopp') {
                            setKickoppMode(false);
                          }
                          if (actionCard.id === 'upgrade') {
                            newMaxAP = newMaxAP + 1;
                            newAP = newAP + 1 - actionCard.cost;
                            const kickoppModifierActive =
                              (currentPlayer === 1 ? player1PendingKickoppBonus : player2PendingKickoppBonus) > 0
                              || attackAnyoneActive || ignoreOppDefActive;
                            const kickoppNotYetUsed = !(currentPlayer === 1 ? player1UsedKickopp : player2UsedKickopp);
                            if (kickoppModifierActive && kickoppNotYetUsed) {
                              setPendingAction({
                                type: 'upgrade_kickopp_warning',
                                computedNewAP: newAP, computedNewMaxAP: newMaxAP,
                                prevMaxAP: currentMaxAP, prevSelected: previousSelected,
                                extraMainActions: currentPlayer === 1 ? player1ExtraMainActions : player2ExtraMainActions,
                                lessInactives: currentPlayer === 1 ? player1LessInactives : player2LessInactives,
                              });
                              setShowAPConfirmation(true);
                              return;
                            }
                            setUpgradeUsed(true);
                            {
                              const lessInactives = currentPlayer === 1 ? player1LessInactives : player2LessInactives;
                              const prevMaxPawns = getMaxKPawns(currentMaxAP, lessInactives);
                              const newMaxPawnsVal = getMaxKPawns(newMaxAP, lessInactives);
                              if (newMaxPawnsVal < prevMaxPawns) {
                                const currentPawns = currentPlayer === 1 ? player1KickoppPawns : player2KickoppPawns;
                                const setPawns = currentPlayer === 1 ? setPlayer1KickoppPawns : setPlayer2KickoppPawns;
                                const excessCount = currentPawns.length - newMaxPawnsVal;
                                if (excessCount > 0) {
                                  const sorted = [...currentPawns].sort((a, b) => a.order - b.order);
                                  setPawns(sorted.slice(excessCount));
                                }
                              }
                            }
                            if (currentPlayer === 1) {
                              setPlayer1UsedUpgrade(true);
                              if (player1ExtraMainActions > 0) setPlayer1ExtraMainActions(prev => prev - 1);
                            } else {
                              setPlayer2UsedUpgrade(true);
                              if (player2ExtraMainActions > 0) setPlayer2ExtraMainActions(prev => prev - 1);
                            }
                            addEventLog({ type: 'upgrade', player: currentPlayer, cost: actionCard.cost });
                          } else if (actionCard.id === 'kickopp') {
                            setKickoppMode(true);
                            setUpgradeUsed(false);
                          }
                          setMaxAP(newMaxAP);
                          setAP(newAP);
                          setSelected(actionCard.id);
                        });
                      }
                    }}
                    onMouseUp={handleButtonHoldEnd}
                    onMouseLeave={handleButtonHoldEnd}
                    onTouchStart={(e) => { e.preventDefault(); if (!isDisabled && !isSelected) handleButtonHoldStart(actionCard.id, () => {}); }}
                    onTouchEnd={handleButtonHoldEnd}
                    onTouchCancel={handleButtonHoldEnd}
                  >
                    {isHoldingBtn && (
                      <svg className="hold-progress-svg" viewBox="0 0 100 100" preserveAspectRatio="none">
                        <path
                          className="hold-progress-path hold-progress-path-btn"
                          pathLength="100"
                          d="M 50,2 H 90 Q 98,2 98,10 V 90 Q 98,98 90,98 H 10 Q 2,98 2,90 V 10 Q 2,2 10,2 H 50"
                        />
                      </svg>
                    )}
                    {actionCard.icon && (
                      <span className={`action-icon-left ${actionCard.iconType === 'square' ? 'icon-square' : actionCard.iconType === 'circle' ? 'icon-circle' : actionCard.iconType === 'stacked' ? 'icon-stacked' : ''}`}>
                        {actionCard.id === 'kickopp' ? (
                          <div className="kickopp-mini-logo">
                            <div className="k-mini-vertical"></div>
                            <div className="k-mini-upper"></div>
                            <div className="k-mini-lower"></div>
                          </div>
                        ) : actionCard.icon}
                      </span>
                    )}
                    <div className="action-card-toggle-name">
                      {actionCard.name}
                      {actionCard.id === 'kickopp' && (() => {
                        const pendingBonus = currentPlayer === 1 ? player1PendingKickoppBonus : player2PendingKickoppBonus;
                        return pendingBonus > 0 ? (
                          <span style={{ marginLeft: 4, fontSize: 10, fontWeight: 900, background: '#f59e0b', color: '#0f172a', borderRadius: 4, padding: '1px 4px' }}>+{pendingBonus}</span>
                        ) : null;
                      })()}
                    </div>
                    <div className="action-card-toggle-cost">
                      {actionCard.cost}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Special Actions - Separate Box Below */}
          <div className="special-actions-box">
            <div className="special-actions-grid">
              {(currentPlayer === 1 ? player1SpecialActions : player2SpecialActions).map((specialAction) => {
                const currentAP = currentPlayer === 1 ? player1ActionPoints : player2ActionPoints;
                // For Move: disabled if 0 AP (cost is variable '1+')
                // For Substitute: disabled if AP < cost OR no decks available
                // For Add Boost: disabled if AP < 1 OR no boosts available
                // For Action Card Decks: always disabled (not yet implemented)
                // For others: disabled if AP < cost
                let isDisabled = false;
                if (specialAction.type === 'action_card_deck') {
                  isDisabled = true; // Action card decks not yet available
                } else if (specialAction.id === 'move') {
                  isDisabled = !freeMovesActive && currentAP === 0;
                } else if (specialAction.id === 'substitute') {
                  const availableSubstituteDecks = getAvailableSubstituteDecks(currentPlayer);
                  isDisabled = (freeSubsRemaining === 0 && currentAP < specialAction.cost) || availableSubstituteDecks.length === 0;
                } else if (specialAction.id === 'add_boost') {
                  const battleBoosts = currentPlayer === 1 ? player1BattleBoosts : player2BattleBoosts;
                  isDisabled = (!freeBoostsActive && currentAP < 1) || (battleBoosts.attack === 0 && battleBoosts.defense === 0);
                } else {
                  isDisabled = currentAP < specialAction.cost;
                }

                // CRITICAL: Disable ALL actions when ANY other action is in progress (prevents multiple actions)
                const isThisActionActive =
                  (specialAction.id === 'move' && moveMode) ||
                  (specialAction.id === 'substitute' && substituteMode) ||
                  (specialAction.id === 'add_boost' && boostMode === 'add_boost') ||
                  (specialAction.id === 'buy_1a_or_1d' && boostMode === 'buy_single') ||
                  (specialAction.id === 'buy_1a_and_1d' && boostMode === 'buy_both');

                const anyOtherActionInProgress =
                  (kickoppMode) ||
                  (moveMode && !isThisActionActive) ||
                  (boostMode !== null && boostStep !== null && !isThisActionActive) ||
                  (substituteMode && substituteStep !== null && !isThisActionActive);

                if (anyOtherActionInProgress) {
                  isDisabled = true;
                }

                // Check if this action is currently active
                const isActive =
                  (specialAction.id === 'move' && moveMode) ||
                  (specialAction.id === 'substitute' && substituteMode) ||
                  (specialAction.id === 'add_boost' && boostMode === 'add_boost') ||
                  (specialAction.id === 'buy_1a_or_1d' && boostMode === 'buy_single') ||
                  (specialAction.id === 'buy_1a_and_1d' && boostMode === 'buy_both');

                return (
                  <div
                    key={specialAction.id}
                    className={`special-action-card ${isDisabled ? 'action-card-disabled' : ''} ${isActive ? 'action-card-selected' : ''}`}
                    onClick={() => {
                      if (!isDisabled) {
                        if (specialAction.id === 'move') {
                          // Move: warn if only 1 AP left (move costs at least 1 AP)
                          if (shouldWarnAboutLastAP(1)) {
                            console.log(`⚠️ Warning: Move will use last AP without Upgrade/Kickopp. Showing confirmation.`);
                            setPendingAction({ type: 'move_activate' });
                            setShowAPConfirmation(true);
                            return;
                          }
                          console.log(`🎯 Move action clicked - activating Move mode`);
                          setMoveMode(true);
                        } else if (specialAction.id === 'substitute') {
                          // Substitute: Check confirmation before entering substitute mode (skip if free subs available)
                          if (freeSubsRemaining === 0 && shouldWarnAboutLastAP(specialAction.cost)) {
                            console.log(`⚠️ Warning: Substitute will use remaining AP without Upgrade/Kickopp. Showing confirmation.`);
                            setPendingAction({
                              type: 'substitute',
                              cost: specialAction.cost
                            });
                            setShowAPConfirmation(true);
                            return;
                          }
                          console.log(`🔄 Substitute action clicked - select card to remove from board`);
                          setSubstituteMode(true);
                          setSubstituteStep('select_board_card');
                        } else if (specialAction.id === 'add_boost') {
                          // Add Boost: Check confirmation before entering boost mode (skip if free boosts active)
                          if (!freeBoostsActive && shouldWarnAboutLastAP(1)) {
                            console.log(`⚠️ Warning: Add Boost will use remaining AP without Upgrade/Kickopp. Showing confirmation.`);
                            setPendingAction({
                              type: 'add_boost',
                              cost: 1
                            });
                            setShowAPConfirmation(true);
                            return;
                          }
                          console.log(`💪 Add Boost action clicked - select card on board`);
                          setBoostMode('add_boost');
                          setBoostStep('select_card');
                          setPendingBoosts({});
                        } else if (specialAction.id === 'buy_1a_or_1d') {
                          // Buy 1A or 1D: show selection popup (confirmation happens when selecting type)
                          console.log(`💰 Buy 1A or 1D action clicked - showing selection`);
                          setBoostMode('buy_single');
                          setBoostStep('select_type');
                        } else if (specialAction.id === 'buy_1a_and_1d') {
                          // Buy 1A and 1D: show confirmation popup (confirmation happens when confirming)
                          console.log(`💰 Buy 1A and 1D action clicked - showing confirmation`);
                          setBoostMode('buy_both');
                          setBoostStep('confirm');
                        } else {
                          // Other special actions: Decrease Action Points by the cost
                          const setAP = currentPlayer === 1 ? setPlayer1ActionPoints : setPlayer2ActionPoints;
                          const currentAP = currentPlayer === 1 ? player1ActionPoints : player2ActionPoints;
                          setAP(currentAP - specialAction.cost);
                          console.log(`${currentPlayer === 1 ? 'Team 1' : 'Team 2'} used ${specialAction.name} (cost: ${specialAction.cost} AP). Remaining AP: ${currentAP - specialAction.cost}`);
                        }
                      }
                    }}
                  >
                    <div className="special-action-name">
                      {specialAction.name}
                      {specialAction.id === 'substitute' && freeSubsRemaining > 0 && (
                        <span style={{ marginLeft: 4, fontSize: 10, fontWeight: 900, background: '#f59e0b', color: '#0f172a', borderRadius: 4, padding: '1px 4px' }}>{freeSubsRemaining}</span>
                      )}
                      {specialAction.icon && !specialAction.iconBelow && specialAction.id !== 'buy_1a_or_1d' && specialAction.id !== 'buy_1a_and_1d' && (
                        <span className={`action-icon ${specialAction.iconType === 'square-small' ? 'icon-square-small' : specialAction.iconType === 'square' ? 'icon-square' : ''}`}>
                          {specialAction.icon}
                        </span>
                      )}
                      {specialAction.id === 'buy_1a_or_1d' && (
                        <span className="action-icon">
                          <span className="buy-icon-group">
                            <span className="buy-box blue">1</span>
                            <span className="buy-or">or</span>
                            <span className="buy-box red">1</span>
                          </span>
                        </span>
                      )}
                      {specialAction.id === 'buy_1a_and_1d' && (
                        <span className="action-icon">
                          <span className="buy-icon-group">
                            <span className="buy-box blue">1</span>
                            <span className="buy-and">&</span>
                            <span className="buy-box red">1</span>
                          </span>
                        </span>
                      )}
                    </div>
                    {specialAction.icon && specialAction.iconBelow && (
                      <div className="special-action-icon-below">
                        {specialAction.icon}
                      </div>
                    )}
                    <div className="special-action-cost">
                      {specialAction.cost}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Next Turn Actions — CPU upcoming boosts preview (Solo mode, human's turn) */}
          {isSolo && currentPlayer !== aiPlayer && (
            <div className="next-turn-actions-panel">
              <div className="next-turn-actions-header">
                <span className="next-turn-label">NEXT TURN ACTIONS</span>
                <span className="next-turn-team">CPU Team {aiPlayer}</span>
              </div>
              <div className="next-turn-actions-body">
                {(() => {
                  const aiLevel = aiPlayer === 1 ? player1MaxActionPoints : player2MaxActionPoints;
                  const currentBoosts = aiPlayer === 1 ? player1BattleBoosts : player2BattleBoosts;
                  // Mirror the real training formula (Step 0 of executeAiTurn): mean
                  // total = stage lambda for this level (or the non-tournament
                  // fallback's expected value), split by the stage's PropAtt.
                  const lambda = cpuStage
                    ? parseFloat(cpuStage[`L${aiLevel}`] || cpuStage['L1'] || 0)
                    : aiLevel + (Math.ceil(aiLevel * 0.7) - 1) / 2;
                  const propAtk = cpuStage ? parseFloat(cpuStage.PropAtt || 0.5) : (0.7 + 0.075);
                  const estTotal = Math.round(lambda);
                  const estAtk   = Math.round(estTotal * propAtk);
                  const estDef   = estTotal - estAtk;
                  return (
                    <>
                      <div className="next-turn-row">
                        <span className="next-turn-icon">🏋️</span>
                        <span className="next-turn-text">
                          {'Training: +'}
                          <span style={{ color: '#4258E0', fontWeight: 'bold' }}>~{estAtk} ATK</span>
                          {' / +'}
                          <span style={{ color: '#E74C3C', fontWeight: 'bold' }}>~{estDef} DEF</span>
                          {' to A/D Boost'}
                        </span>
                      </div>
                      {(currentBoosts.attack > 0 || currentBoosts.defense > 0) && (
                        <div className="next-turn-row">
                          <span className="next-turn-icon">💰</span>
                          <span className="next-turn-text">
                            {'Boost pool: '}
                            <span style={{ color: '#60a5fa', fontWeight: 'bold' }}>{currentBoosts.attack} ATK</span>
                            {' / '}
                            <span style={{ color: '#f87171', fontWeight: 'bold' }}>{currentBoosts.defense} DEF</span>
                            {' available'}
                          </span>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          )}
        </>
      )}

      {/* Sidebar - Card Selection Menu (left or right depending on mode) */}
      {visibleCards.length > 0 && (
        <div className={`card-selection-sidebar ${visibleCards.length === 1 && cardSelections.toPlay ? 'place-card-sidebar' : ''}`}>
          <div className="sidebar-header">
            <h3 className="sidebar-title">
              {visibleCards.length === 1 && cardSelections.toPlay ? 'Place the card' :
               !cardSelections.toPlay ? 'Choose card to PLAY' :
               !cardSelections.toBench ? 'Choose card to BENCH' :
               !cardSelections.toDiscard ? 'Choose card to DISCARD' :
               'Confirm your choices'}
            </h3>
            {/* Close button removed - teams must complete the draft process */}
          </div>

          <div className="sidebar-body">
            <div className="sidebar-cards">
            {visibleCards.map((player, index) => {
              const posColor = getPositionColor(player.CardType);
              const role = getCardRole(index);

              const isPlayCardReadyToDrag = role === 'play' && visibleCards.length === 1;
              // Disable dragging in Draft stage (click-to-place mode)
              const isDragDisabled = gameStage === 'draft' ? true : !isPlayCardReadyToDrag;

              return (
                <div
                  key={`${player.ID}-${index}`}
                  className={`sidebar-card ${role ? `role-${role}` : ''}`}
                  style={{ '--reveal-delay': `${index * 280}ms` }}
                  onClick={() => !role && !isPlayCardReadyToDrag && handleCardRoleClick(player, index)}
                >
                  <DraggableCard
                    id={`sidebar-card-${player.ID}-${index}`}
                    data={{
                      card: player,
                      index: index,
                      role: role,
                      source: 'sidebar',
                      deckType: selectedDeck,
                      alreadyRemovedFromDeck: isPlayCardReadyToDrag
                    }}
                    className={`sidebar-card-wrapper ${isPlayCardReadyToDrag ? 'draggable' : ''}`}
                    disabled={isDragDisabled}
                  >
                    <div className="card-flip-inner">
                      {/* Back face — solid position colour */}
                      <div className="card-back-face" style={{ backgroundColor: posColor }} />

                      {/* Front face — stats and player info */}
                      <div
                        className="sidebar-card-content"
                      >
                      {/* Role Badge */}
                      {role && (
                        <div className={`role-badge role-${role}`}>
                          {role === 'play' && '⚡ PLAY'}
                          {role === 'bench' && '🔄 BENCH'}
                          {role === 'discard' && '✕ OUT'}
                        </div>
                      )}

                      {/* Stats row */}
                      <div className="sidebar-card-stats">
                        <div className="sidebar-stat-box attack">
                          {player.Attack}
                        </div>
                        <div className="sidebar-stat-box defence">
                          {player.Defence}
                        </div>
                      </div>

                      {/* Player name */}
                      <div
                        className="sidebar-card-name"
                        style={{
                          backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.05) 45%, rgba(0,0,0,0.9) 100%), url('${getFaceUrl(player.Card_Id)}')`,
                          backgroundSize: 'cover, 68%',
                          backgroundPosition: 'center, center top',
                          backgroundRepeat: 'no-repeat, no-repeat'
                        }}
                      >
                        {player.PlayerName}
                      </div>

                      {/* Bottom row: position (left) + flag (right) */}
                      <div className="sidebar-card-bottom">
                        <div
                          className="sidebar-card-position"
                          style={{ backgroundColor: posColor }}
                        >
                          {player.PositionType?.trim()}
                        </div>
                        {player.Country && (
                          <span className={`fi fi-${player.Country.toLowerCase()} sidebar-card-flag`} />
                        )}
                      </div>
                      </div>
                    </div>
                  </DraggableCard>
                </div>
              );
            })}
            </div>

            {/* Action Buttons */}
            <div className="sidebar-actions">
            {cardSelections.toPlay && cardSelections.toBench && cardSelections.toDiscard && (
              <button
                className="confirm-btn"
                onClick={handleConfirmSelections}
                title="Confirm Choices"
              >
                ✓
              </button>
            )}
            {(cardSelections.toPlay || cardSelections.toBench || cardSelections.toDiscard) && (
              <button
                className="reset-btn"
                onClick={handleResetSelections}
                title="Reset"
              >
                ↺
              </button>
            )}
            </div>
          </div>
        </div>
      )}

      <div className="arena-layout">
        {/* Recycled Cards Deck - Top Right */}
        <div className="recycled-deck-container">
          <div className="recycled-deck">
            <div
              className="deck-content"
              style={{
                background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)'
              }}
            >
              <div className="deck-label">
                CARDS OUT
              </div>
              <div className="deck-count">
                {recycledCardsCount}
              </div>
            </div>
          </div>

          {/* Random Draft Buttons - Only show during DRAFT stage */}
          {gameStage === 'draft' && (
            <div className="random-draft-buttons">
              <button
                className="random-draft-btn random-next"
                onClick={handleRandomNext}
                disabled={selectedDeck !== null}
              >
                🎲 RANDOM NEXT
              </button>
              <button
                className="random-draft-btn random-all"
                onClick={handleRandomAll}
                disabled={selectedDeck !== null}
              >
                🎲 RANDOM ALL
              </button>
            </div>
          )}
        </div>

        {/* Player Info Cards - Upper Right (Warm Up and Battle stages) */}
        {(gameStage === 'warmup' || gameStage === 'battle') && (
          <div className={`player-info-cards-container${verticalBoard && (!isSolo && currentPlayer === 2 || isSolo && aiPlayer === 1) ? ' board-rotated' : ''}`}>
            {/* Team 1 Info Card */}
            <div className={`player-info-card player1 ${currentPlayer === 1 ? 'active' : ''} ${isSolo && aiPlayer === 1 ? 'cpu-player' : ''}`}>
              <div className="player-info-header">
                {isSolo && aiPlayer === 1 ? <><span className="cpu-badge">CPU</span> Team 1</> : 'Team 1'}
              </div>
              <div className="player-info-stats">
                <div className="player-stat score">
                  <span className="stat-label">SCORE</span>
                  <span className="stat-value">{player1Score}</span>
                </div>
                <div className="player-stat level">
                  <span className="stat-label">Level</span>
                  <span className="stat-value">{player1MaxActionPoints}</span>
                </div>
                {!(isSolo && aiPlayer === 1) && (
                  <div className="player-stat action-cards">
                    <span className="stat-label">Action C.</span>
                    <span className="stat-value" style={{ color: '#4ade80' }}>{player1Hand.length}</span>
                  </div>
                )}
                <div className="player-stat boosts">
                  <span className="stat-label"><span style={{ color: '#4258E0', fontWeight: 'bold' }}>A</span>/<span style={{ color: '#E74C3C', fontWeight: 'bold' }}>D</span> Boost</span>
                  <span className={`stat-value${p1BoostFlash ? ' boost-pool-flash' : ''}`}>
                    {gameStage === 'warmup' ? (
                      <>
                        {Object.values(player1AppliedBoosts).reduce((sum, b) => sum + (b.attack || 0) + (b.defense || 0), 0)}
                        /
                        {player1AvailableBoosts.totalBoosts || 0}
                      </>
                    ) : (
                      <>
                        <span className="boost-atk">{player1BattleBoosts.attack}</span>
                        /
                        <span className="boost-def">{player1BattleBoosts.defense}</span>
                      </>
                    )}
                  </span>
                </div>
              </div>
            </div>

            {/* Team 2 Info Card */}
            <div className={`player-info-card player2 ${currentPlayer === 2 ? 'active' : ''} ${isSolo && aiPlayer === 2 ? 'cpu-player' : ''}`}>
              <div className="player-info-header">
                {isSolo && aiPlayer === 2 ? <><span className="cpu-badge">CPU</span> Team 2</> : 'Team 2'}
              </div>
              <div className="player-info-stats">
                <div className="player-stat score">
                  <span className="stat-label">SCORE</span>
                  <span className="stat-value">{player2Score}</span>
                </div>
                <div className="player-stat level">
                  <span className="stat-label">Level</span>
                  <span className="stat-value">{player2MaxActionPoints}</span>
                </div>
                {!(isSolo && aiPlayer === 2) && (
                  <div className="player-stat action-cards">
                    <span className="stat-label">Action C.</span>
                    <span className="stat-value" style={{ color: '#4ade80' }}>{player2Hand.length}</span>
                  </div>
                )}
                <div className="player-stat boosts">
                  <span className="stat-label"><span style={{ color: '#4258E0', fontWeight: 'bold' }}>A</span>/<span style={{ color: '#E74C3C', fontWeight: 'bold' }}>D</span> Boost</span>
                  <span className={`stat-value${p2BoostFlash ? ' boost-pool-flash' : ''}`}>
                    {gameStage === 'warmup' ? (
                      <>
                        {Object.values(player2AppliedBoosts).reduce((sum, b) => sum + (b.attack || 0) + (b.defense || 0), 0)}
                        /
                        {player2AvailableBoosts.totalBoosts || 0}
                      </>
                    ) : (
                      <>
                        <span className="boost-atk">{player2BattleBoosts.attack}</span>
                        /
                        <span className="boost-def">{player2BattleBoosts.defense}</span>
                      </>
                    )}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}


        {/* Abandon Match Button - Bottom Right (DRAFT, WARMUP, and BATTLE stages) */}
        {(gameStage === 'draft' || gameStage === 'warmup' || gameStage === 'battle') && (
          <button
            className={`abandon-match-btn${holdingButtonId === 'abandon' ? ' btn-holding' : ''}`}
            onMouseDown={() => handleButtonHoldStart('abandon', () => setShowAbandonConfirmation(true), 1000)}
            onMouseUp={handleButtonHoldEnd}
            onMouseLeave={handleButtonHoldEnd}
            onTouchStart={() => handleButtonHoldStart('abandon', () => setShowAbandonConfirmation(true), 1000)}
            onTouchEnd={handleButtonHoldEnd}
            onTouchCancel={handleButtonHoldEnd}
          >
            {holdingButtonId === 'abandon' && (
              <svg className="hold-progress-svg" viewBox="0 0 100 100" preserveAspectRatio="none">
                <path
                  className="hold-progress-path hold-progress-path-btn hold-progress-path-slow"
                  pathLength="100"
                  d="M 50,2 H 90 Q 98,2 98,10 V 90 Q 98,98 90,98 H 10 Q 2,98 2,90 V 10 Q 2,2 10,2 H 50"
                />
              </svg>
            )}
            Abandon Match
          </button>
        )}

        {/* Player Deck Zones - Top (horizontal layout) - ALL STAGES (hidden in Warm Up to keep layout identical) */}
        {(gameStage === 'draft' || gameStage === 'battle' || gameStage === 'warmup') ? (
        <div className={`arena-deck-zones${gameStage === 'warmup' ? ' arena-deck-zones-hidden' : ''}`}>
          {playerDeckZonesData.map((zone, index) => {
            const deckCards = zone.type === 'ForwardCenter'
              ? [...(activeDecks.ForwardCenter || []), ...(activeDecks.ForwardWide || [])]
              : activeDecks[zone.type] || [];
            const cardCount = deckCards.length;

            // For draft stage: disable deck if formation rules don't allow OR another deck is already selected
            // For battle stage with substitute mode: check for actual empty spots on board
            // For battle stage without substitute: decks are display-only (not clickable)
            let isDisabled = true;
            if (gameStage === 'draft') {
              isDisabled = !canPlayerSelectFromDeck(zone.type, currentPlayer) || (selectedDeck && selectedDeck !== zone.type);
            } else if (gameStage === 'battle' && substituteMode && substituteStep === 'select_deck') {
              // In substitute mode, check if there are empty spots for this card type on player's side
              const validColors = getValidSlotsForCardType(zone.type);
              let hasEmptySpot = false;
              cardGameZonesData.forEach((column, colIndex) => {
                column.forEach((slot) => {
                  const slotKey = `${colIndex}-${slot.row}`;
                  const isColorValid = validColors.includes(slot.color);
                  const isColumnValid = isValidColumnForPlayer(colIndex, currentPlayer);
                  const isOccupied = cardGameZones[slotKey] && typeof cardGameZones[slotKey] === 'object';

                  if (isColorValid && isColumnValid && !isOccupied) {
                    hasEmptySpot = true;
                  }
                });
              });
              isDisabled = !hasEmptySpot || cardCount === 0;
            }

            // Handle deck click for substitute mode
            const handleSubstituteDeckClick = (e) => {
              e.preventDefault();
              if (isDisabled) return;

              const cards = zone.type === 'ForwardCenter'
                ? [...(activeDecks.ForwardCenter || []), ...(activeDecks.ForwardWide || [])]
                : activeDecks[zone.type] || [];

              if (cards.length === 0) return;

              // Select this deck and show top 2 cards
              setSubstituteSelectedDeck(zone.type);
              setSubstituteVisibleCards(shuffleArray(cards.slice(0, 2)));
              setSubstituteStep('select_card');
            };

            return (
              <div
                key={index}
                className={`arena-deck ${zone.color} ${isDisabled ? 'deck-disabled' : ''}`}
                onMouseDown={gameStage === 'draft' ? (e) => handleDeckClick(zone.type, e) :
                            (gameStage === 'battle' && substituteMode && substituteStep === 'select_deck') ? handleSubstituteDeckClick : undefined}
                style={(gameStage === 'battle' && (!substituteMode || substituteStep !== 'select_deck')) ? { cursor: 'default' } : {}}
              >
                <div className="deck-content">
                  <div className="deck-label">
                    {zone.label}
                  </div>
                  <div className="deck-count">
                    {cardCount}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        ) : null}


        {/* Card Game Zones - Below decks (horizontal or vertical board) */}
        <div className={`arena-game-zones${verticalBoard ? ' vertical-board' : ''}${verticalBoard && (!isSolo && currentPlayer === 2 || isSolo && aiPlayer === 1) ? ' board-rotated' : ''}`}>
          <div className="arena-board">
            {cardGameZonesData.map((column, colIndex) => (
              <div key={colIndex} className="arena-column">
                {column.map((slot, slotIndex) => {
                  const key = `${colIndex}-${slot.row}`;
                  const hasCard = cardGameZones[key];
                  const isHighlighted = highlightedSlots.includes(key);

                  return (
                    <DroppableSlot
                      key={slotIndex}
                      id={`slot-${colIndex}-${slot.row}`}
                      className={`arena-slot ${slot.color} ${slot.centered ? 'centered' : ''} ${hasCard ? 'has-card' : ''} ${isHighlighted ? 'highlighted' : ''} ${isHighlighted ? `player-${currentPlayer}-highlight` : ''}`}
                      onClick={() => handleCardGameZoneClick(colIndex, slot.row)}
                      dataColIndex={colIndex}
                      dataRowIndex={slot.row}
                    >
                      {hasCard && typeof hasCard === 'object' ? (
                        (() => {
                          // Determine which player owns this card based on column
                          const player1Columns = [0, 1, 3, 5];
                          const isPlayer1Card = player1Columns.includes(colIndex);

                          // Check if this card can be dragged in Kickopp or Move mode
                          // Allow dragging K pawn cards - validation happens in handleKickoppDrop
                          const canDragInKickopp = gameStage === 'battle' && kickoppMode &&
                            ((currentPlayer === 1 && isPlayer1Card) || (currentPlayer === 2 && !isPlayer1Card));
                          const canDragInMove = gameStage === 'battle' && moveMode &&
                            ((currentPlayer === 1 && isPlayer1Card) || (currentPlayer === 2 && !isPlayer1Card));
                          const canDrag = canDragInKickopp || canDragInMove;

                          // Check if this card can be substituted (for removal or replacement)
                          const isCurrentPlayerCard = (currentPlayer === 1 && isPlayer1Card) || (currentPlayer === 2 && !isPlayer1Card);
                          // For select_board_card/confirm_remove step: highlight all player's cards
                          const canRemoveForSubstitute = gameStage === 'battle' && substituteMode &&
                            (substituteStep === 'select_board_card' || substituteStep === 'confirm_remove') &&
                            isCurrentPlayerCard && getAvailableDecks(currentPlayer).length > 0;
                          const canSubstitute = canRemoveForSubstitute;

                          // Check if this card can be boosted
                          const canBoost = gameStage === 'battle' && boostMode === 'add_boost' && boostStep === 'select_card' && isCurrentPlayerCard;

                          // Check if this card is being eliminated
                          const isEliminating = eliminatingCard &&
                            eliminatingCard.colIndex === colIndex &&
                            eliminatingCard.rowIndex === slot.row;

                          return (
                            <DraggableCard
                              id={`placed-card-${key}`}
                              data={{
                                card: hasCard,
                                sourceKey: key,
                                colIndex: colIndex,
                                rowIndex: slot.row,
                                source: 'board'
                              }}
                              className={`placed-card-full ${BOARD_CARD_PHOTOS ? 'has-photo' : ''} ${gameStage === 'draft' ? 'locked' : ''} ${isPlayer1Card ? '' : 'player2-card'} ${canDrag ? 'kickopp-draggable' : ''} ${isEliminating ? 'eliminating' : ''} ${canSubstitute ? 'substitute-target' : ''} ${canBoost ? 'boost-target' : ''} ${isCurrentPlayerCard ? 'current-team-turn' : ''}`}
                              disabled={!canDrag}
                              keepInPlace={kickoppMode}
                            >
                              <div onClick={(e) => {
                                e.stopPropagation();

                                // Toggle selected card panel — click same card again to clear
                                // In removeBoostMode allow clicking any card (including opponent's)
                                if (selectedBoardCard?.card?.Card_Id === hasCard.Card_Id) {
                                    setSelectedBoardCard(null);
                                  } else {
                                    const boosts = isPlayer1Card
                                      ? player1AppliedBoosts[hasCard.Card_Id]
                                      : player2AppliedBoosts[hasCard.Card_Id];
                                    setSelectedBoardCard({
                                      card: hasCard,
                                      isPlayer1Card,
                                      attackBoost: boosts?.attack || 0,
                                      defenseBoost: boosts?.defense || 0,
                                    });
                                  }

                                // Handle Warm Up stage - click to select card for boosting
                                if (gameStage === 'warmup') {
                                  const player1Columns = [0, 1, 3, 5];
                                  const isPlayer1Card = player1Columns.includes(colIndex);
                                  const isCurrentPlayerCard = (currentPlayer === 1 && isPlayer1Card) || (currentPlayer === 2 && !isPlayer1Card);

                                  if (isCurrentPlayerCard) {
                                    setWarmUpSelectedCard(hasCard);
                                  }
                                  return;
                                }

                                // Handle Add Boost mode - click to select card for boosting
                                if (gameStage === 'battle' && boostMode === 'add_boost' && (boostStep === 'select_card' || boostStep === 'apply_boost')) {
                                  const player1Columns = [0, 1, 3, 5];
                                  const isPlayer1Card = player1Columns.includes(colIndex);
                                  const isCurrentPlayerCard = (currentPlayer === 1 && isPlayer1Card) || (currentPlayer === 2 && !isPlayer1Card);

                                  if (!isCurrentPlayerCard) return;

                                  // Don't switch if clicking the already-selected card
                                  if (boostSelectedCard?.Card_Id === hasCard.Card_Id) return;

                                  // Check if player has any boosts available
                                  const battleBoosts = currentPlayer === 1 ? player1BattleBoosts : player2BattleBoosts;
                                  if (battleBoosts.attack === 0 && battleBoosts.defense === 0) return;

                                  // Clear pending boosts for the previous card when switching
                                  if (boostStep === 'apply_boost' && boostSelectedCard) {
                                    setPendingBoosts(prev => {
                                      const u = { ...prev };
                                      delete u[boostSelectedCard.Card_Id];
                                      return u;
                                    });
                                  }

                                  setBoostSelectedCard(hasCard);
                                  setBoostStep('apply_boost');
                                  return;
                                }
                              }}
                              onMouseDown={(e) => {
                                e.stopPropagation();

                                // During DRAFT stage, cards cannot be moved once placed
                                if (gameStage === 'draft') {
                                  return;
                                }

                                // Handle Substitute mode - select card to confirm removal (show preview popup)
                                if (gameStage === 'battle' && substituteMode && (substituteStep === 'select_board_card' || substituteStep === 'confirm_remove')) {
                                  const player1Columns = [0, 1, 3, 5];
                                  const isPlayer1Card = player1Columns.includes(colIndex);
                                  const isCurrentPlayerCard = (currentPlayer === 1 && isPlayer1Card) || (currentPlayer === 2 && !isPlayer1Card);

                                  if (!isCurrentPlayerCard) return;

                                  const availableDecks = getAvailableDecks(currentPlayer);
                                  if (availableDecks.length === 0) return;

                                  const wasCaptain = (currentPlayer === 1 && player1Captain === hasCard.Card_Id) ||
                                                     (currentPlayer === 2 && player2Captain === hasCard.Card_Id);
                                  const wasFormerCaptain = (currentPlayer === 1 && player1FormerCaptain === hasCard.Card_Id) ||
                                                          (currentPlayer === 2 && player2FormerCaptain === hasCard.Card_Id);

                                  // Store pending card (no board removal yet), show confirmation popup
                                  setSubstitutePendingCard({
                                    card: hasCard,
                                    position: key,
                                    wasCaptain,
                                    wasFormerCaptain,
                                    cardType: hasCard.CardType
                                  });
                                  setSubstituteStep('confirm_remove');
                                  return;
                                }

                                // Handle Substitute mode - click to replace card
                                if (gameStage === 'battle' && substituteMode && substituteStep === 'place_card') {
                                  const player1Columns = [0, 1, 3, 5];
                                  const isPlayer1Card = player1Columns.includes(colIndex);
                                  const isCurrentPlayerCard = (currentPlayer === 1 && isPlayer1Card) || (currentPlayer === 2 && !isPlayer1Card);

                                  if (!isCurrentPlayerCard) {
                                    console.log(`❌ Cannot substitute opponent's card`);
                                    return;
                                  }

                                  // Check if card type matches the selected substitute card
                                  if (hasCard.CardType !== substituteSelectedCard.CardType) {
                                    console.log(`❌ Card type mismatch: ${hasCard.CardType} vs ${substituteSelectedCard.CardType}`);
                                    return;
                                  }

                                  // Perform the substitution
                                  console.log(`🔄 Substituting ${hasCard.PlayerName} with ${substituteSelectedCard.PlayerName}`);

                                  // Replace the card on the board
                                  setCardGameZones(prev => ({
                                    ...prev,
                                    [key]: substituteSelectedCard
                                  }));

                                  // Deduct AP cost (3) unless a free sub is available
                                  const subIsFree = freeSubsRemaining > 0;
                                  if (subIsFree) {
                                    setFreeSubsRemaining(prev => prev - 1);
                                  } else {
                                    const setAP = currentPlayer === 1 ? setPlayer1ActionPoints : setPlayer2ActionPoints;
                                    const currentAP = currentPlayer === 1 ? player1ActionPoints : player2ActionPoints;
                                    setAP(currentAP - 3);
                                  }

                                  // Add event log
                                  addEventLog({
                                    type: 'substitute',
                                    player: currentPlayer,
                                    oldCard: hasCard.PlayerName,
                                    newCard: substituteSelectedCard.PlayerName,
                                    position: hasCard.PositionType,
                                    positionColor: getPositionColor(hasCard.CardType),
                                    apCost: subIsFree ? 0 : 3
                                  });

                                  // Reset substitute mode
                                  setSubstituteMode(false);
                                  setSubstituteStep(null);
                                  setSubstituteSelectedDeck(null);
                                  setSubstituteVisibleCards([]);
                                  setSubstituteSelectedCard(null);

                                  return;
                                }


                                // During WARMUP stage, cards cannot be moved (only boosts can be applied)
                                if (gameStage === 'warmup') {
                                  console.log(`❌ Cannot move cards during warmup stage`);
                                  return;
                                }

                                // In BATTLE stage, only allow dragging in Kickopp or Move mode
                                if (gameStage === 'battle') {
                                  if (!kickoppMode && !moveMode) {
                                    console.log(`❌ Cannot drag card in battle stage unless Kickopp or Move mode is active`);
                                    return;
                                  }

                                  // In Kickopp/Move mode, only allow dragging your own cards
                                  const player1Columns = [0, 1, 3, 5];
                                  const isPlayer1Card = player1Columns.includes(colIndex);
                                  const isCurrentPlayerCard = (currentPlayer === 1 && isPlayer1Card) || (currentPlayer === 2 && !isPlayer1Card);

                                  if (!isCurrentPlayerCard) {
                                    console.log(`❌ Cannot drag opponent's card in ${kickoppMode ? 'Kickopp' : 'Move'} mode`);
                                    return;
                                  }

                                  // Allow selecting Kickopp pawns - will show red arrow and feedback
                                  console.log(`✅ Starting to drag YOUR card for ${kickoppMode ? 'Kickopp' : 'Move'}: ${hasCard.PlayerName} from ${key}`);
                                }

                                console.log(`✅ Starting to drag card: ${hasCard.PlayerName} from ${key}, kickoppMode=${kickoppMode}`);
                                setDraggedCard({
                                  card: hasCard,
                                  sourceKey: key,
                                  cardKey: `placed-${key}`
                                });
                                dragPositionRef.current = { x: e.clientX, y: e.clientY };
                                setDragPosition({ x: e.clientX, y: e.clientY });

                                // Add body class during Kickopp to keep card in place
                                if (kickoppMode) {
                                  document.body.classList.add('dragging-kickopp');
                                }
                              }}
                            >
                              <>
                                {(() => {
                                  const boosted = gameStage === 'warmup' || gameStage === 'battle';
                                  const boosts = isPlayer1Card ? player1AppliedBoosts[hasCard.Card_Id] : player2AppliedBoosts[hasCard.Card_Id];
                                  const atkVal = hasCard.Attack + (boosted ? (boosts?.attack || 0) : 0);
                                  const defVal = hasCard.Defence + (boosted ? (boosts?.defense || 0) : 0);
                                  const isCaptain = isPlayer1Card ? player1Captain === hasCard.Card_Id : player2Captain === hasCard.Card_Id;
                                  const positionBadge = (
                                    <div className="placed-position-badge" style={{ borderColor: getPositionColor(hasCard.CardType) }}>
                                      {hasCard.PositionType}
                                    </div>
                                  );
                                  const playerName = (
                                    <div className="placed-player-name">
                                      {hasCard.PlayerName}
                                      {isCaptain && <div className="captain-badge">C</div>}
                                    </div>
                                  );
                                  const atkBox = <div className="placed-stat-box attack">{atkVal}</div>;
                                  const defBox = <div className="placed-stat-box defence">{defVal}</div>;

                                  if (BOARD_CARD_PHOTOS) {
                                    const positionBadgeMini = (
                                      <div className="placed-position-badge-mini" style={{ backgroundColor: getPositionColor(hasCard.CardType) }}>
                                        {hasCard.PositionType?.trim()}
                                      </div>
                                    );

                                    if (verticalBoard) {
                                      const photoBox = (
                                        <div
                                          className="pcv3-photo-bg"
                                          style={{
                                            backgroundImage: `url('${getFaceUrl(hasCard.Card_Id)}')`,
                                            backgroundSize: '100%',
                                            backgroundPosition: 'center top',
                                            backgroundRepeat: 'no-repeat'
                                          }}
                                        />
                                      );
                                      const cornerPosition = (
                                        <div className="pcv3-position-box" style={{ backgroundColor: getPositionColor(hasCard.CardType) }}>
                                          {hasCard.PositionType?.trim()}
                                        </div>
                                      );
                                      const cornerFlag = hasCard.Country ? (
                                        <span className={`fi fi-${hasCard.Country.toLowerCase()} pcv3-flag-icon`} />
                                      ) : <div className="pcv3-flag-icon" />;
                                      const cornerName = (
                                        <div className="pcv3-name-box">
                                          {hasCard.PlayerName}
                                        </div>
                                      );
                                      const pawn = isPlayer1Card
                                        ? player1KickoppPawns.find(p => p.cardId === hasCard.Card_Id)
                                        : player2KickoppPawns.find(p => p.cardId === hasCard.Card_Id);
                                      const pawnBadge = (pawn && gameStage === 'battle') ? (() => {
                                        const pawnList = isPlayer1Card ? player1KickoppPawns : player2KickoppPawns;
                                        const pawnNumber = pawnList.filter(p => p.order <= pawn.order).length;
                                        return <div className="pcv3-k-pawn">K{pawnNumber}</div>;
                                      })() : null;
                                      const captainBadge = isCaptain ? <div className="pcv3-captain-badge">C</div> : null;

                                      return (
                                        <div className="placed-card-v3-vertical">
                                          <div className="pcv3-cell pcv3-stat-atk">{atkBox}{pawnBadge}</div>
                                          <div className="pcv3-cell pcv3-stat-def">{defBox}{captainBadge}</div>
                                          <div className="pcv3-cell pcv3-position">{cornerPosition}</div>
                                          <div className="pcv3-cell pcv3-flag">{cornerFlag}</div>
                                          <div className="pcv3-center">
                                            <div className="pcv3-photo-wrap">{photoBox}</div>
                                            <div className="pcv3-name-wrap">{cornerName}</div>
                                          </div>
                                        </div>
                                      );
                                    }

                                    return (
                                      <div className="placed-card-v2">
                                        <div className="placed-photo-col">
                                          <div
                                            className="placed-photo-box"
                                            style={{
                                              backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.05) 45%, rgba(0,0,0,0.9) 100%), url('${getFaceUrl(hasCard.Card_Id)}')`,
                                              backgroundSize: 'cover, 54%',
                                              backgroundPosition: 'center, center top',
                                              backgroundRepeat: 'no-repeat, no-repeat'
                                            }}
                                          >
                                            {playerName}
                                          </div>
                                          <div className="placed-info-row">
                                            {positionBadgeMini}
                                            {hasCard.Country && (
                                              <span className={`fi fi-${hasCard.Country.toLowerCase()} placed-card-flag`} />
                                            )}
                                          </div>
                                        </div>
                                        <div className="placed-stats-col">
                                          {isPlayer1Card ? <>{atkBox}{defBox}</> : <>{defBox}{atkBox}</>}
                                        </div>
                                      </div>
                                    );
                                  }

                                  // ── Backup layout (no photo/flag) ──
                                  if (verticalBoard) {
                                    if (isPlayer1Card) {
                                      // T1 vertical: Attack · Position · Defense (top), Name (bottom)
                                      return (
                                        <>
                                          <div className="placed-top-row placed-top-row--triple">{atkBox}{positionBadge}{defBox}</div>
                                          <div className="placed-bottom-row placed-bottom-row--name-only">{playerName}</div>
                                        </>
                                      );
                                    } else {
                                      // T2 vertical: Name (top), Defense · Position · Attack (bottom)
                                      return (
                                        <>
                                          <div className="placed-top-row placed-top-row--name-only">{playerName}</div>
                                          <div className="placed-bottom-row placed-bottom-row--triple">{defBox}{positionBadge}{atkBox}</div>
                                        </>
                                      );
                                    }
                                  }

                                  // Horizontal layout (default)
                                  return (
                                    <>
                                      <div className="placed-top-row">
                                        {positionBadge}
                                        {isPlayer1Card ? atkBox : defBox}
                                      </div>
                                      <div className="placed-bottom-row">
                                        {playerName}
                                        {isPlayer1Card ? defBox : atkBox}
                                      </div>
                                    </>
                                  );
                                })()}
                                {/* K pawn badge overlay (pcv3 vertical photo cards render their own) */}
                                {(() => {
                                  if (BOARD_CARD_PHOTOS && verticalBoard) return null;
                                  const pawn = isPlayer1Card
                                    ? player1KickoppPawns.find(p => p.cardId === hasCard.Card_Id)
                                    : player2KickoppPawns.find(p => p.cardId === hasCard.Card_Id);
                                  if (pawn && gameStage === 'battle') {
                                    const pawnList = isPlayer1Card ? player1KickoppPawns : player2KickoppPawns;
                                    const pawnNumber = pawnList.filter(p => p.order <= pawn.order).length;
                                    return <div className={`k-pawn-badge ${!isPlayer1Card ? 'k-pawn-badge-player2' : ''}`}>K{pawnNumber}</div>;
                                  }
                                  return null;
                                })()}
                              </>
                            </div>
                            </DraggableCard>
                          );
                        })()

                      ) : hasCard ? (
                        <span className="card-marker">X</span>
                      ) : null}
                    </DroppableSlot>
                  );
                })}

                {/* Selected card portrait panel — inside GK column (horizontal mode only) */}
                {colIndex === 0 && gameStage === 'battle' && !verticalBoard && (
                  <div
                    className="selected-card-panel"
                    onClick={() => !removeBoostMode && !(boostMode === 'add_boost' && boostStep === 'apply_boost') && selectedBoardCard && setSelectedBoardCard(null)}
                    style={{ cursor: (!removeBoostMode && selectedBoardCard && !(boostMode === 'add_boost' && boostStep === 'apply_boost')) ? 'pointer' : 'default' }}
                  >
                    {boostMode === 'add_boost' && boostStep === 'apply_boost' && boostSelectedCard ? (() => {
                      const appliedBoosts = currentPlayer === 1
                        ? player1AppliedBoosts[boostSelectedCard.Card_Id] || { attack: 0, defense: 0 }
                        : player2AppliedBoosts[boostSelectedCard.Card_Id] || { attack: 0, defense: 0 };
                      const pendingBoost = pendingBoosts[boostSelectedCard.Card_Id] || { attack: 0, defense: 0 };
                      const totalBoostsOnCard = appliedBoosts.attack + appliedBoosts.defense + pendingBoost.attack + pendingBoost.defense;
                      const positionColor = getPositionColor(boostSelectedCard.CardType);
                      const battleBoosts = currentPlayer === 1 ? player1BattleBoosts : player2BattleBoosts;
                      const currentAP = currentPlayer === 1 ? player1ActionPoints : player2ActionPoints;
                      const totalPendingBoosts = Object.values(pendingBoosts).reduce((sum, b) => sum + b.attack + b.defense, 0);
                      const totalPendingAttackBoosts = Object.values(pendingBoosts).reduce((sum, b) => sum + b.attack, 0);
                      const totalPendingDefenseBoosts = Object.values(pendingBoosts).reduce((sum, b) => sum + b.defense, 0);
                      const remainingAttackBoosts = battleBoosts.attack - totalPendingAttackBoosts;
                      const remainingDefenseBoosts = battleBoosts.defense - totalPendingDefenseBoosts;
                      const totalPending = pendingBoost.attack + pendingBoost.defense;

                      const handleInlineApply = () => {
                        const pending = pendingBoosts[boostSelectedCard.Card_Id];
                        if (!pending || (pending.attack === 0 && pending.defense === 0)) {
                          setBoostStep('select_card');
                          setBoostSelectedCard(null);
                          return;
                        }
                        const setApplied = currentPlayer === 1 ? setPlayer1AppliedBoosts : setPlayer2AppliedBoosts;
                        const setBoosts = currentPlayer === 1 ? setPlayer1BattleBoosts : setPlayer2BattleBoosts;
                        const setAP = currentPlayer === 1 ? setPlayer1ActionPoints : setPlayer2ActionPoints;
                        const appliedAttack = doubleBoostsActive ? pending.attack * 2 : pending.attack;
                        const appliedDefense = doubleBoostsActive ? pending.defense * 2 : pending.defense;
                        setApplied(prev => ({
                          ...prev,
                          [boostSelectedCard.Card_Id]: {
                            attack: (prev[boostSelectedCard.Card_Id]?.attack || 0) + appliedAttack,
                            defense: (prev[boostSelectedCard.Card_Id]?.defense || 0) + appliedDefense
                          }
                        }));
                        setBoosts({ attack: battleBoosts.attack - pending.attack, defense: battleBoosts.defense - pending.defense });
                        if (!freeBoostsActive) setAP(currentAP - (pending.attack + pending.defense));
                        if (pending.attack > 0) addEventLog({ type: 'boost_add', player: currentPlayer, playerName: boostSelectedCard.PlayerName, stat: 'attack', count: appliedAttack, positionType: boostSelectedCard.PositionType, positionColor: getPositionColor(boostSelectedCard.CardType), apCost: freeBoostsActive ? 0 : pending.attack, doubled: doubleBoostsActive });
                        if (pending.defense > 0) addEventLog({ type: 'boost_add', player: currentPlayer, playerName: boostSelectedCard.PlayerName, stat: 'defense', count: appliedDefense, positionType: boostSelectedCard.PositionType, positionColor: getPositionColor(boostSelectedCard.CardType), apCost: freeBoostsActive ? 0 : pending.defense, doubled: doubleBoostsActive });
                        setPendingBoosts(prev => { const u = { ...prev }; delete u[boostSelectedCard.Card_Id]; return u; });
                        setBoostMode(null);
                        setBoostStep(null);
                        setBoostSelectedCard(null);
                      };

                      return (
                        <>
                          <div className="sidebar-card-stats boost-inline-stats">
                            {/* ATK column */}
                            <div className="boost-inline-col">
                              <button
                                className="bpp-btn plus boost-inline-btn atk"
                                onClick={() => {
                                  if (totalBoostsOnCard < 20 && remainingAttackBoosts > 0 && (freeBoostsActive || totalPendingBoosts < currentAP)) {
                                    setPendingBoosts(prev => ({ ...prev, [boostSelectedCard.Card_Id]: { attack: pendingBoost.attack + 1, defense: pendingBoost.defense } }));
                                  }
                                }}
                                disabled={totalBoostsOnCard >= 20 || remainingAttackBoosts <= 0 || (!freeBoostsActive && totalPendingBoosts >= currentAP)}
                              >+</button>
                              <div className="sidebar-stat-box attack" style={{ position: 'relative', textAlign: 'center' }}>
                                {boostSelectedCard.Attack + appliedBoosts.attack}
                                {pendingBoost.attack > 0 && <span className="bpp-stat-pending-abs">+{pendingBoost.attack}</span>}
                              </div>
                              <button
                                className="bpp-btn minus boost-inline-btn atk"
                                onClick={() => {
                                  if (pendingBoost.attack > 0) setPendingBoosts(prev => ({ ...prev, [boostSelectedCard.Card_Id]: { attack: pendingBoost.attack - 1, defense: pendingBoost.defense } }));
                                }}
                                disabled={pendingBoost.attack === 0}
                              >−</button>
                              <span className="bpp-ctrl-label" style={{ color: '#60a5fa', fontSize: 9 }}>⚡ ATK<br/>{remainingAttackBoosts} left</span>
                            </div>
                            {/* DEF column */}
                            <div className="boost-inline-col">
                              <button
                                className="bpp-btn plus boost-inline-btn def"
                                onClick={() => {
                                  if (totalBoostsOnCard < 20 && remainingDefenseBoosts > 0 && (freeBoostsActive || totalPendingBoosts < currentAP)) {
                                    setPendingBoosts(prev => ({ ...prev, [boostSelectedCard.Card_Id]: { attack: pendingBoost.attack, defense: pendingBoost.defense + 1 } }));
                                  }
                                }}
                                disabled={totalBoostsOnCard >= 20 || remainingDefenseBoosts <= 0 || (!freeBoostsActive && totalPendingBoosts >= currentAP)}
                              >+</button>
                              <div className="sidebar-stat-box defence" style={{ position: 'relative', textAlign: 'center' }}>
                                {boostSelectedCard.Defence + appliedBoosts.defense}
                                {pendingBoost.defense > 0 && <span className="bpp-stat-pending-abs">+{pendingBoost.defense}</span>}
                              </div>
                              <button
                                className="bpp-btn minus boost-inline-btn def"
                                onClick={() => {
                                  if (pendingBoost.defense > 0) setPendingBoosts(prev => ({ ...prev, [boostSelectedCard.Card_Id]: { attack: pendingBoost.attack, defense: pendingBoost.defense - 1 } }));
                                }}
                                disabled={pendingBoost.defense === 0}
                              >−</button>
                              <span className="bpp-ctrl-label" style={{ color: '#f87171', fontSize: 9 }}>🛡 DEF<br/>{remainingDefenseBoosts} left</span>
                            </div>
                          </div>
                          <div className="sidebar-card-name">
                            {boostSelectedCard.PlayerName}
                            {((currentPlayer === 1 && player1Captain === boostSelectedCard.Card_Id) ||
                              (currentPlayer === 2 && player2Captain === boostSelectedCard.Card_Id)) && (
                              <div className="captain-badge">C</div>
                            )}
                          </div>
                          <div className="sidebar-card-position" style={{ backgroundColor: positionColor }}>
                            {boostSelectedCard.PositionType?.trim()}
                          </div>
                          <button
                            className={`bpp-footer-btn${totalPending > 0 ? ' apply' : ' cancel'}`}
                            style={{ marginTop: 4 }}
                            onClick={totalPending > 0 ? handleInlineApply : () => { setBoostStep('select_card'); setBoostSelectedCard(null); }}
                          >
                            {totalPending === 0
                              ? '← Back'
                              : freeBoostsActive
                                ? `✓ Apply (free${doubleBoostsActive ? ' ×2' : ''})`
                                : `✓ Apply — ${totalPending} AP${doubleBoostsActive ? ' ×2' : ''}`}
                          </button>
                        </>
                      );
                    })() : selectedBoardCard ? (() => {
                      const { card, isPlayer1Card, attackBoost, defenseBoost } = selectedBoardCard;
                      const isCapt = isPlayer1Card
                        ? player1Captain === card.Card_Id
                        : player2Captain === card.Card_Id;
                      const isOpponentCard = (currentPlayer === 1 && !isPlayer1Card) || (currentPlayer === 2 && isPlayer1Card);
                      const hasBoosts = attackBoost > 0 || defenseBoost > 0;
                      return (
                        <>
                          <div className="sidebar-card-stats">
                            <div className="sidebar-stat-box attack" style={{ position: 'relative' }}>
                              {card.Attack + attackBoost}
                              {attackBoost > 0 && (
                                <span style={{ position: 'absolute', top: 2, right: 2, fontSize: 8, fontWeight: 900, background: '#4ade80', color: '#0f172a', borderRadius: 3, padding: '0 3px' }}>+{attackBoost}</span>
                              )}
                            </div>
                            <div className="sidebar-stat-box defence" style={{ position: 'relative' }}>
                              {card.Defence + defenseBoost}
                              {defenseBoost > 0 && (
                                <span style={{ position: 'absolute', top: 2, right: 2, fontSize: 8, fontWeight: 900, background: '#4ade80', color: '#0f172a', borderRadius: 3, padding: '0 3px' }}>+{defenseBoost}</span>
                              )}
                            </div>
                          </div>
                          <div
                            className="sidebar-card-name"
                            style={{
                              backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.05) 45%, rgba(0,0,0,0.9) 100%), url('${getFaceUrl(card.Card_Id)}')`,
                              backgroundSize: 'cover, 58%',
                              backgroundPosition: 'center, center top',
                              backgroundRepeat: 'no-repeat, no-repeat'
                            }}
                          >
                            {card.PlayerName}
                            {isCapt && <div className="captain-badge">C</div>}
                          </div>
                          <div className="sidebar-card-bottom">
                            <div
                              className="sidebar-card-position"
                              style={{ backgroundColor: getPositionColor(card.CardType) }}
                            >
                              {card.PositionType?.trim()}
                            </div>
                            {card.Country && (
                              <span className={`fi fi-${card.Country.toLowerCase()} sidebar-card-flag`} />
                            )}
                          </div>
                          {removeBoostMode && isOpponentCard && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                const opponentPlayer = currentPlayer === 1 ? 2 : 1;
                                const setApplied = opponentPlayer === 1 ? setPlayer1AppliedBoosts : setPlayer2AppliedBoosts;
                                setApplied(prev => {
                                  const updated = { ...prev };
                                  delete updated[card.Card_Id];
                                  return updated;
                                });
                                addEventLog({
                                  type: 'boost_remove_all',
                                  player: currentPlayer,
                                  targetPlayer: opponentPlayer,
                                  playerName: card.PlayerName,
                                  positionType: card.PositionType,
                                  positionColor: getPositionColor(card.CardType),
                                  attackBoost,
                                  defenseBoost
                                });
                                setRemoveBoostMode(false);
                                setSelectedBoardCard(null);
                              }}
                              style={{
                                marginTop: 8,
                                width: '100%',
                                padding: '6px 0',
                                background: hasBoosts ? '#dc2626' : '#475569',
                                color: '#fff',
                                fontWeight: 900,
                                fontSize: 11,
                                border: 'none',
                                borderRadius: 6,
                                cursor: hasBoosts ? 'pointer' : 'default',
                                opacity: hasBoosts ? 1 : 0.5,
                              }}
                              disabled={!hasBoosts}
                            >
                              {hasBoosts ? '✕ REMOVE BOOSTS' : 'NO BOOSTS'}
                            </button>
                          )}
                          {removeBoostMode && !isOpponentCard && (
                            <div style={{ marginTop: 8, fontSize: 10, color: '#94a3b8', textAlign: 'center' }}>
                              Select an opponent card
                            </div>
                          )}
                        </>
                      );
                    })() : removeBoostMode ? (
                      <div style={{ fontSize: 11, color: '#f59e0b', textAlign: 'center', padding: '8px 0' }}>
                        A_017 active — click an opponent card
                      </div>
                    ) : null}
                  </div>
                )}

              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Selected card portrait panel — vertical mode only, outside the rotated board */}
      {verticalBoard && gameStage === 'battle' && (
        <div
          className="selected-card-panel"
          onClick={() => !removeBoostMode && !(boostMode === 'add_boost' && boostStep === 'apply_boost') && selectedBoardCard && setSelectedBoardCard(null)}
          style={{ cursor: (!removeBoostMode && selectedBoardCard && !(boostMode === 'add_boost' && boostStep === 'apply_boost')) ? 'pointer' : 'default' }}
        >
          {boostMode === 'add_boost' && boostStep === 'apply_boost' && boostSelectedCard ? (() => {
            const appliedBoosts = currentPlayer === 1
              ? player1AppliedBoosts[boostSelectedCard.Card_Id] || { attack: 0, defense: 0 }
              : player2AppliedBoosts[boostSelectedCard.Card_Id] || { attack: 0, defense: 0 };
            const pendingBoost = pendingBoosts[boostSelectedCard.Card_Id] || { attack: 0, defense: 0 };
            const totalBoostsOnCard = appliedBoosts.attack + appliedBoosts.defense + pendingBoost.attack + pendingBoost.defense;
            const positionColor = getPositionColor(boostSelectedCard.CardType);
            const battleBoosts = currentPlayer === 1 ? player1BattleBoosts : player2BattleBoosts;
            const currentAP = currentPlayer === 1 ? player1ActionPoints : player2ActionPoints;
            const totalPendingBoosts = Object.values(pendingBoosts).reduce((sum, b) => sum + b.attack + b.defense, 0);
            const totalPendingAttackBoosts = Object.values(pendingBoosts).reduce((sum, b) => sum + b.attack, 0);
            const totalPendingDefenseBoosts = Object.values(pendingBoosts).reduce((sum, b) => sum + b.defense, 0);
            const remainingAttackBoosts = battleBoosts.attack - totalPendingAttackBoosts;
            const remainingDefenseBoosts = battleBoosts.defense - totalPendingDefenseBoosts;
            const totalPending = pendingBoost.attack + pendingBoost.defense;
            const handleInlineApply = () => {
              const pending = pendingBoosts[boostSelectedCard.Card_Id];
              if (!pending || (pending.attack === 0 && pending.defense === 0)) { setBoostStep('select_card'); setBoostSelectedCard(null); return; }
              const setApplied = currentPlayer === 1 ? setPlayer1AppliedBoosts : setPlayer2AppliedBoosts;
              const setBoosts = currentPlayer === 1 ? setPlayer1BattleBoosts : setPlayer2BattleBoosts;
              const setAP = currentPlayer === 1 ? setPlayer1ActionPoints : setPlayer2ActionPoints;
              const appliedAttack = doubleBoostsActive ? pending.attack * 2 : pending.attack;
              const appliedDefense = doubleBoostsActive ? pending.defense * 2 : pending.defense;
              setApplied(prev => ({ ...prev, [boostSelectedCard.Card_Id]: { attack: (prev[boostSelectedCard.Card_Id]?.attack || 0) + appliedAttack, defense: (prev[boostSelectedCard.Card_Id]?.defense || 0) + appliedDefense } }));
              setBoosts({ attack: battleBoosts.attack - pending.attack, defense: battleBoosts.defense - pending.defense });
              if (!freeBoostsActive) setAP(currentAP - (pending.attack + pending.defense));
              if (pending.attack > 0) addEventLog({ type: 'boost_add', player: currentPlayer, playerName: boostSelectedCard.PlayerName, stat: 'attack', count: appliedAttack, positionType: boostSelectedCard.PositionType, positionColor: getPositionColor(boostSelectedCard.CardType), apCost: freeBoostsActive ? 0 : pending.attack, doubled: doubleBoostsActive });
              if (pending.defense > 0) addEventLog({ type: 'boost_add', player: currentPlayer, playerName: boostSelectedCard.PlayerName, stat: 'defense', count: appliedDefense, positionType: boostSelectedCard.PositionType, positionColor: getPositionColor(boostSelectedCard.CardType), apCost: freeBoostsActive ? 0 : pending.defense, doubled: doubleBoostsActive });
              setPendingBoosts(prev => { const u = { ...prev }; delete u[boostSelectedCard.Card_Id]; return u; });
              setBoostMode(null); setBoostStep(null); setBoostSelectedCard(null);
            };
            return (
              <>
                <div className="sidebar-card-stats boost-inline-stats">
                  <div className="boost-inline-col">
                    <button className="bpp-btn plus boost-inline-btn atk" onClick={() => { if (totalBoostsOnCard < 20 && remainingAttackBoosts > 0 && (freeBoostsActive || totalPendingBoosts < currentAP)) { setPendingBoosts(prev => ({ ...prev, [boostSelectedCard.Card_Id]: { attack: pendingBoost.attack + 1, defense: pendingBoost.defense } })); } }} disabled={totalBoostsOnCard >= 20 || remainingAttackBoosts <= 0 || (!freeBoostsActive && totalPendingBoosts >= currentAP)}>+</button>
                    <div className="sidebar-stat-box attack" style={{ position: 'relative', textAlign: 'center' }}>{boostSelectedCard.Attack + appliedBoosts.attack}{pendingBoost.attack > 0 && <span className="bpp-stat-pending-abs">+{pendingBoost.attack}</span>}</div>
                    <button className="bpp-btn minus boost-inline-btn atk" onClick={() => { if (pendingBoost.attack > 0) setPendingBoosts(prev => ({ ...prev, [boostSelectedCard.Card_Id]: { attack: pendingBoost.attack - 1, defense: pendingBoost.defense } })); }} disabled={pendingBoost.attack === 0}>−</button>
                    <span className="bpp-ctrl-label" style={{ color: '#60a5fa', fontSize: 9 }}>⚡ ATK<br/>{remainingAttackBoosts} left</span>
                  </div>
                  <div className="boost-inline-col">
                    <button className="bpp-btn plus boost-inline-btn def" onClick={() => { if (totalBoostsOnCard < 20 && remainingDefenseBoosts > 0 && (freeBoostsActive || totalPendingBoosts < currentAP)) { setPendingBoosts(prev => ({ ...prev, [boostSelectedCard.Card_Id]: { attack: pendingBoost.attack, defense: pendingBoost.defense + 1 } })); } }} disabled={totalBoostsOnCard >= 20 || remainingDefenseBoosts <= 0 || (!freeBoostsActive && totalPendingBoosts >= currentAP)}>+</button>
                    <div className="sidebar-stat-box defence" style={{ position: 'relative', textAlign: 'center' }}>{boostSelectedCard.Defence + appliedBoosts.defense}{pendingBoost.defense > 0 && <span className="bpp-stat-pending-abs">+{pendingBoost.defense}</span>}</div>
                    <button className="bpp-btn minus boost-inline-btn def" onClick={() => { if (pendingBoost.defense > 0) setPendingBoosts(prev => ({ ...prev, [boostSelectedCard.Card_Id]: { attack: pendingBoost.attack, defense: pendingBoost.defense - 1 } })); }} disabled={pendingBoost.defense === 0}>−</button>
                    <span className="bpp-ctrl-label" style={{ color: '#f87171', fontSize: 9 }}>🛡 DEF<br/>{remainingDefenseBoosts} left</span>
                  </div>
                </div>
                <div className="sidebar-card-name">{boostSelectedCard.PlayerName}{((currentPlayer === 1 && player1Captain === boostSelectedCard.Card_Id) || (currentPlayer === 2 && player2Captain === boostSelectedCard.Card_Id)) && (<div className="captain-badge">C</div>)}</div>
                <div className="sidebar-card-position" style={{ backgroundColor: positionColor }}>{boostSelectedCard.PositionType?.trim()}</div>
                <button className={`bpp-footer-btn${totalPending > 0 ? ' apply' : ' cancel'}`} style={{ marginTop: 4 }} onClick={totalPending > 0 ? handleInlineApply : () => { setBoostStep('select_card'); setBoostSelectedCard(null); }}>
                  {totalPending === 0 ? '← Back' : freeBoostsActive ? `✓ Apply (free${doubleBoostsActive ? ' ×2' : ''})` : `✓ Apply — ${totalPending} AP${doubleBoostsActive ? ' ×2' : ''}`}
                </button>
              </>
            );
          })() : selectedBoardCard ? (() => {
            const { card, isPlayer1Card, attackBoost, defenseBoost } = selectedBoardCard;
            const isCapt = isPlayer1Card ? player1Captain === card.Card_Id : player2Captain === card.Card_Id;
            const isOpponentCard = (currentPlayer === 1 && !isPlayer1Card) || (currentPlayer === 2 && isPlayer1Card);
            const hasBoosts = attackBoost > 0 || defenseBoost > 0;
            return (
              <>
                <div className="sidebar-card-stats">
                  <div className="sidebar-stat-box attack" style={{ position: 'relative' }}>{card.Attack + attackBoost}{attackBoost > 0 && (<span style={{ position: 'absolute', top: 2, right: 2, fontSize: 8, fontWeight: 900, background: '#4ade80', color: '#0f172a', borderRadius: 3, padding: '0 3px' }}>+{attackBoost}</span>)}</div>
                  <div className="sidebar-stat-box defence" style={{ position: 'relative' }}>{card.Defence + defenseBoost}{defenseBoost > 0 && (<span style={{ position: 'absolute', top: 2, right: 2, fontSize: 8, fontWeight: 900, background: '#4ade80', color: '#0f172a', borderRadius: 3, padding: '0 3px' }}>+{defenseBoost}</span>)}</div>
                </div>
                <div
                  className="sidebar-card-name"
                  style={{
                    backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.05) 45%, rgba(0,0,0,0.9) 100%), url('${getFaceUrl(card.Card_Id)}')`,
                    backgroundSize: 'cover, 58%',
                    backgroundPosition: 'center, center top',
                    backgroundRepeat: 'no-repeat, no-repeat'
                  }}
                >
                  {card.PlayerName}{isCapt && <div className="captain-badge">C</div>}
                </div>
                <div className="sidebar-card-bottom">
                  <div className="sidebar-card-position" style={{ backgroundColor: getPositionColor(card.CardType) }}>{card.PositionType?.trim()}</div>
                  {card.Country && (
                    <span className={`fi fi-${card.Country.toLowerCase()} sidebar-card-flag`} />
                  )}
                </div>
                {removeBoostMode && isOpponentCard && (
                  <button onClick={(e) => { e.stopPropagation(); const opponentPlayer = currentPlayer === 1 ? 2 : 1; const setApplied = opponentPlayer === 1 ? setPlayer1AppliedBoosts : setPlayer2AppliedBoosts; setApplied(prev => { const updated = { ...prev }; delete updated[card.Card_Id]; return updated; }); addEventLog({ type: 'boost_remove_all', player: currentPlayer, targetPlayer: opponentPlayer, playerName: card.PlayerName, positionType: card.PositionType, positionColor: getPositionColor(card.CardType), attackBoost, defenseBoost }); setRemoveBoostMode(false); setSelectedBoardCard(null); }} style={{ marginTop: 8, width: '100%', padding: '6px 0', background: hasBoosts ? '#dc2626' : '#475569', color: '#fff', fontWeight: 900, fontSize: 11, border: 'none', borderRadius: 6, cursor: hasBoosts ? 'pointer' : 'default', opacity: hasBoosts ? 1 : 0.5 }} disabled={!hasBoosts}>
                    {hasBoosts ? '✕ REMOVE BOOSTS' : 'NO BOOSTS'}
                  </button>
                )}
                {removeBoostMode && !isOpponentCard && (<div style={{ marginTop: 8, fontSize: 10, color: '#94a3b8', textAlign: 'center' }}>Select an opponent card</div>)}
              </>
            );
          })() : removeBoostMode ? (
            <div style={{ fontSize: 11, color: '#f59e0b', textAlign: 'center', padding: '8px 0' }}>A_017 active — click an opponent card</div>
          ) : null}
        </div>
      )}

      {/* Action Card Deck Zones - Bottom (horizontal layout) - BATTLE STAGE ONLY */}
      {gameStage === 'battle' && (
        <>
          <div className="action-card-deck-zones">
            {[
              { deck: actionDeck1, setDeck: setActionDeck1, buyCost: 1, tierClass: 'deck-tier-1',
                cardTier: 'tier-1', revealed: revealedCard1, setRevealed: setRevealedCard1 },
              { deck: actionDeck3, setDeck: setActionDeck3, buyCost: 3, tierClass: 'deck-tier-3',
                cardTier: 'tier-3', revealed: revealedCard3, setRevealed: setRevealedCard3 },
              { deck: actionDeck5, setDeck: setActionDeck5, buyCost: 5, tierClass: 'deck-tier-5',
                cardTier: 'tier-5', revealed: revealedCard5, setRevealed: setRevealedCard5 },
            ].map(({ deck, setDeck, buyCost, tierClass, cardTier, revealed, setRevealed }) => {
              const currentAP = currentPlayer === 1 ? player1ActionPoints : player2ActionPoints;
              const canBuyFaceDown = deck.length > 0 && currentAP >= buyCost;
              const canBuyFaceUp = revealed !== null && currentAP >= buyCost;
              const buyFaceDownId = `buy-facedown-${buyCost}`;
              const buyRevealedId = `buy-revealed-${buyCost}`;
              const isHoldingFaceDown = holdingButtonId === buyFaceDownId;
              const isHoldingRevealed = holdingButtonId === buyRevealedId;
              return (
                <React.Fragment key={buyCost}>
                  {/* Face-down deck */}
                  <div
                    className={`action-card-deck ${tierClass} ${canBuyFaceDown ? 'deck-buyable' : 'deck-disabled'}${isHoldingFaceDown ? ' btn-holding' : ''}`}
                    style={{ cursor: canBuyFaceDown ? 'pointer' : 'not-allowed', position: 'relative' }}
                    title={canBuyFaceDown ? `Hold to buy random card (${buyCost} AP)` : ''}
                    onMouseDown={() => { if (canBuyFaceDown) handleButtonHoldStart(buyFaceDownId, () => handleBuyActionCard(deck, setDeck, buyCost)); }}
                    onMouseUp={handleButtonHoldEnd}
                    onMouseLeave={handleButtonHoldEnd}
                    onTouchStart={(e) => { e.preventDefault(); if (canBuyFaceDown) handleButtonHoldStart(buyFaceDownId, () => handleBuyActionCard(deck, setDeck, buyCost)); }}
                    onTouchEnd={handleButtonHoldEnd}
                    onTouchCancel={handleButtonHoldEnd}
                  >
                    {isHoldingFaceDown && (
                      <svg className="hold-progress-svg" viewBox="0 0 100 100" preserveAspectRatio="none">
                        <path
                          className="hold-progress-path hold-progress-path-btn"
                          pathLength="100"
                          d="M 50,2 H 90 Q 98,2 98,10 V 90 Q 98,98 90,98 H 10 Q 2,98 2,90 V 10 Q 2,2 10,2 H 50"
                        />
                      </svg>
                    )}
                    <div className="deck-content">
                      <div className="action-cost-circle">{buyCost}</div>
                      <div className="deck-label">ACTION CARDS</div>
                      <div className="deck-count">{deck.length}</div>
                    </div>
                  </div>
                  {/* Face-up revealed card */}
                  {revealed ? (
                    <div
                      className={`action-card-face ${cardTier} ${canBuyFaceUp ? 'card-buyable' : 'card-not-affordable'}${isHoldingRevealed ? ' btn-holding' : ''}`}
                      style={{ cursor: canBuyFaceUp ? 'pointer' : 'default', position: 'relative' }}
                      title={canBuyFaceUp ? `Hold to buy this card (${buyCost} AP)` : ''}
                      onMouseDown={() => { if (canBuyFaceUp) handleButtonHoldStart(buyRevealedId, () => handleBuyRevealedCard(revealed, deck, setDeck, setRevealed, buyCost)); }}
                      onMouseUp={handleButtonHoldEnd}
                      onMouseLeave={handleButtonHoldEnd}
                      onTouchStart={(e) => { e.preventDefault(); if (canBuyFaceUp) handleButtonHoldStart(buyRevealedId, () => handleBuyRevealedCard(revealed, deck, setDeck, setRevealed, buyCost)); }}
                      onTouchEnd={handleButtonHoldEnd}
                      onTouchCancel={handleButtonHoldEnd}
                    >
                      {isHoldingRevealed && (
                        <svg className="hold-progress-svg" viewBox="0 0 165 110" preserveAspectRatio="none">
                          <path
                            className="hold-progress-path hold-progress-path-btn"
                            pathLength="100"
                            d="M 82,2 H 155 Q 163,2 163,10 V 100 Q 163,108 155,108 H 10 Q 2,108 2,100 V 10 Q 2,2 10,2 H 82"
                          />
                        </svg>
                      )}
                      <div className="action-card-header">
                        <div className="action-card-play-cost">{revealed.playCost}</div>
                        <div className="action-card-name">{revealed.name}</div>
                        <div className="action-card-buy-badge">{buyCost}</div>
                      </div>
                      <div className="action-card-content">{revealed.content}</div>
                    </div>
                  ) : (
                    <div className={`action-card-deck empty-deck ${cardTier}`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>

          {/* Player Hand Display */}
          {(() => {
            const hand = currentPlayer === 1 ? player1Hand : player2Hand;
            if (hand.length === 0) return null;
            const currentAP = currentPlayer === 1 ? player1ActionPoints : player2ActionPoints;
            const offset = currentPlayer === 1 ? player1HandOffset : player2HandOffset;
            const setOffset = currentPlayer === 1 ? setPlayer1HandOffset : setPlayer2HandOffset;
            const canGoLeft = offset > 0;
            const canGoRight = offset + 2 < hand.length;
            // rendered card = 169px (165 content + 2px border each side) + 8px gap = 177px per step
            const stripShift = offset * 177;
            return (
              <div className="action-hand">
                <div className="action-hand-label">Your Action Cards <span className="action-hand-count">({hand.length})</span></div>
                <div className="action-hand-carousel">
                  <button
                    className={`hand-carousel-btn hand-carousel-prev${canGoLeft ? '' : ' invisible'}`}
                    onClick={() => canGoLeft && setOffset(o => o - 1)}
                  >&#8249;</button>
                  <div className="action-hand-filmstrip-outer">
                    <div
                      className="action-hand-filmstrip-inner"
                      style={{ transform: `translateX(-${stripShift}px)` }}
                    >
                      {(() => {
                        const playerCols = currentPlayer === 1 ? [0, 1, 3, 5] : [2, 4, 6, 7];
                        const boardCards = Object.entries(cardGameZones)
                          .filter(([key]) => playerCols.includes(parseInt(key.split('-')[0])))
                          .map(([, c]) => c)
                          .filter(Boolean);
                        const liveCounts = {
                          A_001: boardCards.filter(c => ['SK', 'BPD', 'AM', 'S'].includes(c.PositionType?.trim())).length,
                          A_002: boardCards.filter(c => ['GK', 'DCB', 'DM', 'DF'].includes(c.PositionType?.trim())).length,
                          A_003: boardCards.filter(c => ['CD', 'CM', 'CF'].includes(c.PositionType?.trim())).length,
                          A_009: boardCards.filter(c => ['MidfielderCenter', 'MidfielderWide', 'ForwardCenter', 'ForwardWide'].includes(c.CardType)).length,
                          A_010: boardCards.filter(c => ['Keeper', 'DefenderCenter', 'DefenderWide'].includes(c.CardType)).length,
                        };
                        // Determine if Kickopp is currently possible (for modifier card disabling)
                        const usedKickoppH = currentPlayer === 1 ? player1UsedKickopp : player2UsedKickopp;
                        const usedUpgradeH = currentPlayer === 1 ? player1UsedUpgrade : player2UsedUpgrade;
                        const extraMainActionsH = currentPlayer === 1 ? player1ExtraMainActions : player2ExtraMainActions;
                        const eitherUsedH = (usedKickoppH || usedUpgradeH) && extraMainActionsH === 0;
                        const hasKickoppTargets = Object.entries(cardGameZones).some(([key, zCard]) => {
                          if (!zCard || typeof zCard !== 'object') return false;
                          const col = parseInt(key.split('-')[0]);
                          const row = parseInt(key.split('-')[1]);
                          if (!playerCols.includes(col)) return false;
                          return getAttackablePositions(col, row, currentPlayer).length > 0;
                        });
                        const kickoppPossible = !eitherUsedH && currentAP >= 1 && hasKickoppTargets;
                        const KICKOPP_MODIFIER_IDS = ['A_005', 'A_014', 'A_015', 'A_020', 'A_021'];

                        return hand.map((card, idx) => {
                          const cardTier = card.buyCost === 1 ? 'tier-1' : card.buyCost === 3 ? 'tier-3' : 'tier-5';
                          const canPlay = currentAP >= card.playCost
                            && (!KICKOPP_MODIFIER_IDS.includes(card.id) || kickoppPossible);
                          const liveCount = liveCounts[card.id];
                          const cardKey = `${card.id}-${idx}`;
                          const isHolding = holdingCardKey === cardKey;
                          return (
                            <div
                              key={cardKey}
                              className={`action-card-face ${cardTier} ${canPlay ? 'card-playable' : 'card-not-affordable'}${isHolding ? ' card-holding' : ''}`}
                              onMouseDown={() => handleCardHoldStart(card, idx, canPlay)}
                              onMouseUp={handleCardHoldEnd}
                              onMouseLeave={handleCardHoldEnd}
                              onTouchStart={(e) => { e.preventDefault(); handleCardHoldStart(card, idx, canPlay); }}
                              onTouchEnd={handleCardHoldEnd}
                              onTouchCancel={handleCardHoldEnd}
                              title={canPlay ? `Hold to play ${card.name} (${card.playCost} AP)` : `Need ${card.playCost} AP to play`}
                            >
                              {isHolding && (
                                <svg className="hold-progress-svg" viewBox="0 0 165 110" preserveAspectRatio="none">
                                  <path
                                    className="hold-progress-path"
                                    pathLength="100"
                                    d="M 82.5,2 H 155 Q 163,2 163,10 V 100 Q 163,108 155,108 H 10 Q 2,108 2,100 V 10 Q 2,2 10,2 H 82.5"
                                  />
                                </svg>
                              )}
                              <div className="action-card-header">
                                <div className="action-card-play-cost">{card.playCost}</div>
                                <div className="action-card-name">{card.name}</div>
                              </div>
                              <div className="action-card-content">
                                {card.content}
                                {liveCount !== undefined && (
                                  <span style={{ display: 'block', marginTop: 2, fontSize: 11, color: '#fbbf24', fontWeight: 'bold' }}>
                                    ({liveCount} boost{liveCount !== 1 ? 's' : ''})
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </div>
                  <button
                    className={`hand-carousel-btn hand-carousel-next${canGoRight ? '' : ' invisible'}`}
                    onClick={() => canGoRight && setOffset(o => o + 1)}
                  >&#8250;</button>
                </div>
              </div>
            );
          })()}
        </>
      )}


      {/* Kickopp Arrow - Show arrow instead of dragging card during kickopp mode */}
      {draggedCard && kickoppMode && draggedCard.sourceKey && (() => {
        // Get the source card position from the DOM
        const sourceKey = draggedCard.sourceKey;
        const sourceElement = document.querySelector(`[data-col-index="${sourceKey.split('-')[0]}"][data-row-index="${sourceKey.split('-')[1]}"]`);

        if (!sourceElement) return null;

        const sourceRect = sourceElement.getBoundingClientRect();

        // Convert screen coordinates to local viewport space (handles zoom + transform on mobile)
        const gameViewport = document.querySelector('.game-viewport');
        let sourceCenterX, sourceCenterY, targetX, targetY;

        if (gameViewport) {
          const vpRect = gameViewport.getBoundingClientRect();
          const scaleX = vpRect.width / 2160;
          const scaleY = vpRect.height / 1080;

          sourceCenterX = (sourceRect.left + sourceRect.width / 2 - vpRect.left) / scaleX;
          sourceCenterY = (sourceRect.top + sourceRect.height / 2 - vpRect.top) / scaleY;
          targetX = (dragPosition.x - vpRect.left) / scaleX;
          targetY = (dragPosition.y - vpRect.top) / scaleY;
        } else {
          sourceCenterX = sourceRect.left + sourceRect.width / 2;
          sourceCenterY = sourceRect.top + sourceRect.height / 2;
          targetX = dragPosition.x;
          targetY = dragPosition.y;
        }

        const deltaX = targetX - sourceCenterX;
        const deltaY = targetY - sourceCenterY;

        // Determine arrow color based on preview status
        const arrowColor = kickoppPreview?.wouldSucceed ? '#22c55e' : '#ef4444';
        const glowColor = kickoppPreview?.wouldSucceed ? 'rgba(34, 197, 94, 0.6)' : 'rgba(239, 68, 68, 0.6)';

        return (
          <svg
            className="kickopp-arrow"
            style={{
              position: 'fixed',
              left: 0,
              top: 0,
              width: '100%',
              height: '100%',
              pointerEvents: 'none',
              zIndex: 999
            }}
          >
            <defs>
              <filter id="arrow-glow">
                <feGaussianBlur stdDeviation="4" result="coloredBlur"/>
                <feMerge>
                  <feMergeNode in="coloredBlur"/>
                  <feMergeNode in="SourceGraphic"/>
                </feMerge>
              </filter>
            </defs>

            {/* Main arrow line with curve */}
            <path
              d={`M ${sourceCenterX} ${sourceCenterY} Q ${sourceCenterX + deltaX / 2} ${sourceCenterY + deltaY / 2 - 30} ${targetX} ${targetY}`}
              stroke={arrowColor}
              strokeWidth="8"
              fill="none"
              filter="url(#arrow-glow)"
              strokeLinecap="round"
              opacity="0.9"
            />

            {/* Glow effect line (thicker, more transparent) */}
            <path
              d={`M ${sourceCenterX} ${sourceCenterY} Q ${sourceCenterX + deltaX / 2} ${sourceCenterY + deltaY / 2 - 30} ${targetX} ${targetY}`}
              stroke={glowColor}
              strokeWidth="16"
              fill="none"
              strokeLinecap="round"
              opacity="0.4"
            />

            {/* Source indicator circle (white center) */}
            <circle
              cx={sourceCenterX}
              cy={sourceCenterY}
              r="12"
              fill={arrowColor}
              opacity="0.8"
              filter="url(#arrow-glow)"
            />
            <circle
              cx={sourceCenterX}
              cy={sourceCenterY}
              r="8"
              fill="white"
              opacity="0.9"
            />

            {/* Target indicator circle (colored based on success/failure) */}
            <circle
              cx={targetX}
              cy={targetY}
              r="12"
              fill={arrowColor}
              opacity="0.8"
              filter="url(#arrow-glow)"
            />
            <circle
              cx={targetX}
              cy={targetY}
              r="8"
              fill={arrowColor}
              opacity="0.9"
            />
          </svg>
        );
      })()}

      {/* Dragging Card - Only show when NOT in kickopp mode and NOT in draft stage (draft uses click-to-place) */}
      {draggedCard && !kickoppMode && gameStage !== 'draft' && (() => {
        // Convert screen coordinates to local viewport space on mobile
        const gameVP = document.querySelector('.game-viewport');
        let cardLeft, cardTop;
        if (gameVP) {
          const vpR = gameVP.getBoundingClientRect();
          const sX = vpR.width / 2160;
          const sY = vpR.height / 1080;
          cardLeft = (dragPosition.x - vpR.left) / sX - 90;
          cardTop = (dragPosition.y - vpR.top) / sY - 60;
        } else {
          cardLeft = dragPosition.x - 90;
          cardTop = dragPosition.y - 60;
        }
        return (
        <div
          className="dragging-card"
          style={{
            left: cardLeft,
            top: cardTop,
            zIndex: 1000
          }}
        >
          <div className={`dragging-card-content ${currentPlayer === 2 ? 'player2-card' : ''}`}>
            {currentPlayer === 1 ? (
              <>
                {/* Player 1: Position/Name on left, Stats on right */}
                <div className="drag-left-section">
                  <div
                    className="drag-position-badge"
                    style={{
                      borderColor: getPositionColor(draggedCard.card.CardType)
                    }}
                  >
                    {draggedCard.card.PositionType}
                  </div>
                  <div className="drag-player-name">
                    {draggedCard.card.PlayerName}
                  </div>
                </div>

                <div className="drag-stats-section">
                  <div className="drag-stat-box attack">
                    {draggedCard.card.Attack}
                  </div>
                  <div className="drag-stat-box defence">
                    {draggedCard.card.Defence}
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* Player 2: Stats on left, Position/Name on right */}
                <div className="drag-stats-section">
                  <div className="drag-stat-box attack">
                    {draggedCard.card.Attack}
                  </div>
                  <div className="drag-stat-box defence">
                    {draggedCard.card.Defence}
                  </div>
                </div>

                <div className="drag-left-section">
                  <div
                    className="drag-position-badge"
                    style={{
                      borderColor: getPositionColor(draggedCard.card.CardType)
                    }}
                  >
                    {draggedCard.card.PositionType}
                  </div>
                  <div className="drag-player-name">
                    {draggedCard.card.PlayerName}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
        );
      })()}

      {/* Event Log - shown during Draft, Warm Up, and Battle */}
      {(gameStage === 'draft' || gameStage === 'warmup' || gameStage === 'battle') && (
        <div className={`event-log-window ${eventLogExpanded ? 'expanded' : ''}`}>
          <button
            className="event-log-toggle"
            onClick={() => setEventLogExpanded(!eventLogExpanded)}
            title={eventLogExpanded ? 'Show less' : 'Show more'}
          >
            {eventLogExpanded ? '◄' : '►'}
          </button>
          <div className="event-log-content">
            {eventLog.length === 0 ? (
              <div className="event-log-empty">No events yet...</div>
            ) : (
              <div className="event-log-list">
                {eventLog.slice().reverse().slice(0, eventLogExpanded ? eventLog.length : 3).map((event, index) => (
                  <div key={index} className="event-log-entry">
                    <span className="event-log-timestamp">{event.timestamp}</span>
                    <span className="event-log-message">{renderEventLogEntry(event)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Action card tooltip popup — shown when tapping/clicking an underlined action card title in the event log */}
      {actionCardTooltip && (
        <div className="action-card-tooltip-overlay" onClick={() => setActionCardTooltip(null)}>
          <div className="action-card-tooltip-popup" onClick={(e) => e.stopPropagation()}>
            <div className="action-card-tooltip-title">{actionCardTooltip.name}</div>
            <div className="action-card-tooltip-text">{actionCardTooltip.content}</div>
            <button className="action-card-tooltip-close" onClick={() => setActionCardTooltip(null)}>✕</button>
          </div>
        </div>
      )}

      {/* Substitute - Step 1: Select board card */}
      {substituteMode && gameStage === 'battle' && substituteStep === 'select_board_card' && (
        <div className="boost-action-window substitute-board-step">
          <div className="boost-selection">
            <h3 className="boost-title">Select a Player to Substitute</h3>
            <button
              className="boost-cancel-btn"
              onClick={() => {
                setSubstituteMode(false);
                setSubstituteStep(null);
                setSubstituteSelectedDeck(null);
                setSubstituteVisibleCards([]);
                setSubstituteRemovedCard(null);
                setSubstitutePendingCard(null);
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Substitute - Step 1b: Confirm player removal (portrait card preview) */}
      {substituteMode && gameStage === 'battle' && substituteStep === 'confirm_remove' && substitutePendingCard && (
        <div className="boost-action-window substitute-board-step sub-confirm-popup">
          {(() => {
            const { card } = substitutePendingCard;
            const posColor = getPositionColor(card.CardType);
            const isCapt = (currentPlayer === 1 && player1Captain === card.Card_Id) ||
                           (currentPlayer === 2 && player2Captain === card.Card_Id);
            const appliedBoosts = currentPlayer === 1
              ? player1AppliedBoosts[card.Card_Id] || { attack: 0, defense: 0 }
              : player2AppliedBoosts[card.Card_Id] || { attack: 0, defense: 0 };

            const doConfirm = () => {
              const { card: c, position, wasCaptain, wasFormerCaptain } = substitutePendingCard;

              setSubstituteRemovedCard(substitutePendingCard);
              setSubstitutePendingCard(null);

              // Remove from board
              setCardGameZones(prev => { const n = { ...prev }; delete n[position]; return n; });
              setRecycledCardsCount(prev => prev + 1);

              if (wasCaptain) {
                currentPlayer === 1 ? setPlayer1Captain(null) : setPlayer2Captain(null);
              }
              if (wasFormerCaptain) {
                currentPlayer === 1 ? setPlayer1FormerCaptain(null) : setPlayer2FormerCaptain(null);
              }

              const pawns = currentPlayer === 1 ? player1KickoppPawns : player2KickoppPawns;
              const setPawns = currentPlayer === 1 ? setPlayer1KickoppPawns : setPlayer2KickoppPawns;
              const pawnIdx = pawns.findIndex(p => p.cardId === c.Card_Id);
              if (pawnIdx !== -1) setPawns(pawns.filter((_, i) => i !== pawnIdx));

              setSubstituteStep('select_deck');
            };

            return (
              <>
                <div className="selected-card-panel bpp-portrait-card sub-confirm-card">
                  <div className="sidebar-card-stats">
                    <div className="sidebar-stat-box attack">
                      {card.Attack + appliedBoosts.attack}
                    </div>
                    <div className="sidebar-stat-box defence">
                      {card.Defence + appliedBoosts.defense}
                    </div>
                  </div>
                  <div
                    className="sidebar-card-name"
                    style={{
                      backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.05) 45%, rgba(0,0,0,0.9) 100%), url('${getFaceUrl(card.Card_Id)}')`,
                      backgroundSize: 'cover, 48%',
                      backgroundPosition: 'center, center top',
                      backgroundRepeat: 'no-repeat, no-repeat'
                    }}
                  >
                    {card.PlayerName}
                    {isCapt && <div className="captain-badge">C</div>}
                  </div>
                  <div className="sidebar-card-bottom">
                    <div className="sidebar-card-position" style={{ backgroundColor: posColor }}>
                      {card.PositionType?.trim()}
                    </div>
                    {card.Country && (
                      <span className={`fi fi-${card.Country.toLowerCase()} sidebar-card-flag`} />
                    )}
                  </div>
                </div>
                <button className="boost-confirm-btn" onClick={doConfirm}>
                  ✓ Confirm Substitution
                </button>
                <button
                  className="boost-cancel-btn"
                  onClick={() => {
                    setSubstituteMode(false);
                    setSubstituteStep(null);
                    setSubstituteSelectedDeck(null);
                    setSubstituteVisibleCards([]);
                    setSubstituteRemovedCard(null);
                    setSubstitutePendingCard(null);
                  }}
                >
                  Cancel
                </button>
              </>
            );
          })()}
        </div>
      )}

      {/* Action Card AP Warning — playing this card leaves 0 AP with Kickopp/Upgrade unused */}
      {pendingActionCard && (
        <div className="boost-action-window sub-confirm-popup ap-warning-popup" style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '28px', marginBottom: '8px' }}>⚠️</div>
          <h3 className="boost-title" style={{ marginBottom: '8px' }}>No Action Points Left</h3>
          <p style={{ color: '#94a3b8', fontSize: '13px', lineHeight: 1.5, marginBottom: '16px' }}>
            Playing <strong style={{ color: 'white' }}>{pendingActionCard.card.name}</strong> will use all your remaining AP.<br/>
            You won't be able to play <strong style={{ color: '#fbbf24' }}>Kickopp</strong> or <strong style={{ color: '#a855f7' }}>Upgrade</strong> this turn.
          </p>
          <button
            className="boost-confirm-btn"
            onClick={() => {
              const { card, handIndex } = pendingActionCard;
              setPendingActionCard(null);
              handlePlayActionCard(card, handIndex);
            }}
          >
            Play Anyway
          </button>
          <button
            className="boost-cancel-btn"
            onClick={() => setPendingActionCard(null)}
            style={{ marginTop: '6px' }}
          >
            Cancel
          </button>
        </div>
      )}

      {/* Substitute - Step 2: Select deck position */}
      {substituteMode && gameStage === 'battle' && substituteStep === 'select_deck' && (
        <div className="boost-action-window substitute-deck-step">
          <div className="boost-selection">
            <h3 className="boost-title">Select a Player position from the decks</h3>
            <p className="substitute-hint">Out: <strong>{substituteRemovedCard?.card?.PlayerName}</strong> ({substituteRemovedCard?.card?.PositionType?.trim()})</p>
            <p className="substitute-hint">Click on an available position type deck</p>
            <button
              className="boost-cancel-btn"
              onClick={() => {
                // Restore the removed card to the board
                if (substituteRemovedCard) {
                  setCardGameZones(prev => ({
                    ...prev,
                    [substituteRemovedCard.position]: substituteRemovedCard.card
                  }));
                  if (substituteRemovedCard.wasCaptain) {
                    currentPlayer === 1 ? setPlayer1Captain(substituteRemovedCard.card.Card_Id) : setPlayer2Captain(substituteRemovedCard.card.Card_Id);
                  }
                  if (substituteRemovedCard.wasFormerCaptain) {
                    currentPlayer === 1 ? setPlayer1FormerCaptain(substituteRemovedCard.card.Card_Id) : setPlayer2FormerCaptain(substituteRemovedCard.card.Card_Id);
                  }
                  setRecycledCardsCount(prev => prev - 1);
                }
                setSubstituteMode(false);
                setSubstituteStep(null);
                setSubstituteSelectedDeck(null);
                setSubstituteVisibleCards([]);
                setSubstituteRemovedCard(null);
                setSubstitutePendingCard(null);
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Substitute - Step 3: Select card (standalone sidebar, positioned by element 17) */}
      {substituteMode && gameStage === 'battle' && substituteStep === 'select_card' && (
        <div className="card-selection-sidebar substitute-sidebar">
          <div className="sidebar-header">
            <h3 className="sidebar-title">Choose card to PLAY</h3>
          </div>

          <div className="sidebar-body">
            <div className="sidebar-cards">
              {substituteVisibleCards.map((card, index) => {
                const positionColor = getPositionColor(card.CardType);
                return (
                  <div
                    key={card.Card_Id || index}
                    className="sidebar-card"
                    style={{ '--reveal-delay': `${index * 280}ms` }}
                  >
                    <div
                      className="sidebar-card-wrapper"
                      onClick={() => {
                        // Remove the other card from deck (clear it)
                        const otherCard = substituteVisibleCards.find((c, i) => i !== index);

                        // Update decks - remove both selected and cleared cards
                        setDecksForPlayer(currentPlayer, prev => {
                          const newDecks = { ...prev };
                          const targetDeckTypes = substituteSelectedDeck === 'ForwardCenter' || substituteSelectedDeck === 'ForwardWide'
                            ? ['ForwardCenter', 'ForwardWide']
                            : [substituteSelectedDeck];

                          targetDeckTypes.forEach(dType => {
                            if (newDecks[dType]) {
                              newDecks[dType] = newDecks[dType].filter(
                                c => c.Card_Id !== card.Card_Id && c.Card_Id !== otherCard?.Card_Id
                              );
                            }
                          });
                          return newDecks;
                        });

                        // Store the selected card and move to place_card step
                        setSubstituteSelectedCard(card);
                        setSubstituteStep('place_card');

                        // Highlight valid board positions for this card type
                        const validColors = getValidSlotsForCardType(card.CardType);
                        const validSlotKeys = [];
                        cardGameZonesData.forEach((column, colIndex) => {
                          column.forEach((slot) => {
                            const slotKey = `${colIndex}-${slot.row}`;
                            const isColorValid = validColors.includes(slot.color);
                            const isColumnValid = isValidColumnForPlayer(colIndex, currentPlayer);
                            const isOccupied = cardGameZones[slotKey] && typeof cardGameZones[slotKey] === 'object';

                            if (isColorValid && isColumnValid && !isOccupied) {
                              validSlotKeys.push(slotKey);
                            }
                          });
                        });
                        setHighlightedSlots(validSlotKeys);

                        console.log(`📋 Selected ${card.PlayerName} - click a highlighted position to place`);
                      }}
                    >
                      <div className="card-flip-inner">
                        {/* Back face — solid position colour */}
                        <div className="card-back-face" style={{ backgroundColor: positionColor }} />

                        {/* Front face */}
                        <div className="sidebar-card-content">
                          {/* Stats row */}
                          <div className="sidebar-card-stats">
                            <div className="sidebar-stat-box attack">
                              {card.Attack}
                            </div>
                            <div className="sidebar-stat-box defence">
                              {card.Defence}
                            </div>
                          </div>

                          {/* Player name */}
                          <div
                            className="sidebar-card-name"
                            style={{
                              backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.05) 45%, rgba(0,0,0,0.9) 100%), url('${getFaceUrl(card.Card_Id)}')`,
                              backgroundSize: 'cover, 68%',
                              backgroundPosition: 'center, center top',
                              backgroundRepeat: 'no-repeat, no-repeat'
                            }}
                          >
                            {card.PlayerName}
                          </div>

                          {/* Bottom row: position (left) + flag (right) */}
                          <div className="sidebar-card-bottom">
                            <div
                              className="sidebar-card-position"
                              style={{ backgroundColor: positionColor }}
                            >
                              {card.PositionType?.trim()}
                            </div>
                            {card.Country && (
                              <span className={`fi fi-${card.Country.toLowerCase()} sidebar-card-flag`} />
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Substitute - Step 4: Place card on board (standalone, same style as boost-action-window) */}
      {substituteMode && gameStage === 'battle' && substituteStep === 'place_card' && (
        <div className="boost-action-window substitute-place-step">
          <div className="boost-selection">
            <h3 className="boost-title">Select a Position for the Player</h3>
            <div className="substitute-selected-preview">
              <div
                className="substitute-card-option selected"
                style={{ borderColor: getPositionColor(substituteSelectedCard?.CardType) }}
              >
                <div
                  className="substitute-card-header"
                  style={{ backgroundColor: getPositionColor(substituteSelectedCard?.CardType) }}
                >
                  {substituteSelectedCard?.PositionType || substituteSelectedCard?.CardType}
                </div>
                <div className="substitute-card-name">{substituteSelectedCard?.PlayerName}</div>
                <div className="substitute-card-stats">
                  <span className="stat-attack">ATK: {substituteSelectedCard?.Attack || 0}</span>
                  <span className="stat-defense">DEF: {substituteSelectedCard?.Defence || 0}</span>
                </div>
              </div>
            </div>
            <button
              className="boost-cancel-btn"
              onClick={() => {
                // Restore the removed card to the board
                if (substituteRemovedCard) {
                  setCardGameZones(prev => ({
                    ...prev,
                    [substituteRemovedCard.position]: substituteRemovedCard.card
                  }));

                  // Restore captain status if it was captain
                  if (substituteRemovedCard.wasCaptain) {
                    if (currentPlayer === 1) {
                      setPlayer1Captain(substituteRemovedCard.card.Card_Id);
                    } else {
                      setPlayer2Captain(substituteRemovedCard.card.Card_Id);
                    }
                  }

                  // Restore former captain status if it was former captain
                  if (substituteRemovedCard.wasFormerCaptain) {
                    if (currentPlayer === 1) {
                      setPlayer1FormerCaptain(substituteRemovedCard.card.Card_Id);
                    } else {
                      setPlayer2FormerCaptain(substituteRemovedCard.card.Card_Id);
                    }
                  }

                  // Restore cards out count
                  setRecycledCardsCount(prev => prev - 1);
                }

                // Put selected card back in deck
                if (substituteSelectedCard) {
                  setDecksForPlayer(currentPlayer, prev => {
                    const newDecks = { ...prev };
                    const targetDeckType = substituteSelectedCard.CardType;
                    if (!newDecks[targetDeckType]) {
                      newDecks[targetDeckType] = [];
                    }
                    newDecks[targetDeckType].unshift(substituteSelectedCard);
                    return newDecks;
                  });
                }

                setSubstituteMode(false);
                setSubstituteStep(null);
                setSubstituteSelectedDeck(null);
                setSubstituteVisibleCards([]);
                setSubstituteSelectedCard(null);
                setSubstituteRemovedCard(null);
                setHighlightedSlots([]);
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Move Action Instruction UI */}
      {moveMode && gameStage === 'battle' && (
        <div className="boost-action-window move-action-window">
          <div className="boost-selection">
            <h3 className="boost-title">Move a Player Card on its Position Line</h3>
            <button
              className="boost-cancel-btn"
              onClick={() => {
                setMoveMode(false);
                setMoveOriginalPosition(null);
                const setSelected = currentPlayer === 1 ? setPlayer1SelectedActionCard : setPlayer2SelectedActionCard;
                setSelected(null);
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Boost Purchase/Apply UI — hidden when apply_boost step (card panel takes over) */}
      {boostMode && gameStage === 'battle' && boostStep !== 'apply_boost' && (
        <div className={`boost-action-window boost-mode-${boostMode}`}>
          {/* Add Boost - Select card instruction */}
          {boostMode === 'add_boost' && boostStep === 'select_card' && (
            <div className="boost-selection">
              <h3 className="boost-title">Select a Player to Add Boost</h3>
              <button
                className="boost-cancel-btn"
                onClick={() => {
                  setBoostMode(null);
                  setBoostStep(null);
                }}
              >
                Cancel
              </button>
            </div>
          )}

          {/* Buy 1A or 1D - Select type */}
          {boostMode === 'buy_single' && boostStep === 'select_type' && (
            <div className="boost-selection">
              <h3 className="boost-title">Choose a Boost  <span className="boost-cost-badge">2 AP</span></h3>
              <p className="boost-subtitle">Select Attack or Defense</p>
              <div className="boost-type-grid">
                <div
                  className="boost-type-option attack"
                  onClick={() => {
                    const currentAP = currentPlayer === 1 ? player1ActionPoints : player2ActionPoints;

                    // Check if we should warn about spending last AP without Upgrade/Kickopp
                    if (shouldWarnAboutLastAP(2)) {
                      console.log(`⚠️ Warning: Buying +1 Attack will use all AP without Upgrade/Kickopp. Showing confirmation.`);
                      setPendingAction({
                        type: 'buy_boost',
                        boostType: 'attack',
                        cost: 2
                      });
                      setShowAPConfirmation(true);
                      return;
                    }

                    const setBoosts = currentPlayer === 1 ? setPlayer1BattleBoosts : setPlayer2BattleBoosts;
                    const setAP = currentPlayer === 1 ? setPlayer1ActionPoints : setPlayer2ActionPoints;

                    setBoosts(prev => ({ ...prev, attack: prev.attack + 1 }));
                    setAP(currentAP - 2);
                    setBoostMode(null);
                    setBoostStep(null);

                    addEventLog({
                      type: 'boost_buy',
                      player: currentPlayer,
                      boostType: 'attack',
                      cost: 2
                    });
                  }}
                >
                  <div className="buy-boost-icon-wrapper">
                    <span className="buy-boost-box blue">1</span>
                  </div>
                  <span className="boost-name">+1 Attack</span>
                </div>
                <div
                  className="boost-type-option defense"
                  onClick={() => {
                    const currentAP = currentPlayer === 1 ? player1ActionPoints : player2ActionPoints;

                    // Check if we should warn about spending last AP without Upgrade/Kickopp
                    if (shouldWarnAboutLastAP(2)) {
                      console.log(`⚠️ Warning: Buying +1 Defense will use all AP without Upgrade/Kickopp. Showing confirmation.`);
                      setPendingAction({
                        type: 'buy_boost',
                        boostType: 'defense',
                        cost: 2
                      });
                      setShowAPConfirmation(true);
                      return;
                    }

                    const setBoosts = currentPlayer === 1 ? setPlayer1BattleBoosts : setPlayer2BattleBoosts;
                    const setAP = currentPlayer === 1 ? setPlayer1ActionPoints : setPlayer2ActionPoints;

                    setBoosts(prev => ({ ...prev, defense: prev.defense + 1 }));
                    setAP(currentAP - 2);
                    setBoostMode(null);
                    setBoostStep(null);

                    addEventLog({
                      type: 'boost_buy',
                      player: currentPlayer,
                      boostType: 'defense',
                      cost: 2
                    });
                  }}
                >
                  <div className="buy-boost-icon-wrapper">
                    <span className="buy-boost-box red">1</span>
                  </div>
                  <span className="boost-name">+1 Defense</span>
                </div>
              </div>
              <button
                className="boost-cancel-btn"
                onClick={() => {
                  setBoostMode(null);
                  setBoostStep(null);
                }}
              >
                Cancel
              </button>
            </div>
          )}

          {/* Buy 1A and 1D - Confirm */}
          {boostMode === 'buy_both' && boostStep === 'confirm' && (
            <div className="boost-selection">
              <h3 className="boost-title">Buy 1A and 1D (3 AP)</h3>
              <div className="boost-preview">
                <div className="boost-preview-item">
                  <span className="buy-boost-box blue">1</span> +1 Attack
                </div>
                <div className="boost-preview-item">
                  <span className="buy-boost-box red">1</span> +1 Defense
                </div>
              </div>
              <button
                className="boost-confirm-btn"
                onClick={() => {
                  const currentAP = currentPlayer === 1 ? player1ActionPoints : player2ActionPoints;

                  // Check if we should warn about spending last AP without Upgrade/Kickopp
                  if (shouldWarnAboutLastAP(3)) {
                    console.log(`⚠️ Warning: Buying +1A and +1D will use all AP without Upgrade/Kickopp. Showing confirmation.`);
                    setPendingAction({
                      type: 'buy_boost',
                      boostType: 'both',
                      cost: 3
                    });
                    setShowAPConfirmation(true);
                    return;
                  }

                  const setBoosts = currentPlayer === 1 ? setPlayer1BattleBoosts : setPlayer2BattleBoosts;
                  const setAP = currentPlayer === 1 ? setPlayer1ActionPoints : setPlayer2ActionPoints;

                  setBoosts(prev => ({
                    attack: prev.attack + 1,
                    defense: prev.defense + 1
                  }));
                  setAP(currentAP - 3);
                  setBoostMode(null);
                  setBoostStep(null);

                  addEventLog({
                    type: 'boost_buy',
                    player: currentPlayer,
                    boostType: 'both',
                    cost: 3
                  });
                }}
              >
                ✓ Confirm Purchase
              </button>
              <button
                className="boost-cancel-btn"
                onClick={() => {
                  setBoostMode(null);
                  setBoostStep(null);
                }}
              >
                Cancel
              </button>
            </div>
          )}

        </div>
      )}

      {/* ── Trial 8 Completion Celebration ─────────────────────────── */}
      {gameEnded && gameResult && challengeMode && onChallengeEnd && cpuStage &&
       !dismissedCompletePopup && (() => {
        const hwKey_ = aiPlayer === 1 ? 'player2' : 'player1';
        const won_   = gameResult.winner === hwKey_;
        const hScore_= aiPlayer === 1 ? gameResult.p2Score : gameResult.p1Score;
        const isSW_  = won_ && hScore_ === 12;
        const lvl_   = parseInt(cpuStage.Stage, 10);
        const meets_ = won_ && (
          challengeDifficulty === 'easy'   ? true :
          challengeDifficulty === 'medium' ? (lvl_ <= 4 ? true : isSW_) :
          isSW_
        );
        if (lvl_ !== 8 || !meets_) return null;

        const stopMusic_ = () => {
          if (gameplayMusicRef?.current) {
            gameplayMusicRef.current.pause();
            gameplayMusicRef.current.currentTime = 0;
            gameplayMusicRef.current = null;
          }
        };

        const easy   = challengeDifficulty === 'easy';
        const medium = challengeDifficulty === 'medium';
        // hard = neither

        const accent  = easy ? '#10b981' : medium ? '#f59e0b' : '#eab308';
        const glow    = easy ? 'rgba(16,185,129,0.3)' : medium ? 'rgba(245,158,11,0.3)' : 'rgba(234,179,8,0.3)';
        const icon    = easy ? '🎉' : medium ? '⚡' : '👑';
        const badge   = easy ? 'EASY MODE — COMPLETE'
                      : medium ? 'MEDIUM MODE — MASTERED'
                      : 'HARD MODE — CONQUERED';
        const heading = easy ? 'All 8 Trials Cleared!'
                      : medium ? '8 Trials. Real Super Wins.'
                      : 'Eight Perfect Victories. No Lives. No Retries.';
        const body    = easy
          ? 'Your squad has levelled up and your collection has grown — but you\'ve only scratched the surface of Kickopp. Every card you discovered opens new possibilities on the pitch.'
          : medium
          ? ''
          : 'You have conquered every trial Kickopp has to offer. No second chances, no safety net — just eight flawless performances. Your squad stands in a league of its own.';
        const suggestionBg = easy ? 'rgba(16,185,129,0.08)' : medium ? 'rgba(245,158,11,0.08)' : 'rgba(234,179,8,0.08)';
        const suggestion = easy ? (
          <>
            Ready for a bigger test?{' '}
            <strong style={{color:'#f59e0b'}}>Medium Mode</strong> introduces 1-retry trials and ⚡ Super Win challenges for Trials 5–8.
            Or go straight to{' '}
            <strong style={{color:'#ef4444'}}>Hard</strong> — where every trial demands perfection from the first whistle.
          </>
        ) : medium ? (
          <>
            One final frontier remains —{' '}
            <strong style={{color:'#ef4444'}}>Hard Mode</strong>.
            No lives. No retries. Every single trial demands a Super Win.
            Are <em>you</em>?
          </>
        ) : (
          <>
            The challenge has been beaten — but mastery has no ceiling.
            Every new run is a chance to draft a different squad, try different tactics,
            and write a completely new story.
          </>
        );

        return (
          <div className="game-end-overlay" style={{ zIndex: 10600 }}>
            <div style={{
              background: 'linear-gradient(145deg,#1e293b 0%,#0a0f1e 100%)',
              border: `2px solid ${accent}`,
              borderRadius: '22px',
              padding: '48px 56px',
              maxWidth: '600px',
              width: '90%',
              textAlign: 'center',
              color: '#fff',
              boxShadow: `0 0 100px ${glow}, 0 32px 64px rgba(0,0,0,0.7)`,
              position: 'relative',
            }}>
              {/* dismiss × */}
              <button
                onClick={() => setDismissedCompletePopup(true)}
                style={{
                  position:'absolute', top:'16px', right:'20px',
                  background:'none', border:'none', color:'#475569',
                  fontSize:'22px', cursor:'pointer', lineHeight:1,
                }}
                title="See end screen"
              >✕</button>

              {/* icon + badge */}
              <div style={{ fontSize:'72px', marginBottom:'6px', filter:`drop-shadow(0 0 20px ${accent})` }}>{icon}</div>
              <div style={{
                display:'inline-block',
                background: suggestionBg,
                border: `1px solid ${accent}55`,
                borderRadius:'8px',
                padding:'5px 18px',
                fontSize:'11px', fontWeight:800, letterSpacing:'2.5px',
                color: accent, marginBottom:'20px',
              }}>{badge}</div>

              <h2 style={{ fontSize:'21px', fontWeight:900, color:'#fff', marginBottom:'12px', lineHeight:1.3 }}>
                {heading}
              </h2>
              {body && (
                <p style={{ fontSize:'14px', lineHeight:'1.8', color:'#94a3b8', marginBottom:'22px' }}>
                  {body}
                </p>
              )}

              {/* suggestion box */}
              <div style={{
                background: suggestionBg,
                border: `1px solid ${accent}44`,
                borderRadius:'12px',
                padding:'16px 20px',
                fontSize:'13px', lineHeight:'1.75', color:'#cbd5e1',
                marginBottom:'30px', textAlign:'left',
              }}>
                {suggestion}
              </div>

              {/* action buttons */}
              <div style={{ display:'flex', flexDirection:'column', gap:'12px' }}>
                <button
                  onClick={() => { stopMusic_(); onChallengeEnd({ win: true, superWin: isSW_, skipToChallenge: true }); }}
                  style={{
                    background: `linear-gradient(135deg,${accent},${easy?'#059669':medium?'#d97706':'#ca8a04'})`,
                    color: easy ? '#fff' : '#000',
                    border:'none', borderRadius:'12px',
                    padding:'15px 36px', fontSize:'15px', fontWeight:800,
                    letterSpacing:'1px', cursor:'pointer',
                    boxShadow:`0 6px 24px ${glow}`,
                  }}
                >
                  {easy ? '🏠 Go to Challenge Menu' : '🏆 Start New Challenge'}
                </button>
                {easy && (
                  <button
                    onClick={() => { stopMusic_(); onChallengeEnd({ win: true, superWin: isSW_, skipToMenu: true }); }}
                    style={{
                      background:'rgba(255,255,255,0.05)',
                      color:'#94a3b8', border:'1px solid rgba(255,255,255,0.1)',
                      borderRadius:'12px', padding:'12px 36px',
                      fontSize:'14px', fontWeight:600, cursor:'pointer',
                    }}
                  >
                    🏠 Go to Main Menu
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Super Win Required briefing — shown once at match start ── */}
      {showSuperWinPopup && (
        <div className="game-end-overlay" style={{ zIndex: 10500 }}>
          <div style={{
            background: 'linear-gradient(145deg,#1e293b 0%,#0f172a 100%)',
            border: '2px solid #f59e0b',
            borderRadius: '20px',
            padding: '44px 52px',
            maxWidth: '560px',
            textAlign: 'center',
            color: '#fff',
            boxShadow: '0 0 80px rgba(245,158,11,0.35), 0 24px 48px rgba(0,0,0,0.6)',
            position: 'relative',
          }}>
            {/* close × */}
            <button
              onClick={dismissSuperWinPopup}
              style={{
                position:'absolute', top:'14px', right:'18px',
                background:'none', border:'none', color:'#64748b',
                fontSize:'22px', cursor:'pointer', lineHeight:1,
              }}
              title="Dismiss"
            >✕</button>

            {/* lightning badge */}
            <div style={{ fontSize:'64px', marginBottom:'8px' }}>⚡</div>
            <div style={{
              display:'inline-block',
              background:'rgba(245,158,11,0.15)',
              border:'1px solid rgba(245,158,11,0.4)',
              borderRadius:'8px',
              padding:'4px 14px',
              fontSize:'11px', fontWeight:800, letterSpacing:'2px',
              color:'#f59e0b', marginBottom:'18px',
            }}>
              SUPER WIN REQUIRED
            </div>

            <h2 style={{ fontSize:'22px', fontWeight:900, color:'#fff', marginBottom:'14px', letterSpacing:'0.5px' }}>
              {challengeDifficulty === 'medium'
                ? 'Trials 5 to 8 — Super Win Required'
                : 'Trials 1 to 8 — Super Win Required'}
            </h2>
            <p style={{ fontSize:'15px', lineHeight:'2', color:'#cbd5e1', marginBottom:'30px' }}>
              {challengeDifficulty === 'medium' ? (
                <>
                  From <strong style={{color:'#fff'}}>Trial 5 to Trial 8</strong>, a regular win is not enough.<br />
                  You must <strong style={{color:'#f59e0b'}}>eliminate all 12 CPU cards</strong> to advance.
                </>
              ) : (
                <>
                  In <strong style={{color:'#fff'}}>Hard Mode</strong>, every trial demands a Super Win — from Trial 1 to 8.<br />
                  You must <strong style={{color:'#f59e0b'}}>eliminate all 12 CPU cards</strong> in every match to advance.
                </>
              )}
            </p>

            <button
              onClick={dismissSuperWinPopup}
              style={{
                background:'linear-gradient(135deg,#f59e0b,#d97706)',
                color:'#000', border:'none', borderRadius:'12px',
                padding:'14px 40px', fontSize:'16px', fontWeight:800,
                letterSpacing:'1px', cursor:'pointer',
                boxShadow:'0 6px 24px rgba(245,158,11,0.45)',
              }}
            >
              ⚡ Got it — Let's Go!
            </button>
          </div>
        </div>
      )}

      {/* ── Retry Confirmation popup (Easy / Medium loss with retries left) ── */}
      {showRetryPopup && challengeMode && challengeDifficulty && onChallengeEnd && (() => {
        const trialNum   = cpuStage ? parseInt(cpuStage.Stage, 10) : 1;
        const isEasy     = challengeDifficulty === 'easy';
        const isMedium   = challengeDifficulty === 'medium';
        // Lives AFTER this loss (decrement happens in App.jsx on confirm)
        const livesAfter = isEasy
          ? (challengeLives ?? 3) - 1
          : isMedium ? (challengeLives ?? 1) - 1 : 0;
        const isLastChance = livesAfter <= 0;
        const accentCol  = isLastChance ? '#ef4444' : '#3b82f6';
        const stopMusicNow = () => {
          if (gameplayMusicRef && gameplayMusicRef.current) {
            gameplayMusicRef.current.pause();
            gameplayMusicRef.current.currentTime = 0;
            gameplayMusicRef.current = null;
          }
        };
        const livesLabel = isEasy
          ? (livesAfter === 1 ? '1 life remaining' : `${livesAfter} lives remaining`)
          : 'Last retry for this Trial';
        const urgencyLine = isLastChance
          ? (isEasy ? 'Lose again and the challenge resets to Trial 1 — with fresh lives.' : 'Lose again and you restart from Trial 1.')
          : `You still have ${livesAfter} ${isEasy ? (livesAfter === 1 ? 'life' : 'lives') : 'retry'} left after this.`;
        return (
          <div className="game-end-overlay" style={{ zIndex: 10600 }}>
            <div style={{
              background: 'linear-gradient(145deg,#1e293b 0%,#0f172a 100%)',
              border: `2px solid ${accentCol}`,
              borderRadius: '20px',
              padding: '44px 52px',
              maxWidth: '520px', width: '90%',
              textAlign: 'center',
              color: '#fff',
              boxShadow: `0 0 80px ${accentCol}55, 0 24px 48px rgba(0,0,0,0.6)`,
              position: 'relative',
            }}>
              {/* emoji */}
              <div style={{ fontSize: '56px', marginBottom: '8px' }}>
                {isLastChance ? '⚠️' : '🔄'}
              </div>
              {/* badge */}
              <div style={{
                display: 'inline-block',
                background: `${accentCol}22`,
                border: `1px solid ${accentCol}66`,
                borderRadius: '8px',
                padding: '4px 14px',
                fontSize: '11px', fontWeight: 800, letterSpacing: '2px',
                color: accentCol, marginBottom: '16px',
                textTransform: 'uppercase',
              }}>
                {isLastChance ? 'Last Chance' : livesLabel}
              </div>

              <h2 style={{ fontSize: '22px', fontWeight: 900, marginBottom: '12px' }}>
                Trial {trialNum} — Not Passed
              </h2>

              <p style={{ fontSize: '14px', lineHeight: '1.9', color: '#cbd5e1', marginBottom: '8px' }}>
                {isLastChance
                  ? <><strong style={{ color: accentCol }}>{livesLabel}.</strong><br /></>
                  : <><strong style={{ color: '#e2e8f0' }}>{livesLabel}</strong><br /></>
                }
                {urgencyLine}
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '28px' }}>
                <button
                  onClick={() => { stopMusicNow(); setShowRetryPopup(false); onChallengeEnd({ win: false }); }}
                  style={{
                    background: `linear-gradient(135deg,${accentCol},${accentCol}cc)`,
                    color: '#fff', border: 'none', borderRadius: '12px',
                    padding: '15px 36px', fontSize: '16px', fontWeight: 800,
                    letterSpacing: '1px', cursor: 'pointer',
                    boxShadow: `0 6px 24px ${accentCol}55`,
                  }}
                >
                  🔄 Retry Trial {trialNum}
                </button>
                <button
                  onClick={() => { stopMusicNow(); setShowRetryPopup(false); onChallengeEnd({ win: false, skipToChallenge: true }); }}
                  style={{
                    background: 'rgba(255,255,255,0.05)',
                    color: '#94a3b8', border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '12px',
                    padding: '12px 36px', fontSize: '14px', fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  🏆 Challenge Menu
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Abandon Match Confirmation Popup */}
      {showAbandonConfirmation && (
        <div className="game-end-overlay">
          <div className="ap-confirmation-popup">
            <div className="ap-confirmation-header">
              <h2 className="ap-confirmation-title">⚠️ Abandon Match</h2>
            </div>
            <div className="ap-confirmation-body">
              <p className="ap-confirmation-message">
                Are you sure you want to abandon the match?
              </p>
            </div>
            <div className="ap-confirmation-buttons">
              <button
                className="ap-confirmation-btn ap-confirm-yes"
                onClick={() => {
                  // Current player abandons, opponent wins 12-0
                  const winner = currentPlayer === 1 ? 'player2' : 'player1';
                  setGameResult({
                    winner: winner,
                    p1Score: winner === 'player1' ? 12 : 0,
                    p2Score: winner === 'player2' ? 12 : 0,
                    reason: 'abandon'
                  });
                  setGameEnded(true);
                  setGameStage('end');
                  setShowAbandonConfirmation(false);
                  console.log(`🏁 Match Abandoned! Team ${currentPlayer === 1 ? '1' : '2'} abandoned. Team ${currentPlayer === 1 ? '2' : '1'} wins 12-0!`);
                }}
              >
                Yes
              </button>
              <button
                className="ap-confirmation-btn ap-confirm-no"
                onClick={() => setShowAbandonConfirmation(false)}
              >
                No
              </button>
            </div>
          </div>
        </div>
      )}

      {/* A_003 Boost Split Dialog */}
      {a003ChoiceState && (
        <div className="game-end-overlay ap-confirmation-overlay">
          <div className="ap-confirmation-popup">
            <div className="ap-confirmation-header">
              <h2 className="ap-confirmation-title">Any Boost — Split your boosts</h2>
            </div>
            <div className="ap-confirmation-body">
              <p className="ap-confirmation-message">
                {a003ChoiceState.count} eligible position{a003ChoiceState.count !== 1 ? 's' : ''} (CD, CM, CF) in play.
                Distribute {a003ChoiceState.count} boost{a003ChoiceState.count !== 1 ? 's' : ''}:
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', margin: '12px 0', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ color: '#4258E0', fontWeight: 'bold', width: '70px' }}>Attack</span>
                  <button className="ap-confirmation-btn ap-confirm-no" style={{ width: '28px', padding: '2px' }}
                    onClick={() => setA003ChoiceState(prev => ({ ...prev, attackSplit: Math.max(0, prev.attackSplit - 1) }))}>−</button>
                  <span style={{ color: '#ffffff', fontWeight: 'bold', fontSize: '18px', width: '24px', textAlign: 'center' }}>{a003ChoiceState.attackSplit}</span>
                  <button className="ap-confirmation-btn ap-confirm-no" style={{ width: '28px', padding: '2px' }}
                    onClick={() => setA003ChoiceState(prev => ({ ...prev, attackSplit: Math.min(prev.count, prev.attackSplit + 1) }))}>+</button>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ color: '#E74C3C', fontWeight: 'bold', width: '70px' }}>Defense</span>
                  <span style={{ color: '#ffffff', fontWeight: 'bold', fontSize: '18px', width: '24px', textAlign: 'center', marginLeft: '38px' }}>{a003ChoiceState.count - a003ChoiceState.attackSplit}</span>
                </div>
              </div>
            </div>
            <div className="ap-confirmation-buttons">
              <button className="ap-confirmation-btn ap-confirm-yes" onClick={handleA003Confirm}>
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AP Confirmation Popup */}
      {showAPConfirmation && (
        <div className="game-end-overlay ap-confirmation-overlay">
          <div className="ap-confirmation-popup">
            <div className="ap-confirmation-header">
              <h2 className="ap-confirmation-title">⚠️ Confirm Action</h2>
            </div>
            <div className="ap-confirmation-body">
              <p className="ap-confirmation-message">
                {pendingAction?.type === 'finish_turn'
                  ? 'Are you sure you want to Finish Turn without using Kickopp or Upgrade?'
                  : pendingAction?.type === 'move_activate'
                  ? 'Are you sure? This Move will use your last Action Point without Upgrade or Kickopp.'
                  : pendingAction?.type === 'buy_action_card_facedown'
                  ? `Are you sure you want to buy a random Action Card (${pendingAction.buyCost} AP) without using Kickopp or Upgrade first?`
                  : pendingAction?.type === 'buy_action_card_revealed'
                  ? `Are you sure you want to buy this Action Card (${pendingAction.buyCost} AP) without using Kickopp or Upgrade first?`
                  : pendingAction?.type === 'upgrade_kickopp_warning'
                  ? 'Are you sure you want to Upgrade? Your active Kickopp Action Card effect will be lost without being used.'
                  : 'Are you sure you want to use all Action Points without Upgrade or Kickopp?'}
              </p>
            </div>
            <div className="ap-confirmation-buttons">
              <button
                className="ap-confirmation-btn ap-confirm-yes"
                onClick={executePendingAction}
              >
                Yes
              </button>
              <button
                className="ap-confirmation-btn ap-confirm-no"
                onClick={() => {
                  setPendingAction(null);
                  setShowAPConfirmation(false);
                  setDraggedCard(null);
                }}
              >
                No
              </button>
            </div>
          </div>
        </div>
      )}

      {/* End Game Popup */}
      {gameEnded && gameResult && (
        <div className="game-end-overlay">
          <div className="game-end-popup">
            {/* Kickopp Logo */}
            <div className="game-end-logo-section">
              <div className="game-end-logo-k">
                <div className="k-vertical"></div>
                <div className="k-diagonals">
                  <div className="k-upper"></div>
                  <div className="k-lower"></div>
                </div>
              </div>
            </div>

            {/* Match Complete Title */}
            <div className="game-end-header">
              <h1 className="game-end-title">MATCH COMPLETE</h1>
              <div className="game-end-subtitle">
                {gameResult.reason === 'score' && 'Victory by Score'}
                {gameResult.reason === 'turns' && 'After 20 Turns'}
                {gameResult.reason === 'abandon' && 'Match Abandoned'}
              </div>
            </div>

            {/* Winner Announcement */}
            <div className="game-end-result">
              {gameResult.winner === 'draw' ? (
                <h2 className="game-end-result-text draw">IT'S A DRAW!</h2>
              ) : (
                <h2 className={`game-end-result-text ${gameResult.winner}`}>
                  {gameResult.winner === 'player1' ? 'TEAM 1 WINS!' : 'TEAM 2 WINS!'}
                </h2>
              )}
            </div>

            {/* Challenge Level + Continuation Info */}
            {challengeMode && cpuStage && (() => {
              const humanWinnerKey = aiPlayer === 1 ? 'player2' : 'player1';
              const won = gameResult.winner === humanWinnerKey;
              const humanScore = aiPlayer === 1 ? gameResult.p2Score : gameResult.p1Score;
              const isSuperWin = won && humanScore === 12;
              const levelNum = parseInt(cpuStage.Stage, 10);
              const meetsRequirement = won && (
                challengeDifficulty === 'easy' ? true :
                challengeDifficulty === 'medium' ? (levelNum <= 4 ? true : isSuperWin) :
                isSuperWin
              );
              const superWinRequired = challengeDifficulty === 'hard' || (challengeDifficulty === 'medium' && levelNum >= 5);
              let continuationText;
              if (meetsRequirement) {
                continuationText = levelNum < 8 ? `Continuing to Challenge Trial ${levelNum + 1}` : 'Challenge Complete! 🎉';
              } else if (won) {
                // Won by score but Super Win was required (medium/hard)
                if (challengeDifficulty === 'medium') {
                  const hasRetry = (challengeLives ?? 1) > 0;
                  continuationText = hasRetry
                    ? `Retry Trial ${levelNum} (last chance)`
                    : `Restart from Trial 1`;
                } else {
                  continuationText = `Restart from Trial 1`;
                }
              } else if (challengeDifficulty === 'easy') {
                const livesLeft = (challengeLives ?? 3) - 1;
                continuationText = livesLeft <= 0
                  ? `Out of lives — restart from Trial 1 (lives reset to 3)`
                  : `Retry Challenge Trial ${levelNum} (${livesLeft} ${livesLeft === 1 ? 'life' : 'lives'} left)`;
              } else if (challengeDifficulty === 'medium') {
                const hasRetry = (challengeLives ?? 1) > 0;
                continuationText = hasRetry
                  ? `Retry Challenge Trial ${levelNum} (last chance)`
                  : `Out of chances — Restart from Trial 1`;
              } else {
                continuationText = 'Challenge reset — back to Trial 1';
              }
              const swBannerColor = isSuperWin ? '#fbbf24' : (won ? '#f97316' : '#ef4444');
              const swBannerBg   = isSuperWin ? 'rgba(251,191,36,0.12)' : (won ? 'rgba(249,115,22,0.15)' : 'rgba(239,68,68,0.12)');
              return (
                <div className="game-end-challenge-info">
                  <span className="game-end-challenge-level">CHALLENGE TRIAL {levelNum}</span>
                  {superWinRequired && (
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: '10px',
                      padding: '10px 16px', borderRadius: '10px', margin: '6px 0 2px',
                      background: swBannerBg, border: `1.5px solid ${swBannerColor}`,
                    }}>
                      <span style={{ fontSize: '22px' }}>{isSuperWin ? '⚡' : '⚠️'}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: '800', fontSize: '13px', letterSpacing: '1px', color: swBannerColor }}>
                          {isSuperWin ? 'SUPER WIN ACHIEVED!' : 'SUPER WIN REQUIRED'}
                        </div>
                        {!isSuperWin && (
                          <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '3px' }}>
                            {won
                              ? 'Win is not enough — eliminate all 12 CPU cards to advance'
                              : 'You must eliminate all 12 CPU cards to pass this trial'}
                          </div>
                        )}
                      </div>
                      <span style={{ fontWeight: '900', fontSize: '20px', color: swBannerColor }}>
                        {isSuperWin ? '✓' : '✗'}
                      </span>
                    </div>
                  )}
                  <span className="game-end-challenge-continuation">{continuationText}</span>
                </div>
              );
            })()}

            {/* Friendly Super Win result — shown when the player chose Super Win mode */}
            {!challengeMode && requireSuperWin && cpuStage && (() => {
              const humanWinnerKey = aiPlayer === 1 ? 'player2' : 'player1';
              const won = gameResult.winner === humanWinnerKey;
              const humanScore = aiPlayer === 1 ? gameResult.p2Score : gameResult.p1Score;
              const isSuperWin = won && humanScore === 12;
              const col = isSuperWin ? '#fbbf24' : (won ? '#f97316' : '#ef4444');
              const bg  = isSuperWin ? 'rgba(251,191,36,0.12)' : (won ? 'rgba(249,115,22,0.14)' : 'rgba(239,68,68,0.1)');
              return (
                <div style={{ display:'flex', alignItems:'center', gap:'10px', padding:'10px 16px', borderRadius:'10px', margin:'2px 0 6px', background:bg, border:`1.5px solid ${col}` }}>
                  <span style={{ fontSize:'22px' }}>{isSuperWin ? '⚡' : (won ? '⚠️' : '💔')}</span>
                  <div style={{ flex:1 }}>
                    <div style={{ fontWeight:'800', fontSize:'13px', letterSpacing:'1px', color:col }}>
                      {isSuperWin ? 'SUPER WIN!' : 'SUPER WIN NOT ACHIEVED'}
                    </div>
                    <div style={{ fontSize:'11px', color:'#94a3b8', marginTop:'3px' }}>
                      {isSuperWin
                        ? 'All 12 CPU cards eliminated — perfect result!'
                        : won
                          ? 'You won on score but didn\'t eliminate all 12 CPU cards'
                          : 'You must win the match and eliminate all 12 CPU cards'}
                    </div>
                  </div>
                  <span style={{ fontWeight:'900', fontSize:'20px', color:col }}>{isSuperWin ? '✓' : '✗'}</span>
                </div>
              );
            })()}

            {/* Score Display */}
            <div className="game-end-score">
              <div className={`game-end-score-card ${gameResult.winner === 'player1' ? 'winner' : ''}`}>
                <div className="game-end-score-label">TEAM 1</div>
                <div className="game-end-score-value">{gameResult.p1Score}</div>
              </div>
              <div className="game-end-score-separator">-</div>
              <div className={`game-end-score-card ${gameResult.winner === 'player2' ? 'winner' : ''}`}>
                <div className="game-end-score-label">TEAM 2</div>
                <div className="game-end-score-value">{gameResult.p2Score}</div>
              </div>
            </div>

            {/* Match Summary Stats */}
            {(() => {
              const p1Cols = [0,1,3,5], p2Cols = [2,4,6,7];
              const boardCards = Object.entries(cardGameZones).filter(([,c]) => c?.Card_Id);
              const p1OnBoard = boardCards.filter(([k]) => p1Cols.includes(parseInt(k.split('-')[0]))).length;
              const p2OnBoard = boardCards.filter(([k]) => p2Cols.includes(parseInt(k.split('-')[0]))).length;
              const p1Eliminated = 11 - p2OnBoard;
              const p2Eliminated = 11 - p1OnBoard;
              const p1Boosts = Object.values(player1AppliedBoosts).reduce((s,b) => s + (b.attack||0) + (b.defense||0), 0);
              const p2Boosts = Object.values(player2AppliedBoosts).reduce((s,b) => s + (b.attack||0) + (b.defense||0), 0);
              const statRow = (label, v1, v2) => (
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'6px 0', borderBottom:'1px solid rgba(51,65,85,0.4)' }}>
                  <span style={{ color:'#4FA8DB', fontWeight:'700', fontSize:'13px', minWidth:'32px', textAlign:'center' }}>{v1}</span>
                  <span style={{ color:'#64748b', fontSize:'11px', letterSpacing:'0.5px', textTransform:'uppercase', flex:1, textAlign:'center' }}>{label}</span>
                  <span style={{ color:'#FF6B6B', fontWeight:'700', fontSize:'13px', minWidth:'32px', textAlign:'center' }}>{v2}</span>
                </div>
              );
              return (
                <div style={{ margin:'16px 0', background:'rgba(0,0,0,0.25)', borderRadius:'10px', padding:'10px 20px' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'8px' }}>
                    <span style={{ color:'#4FA8DB', fontSize:'11px', fontWeight:'800', letterSpacing:'1px' }}>TEAM 1</span>
                    <span style={{ color:'#64748b', fontSize:'11px', letterSpacing:'1px' }}>MATCH STATS</span>
                    <span style={{ color:'#FF6B6B', fontSize:'11px', fontWeight:'800', letterSpacing:'1px' }}>TEAM 2</span>
                  </div>
                  {statRow('Turns played',     player1TurnCount,        player2TurnCount)}
                  {statRow('Cards eliminated', p1Eliminated,             p2Eliminated)}
                  {statRow('Boosts applied',   p1Boosts,                 p2Boosts)}
                  {statRow('Level reached',    player1MaxActionPoints,   player2MaxActionPoints)}
                </div>
              );
            })()}

            {/* Action Buttons */}
            <div className="game-end-actions">
              {challengeMode && onChallengeEnd ? (() => {
                const hwKey = aiPlayer === 1 ? 'player2' : 'player1';
                const won_ = gameResult.winner === hwKey;
                const hScore = aiPlayer === 1 ? gameResult.p2Score : gameResult.p1Score;
                const isSuperWin_ = won_ && hScore === 12;
                const levelNum_ = cpuStage ? parseInt(cpuStage.Stage, 10) : 1;
                const meets = won_ && (
                  challengeDifficulty === 'easy' ? true :
                  challengeDifficulty === 'medium' ? (levelNum_ <= 4 ? true : isSuperWin_) :
                  isSuperWin_
                );
                // Does this loss trigger a full reset (no retries left)?
                // Hard: always. Medium: when lives=0 (last retry already spent).
                // Easy: when lives<=1 (losing one more life empties the pool).
                const willReset = !meets && (
                  challengeDifficulty === 'hard' ? true :
                  challengeDifficulty === 'medium' ? (challengeLives ?? 1) <= 0 :
                  /* easy */ (challengeLives ?? 3) <= 1
                );
                const stopMusic = () => {
                  if (gameplayMusicRef && gameplayMusicRef.current) {
                    gameplayMusicRef.current.pause();
                    gameplayMusicRef.current.currentTime = 0;
                    gameplayMusicRef.current = null;
                  }
                };
                return (
                  <>
                    {meets ? (
                      /* WIN: collect rewards */
                      <button
                        className="game-end-btn new-match"
                        onClick={() => { stopMusic(); onChallengeEnd({ win: true, superWin: isSuperWin_ }); }}
                      >
                        <span className="btn-icon">{isSuperWin_ ? '⚡' : '🏆'}</span>
                        <span className="btn-text">{isSuperWin_ ? 'Super Win! Collect Rewards' : 'Collect Rewards'}</span>
                      </button>
                    ) : willReset ? null : (
                      /* LOSS with retry still available: show retry popup */
                      <button
                        className="game-end-btn new-match"
                        onClick={() => { stopMusic(); setShowRetryPopup(true); }}
                      >
                        <span className="btn-icon">🔄</span>
                        <span className="btn-text">Continue</span>
                      </button>
                    )}
                    <button
                      className="game-end-btn back-menu"
                      onClick={() => {
                        stopMusic();
                        // Always go through onChallengeEnd so lives/rewardPending
                        // are written to storage before navigating — prevents the
                        // lives-not-decrement loop on repeated losses.
                        onChallengeEnd({ win: meets, superWin: isSuperWin_, skipToChallenge: true });
                      }}
                    >
                      <span className="btn-icon">🏆</span>
                      <span className="btn-text">Challenge Menu</span>
                    </button>
                    {/* RESET case: also offer Main Menu so the player isn't forced back to Challenge */}
                    {willReset && (
                      <button
                        className="game-end-btn back-menu"
                        style={{ background: 'linear-gradient(135deg,#334155,#1e293b)', borderColor: '#64748b' }}
                        onClick={() => {
                          stopMusic();
                          onChallengeEnd({ win: false, superWin: isSuperWin_, skipToMenu: true });
                        }}
                      >
                        <span className="btn-icon">🏠</span>
                        <span className="btn-text">Menu</span>
                      </button>
                    )}
                  </>
                );
              })() : (
                <>
                  <button
                    className="game-end-btn new-match"
                    onClick={() => {
                      if (soundEnabled) {
                        const impactSound = new Audio('/production-elements-impactor-e-188986.mp3');
                        impactSound.volume = 0.3;
                        impactSound.play().catch(() => {});
                      }
                      if (gameplayMusicRef && gameplayMusicRef.current) {
                        gameplayMusicRef.current.pause();
                        gameplayMusicRef.current.currentTime = 0;
                        gameplayMusicRef.current = null;
                      }
                      localStorage.setItem('kickopp-auto-start-match', 'true');
                      setTimeout(() => window.location.reload(), 100);
                    }}
                  >
                    <span className="btn-icon">⚽</span>
                    <span className="btn-text">New Match</span>
                  </button>
                  <button
                    className="game-end-btn back-menu"
                    onClick={() => {
                      if (gameplayMusicRef && gameplayMusicRef.current) {
                        gameplayMusicRef.current.pause();
                        gameplayMusicRef.current.currentTime = 0;
                        gameplayMusicRef.current = null;
                      }
                      window.location.reload();
                    }}
                  >
                    <span className="btn-icon">🏠</span>
                    <span className="btn-text">Back to Menu</span>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>

    {/* DragOverlay intentionally empty - custom dragging-card div handles the visual (with mobile coordinate conversion) */}
    <DragOverlay dropAnimation={null} />

    </DndContext>

    {/* Stage-specific tutorial overlays — rendered outside DndContext so they sit above everything */}
    {showWarmupTutorial && (
      <WarmupTutorial
        isVertical={verticalBoard}
        onComplete={() => setShowWarmupTutorial(false)}
        onSkip={() => setShowWarmupTutorial(false)}
      />
    )}
    {showBattleTutorial && (
      <BattleTutorial
        isVertical={verticalBoard}
        onComplete={() => setShowBattleTutorial(false)}
        onSkip={() => setShowBattleTutorial(false)}
      />
    )}
    </>
  );
};

export default BoardArena;